import React, { useEffect, Children, useState, PureComponent } from 'react';
import Imgix, { Picture, Source } from 'react-imgix';
import classnames from 'classnames';
import hash from 'object-hash';
import isNode from 'in-node';
import hashIndex from 'hash-index';
import snakeCase from 'lodash/snakeCase';
import { Transition, CSSTransition } from 'react-transition-group';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import Masked from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { format, isSameDay, isBefore } from 'date-fns';
import ReactDOM from 'react-dom';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import camelCase from 'lodash/camelCase';
import Headroom from 'react-headroom';
import Measure from 'react-measure';
import once from 'lodash/once';
import times from 'lodash/times';
import flatten from 'lodash/flatten';
import isInteger from 'lodash/isInteger';
import sortBy from 'lodash/sortBy';
import pickBy from 'lodash/pickBy';
import toPairs from 'lodash/toPairs';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import upperFirst from 'lodash/upperFirst';
import uniq from 'lodash/uniq';
import pick from 'lodash/pick';
import ScrollLock from 'react-scrolllock';
import ReactModal from 'react-modal';
import { Slider, Rail, Handles, Tracks } from 'react-compound-slider';
import Checkit from 'checkit';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import Autosuggest from 'react-autosuggest';
import StarRatings from '@deal/react-star-ratings';
import Observer from '@researchgate/react-intersection-observer';
import { parsePhoneNumberCharacters, formatPhoneNumber, PhoneInput } from 'react-phone-number-input';
import Select from 'react-responsive-ui/commonjs/Select';
import Popover from 'react-awesome-popover';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

function addDefaultImgixParams(params) {
  return Object.assign({
    auto: ['compress', 'format'],
    ch: ['Width', 'DPR', 'Save-Data'] // Default to setting all client-hints (https://docs.imgix.com/apis/url/format/ch)

  }, params);
}

var Image = function Image(props) {
  var imgixParams = props.imgixParams,
      htmlAttributes = props.htmlAttributes,
      className = props.className,
      _a = props.lazyLoad,
      lazyLoad = _a === void 0 ? false : _a,
      rest = __rest(props // In case LazySizes has not been initialized yet, we do so as a side-effect of rendering.
  , ["imgixParams", "htmlAttributes", "className", "lazyLoad"]); // In case LazySizes has not been initialized yet, we do so as a side-effect of rendering.


  useEffect(function () {
    Image.load();
  }, []);
  var imgixParamsWithDefault = addDefaultImgixParams(imgixParams); // When lazy-loading is enabled, LazySizes directly mutates the DOM node, so we do not allow
  //  React to overwrite attributes/props set by that library (src, srcset, sizes, and className).
  //  This is important for render "purity", especially when hydrating a SSR response.
  //
  // For example: Images are eagerly loaded as soon as the initial HTML is parsed (LazySizes
  //  is initialized outside of the React render pipeline). Therefore, it's possible for images
  //  to already be loaded before the corresponding <Image> tag is hydrated/rendered on the client
  //  side for the first time. LazySizes will change the classnames independently of React, so by
  //  the time `React.hydrate` is called the document HTML may have been mutated. In that case, we
  //  need to set the appropriate className (`lazyloading` or `lazyloaded` instead of `lazyload`)
  //  in order to match the DOM. In general, we ensure certain classes matches those currently set
  //  in the DOM since that state is only partially managed by React.

  var lazySizesClassNames = ['ls-is-cached', 'lazyloading', 'lazyloaded', 'lazypreload', 'lazyerror', 'lazyautosizes'];
  var existingLazySizesClasses = []; // We generate a unique identifier based on the props, in order to find this element in the DOM
  //  during client-side rehydration. There are edge-cases where this won't work (multiple images
  //  with identical props), so we use a className as a "best-effort". In order to generate truly
  //  unique IDs we could theoretically hash the path to the React component from the root node.
  //  That data is not currently exposed by React.
  //
  // See: https://github.com/facebook/react/issues/15435

  var hashedPropsClassName = "image-" + hash(props).slice(0, 8);

  if (lazyLoad && !isNode) {
    // Get the DOM node of an element that matches this component's props exactly. It may not be
    //  the same element as we're actively rendering now: there's an edge case where the same image
    //  is rendered twice, once below-the-fold and once above-the-fold, in which case they will
    //  have different LazySizes classnames and attributes. In that scenario we prefer the image that
    //  is the "most loaded". The markup rendered by React will not match the markup in the DOM, so
    //  we have to suppress the warnings from React hydration.
    var loadingStateClassNames_1 = ['lazyload', 'lazypreload', 'lazyloading', 'lazyloaded'];
    var matchingDomNodes = Array.from(document.getElementsByClassName(hashedPropsClassName));
    var mostLoadedDomNode = matchingDomNodes.sort(function (a, b) {
      var stateOfImageA = loadingStateClassNames_1.findIndex(function (className) {
        return a.classList.contains(className);
      });
      var stateOfImageB = loadingStateClassNames_1.findIndex(function (className) {
        return b.classList.contains(className);
      });
      return stateOfImageB - stateOfImageA;
    })[0];

    if (mostLoadedDomNode) {
      existingLazySizesClasses = Array.from(mostLoadedDomNode.classList).filter(function (className) {
        return lazySizesClassNames.indexOf(className) !== -1;
      }); // Allow LazySizes to manage `src`, `srcset`, and `sizes` itself.
      //  We set those attributes as `data-` attributes instead. LazySizes will manage converting
      //  those to the corresponding DOM attributes for us, so we always prefer the existing DOM
      //  attributes.

      htmlAttributes = __assign({
        src: mostLoadedDomNode.getAttribute('src') || undefined,
        srcSet: mostLoadedDomNode.getAttribute('srcset') || undefined,
        sizes: mostLoadedDomNode.getAttribute('sizes') || undefined,
        suppressHydrationWarning: matchingDomNodes.length > 1
      }, htmlAttributes);
    }
  }

  var classNames = classnames.apply(void 0, [className, hashedPropsClassName, {
    lazyload: lazyLoad && existingLazySizesClasses.length === 0
  }].concat(existingLazySizesClasses));
  return React.createElement(Imgix, __assign({
    htmlAttributes: htmlAttributes,
    className: classNames,
    attributeConfig: lazyLoad ? {
      src: 'data-src',
      srcSet: 'data-srcset',
      sizes: 'data-sizes'
    } : undefined,
    imgixParams: imgixParamsWithDefault
  }, rest));
}; // We expose this method so that in the browser, we can initialize LazySizes as early
//  as possible. We do not want to wait for the React application to be fully hydrated
//  and mounted before loading images.


Image.load = function () {
  if (!isNode) {
    require('lazysizes');

    require('lazysizes/plugins/attrchange/ls.attrchange');
  }
};

var Source$1 = function Source$$1(_a) {
  var imgixParams = _a.imgixParams,
      props = __rest(_a, ["imgixParams"]);

  imgixParams = addDefaultImgixParams(imgixParams);
  return React.createElement(Source, __assign({
    imgixParams: imgixParams
  }, props));
};

var Picture$1 = function Picture$$1(props) {
  return React.createElement(Picture, __assign({}, props));
}; // Exports

var styles = {"avatar":"styles_avatar__gdIPG flex justify-center items-center","color-one":"styles_color-one__3fKEt","color-two":"styles_color-two__2_I6C","color-three":"styles_color-three__1exxQ","color-four":"styles_color-four__3L3_P","color-five":"styles_color-five__26LZl","colorOne":"styles_color-one__3fKEt","colorTwo":"styles_color-two__2_I6C","colorThree":"styles_color-three__1exxQ","colorFour":"styles_color-four__3L3_P","colorFive":"styles_color-five__26LZl"};

// We will apply one of these classes for users without avatars

var CLASS_PREFIX = 'color-';
var PALETTE = Object.keys(styles).filter(function (key) {
  return key.slice(0, CLASS_PREFIX.length) === CLASS_PREFIX;
}).map(function (className) {
  return styles[className];
}); // Convert the size prop to a pixel dimension so we can request properly sized avatar images

function sizeToDimension(size) {
  switch (size) {
    case 'small':
      return 24;

    case 'medium':
      return 32;

    case 'large':
      return 44;

    case 'xlarge':
      return 64;
  }
}
/**
 * Displays a chat participants photo/avatar, or a consistently colored circle with their initials
 */


var Avatar = function Avatar(_a) {
  var name = _a.name,
      hash$$1 = _a.hash,
      src = _a.src,
      _b = _a.size,
      size = _b === void 0 ? 'medium' : _b,
      className = _a.className,
      props = __rest(_a, ["name", "hash", "src", "size", "className"]);

  var imageSize = typeof size === 'number' ? size : sizeToDimension(size);
  var classes = classnames(className, styles.avatar);

  if (src) {
    return React.createElement(Image, {
      src: src,
      height: imageSize,
      width: imageSize,
      className: classes,
      htmlAttributes: props
    });
  } else {
    // Concatenate first and last initials
    var names_1 = name.trim().split(' ');
    var initials = names_1.filter(function (_name, i) {
      return i === 0 || i === names_1.length - 1;
    }).map(function (name) {
      return name.charAt(0).toUpperCase();
    }).join(''); // Choose a consistent color for this user's avatar

    var maxPalette = PALETTE.length > 1 ? PALETTE.length - 1 : 1;
    var color = PALETTE[hashIndex(hash$$1 || name, maxPalette)];
    return React.createElement("div", __assign({
      className: classnames(color, classes),
      style: {
        height: imageSize + "px",
        width: imageSize + "px",
        flexBasis: imageSize + "px",
        lineHeight: imageSize + "px",
        fontSize: Math.round(imageSize / 2.2)
      }
    }, props), initials);
  }
};

var styles$1 = {"common":"styles_common__25RxR","success":"styles_success__mE3P6 styles_common__25RxR","info":"styles_info__1MUkN styles_common__25RxR","regular":"styles_regular__16FRp styles_common__25RxR","warning":"styles_warning__2l2bw styles_common__25RxR","danger":"styles_danger__22x-x styles_common__25RxR","primary":"styles_primary__QO-AI styles_common__25RxR"};

var Badge = function Badge(_a) {
  var className = _a.className,
      _b = _a.type,
      type = _b === void 0 ? 'primary' : _b,
      children = _a.children;
  var classes = classnames(styles$1[type], className);
  return React.createElement("span", {
    className: classes
  }, children);
};

var styles$2 = {"item":"styles_item__3Gv3F","none":"styles_none__3jj7_","extra-tight":"styles_extra-tight__9332O","tight":"styles_tight__3mrja","normal":"styles_normal__22KdM","loose":"styles_loose__3WIh9","extra-loose":"styles_extra-loose__3GTFd","extraTight":"styles_extra-tight__9332O","extraLoose":"styles_extra-loose__3GTFd"};

var Box = function Box(_a) {
  var _b;

  var column = _a.column,
      wrap = _a.wrap,
      justify = _a.justify,
      items = _a.items,
      content = _a.content,
      _c = _a.spacing,
      spacing = _c === void 0 ? 'normal' : _c,
      className = _a.className,
      children = _a.children,
      props = _a.props; // Always include "flex", the spacing class,  and the custom class (if provided)

  var classes = classnames('flex', styles$2[spacing], className, (_b = {
    'flex-wrap': wrap,
    'flex-column': column
  }, _b["justify-" + justify] = !!justify, _b["items-" + items] = !!items, _b["content-" + content] = !!content, _b));
  return React.createElement("div", __assign({
    className: classes
  }, props), Children.map(children, function (child) {
    if (child && ( // Check if the child is a string or number before accessing ".type"
    typeof child === 'string' || typeof child === 'number' || // Child is a React element, but don't wrap BoxItem's or Fragments
    React.isValidElement(child) && child.type !== boxItemElement.type && child.type !== fragmentElement.type)) {
      return React.createElement(BoxItem, null, child);
    } else {
      return child;
    }
  }));
};

var BoxItem = function BoxItem(_a) {
  var _b;

  var className = _a.className,
      grow = _a.grow,
      shrink = _a.shrink,
      self = _a.self,
      rest = __rest(_a, ["className", "grow", "shrink", "self"]);

  var classes = classnames(styles$2.item, className, (_b = {}, _b["self-" + self] = !!self, _b));
  var style = {
    flexGrow: grow,
    flexShrink: shrink
  };
  return React.createElement("div", __assign({
    className: classes,
    style: style
  }, rest));
};

Box.Item = BoxItem; // Create a single BoxItem and use that for type comparisons
//   See: https://github.com/gaearon/react-hot-loader#checking-element-types

var boxItemElement = React.createElement(BoxItem, null);
var fragmentElement = React.createElement(React.Fragment, null);

var styles$3 = {"button":"styles_button__3Yr7O bold border-box align-middle items-center justify-center","normal-box-shadow":"styles_normal-box-shadow__ZDRl8","heavy-box-shadow":"styles_heavy-box-shadow__scDgj","border":"styles_border__1lwcy","primary-disabled":"styles_primary-disabled__1uBUh","primary":"styles_primary__15nU3 styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_primary-disabled__1uBUh","primary-outline":"styles_primary-outline__1SYQD styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_border__1lwcy","secondary":"styles_secondary__3o6Gs styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_border__1lwcy styles_normal-box-shadow__ZDRl8","black":"styles_black__1lt79 styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8","black-outline":"styles_black-outline__1fgOc styles_secondary__3o6Gs styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_border__1lwcy styles_normal-box-shadow__ZDRl8","destructive":"styles_destructive__2CJ2V styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8","success":"styles_success__21LEY styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8","primary-contrast":"styles_primary-contrast__2wNCF styles_primary-outline__1SYQD styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_border__1lwcy styles_heavy-box-shadow__scDgj","secondary-contrast":"styles_secondary-contrast__3nXUd styles_primary-contrast__2wNCF styles_primary-outline__1SYQD styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_border__1lwcy styles_heavy-box-shadow__scDgj","clear":"styles_clear__Qgi7u styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_border__1lwcy","clear-black":"styles_clear-black__1w5uj styles_button__3Yr7O bold border-box align-middle items-center justify-center","link":"styles_link__3xbD4 styles_button__3Yr7O bold border-box align-middle items-center justify-center","xsmall":"styles_xsmall__2YWfw","small":"styles_small__AIqd8","medium":"styles_medium__3Gg19","large":"styles_large__YVPD8","xlarge":"styles_xlarge__1Arun","blue":"styles_blue__2S9t7 styles_button__3Yr7O bold border-box align-middle items-center justify-center","normalBoxShadow":"styles_normal-box-shadow__ZDRl8","heavyBoxShadow":"styles_heavy-box-shadow__scDgj","primaryDisabled":"styles_primary-disabled__1uBUh","primaryOutline":"styles_primary-outline__1SYQD styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_border__1lwcy","blackOutline":"styles_black-outline__1fgOc styles_secondary__3o6Gs styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_border__1lwcy styles_normal-box-shadow__ZDRl8","primaryContrast":"styles_primary-contrast__2wNCF styles_primary-outline__1SYQD styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_border__1lwcy styles_heavy-box-shadow__scDgj","secondaryContrast":"styles_secondary-contrast__3nXUd styles_primary-contrast__2wNCF styles_primary-outline__1SYQD styles_button__3Yr7O bold border-box align-middle items-center justify-center styles_normal-box-shadow__ZDRl8 styles_border__1lwcy styles_heavy-box-shadow__scDgj","clearBlack":"styles_clear-black__1w5uj styles_button__3Yr7O bold border-box align-middle items-center justify-center"};

/**
 * For Buttons with complex content (i.e. not just a string), we wrap each text node in a <span>
 * This allows us to style the children with accessors like :last-child
 */

var wrapChildTextNodesInSpans = function wrapChildTextNodesInSpans(children) {
  return React.Children.map(children, function (child) {
    if (typeof child === 'string') {
      return React.createElement("span", null, child);
    } else {
      return child;
    }
  });
};

var Button = function Button(_a) {
  var _b = _a.variant,
      variant = _b === void 0 ? 'primary' : _b,
      _c = _a.size,
      size = _c === void 0 ? 'medium' : _c,
      disabled = _a.disabled,
      testId = _a.testId,
      className = _a.className,
      children = _a.children,
      rest = __rest(_a, ["variant", "size", "disabled", "testId", "className", "children"]);

  var classes = classnames(styles$3[variant], styles$3[size], className);
  return React.createElement("button", __assign({
    className: classes,
    disabled: disabled,
    "data-testid": testId
  }, rest), React.Children.count(children) > 1 ? wrapChildTextNodesInSpans(children) : children);
};

var styles$4 = {"card":"styles_card__1Qd8e","accent-one":"styles_accent-one__2CXu6","accent-two":"styles_accent-two__20Ykb","accent-three":"styles_accent-three__3d5e8","accent-four":"styles_accent-four__xeTQv","accent-five":"styles_accent-five__24i86","accent-six":"styles_accent-six__3ngzt","accent-seven":"styles_accent-seven__2jvun","accent-eight":"styles_accent-eight__Xx3o8","accent-nine":"styles_accent-nine__wFxpO","accentOne":"styles_accent-one__2CXu6","accentTwo":"styles_accent-two__20Ykb","accentThree":"styles_accent-three__3d5e8","accentFour":"styles_accent-four__xeTQv","accentFive":"styles_accent-five__24i86","accentSix":"styles_accent-six__3ngzt","accentSeven":"styles_accent-seven__2jvun","accentEight":"styles_accent-eight__Xx3o8","accentNine":"styles_accent-nine__wFxpO"};

// We will apply one of these classes if if "accentHash" is provided

var CLASS_PREFIX$1 = 'accent-';
var PALETTE$1 = Object.keys(styles$4).filter(function (key) {
  return key.slice(0, CLASS_PREFIX$1.length) === CLASS_PREFIX$1;
}).map(function (className) {
  return styles$4[className];
});

var Card = function Card(_a) {
  var accent = _a.accent,
      accentHash = _a.accentHash,
      className = _a.className,
      children = _a.children,
      style = _a.style; // Choose an accent color for this card
  // For numeric values, use modulo to ensure incrementing numbers produce discrete colors

  var accentIndex;

  if (typeof accentHash === 'number') {
    accentIndex = accentHash % PALETTE$1.length;
  } else if (accentHash) {
    accentIndex = hashIndex(accentHash, PALETTE$1.length - 1);
  }

  var accentClassName = accentIndex !== undefined && PALETTE$1[accentIndex];
  return React.createElement("div", {
    className: classnames(className, accentClassName, styles$4.card),
    style: __assign({
      boxShadow: accent && "inset 0 2px " + accent
    }, style)
  }, children);
};

var styles$5 = {"icon":"styles_icon__3xVMY"};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Bike = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties(_ref, ["styles"]);

  return React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#9763DC",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    d: "M24.214 37.742a8.201 8.201 0 1 1-16.402-.001 8.201 8.201 0 0 1 16.402 0m31.974.001a8.201 8.201 0 1 1-16.403-.001 8.201 8.201 0 0 1 16.403 0",
    fill: "#FFF",
    opacity: ".5"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "1.5",
    d: "M40.804 23.449H24.627l-9.16 14.38h14.511l11.814-11.734m6.26 11.735l-9.2-19.075 7.661-.036a2.694 2.694 0 0 1 0 5.388M29.688 37.83l-6.312-17.2"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M27.113 19.416c0-.75-.608-1.358-1.358-1.358h-5.392a1.727 1.727 0 0 0 0 3.453c.642 0 5.659-.764 5.659-.764a1.357 1.357 0 0 0 1.09-1.331"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "1.5",
    d: "M29.413 41.354h3.369"
  })));
});

var _extends$1 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$1(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Camera = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$1(_ref, ["styles"]);

  return React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#F25656",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#FFB7B7",
    d: "M49.333 48.25H14.667A4.681 4.681 0 0 1 10 43.583V22.917c0-2.566 2.1-4.667 4.667-4.667h34.666A4.682 4.682 0 0 1 54 22.917v20.666a4.682 4.682 0 0 1-4.667 4.667"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M41.414 33.25a9.414 9.414 0 1 1-18.828 0 9.414 9.414 0 0 1 18.828 0"
  }), React.createElement("path", {
    stroke: "#F25656",
    strokeWidth: "3",
    d: "M41.414 33.25a9.414 9.414 0 1 1-18.828 0 9.414 9.414 0 0 1 18.828 0z"
  }), React.createElement("path", {
    stroke: "#FFB7B7",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M42.95 13.833h6"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M15.783 24.083h2.792"
  })));
});

var _extends$2 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$2(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Charters = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$2(_ref, ["styles"]);

  return React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#142E6E",
    d: "M0 0h64v64H0z"
  }), React.createElement("g", {
    fill: "#FFF",
    fillRule: "nonzero"
  }, React.createElement("path", {
    d: "M30 23h2v24h-2z"
  }), React.createElement("path", {
    d: "M31 24a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-5 6v-2h10v2zm16.148 11.69l1.714 1.031A14.989 14.989 0 0 1 31 48a14.989 14.989 0 0 1-12.853-7.264l1.712-1.032A12.99 12.99 0 0 0 31 46c4.616 0 8.812-2.425 11.148-6.31z"
  }), React.createElement("path", {
    d: "M42 41h-6v-2h8v8h-2zm-22 0v6h-2v-8h8v2z"
  }))));
});

var _extends$3 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$3(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Finance = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$3(_ref, ["styles"]);

  return React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#66E382",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "5",
    d: "M43.707 48.157L32.009 34.685 20.311 48.157"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M52.416 33.88c0 1.516-1.15 2.742-2.57 2.742s-2.57-1.226-2.57-2.741"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M48.729 35c0 9.238-5.623 14.843-16.728 14.843-11.277 0-16.728-5.605-16.728-14.843s6.511-14.843 16.728-14.843c10.634 0 16.728 5.605 16.728 14.843"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "5",
    d: "M25.628 25.646l-6.026-3.723"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M17.12 40.89h-3.368a2.133 2.133 0 0 1-2.127-2.127v-5.317c0-1.17.957-2.127 2.127-2.127h3.368c1.174 0 2.127.952 2.127 2.127v5.317a2.127 2.127 0 0 1-2.127 2.127"
  }), React.createElement("path", {
    stroke: "#66E382",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M28.68 23.775c.908-.412 5.471-.37 6.428.108"
  }), React.createElement("path", {
    stroke: "#66E382",
    strokeLinecap: "round",
    strokeWidth: "2",
    d: "M19.513 32.825v1.24"
  }), React.createElement("path", {
    fill: "#DBFDC7",
    d: "M38.41 16.807a6.41 6.41 0 1 1-12.82 0 6.41 6.41 0 0 1 12.82 0"
  })));
});

var _extends$4 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$4(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Fresh = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$4(_ref, ["styles"]);

  return React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 30 30"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#1EB594",
    d: "M0 0h30v30H0z"
  }), React.createElement("g", {
    fill: "#FFF",
    fillRule: "nonzero"
  }, React.createElement("path", {
    d: "M15 7c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 14c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z"
  }), React.createElement("path", {
    d: "M16 11h-2v5h5v-2h-3z"
  }))));
});

var _extends$5 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$5(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Golf = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$5(_ref, ["styles"]);

  return React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#49C966",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M41.742 21.806c0 5.38-4.362 9.742-9.742 9.742-5.38 0-9.742-4.362-9.742-9.742 0-5.38 4.361-9.742 9.742-9.742 5.38 0 9.742 4.362 9.742 9.742"
  }), React.createElement("path", {
    fill: "#DBFDC7",
    d: "M40.964 33.477a1.99 1.99 0 0 0-2.813-.863 12.49 12.49 0 0 1-6.375 1.737c-2.144 0-4.15-.534-5.91-1.477-.9-.48-2.063-.269-2.647.567a1.992 1.992 0 0 0 .678 2.896c.44.24.892.458 1.355.658 2.77 1.19 4.533 3.948 4.533 6.961v5.875c0 1.042.75 1.991 1.786 2.095a1.992 1.992 0 0 0 2.197-1.98v-5.99a7.6 7.6 0 0 1 4.578-6.982 16.278 16.278 0 0 0 1.705-.859c.918-.532 1.37-1.68.913-2.638"
  })));
});

var _extends$6 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$6(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Handbag = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$6(_ref, ["styles"]);

  return React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#FB73A5",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeWidth: "2",
    d: "M32 38.047c-5.795 0-10.535-4.741-10.535-10.535v-7.024c0-5.794 4.74-10.535 10.535-10.535 5.794 0 10.535 4.74 10.535 10.535v7.024c0 5.794-4.74 10.535-10.535 10.535z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M45.468 47.75H18.532c-2.956 0-5.142-2.752-4.472-5.632l3.69-15.868h28.5l3.691 15.868c.669 2.88-1.517 5.632-4.473 5.632"
  }), React.createElement("path", {
    fill: "#FFBED6",
    d: "M35.596 36.747L46.092 26.25H17.909l10.496 10.497a5.086 5.086 0 0 0 7.19 0m-7.345 3.837c0-.964.787-1.75 1.75-1.75h4c.962 0 1.75.786 1.75 1.75 0 .961-.788 1.75-1.75 1.75h-4c-.963 0-1.75-.789-1.75-1.75"
  })));
});

var _extends$7 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$7(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Home = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$7(_ref, ["styles"]);

  return React.createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#8C86DD",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#6963B5",
    d: "M39.027 18.61L25.382 20l5.077 25.39h21.025V30.688z"
  }), React.createElement("path", {
    stroke: "#6963B5",
    strokeLinecap: "round",
    strokeWidth: "4",
    d: "M25.641 19.63s8.652-.042 12.877-.057c.532-.002 1.124.043 1.987.76l10.972 10.664"
  }), React.createElement("path", {
    fill: "#6963B5",
    d: "M38.305 32.995l13.211.003-11.903-12.827-10.669.847z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M25.382 20.001l-11.67 11.452v13.938h24.415V31.453z"
  }), React.createElement("path", {
    fill: "#6963B5",
    d: "M21.14 45.39V34.937c0-1.938 8.624-1.938 8.624 0V45.39H21.14z"
  }), React.createElement("path", {
    stroke: "#6963B5",
    strokeLinecap: "round",
    strokeWidth: "4",
    d: "M12.483 31.035l11.07-10.56c1.215-1.11 2.424-1.152 3.797 0l11.032 10.522"
  }), React.createElement("path", {
    fill: "#6963B5",
    d: "M14.478 20.617v7.395l4.542-4.542v-2.836a.79.79 0 0 0-.786-.79l-2.962-.017a.79.79 0 0 0-.794.79"
  })));
});

var _extends$8 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$8(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Hot = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$8(_ref, ["styles"]);

  return React.createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 30 30"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#1EB594",
    d: "M0 0h30v30H0z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M15.156 24.688c-3.883 0-7.031-3.218-7.031-7.187 0-2.5 2.5-6.23 3.438-8.784.264-.722 1.312 2.555 1.312 3.194 0 .639 2.219-4.344 2.25-5.878.031-1.533 2.25 1.661 2.75 2.811.224.515 1.25 3.322.875 5.622s1.05-2.15 1.563-3.513c.312-.831 1.875 4.2 1.875 6.548 0 3.969-3.148 7.186-7.032 7.186zm0-1.25c1.295 0 2.344-1.024 2.344-2.286s-2.083-5.84-2.344-5.84c-.26 0-2.344 4.578-2.344 5.84 0 1.262 1.05 2.285 2.344 2.285z"
  })));
});

var _extends$9 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$9(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Massage = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$9(_ref, ["styles"]);

  return React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#45D0A0",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#A0FFEA",
    d: "M42.325 38.983H32.223a3.188 3.188 0 0 1-3.18-3.179v-9.15a3.188 3.188 0 0 1 3.18-3.179h10.102a3.188 3.188 0 0 1 3.179 3.178v9.151a3.188 3.188 0 0 1-3.18 3.18"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M53.19 33.106c0 8.791-7.125 15.917-15.916 15.917s-15.917-7.126-15.917-15.917c0-3.265 31.834-2.285 31.834 0M40.58 18.022c.127 1.829-1.19 3.425-3.022 3.491-1.811.065-3.6-1.051-3.6-3.18 0-2.459 1.825-5.229 4.488-6.319.145-.058.296.073.278.228-.382 3.278 1.676 3.161 1.857 5.78"
  }), React.createElement("path", {
    fill: "#A0FFEA",
    d: "M39.041 50.23c-4.457 2.958-10.508 1.316-10.508 1.316s.973-6.301 5.298-9.17c4.126-2.736 12.462-2.61 12.462-2.61s-3.358 7.882-7.252 10.465"
  }), React.createElement("path", {
    stroke: "#6BCEAC",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "1.5",
    d: "M39.041 50.23c-4.457 2.958-10.508 1.316-10.508 1.316s.973-6.301 5.298-9.17c4.126-2.736 12.462-2.61 12.462-2.61s-3.358 7.882-7.252 10.465z"
  }), React.createElement("path", {
    fill: "#A0FFEA",
    d: "M23.267 42.31c4.453 2.963 5.285 9.178 5.285 9.178s-6.186 1.544-10.507-1.332c-4.12-2.742-7.236-10.476-7.236-10.476s8.568.04 12.458 2.63"
  }), React.createElement("path", {
    stroke: "#6BCEAC",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "1.5",
    d: "M23.267 42.31c4.453 2.963 5.285 9.178 5.285 9.178s-6.186 1.544-10.507-1.332c-4.12-2.742-7.236-10.476-7.236-10.476s8.568.04 12.458 2.63z"
  }), React.createElement("path", {
    fill: "#A0FFEA",
    d: "M33.248 42.016c0 5.35-4.712 9.485-4.712 9.485s-4.713-4.295-4.713-9.485c0-4.95 4.713-11.828 4.713-11.828s4.712 7.156 4.712 11.828"
  }), React.createElement("path", {
    stroke: "#6BCEAC",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "1.5",
    d: "M33.248 42.016c0 5.35-4.712 9.485-4.712 9.485s-4.713-4.295-4.713-9.485c0-4.95 4.713-11.828 4.713-11.828s4.712 7.156 4.712 11.828z"
  })));
});

var _extends$a = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$a(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Nail = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$a(_ref, ["styles"]);

  return React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#F98DBB",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M36.106 29.4a7.12 7.12 0 0 1 5.473 2.563l.01.01a7.123 7.123 0 0 1 1.555 5.708l-1.65 10.095a7.124 7.124 0 0 1-7.03 5.974h-4.928a7.123 7.123 0 0 1-7.03-5.974l-1.65-10.095a7.12 7.12 0 0 1 1.556-5.708l.008-.01a7.12 7.12 0 0 1 5.473-2.564h8.213z"
  }), React.createElement("path", {
    fill: "#F98DBB",
    d: "M30.792 43.86h2.416V15.75h-2.416z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M31.93 29.248c-2.08 0-4.624-1.006-4.624-2.19l1.013-15.869a1.003 1.003 0 0 1 1.001-.939h5.36c.53 0 .967.411 1 .939l1.013 15.869c0 1.184-2.417 2.19-4.764 2.19"
  }), React.createElement("path", {
    fill: "#F98DBB",
    d: "M32 50.998c-.888 0-2.512.187-2.512-1.194v-4.612c0-1.381 1.272-3.83 1.272-3.83h2.416s1.336 2.449 1.336 3.83v4.612c0 1.381-1.624 1.194-2.512 1.194"
  })));
});

var _extends$b = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$b(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Product = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$b(_ref, ["styles"]);

  return React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#66E382",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "5",
    d: "M43.707 48.157L32.009 34.685 20.311 48.157"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M52.416 33.88c0 1.516-1.15 2.742-2.57 2.742s-2.57-1.226-2.57-2.741"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M48.729 35c0 9.238-5.623 14.843-16.728 14.843-11.277 0-16.728-5.605-16.728-14.843s6.511-14.843 16.728-14.843c10.634 0 16.728 5.605 16.728 14.843"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "5",
    d: "M25.628 25.646l-6.026-3.723"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M17.12 40.89h-3.368a2.133 2.133 0 0 1-2.127-2.127v-5.317c0-1.17.957-2.127 2.127-2.127h3.368c1.174 0 2.127.952 2.127 2.127v5.317a2.127 2.127 0 0 1-2.127 2.127"
  }), React.createElement("path", {
    stroke: "#66E382",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M28.68 23.775c.908-.412 5.471-.37 6.428.108"
  }), React.createElement("path", {
    stroke: "#66E382",
    strokeLinecap: "round",
    strokeWidth: "2",
    d: "M19.513 32.825v1.24"
  }), React.createElement("path", {
    fill: "#DBFDC7",
    d: "M38.41 16.807a6.41 6.41 0 1 1-12.82 0 6.41 6.41 0 0 1 12.82 0"
  })));
});

var _extends$c = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$c(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Placeholder = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$c(_ref, ["styles"]);

  return React.createElement("svg", _extends$c({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#66E382",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "5",
    d: "M43.707 48.157L32.009 34.685 20.311 48.157"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M52.416 33.88c0 1.516-1.15 2.742-2.57 2.742s-2.57-1.226-2.57-2.741"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M48.729 35c0 9.238-5.623 14.843-16.728 14.843-11.277 0-16.728-5.605-16.728-14.843s6.511-14.843 16.728-14.843c10.634 0 16.728 5.605 16.728 14.843"
  }), React.createElement("path", {
    stroke: "#FFF",
    strokeLinecap: "round",
    strokeWidth: "5",
    d: "M25.628 25.646l-6.026-3.723"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M17.12 40.89h-3.368a2.133 2.133 0 0 1-2.127-2.127v-5.317c0-1.17.957-2.127 2.127-2.127h3.368c1.174 0 2.127.952 2.127 2.127v5.317a2.127 2.127 0 0 1-2.127 2.127"
  }), React.createElement("path", {
    stroke: "#66E382",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M28.68 23.775c.908-.412 5.471-.37 6.428.108"
  }), React.createElement("path", {
    stroke: "#66E382",
    strokeLinecap: "round",
    strokeWidth: "2",
    d: "M19.513 32.825v1.24"
  }), React.createElement("path", {
    fill: "#DBFDC7",
    d: "M38.41 16.807a6.41 6.41 0 1 1-12.82 0 6.41 6.41 0 0 1 12.82 0"
  })));
});

var _extends$d = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$d(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Service = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$d(_ref, ["styles"]);

  return React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#FF835E",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M47.737 37.318H16.264c-1.118 0-1.98-.915-1.854-2.025 1.005-8.825 8.497-15.68 17.59-15.68s16.585 6.855 17.59 15.68c.126 1.11-.735 2.025-1.853 2.025"
  }), React.createElement("path", {
    stroke: "#FFBD96",
    strokeLinecap: "round",
    strokeWidth: "2",
    d: "M38.613 24.072a10.706 10.706 0 0 1 6.356 8.587"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M49.293 46.636H14.707A3.216 3.216 0 0 1 11.5 43.43v-.108a3.216 3.216 0 0 1 3.207-3.207h34.586a3.216 3.216 0 0 1 3.207 3.207v.108a3.216 3.216 0 0 1-3.207 3.207"
  }), React.createElement("path", {
    fill: "#FFBD96",
    d: "M36.193 13.557a4.193 4.193 0 1 1-8.387 0 4.194 4.194 0 0 1 8.387 0"
  }), React.createElement("path", {
    stroke: "#FFBD96",
    strokeLinecap: "round",
    strokeWidth: "2",
    d: "M32 20.545V13.09"
  })));
});

var _extends$e = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$e(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Skis = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$e(_ref, ["styles"]);

  return React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#1FB0F3",
    d: "M0 0h64v64H0z"
  }), React.createElement("g", {
    fill: "#FFF",
    fillRule: "nonzero"
  }, React.createElement("path", {
    d: "M45.99 24.496a3.072 3.072 0 1 0-2.705-5.517 3.072 3.072 0 0 0 2.704 5.517zm-8.339 3.961c-.245 1.195-.137 2.265 1.053 2.592l5.48 1.505.003.001a1.661 1.661 0 0 0 .883-3.204l-.003-.001-2.793-.767-1.104-.737.512-2.495c.157-.761-.427-1.735-.769-2.433a2.646 2.646 0 0 0-3.54-1.212l-1.834.9 2.926 1.954c-.067.232-.226 1.035-.279 1.293l-12.922-8.627a1.23 1.23 0 1 0-1.367 2.047l13.754 9.184z"
  }), React.createElement("path", {
    d: "M47.796 42.89a1.23 1.23 0 0 0-1.688-.424l-1.695 1.014L33.2 39.621l5.287-4.454a2.033 2.033 0 0 0-.113-3.195l-2.958-2.156 1.062-.52.123-.06-6.175-4.124h-.002a3.4 3.4 0 0 0-1.858 2.518c-.214 1.3.33 2.43 1.558 3.356l3.764 2.743-5.124 4.365-11.067-3.809a1.231 1.231 0 0 0-.802 2.328l27.257 9.382a1.229 1.229 0 0 0 1.034-.108l2.186-1.309a1.23 1.23 0 0 0 .424-1.688z"
  }))));
});

var _extends$f = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$f(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Snowboard = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$f(_ref, ["styles"]);

  return React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#007CF6",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M51.346 32.715c-3.512 1.98-11.026 5.069-14.537 7.05-3.675 2.072-10.533 7.087-14.208 9.159-4.851 2.735-11.058 1.004-13.793-3.847-2.735-4.85-1.004-11.056 3.847-13.792 3.592-2.026 11.27-5.208 14.863-7.233 3.594-2.026 10.289-6.95 13.882-8.976 4.85-2.735 11.057-1.004 13.793 3.847 2.735 4.85 1.004 11.057-3.847 13.792"
  }), React.createElement("path", {
    fill: "#8DC2EF",
    d: "M26.59 38.858a1.771 1.771 0 0 1-1.766-1.766v-4.185c0-.972.795-1.766 1.767-1.766a1.77 1.77 0 0 1 1.766 1.766v4.185a1.77 1.77 0 0 1-1.766 1.766m9.003-5a1.77 1.77 0 0 1-1.766-1.766v-4.185a1.77 1.77 0 0 1 1.766-1.766 1.77 1.77 0 0 1 1.766 1.766v4.185a1.77 1.77 0 0 1-1.766 1.766"
  }), React.createElement("path", {
    stroke: "#FFF",
    d: "M23.33 34.216h5.955m3.048-5h5.955"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M18.417 18.833a1.166 1.166 0 1 1-2.333.001 1.166 1.166 0 0 1 2.333-.001m8 0a1.166 1.166 0 1 1-2.333.001 1.166 1.166 0 0 1 2.333-.001M18.417 8a1.166 1.166 0 1 1-2.333.001A1.166 1.166 0 0 1 18.417 8m8 0a1.166 1.166 0 1 1-2.333.001A1.166 1.166 0 0 1 26.417 8m9 0a1.166 1.166 0 1 1-2.333.001A1.166 1.166 0 0 1 35.417 8m-25 10.833a1.166 1.166 0 1 1-2.333.001 1.166 1.166 0 0 1 2.333-.001m12-5.416a1.166 1.166 0 1 1-2.332 0 1.166 1.166 0 0 1 2.332 0m-7.875 0a1.166 1.166 0 1 1-2.332 0 1.166 1.166 0 0 1 2.332 0m15.875 0a1.166 1.166 0 1 1-2.332 0 1.166 1.166 0 0 1 2.332 0"
  })));
});

var _extends$g = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$g(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Travel = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$g(_ref, ["styles"]);

  return React.createElement("svg", _extends$g({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#007CF6",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#8DC2EF",
    d: "M20.825 41.642l-6.158-5.173a1.045 1.045 0 0 1 0-1.478l.512-.512a1.045 1.045 0 0 1 1.1-.242l8.733 3.218-4.187 4.187zm1.855 1.854l5.172 6.158c.408.408 1.07.408 1.478 0l.513-.512c.288-.289.382-.718.241-1.1l-3.218-8.733-4.187 4.187z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M42.279 40.252l1.567 1.567c.592.592 1.55.592 2.14 0l2.805-2.803a1.514 1.514 0 0 0 0-2.14l-1.568-1.568a1.513 1.513 0 0 0-2.141 0l-2.803 2.803a1.514 1.514 0 0 0 0 2.141M24.068 22.043l-1.567-1.567a1.513 1.513 0 0 1 0-2.141l2.803-2.805a1.514 1.514 0 0 1 2.141 0l1.567 1.567c.592.591.592 1.55 0 2.141l-2.803 2.804a1.514 1.514 0 0 1-2.14 0"
  }), React.createElement("path", {
    fill: "#8DC2EF",
    d: "M28.49 31.668L14.663 20.055a2.347 2.347 0 0 1 0-3.318l1.151-1.151a2.347 2.347 0 0 1 2.47-.542l19.605 7.223-9.4 9.401zm4.163 4.163l11.613 13.825a2.347 2.347 0 0 0 3.318 0l1.15-1.15a2.345 2.345 0 0 0 .543-2.47l-7.223-19.605-9.401 9.4z"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M17.237 47.063c-2.497-2.512 4.94-10.187 6.919-12.36 4.792-5.265 19.836-20.39 19.836-20.39a4.267 4.267 0 0 1 6.017 0 4.267 4.267 0 0 1 0 6.016S34.795 35.453 29.5 40.27c-2.144 1.952-9.793 9.277-12.264 6.792"
  }), React.createElement("path", {
    fill: "#56A8F9",
    d: "M48.748 20.041a.624.624 0 0 0 .199-.448c-.018-.82-.534-1.879-1.432-2.776v-.001c-.988-.987-2.172-1.513-3.017-1.421a.63.63 0 0 0-.373.195l-1.294 1.294c-.214.215-.11.584.189.639.75.139 1.633.627 2.397 1.392.727.726 1.203 1.558 1.368 2.283.066.287.429.378.637.17l1.326-1.327z"
  })));
});

var _extends$h = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$h(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Trending = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$h(_ref, ["styles"]);

  return React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 30 30"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#1EB594",
    d: "M0 0h30v30H0z"
  }), React.createElement("path", {
    fill: "#FFF",
    fillRule: "nonzero",
    d: "M23 10.636h-7.273l3.275 3.275-3.302 3.67-3.822-3.822a.726.726 0 0 0-1.028 0l-3.637 3.636a.726.726 0 1 0 1.029 1.028l3.122-3.122 3.85 3.85a.725.725 0 0 0 1.054-.028l3.763-4.182 2.97 2.968v-7.273z"
  })));
});

var _extends$i = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$i(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Watch = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$i(_ref, ["styles"]);

  return React.createElement("svg", _extends$i({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#FFA400",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    fill: "#FFDA17",
    d: "M38.228 54.333H25.772a3.867 3.867 0 0 1-3.856-3.855V13.522a3.867 3.867 0 0 1 3.856-3.855h12.456a3.866 3.866 0 0 1 3.855 3.855v36.956a3.866 3.866 0 0 1-3.855 3.855"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M47.626 32c0 8.63-6.996 15.626-15.626 15.626-8.63 0-15.626-6.996-15.626-15.626 0-8.63 6.996-15.626 15.626-15.626 8.63 0 15.626 6.996 15.626 15.626"
  }), React.createElement("path", {
    stroke: "#FFDA17",
    strokeLinecap: "round",
    strokeWidth: "3",
    d: "M32 24.855v7.165m6.513.043L32 32.02"
  })));
});

var _extends$j = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$j(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Wine = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$j(_ref, ["styles"]);

  return React.createElement("svg", _extends$j({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#C80B4A",
    d: "M0 0h64v64H0z"
  }), React.createElement("path", {
    stroke: "#FF83AC",
    strokeLinecap: "round",
    strokeWidth: "2",
    d: "M27 51.75h10m-5-14.125V51.25"
  }), React.createElement("path", {
    fill: "#FF83AC",
    d: "M32 39.521c-5.439 0-9.889-4.687-9.889-9.889 0-2.951 1.915-10.423 2.686-13.306a1.562 1.562 0 0 1 1.515-1.159h11.375c.711 0 1.333.473 1.516 1.159.77 2.883 2.686 10.355 2.686 13.306 0 5.868-4.45 9.889-9.889 9.889"
  }), React.createElement("path", {
    fill: "#FFF",
    d: "M40.43 31.55c-4.503.057-8.202-2.147-12.356-3.54-1.459-.49-3.705-1.088-5.698-.993-.162 1.032-.264 1.944-.264 2.615 0 5.201 4.449 9.89 9.888 9.89 4.844 0 8.895-3.196 9.725-8.04-.43.04-.861.062-1.295.067"
  })));
});

var CategoryIcon = function CategoryIcon(_a) {
  var category = _a.category,
      className = _a.className,
      rest = __rest(_a, ["category", "className"]);

  var classes = classnames(styles$5.icon, className);

  switch (category && snakeCase(category)) {
    case 'bike':
      return React.createElement(Bike, __assign({
        className: classes
      }, rest));

    case 'camera':
      return React.createElement(Camera, __assign({
        className: classes
      }, rest));

    case 'charters':
      return React.createElement(Charters, __assign({
        className: classes
      }, rest));

    case 'finance':
      return React.createElement(Finance, __assign({
        className: classes
      }, rest));

    case 'fresh':
      return React.createElement(Fresh, __assign({
        className: classes
      }, rest));

    case 'golf':
      return React.createElement(Golf, __assign({
        className: classes
      }, rest));

    case 'handbag':
      return React.createElement(Handbag, __assign({
        className: classes
      }, rest));

    case 'home':
      return React.createElement(Home, __assign({
        className: classes
      }, rest));

    case 'hot':
      return React.createElement(Hot, __assign({
        className: classes
      }, rest));

    case 'massage':
      return React.createElement(Massage, __assign({
        className: classes
      }, rest));

    case 'nail':
      return React.createElement(Nail, __assign({
        className: classes
      }, rest));

    case 'product':
      return React.createElement(Product, __assign({
        className: classes
      }, rest));

    case 'service':
      return React.createElement(Service, __assign({
        className: classes
      }, rest));

    case 'skis':
      return React.createElement(Skis, __assign({
        className: classes
      }, rest));

    case 'snowboard':
      return React.createElement(Snowboard, __assign({
        className: classes
      }, rest));

    case 'travel':
      return React.createElement(Travel, __assign({
        className: classes
      }, rest));

    case 'trending':
      return React.createElement(Trending, __assign({
        className: classes
      }, rest));

    case 'watch':
      return React.createElement(Watch, __assign({
        className: classes
      }, rest));

    case 'wine':
      return React.createElement(Wine, __assign({
        className: classes
      }, rest));

    default:
      return React.createElement(Placeholder, __assign({
        className: classes
      }, rest));
  }
};

var styles$6 = {"wrapper":"styles_wrapper__1eXnU items-center","disabled":"styles_disabled__39uhX","input":"styles_input__21Dwb","checkbox":"styles_checkbox__nWaMC mr1","label":"styles_label__2Jvgr","xsmall":"styles_xsmall__VWa-i","small":"styles_small__28aNV","medium":"styles_medium__cd4AT","large":"styles_large__2yFCv","xlarge":"styles_xlarge__ipRx2"};

// All other props are applied to the hidden `input`.

var Checkbox = function Checkbox(_a) {
  var _b;

  var _c = _a.height,
      height = _c === void 0 ? 'medium' : _c,
      className = _a.className,
      children = _a.children,
      disabled = _a.disabled,
      rest = __rest(_a, ["height", "className", "children", "disabled"]);

  var classes = classnames(styles$6.wrapper, styles$6[height], className, (_b = {}, _b[styles$6.disabled] = disabled, _b));
  return React.createElement("label", {
    className: classes
  }, React.createElement("input", __assign({
    type: "checkbox",
    className: styles$6.input,
    disabled: disabled
  }, rest)), React.createElement("span", {
    className: styles$6.checkbox
  }), React.createElement("span", {
    className: styles$6.label
  }, children));
};

var cssVars = {"breakpoint-xs":"360px","breakpoint-sm":"768px","breakpoint-md":"960px","breakpoint-lg":"1200px","breakpoint-xlg":"1440px","breakpoint-xxlg":"1600px","default-columns":"2","columns-sm":"8","columns-md":"12","breakpointXs":"360px","breakpointSm":"768px","breakpointMd":"960px","breakpointLg":"1200px","breakpointXlg":"1440px","breakpointXxlg":"1600px","defaultColumns":"2","columnsSm":"8","columnsMd":"12"};

var useBreakpoint = function useBreakpoint(breakpointName, defaultMatches) {
  if (defaultMatches === void 0) {
    defaultMatches = true;
  }

  var _a = useState(defaultMatches),
      matches = _a[0],
      setMatches = _a[1];

  useEffect(function () {
    var breakpointSize = cssVars["breakpoint-" + breakpointName.toLowerCase()];
    var mediaQueryFeature = "(min-width: " + breakpointSize + ")";
    var mediaQueryList = window.matchMedia(mediaQueryFeature);

    var listener = function listener() {
      if (mediaQueryList.matches) {
        setMatches(true);
      } else {
        setMatches(false);
      }
    };

    mediaQueryList.addListener(listener);
    setMatches(mediaQueryList.matches);
    return function () {
      return mediaQueryList.removeListener(listener);
    };
  }, [breakpointName]);
  return matches;
};

var styles$7 = {"spinner":"styles_spinner__1L4nF flex flex-auto items-center justify-center","bounce":"styles_bounce__r0rIa inline-block","one":"styles_one__2dxRp styles_bounce__r0rIa inline-block","two":"styles_two__8DYP2 styles_bounce__r0rIa inline-block","three":"styles_three__12Qhn styles_bounce__r0rIa inline-block"};

var BubbleLoader = function BubbleLoader(_a) {
  var className = _a.className,
      color = _a.color,
      rest = __rest(_a, ["className", "color"]);

  var dotStyle = {
    backgroundColor: color
  };
  return React.createElement("div", __assign({
    className: classnames(styles$7.spinner, className)
  }, rest), React.createElement("div", {
    className: styles$7.one,
    style: dotStyle
  }), React.createElement("div", {
    className: styles$7.two,
    style: dotStyle
  }), React.createElement("div", {
    className: styles$7.three,
    style: dotStyle
  }));
};

var styles$8 = {"loader":"styles_loader__3AMDb relative inline-block","inner":"styles_inner__1s069 absolute top-0 right-0 bottom-0 left-0","spinner-clip":"styles_spinner-clip__3Ijvg","circle":"styles_circle__1P6Fl absolute top-0 right-0 bottom-0 left-0","spinner-cycle":"styles_spinner-cycle__3sLYB","spinnerClip":"styles_spinner-clip__3Ijvg","spinnerCycle":"styles_spinner-cycle__3sLYB"};

function CircleLoader(_a) {
  var className = _a.className,
      _b = _a.color,
      color = _b === void 0 ? '#f55700' : _b,
      _c = _a.size,
      size = _c === void 0 ? '20px' : _c,
      _d = _a.thickness,
      thickness = _d === void 0 ? '0.1em' : _d;
  var spinnerClasses = classnames(styles$8.loader, className);
  var boxShadow = "inset 0 0 0 " + thickness + " " + color;
  return React.createElement("span", {
    className: spinnerClasses,
    style: {
      fontSize: size
    }
  }, React.createElement("i", {
    className: styles$8.inner
  }, React.createElement("span", {
    className: styles$8.circle,
    style: {
      boxShadow: boxShadow
    }
  })));
}

var styles$9 = {"wrapper":"styles_wrapper__2y8E7","loader":"styles_loader__MlXOX mx-auto"};

var FullBlockLoader = function FullBlockLoader(_a) {
  var className = _a.className,
      Loader = _a.loader;
  return React.createElement("div", {
    className: styles$9.wrapper
  }, React.createElement("div", {
    className: styles$9.loader
  }, React.createElement(Loader, {
    className: className
  })));
};

var styles$a = {"wrapper":"styles_wrapper__2o8dC fixed top-0 right-0 bottom-0 left-0","loaderWrapper":"styles_loaderWrapper__KoKPH relative center"};

function FullPageLoader(_a) {
  var wrapperClassName = _a.wrapperClassName,
      className = _a.className,
      _b = _a.loader,
      Loader = _b === void 0 ? CircleLoader : _b;
  return React.createElement("section", {
    className: classnames(styles$a.wrapper, wrapperClassName)
  }, React.createElement("div", {
    className: styles$a.loaderWrapper
  }, React.createElement(Loader, {
    className: className
  })));
}

var styles$b = {"reloader":"styles_reloader__2r9pU fixed top-0 left-0","exited":"styles_exited__35OR3","entering":"styles_entering__3fk6V","entered":"styles_entered__3kcSt","exiting":"styles_exiting__3TAUD"};

var GlobalLoader = function GlobalLoader(_a) {
  var loading = _a.loading;
  return React.createElement(Transition, {
    in: loading,
    timeout: {
      enter: 2000,
      exit: 1000
    }
  }, function (state) {
    return React.createElement("div", {
      className: classnames(styles$b.reloader, styles$b[state])
    });
  });
};

var _extends$k = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$k(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ErrorIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$k(_ref, ["styles"]);

  return React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "10",
    height: "10",
    viewBox: "0 0 10 10"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M-7-7h24v24H-7z"
  }), React.createElement("path", {
    d: "M9 1L1 9m8 0L1 1",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2"
  })));
});

var _extends$l = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$l(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ValidIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$l(_ref, ["styles"]);

  return React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "10",
    viewBox: "0 0 14 10"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M-5-7h24v24H-5z"
  }), React.createElement("path", {
    d: "M1 5l4 4 8-8"
  })));
});

var styles$c = {"breakpoint-sm":"(min-width: 768px)","field":"styles_field__3zKTz flex flex-column","inputContainer":"styles_inputContainer__1QQOm relative flex flex-column","innerInputContainer":"styles_innerInputContainer__2_Vmz flex flex-auto","fieldContainer":"styles_fieldContainer__19hAc relative flex-auto","unit":"styles_unit__3oOYW flex","label":"styles_label__3d47w absolute bottom-0","input":"styles_input__18X13 absolute bottom-0","withIcon":"styles_withIcon__3TZlY","errorText":"styles_errorText__2DmYX mt1","helpText":"styles_helpText__ZKp3e mt1","icon":"styles_icon__3sqkt absolute","errorIcon":"styles_errorIcon__5TVGI styles_icon__3sqkt absolute","validIcon":"styles_validIcon__2zj_T styles_icon__3sqkt absolute","loadingIcon":"styles_loadingIcon__3-WWv styles_icon__3sqkt absolute","additionalIcon":"styles_additionalIcon__XFN2Y styles_icon__3sqkt absolute","isValid":"styles_isValid__2-k0i","hasFocus":"styles_hasFocus__2LmFD","hasValue":"styles_hasValue__2qOph","hasError":"styles_hasError__15mhW","isLoading":"styles_isLoading__-g40c","isDisabled":"styles_isDisabled__3PTEL","isLabelHidden":"styles_isLabelHidden__1QBSr","standard":"styles_standard__2To-1","withUnit":"styles_withUnit__2EmuL","breakpointSm":"(min-width: 768px)"};

var ResponsiveTextField =
/** @class */
function (_super) {
  __extends(ResponsiveTextField, _super);

  function ResponsiveTextField(props) {
    var _this = _super.call(this, props) || this;

    _this.checkPropInvariants(_this.props);
    /**
     * Generate consistent ID for this input based on the props received:
     *  - Used to ensure the label and input are properly associated.
     *  - Must be consistent across server and browser renders.
     *  - Only used if an explicit `id` prop is not passed.
     */


    _this.generatedId = 'text-field-' + hash(props).substr(0, 8);
    _this.state = {
      // Autofocus will always exist via `defaultProps`
      hasFocus: _this.props.autoFocus
    };
    _this.handleInputBlur = _this.handleInputBlur.bind(_this);
    _this.handleInputChange = _this.handleInputChange.bind(_this);
    _this.handleInputFocus = _this.handleInputFocus.bind(_this);
    return _this;
  }

  ResponsiveTextField.prototype.componentDidUpdate = function (prevProps) {
    var id = this.props.id || this.generatedId; // Controlled focus => uncontrolled focus isn't allowed

    if (!isUndefined(prevProps.focused) && isUndefined(this.props.focused)) {
      console.warn("TextField (" + id + ") is changing from controlled focus to uncontrolled focus. TextFields should not switch from controlled to uncontrolled (or vice versa).");
    } // Uncontrolled focus => controlled focus isn't allowed


    if (isUndefined(prevProps.focused) && !isUndefined(this.props.focused)) {
      console.warn("TextField (" + id + ") is changing from uncontrolled focus to controlled focus. TextFields should not switch from uncontrolled to controlled (or vice versa).");
    } // Warn about "valid" and "errorText" conflicts, but only if they've changed meaningfully
    //   i.e. only if one of those props was previously "false"


    if (this.props.errorText && this.props.valid && (!prevProps.errorText || !prevProps.valid)) {
      this.checkPropInvariants(this.props);
    }
  };

  ResponsiveTextField.prototype.checkPropInvariants = function (props) {
    var id = props.id || this.generatedId;

    if (props.valid && props.errorText) {
      console.warn("TextField (" + id + ") received both \"errorText\" and \"valid\" props. The error state will take precedence.");
    }
  };

  ResponsiveTextField.prototype.handleInputFocus = function (e) {
    // Only manage the input focus if the focus is not controlled by props
    if (isUndefined(this.props.focused)) {
      this.setState({
        hasFocus: true
      });
    }

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  ResponsiveTextField.prototype.handleInputBlur = function (e) {
    // Only manage the input focus if the focus is not controlled by props
    if (isUndefined(this.props.focused)) {
      this.setState({
        hasFocus: false
      });
    }

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  ResponsiveTextField.prototype.handleInputChange = function (e) {
    // Only manage the input value if the value is not controlled by props
    if (isUndefined(this.props.value)) {
      this.setState({
        hasValue: !!(e.target.value && e.target.value.length > 0)
      });
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  ResponsiveTextField.prototype.render = function () {
    var _a, _b;

    var _c = this.props,
        Icon = _c.icon,
        testId = _c.testId;
    var id = this.props.id || this.generatedId;
    var hasValue = !!(this.props.value || this.props.defaultValue || this.state.hasValue || this.props.inputProps && this.props.inputProps.value);
    var hasFocus = !!(this.props.focused || this.state.hasFocus);
    var hasError = this.props.errorText !== undefined;
    var isValid = !!(this.props.valid && !hasError);
    var isLoading = !!this.props.loading;
    var isDisabled = !!this.props.disabled;
    var isLabelHidden = !!this.props.labelHidden;
    var hasUnit = !!this.props.unit;
    var display = this.props.display;
    return React.createElement("div", {
      className: classnames(styles$c.field, (_a = {}, _a[styles$c.standard] = !this.props.isMobile && display === 'responsive' || display === 'standard', _a[styles$c.hasFocus] = hasFocus, _a[styles$c.hasValue] = hasValue, _a[styles$c.hasError] = hasError, _a[styles$c.isValid] = isValid, _a[styles$c.isLoading] = isLoading, _a[styles$c.isDisabled] = isDisabled, _a[styles$c.isLabelHidden] = isLabelHidden, _a))
    }, React.createElement("div", {
      className: styles$c.inputContainer
    }, React.createElement("label", {
      id: 'label-' + id,
      htmlFor: id,
      className: styles$c.label
    }, this.props.label), React.createElement("div", {
      className: styles$c.innerInputContainer
    }, React.createElement("div", {
      className: styles$c.fieldContainer
    }, Icon && React.createElement(Icon, {
      className: styles$c.additionalIcon
    }), React.createElement("input", __assign({
      "data-testid": testId,
      className: classnames(styles$c.input, this.props.inputProps && this.props.inputProps.className, (_b = {}, _b[styles$c.withUnit] = hasUnit, _b[styles$c.withIcon] = Icon, _b)),
      disabled: this.props.disabled,
      defaultValue: this.props.defaultValue,
      id: id,
      name: this.props.name,
      onBlur: this.handleInputBlur,
      onChange: this.handleInputChange,
      onFocus: this.handleInputFocus,
      placeholder: this.props.placeholder || isLabelHidden && this.props.label || undefined,
      readOnly: this.props.readOnly,
      type: this.props.type,
      value: this.props.value
    }, omit(this.props.inputProps, 'className'), {
      ref: this.props.inputRef
    })), React.createElement(ErrorIcon, {
      className: styles$c.errorIcon
    }), React.createElement(ValidIcon, {
      className: styles$c.validIcon
    }), React.createElement(CircleLoader, {
      color: "#087bcc",
      size: "16px",
      thickness: "2px",
      className: styles$c.loadingIcon
    })), hasUnit && React.createElement("div", {
      className: styles$c.unit
    }, this.props.unit))), this.props.errorText && React.createElement("div", {
      className: styles$c.errorText
    }, this.props.errorText), this.props.helpText && React.createElement("div", {
      className: styles$c.helpText
    }, this.props.helpText));
  };

  ResponsiveTextField.defaultProps = {
    autoFocus: false,
    display: 'responsive'
  };
  return ResponsiveTextField;
}(React.PureComponent);

var TextField = React.forwardRef(function (props, ref) {
  var isMobile = !useBreakpoint('sm');
  return React.createElement(ResponsiveTextField, __assign({
    inputRef: ref,
    isMobile: isMobile
  }, props));
});

var MaskedField = function MaskedField(_a) {
  var maskedFieldProps = _a.maskedFieldProps,
      inputProps = _a.inputProps,
      rest = __rest(_a, ["maskedFieldProps", "inputProps"]);

  return React.createElement(Masked, __assign({
    render: function render(ref, props) {
      return React.createElement(TextField, __assign({
        inputProps: inputProps,
        ref: ref
      }, props));
    }
  }, rest, maskedFieldProps));
};

var numberMask = createNumberMask({
  prefix: '$',
  allowDecimal: true
});

var CurrencyField =
/** @class */
function (_super) {
  __extends(CurrencyField, _super);

  function CurrencyField() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  CurrencyField.prototype.render = function () {
    var _a = this.props,
        placeholder = _a.placeholder,
        maskedFieldProps = _a.maskedFieldProps,
        rest = __rest(_a, ["placeholder", "maskedFieldProps"]);

    return React.createElement(MaskedField, __assign({
      placeholder: placeholder,
      maskedFieldProps: __assign({
        mask: numberMask
      }, maskedFieldProps)
    }, rest));
  };

  CurrencyField.defaultProps = {
    placeholder: 'Enter amount',
    autoFocus: false
  };
  return CurrencyField;
}(React.Component);

var _extends$m = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$m(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ChevronLeftIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$m(_ref, ["styles"]);

  return React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "9",
    height: "14",
    viewBox: "0 0 9 14"
  }, props), React.createElement("path", {
    fill: "none",
    stroke: "#767676",
    strokeWidth: "2",
    d: "M8 13L2 7l6-6"
  }));
});

var _extends$n = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$n(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ChevronRightIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$n(_ref, ["styles"]);

  return React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "9",
    height: "14",
    viewBox: "0 0 9 14"
  }, props), React.createElement("path", {
    fill: "none",
    stroke: "#767676",
    strokeWidth: "2",
    d: "M1 13l6-6-6-6"
  }));
});

var styles$d = {"navigation":"styles_navigation__5XARE flex justify-between px2 pt2"};

var Navigation = function Navigation(_a) {
  var month = _a.month,
      onNextClick = _a.onNextClick,
      onPreviousClick = _a.onPreviousClick,
      className = _a.className;
  var formattedMonth = format(month, 'MMMM y');
  return React.createElement("div", {
    className: classnames(styles$d.navigation, className)
  }, React.createElement(ChevronLeftIcon, {
    onClick: function onClick() {
      return onPreviousClick();
    }
  }), React.createElement("div", {
    className: styles$d.month
  }, formattedMonth), React.createElement(ChevronRightIcon, {
    onClick: function onClick() {
      return onNextClick();
    }
  }));
};

var _extends$o = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$o(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ArrowDownIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$o(_ref, ["styles"]);

  return React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M3.115 5.295l-1.41 1.41 6 6 6-6-1.41-1.41-4.59 4.58z"
  }));
});

var styles$e = {"breakpoint-sm":"(min-width: 768px)","container":"styles_container__37SFI relative block","selectContainer":"styles_selectContainer__2eVbU relative flex flex-column","desktopSelectContainer":"styles_desktopSelectContainer__Dnfaz","label":"styles_label__3RRbT absolute bottom-0","desktopLabel":"styles_desktopLabel__280KV","errorText":"styles_errorText__wqFIb mt1","helpText":"styles_helpText__QzyYG mt1","icon":"styles_icon__d-btY absolute","desktopIcon":"styles_desktopIcon__3MZJy","select":"styles_select__hpaxr absolute bottom-0","isValid":"styles_isValid__3n9kQ","hasError":"styles_hasError__dAF_v","isDisabled":"styles_isDisabled__ZLEMh","hasValue":"styles_hasValue__2zVrJ","isLabelHidden":"styles_isLabelHidden__1Uo8e","isSelectedOptionDisabled":"styles_isSelectedOptionDisabled__1oTGd","desktopIsLabelHidden":"styles_desktopIsLabelHidden__2TfjT","desktopSelect":"styles_desktopSelect__3hZu-","breakpointSm":"(min-width: 768px)"};

var Option = function Option(_a) {
  var option = _a.option;
  return React.createElement("option", __assign({
    value: option.value,
    disabled: option.disabled,
    selected: option.selected
  }, option.optionProps), option.label);
};

var ResponsiveSelect =
/** @class */
function (_super) {
  __extends(ResponsiveSelect, _super);

  function ResponsiveSelect(props) {
    var _this = _super.call(this, props) || this;

    _this.selectRef = React.createRef();

    _this.checkPropInvariants(_this.props);
    /**
     * Generate consistent ID for this input based on the props received:
     *  - Used to ensure the label and input are properly associated.
     *  - Must be consistent across server and browser renders.
     *  - Only used if an explicit `id` prop is not passed.
     */


    _this.generatedId = 'select-' + hash(props).substr(0, 8);
    _this.state = {
      hasValue: isUndefined(_this.props.defaultValue) ? undefined : !!_this.props.defaultValue
    };
    _this.handleSelectChange = _this.handleSelectChange.bind(_this);
    return _this;
  } // Re-render component when ref is set. Ref assignment happens before componentDidMount lifecycle method
  // https://reactjs.org/docs/refs-and-the-dom.html#adding-a-ref-to-a-dom-element


  ResponsiveSelect.prototype.componentDidMount = function () {
    this.forceUpdate();
  };

  ResponsiveSelect.prototype.componentDidUpdate = function (prevProps) {
    // Warn about "valid" and "errorText" conflicts, but only if they've changed meaningfully
    //   i.e. only if one of those props was previously "false"
    if (this.props.errorText && this.props.valid && (!prevProps.errorText || !prevProps.valid)) {
      this.checkPropInvariants(this.props);
    }
  };

  ResponsiveSelect.prototype.checkPropInvariants = function (props) {
    var id = props.id || this.generatedId;

    if (props.valid && props.errorText) {
      console.warn("Select (" + id + ") received both \"errorText\" and \"valid\" props. The error state will take precedence.");
    }
  };

  ResponsiveSelect.prototype.handleSelectChange = function (e) {
    // Only manage the input value if the value is not controlled by props
    if (isUndefined(this.props.value)) {
      this.setState({
        hasValue: !!(e.target.value && e.target.value.length > 0)
      });
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  ResponsiveSelect.prototype.render = function () {
    var _a, _b, _c, _d, _e;

    var id = this.props.id || this.generatedId;
    var testId = this.props.testId;
    var hasError = this.props.errorText !== undefined;
    var isValid = !!(this.props.valid && !hasError);
    var isDisabled = !!this.props.disabled;
    var isLabelHidden = !!this.props.labelHidden;
    var isSelectedOptionDisabled = this.selectRef.current && this.selectRef.current.options[this.selectRef.current.selectedIndex].disabled;
    var addDesktopStyles = this.props.display === 'standard' || this.props.display === 'responsive-inverse' && this.props.isMobile || this.props.display === 'responsive' && !this.props.isMobile;
    return React.createElement("div", {
      className: classnames(styles$e.container, (_a = {}, _a[styles$e.hasError] = hasError, _a[styles$e.isValid] = isValid, _a[styles$e.isDisabled] = isDisabled, _a[styles$e.isSelectedOptionDisabled] = isSelectedOptionDisabled, _a[styles$e.isLabelHidden] = isLabelHidden, _a[styles$e.desktopIsLabelHidden] = addDesktopStyles, _a), this.props.className)
    }, React.createElement("div", {
      className: classnames(styles$e.selectContainer, (_b = {}, _b[styles$e.desktopSelectContainer] = addDesktopStyles, _b))
    }, React.createElement("label", {
      id: 'label-' + id,
      htmlFor: id,
      className: classnames(styles$e.label, (_c = {}, _c[styles$e.desktopLabel] = addDesktopStyles, _c))
    }, this.props.label), React.createElement("select", __assign({
      id: id,
      ref: this.selectRef,
      "data-testid": testId,
      className: classnames(styles$e.select, (_d = {}, _d[styles$e.desktopSelect] = addDesktopStyles, _d), this.props.selectClassName),
      defaultValue: this.props.defaultValue,
      disabled: this.props.disabled,
      name: this.props.name,
      onBlur: this.props.onBlur,
      onChange: this.handleSelectChange,
      onFocus: this.props.onFocus,
      value: this.props.value
    }, this.props.selectProps), this.props.options.map(function (optionOrOptionGroup) {
      if ('options' in optionOrOptionGroup) {
        return React.createElement("optgroup", __assign({
          label: optionOrOptionGroup.label,
          disabled: optionOrOptionGroup.disabled,
          key: 'optgroup' + hash(optionOrOptionGroup)
        }, optionOrOptionGroup.optGroupProps), optionOrOptionGroup.options.map(function (option) {
          return React.createElement(Option, {
            option: option,
            key: option.value.toString()
          });
        }));
      } else {
        return React.createElement(Option, {
          option: optionOrOptionGroup,
          key: optionOrOptionGroup.value.toString()
        });
      }
    })), this.props.icon && React.createElement(ArrowDownIcon, {
      className: classnames(styles$e.icon, (_e = {}, _e[styles$e.desktopIcon] = addDesktopStyles, _e))
    })), this.props.errorText && React.createElement("div", {
      className: styles$e.errorText
    }, this.props.errorText), this.props.helpText && React.createElement("div", {
      className: styles$e.helpText
    }, this.props.helpText));
  };

  ResponsiveSelect.defaultProps = {
    icon: true,
    fieldSize: 'medium',
    display: 'responsive'
  };
  return ResponsiveSelect;
}(React.Component);

var Select$1 = function Select$$1(_a) {
  var rest = __rest(_a, []);

  var isMobile = !useBreakpoint('sm');
  return React.createElement(ResponsiveSelect, __assign({
    isMobile: isMobile
  }, rest));
};

var styles$f = {"container":"styles_container__1VYk8"};

var TimePicker =
/** @class */
function (_super) {
  __extends(TimePicker, _super);

  function TimePicker(props) {
    var _this = _super.call(this, props) || this;

    _this.get24HourTime = function (marker, hour, minutes) {
      if (marker === 'AM') {
        if (hour === 12) {
          return "00:" + minutes;
        }

        return hour + ":" + minutes;
      } else {
        if (hour === 12) {
          return "12:" + minutes;
        }

        return hour + 12 + ":" + minutes;
      }
    };

    _this.generateTime = function (selectedDay, minDate) {
      var hourItems = ['AM', 'PM'].reduce(function (prev, marker) {
        return prev.concat([12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].reduce(function (prev1, hour) {
          return prev1.concat([{
            label: hour + ":00 " + marker,
            value: _this.get24HourTime(marker, hour, '00')
          }, {
            label: hour + ":30 " + marker,
            value: _this.get24HourTime(marker, hour, '30')
          }]);
        }, []));
      }, []);

      if (selectedDay && minDate && isSameDay(selectedDay, minDate)) {
        var hour = minDate.getHours();
        var minutes = minDate.getMinutes();
        hourItems = hourItems.slice(hour * 2 + (minutes >= 30 ? 2 : 1));
      }

      return hourItems;
    };

    _this.getValueFromDate = function (date) {
      if (date) {
        return date.getHours() + ":" + (date.getMinutes() === 0 ? '00' : '30');
      }
    };

    _this.onChangeTime = function (event) {
      var _a = _this.props,
          selectedDay = _a.selectedDay,
          onDayChange = _a.onDayChange;

      if (selectedDay && onDayChange) {
        _this.setState({
          value: event.target.value
        });

        var _b = event.target.value.split(':'),
            hour = _b[0],
            minutes = _b[1];

        var newDate = new Date(selectedDay);
        newDate.setHours(parseInt(hour, 10));
        newDate.setMinutes(parseInt(minutes, 10));
        onDayChange(newDate);
      }
    };

    var selectedDay = props.selectedDay,
        defaultTime = props.defaultTime;
    _this.parsedDefaultTime = selectedDay ? _this.getValueFromDate(selectedDay) : defaultTime;
    _this.state = {
      value: _this.parsedDefaultTime
    };
    return _this;
  }

  TimePicker.prototype.componentDidUpdate = function (prevProps) {
    var selectedDay = this.props.selectedDay;

    if (selectedDay && prevProps.selectedDay !== selectedDay) {
      this.setState({
        value: this.getValueFromDate(selectedDay)
      });
    }
  };

  TimePicker.prototype.render = function () {
    var value = this.state.value;
    var _a = this.props,
        selectedDay = _a.selectedDay,
        minDate = _a.minDate,
        noLabel = _a.noLabel,
        className = _a.className;
    return React.createElement("div", {
      className: classnames(styles$f.container, className)
    }, React.createElement(Select$1, {
      display: "standard",
      onChange: this.onChangeTime,
      options: this.generateTime(selectedDay, minDate),
      defaultValue: this.parsedDefaultTime,
      value: value,
      label: "Select a time",
      labelHidden: noLabel
    }));
  };

  return TimePicker;
}(React.Component);

var styles$g = {"breakpoint-sm":"(min-width: 768px)","z-index":"styles_z-index__nKm9W","desktop":"styles_desktop__LFYMI fixed styles_z-index__nKm9W","mobileContainer":"styles_mobileContainer__dq78S fixed flex flex-column justify-end items-center styles_z-index__nKm9W","mobile":"styles_mobile__24iV- flex flex-column items-center","overlay":"styles_overlay__1un4r flex flex-column justify-end","buttonsContainer":"styles_buttonsContainer__1ZuUi flex","buttonMobile":"styles_buttonMobile__3CqL7 flex items-center justify-center p2","cancel":"styles_cancel__7T7FQ styles_buttonMobile__3CqL7 flex items-center justify-center p2","done":"styles_done__2MOa7 styles_buttonMobile__3CqL7 flex items-center justify-center p2","appear":"styles_appear__2TQmA","appearActive":"styles_appearActive__zkuJ5","breakpointSm":"(min-width: 768px)","zIndex":"styles_z-index__nKm9W"};

var OverlayComponent =
/** @class */
function (_super) {
  __extends(OverlayComponent, _super);

  function OverlayComponent(props) {
    var _this = _super.call(this, props) || this;

    _this.updateDimensions = _this.updateDimensions.bind(_this);
    return _this;
  }

  OverlayComponent.prototype.updateDimensions = function () {
    this.forceUpdate();
  };

  OverlayComponent.prototype.componentDidMount = function () {
    window.addEventListener('resize', this.updateDimensions);
  };

  OverlayComponent.prototype.componentWillUnmount = function () {
    window.removeEventListener('resize', this.updateDimensions);
  };

  OverlayComponent.prototype.render = function () {
    // Client-side rendering only
    if (typeof window === 'undefined') {
      return null;
    }

    var _a = this.props,
        children = _a.children,
        onCloseOverlay = _a.onCloseOverlay,
        classNames = _a.classNames,
        selectedDay = _a.selectedDay,
        inputRef = _a.inputRef,
        isTimePicker = _a.isTimePicker,
        onDayChange = _a.onDayChange,
        defaultTime = _a.defaultTime,
        minDate = _a.minDate,
        rest = __rest(_a, ["children", "onCloseOverlay", "classNames", "selectedDay", "inputRef", "isTimePicker", "onDayChange", "defaultTime", "minDate"]);

    var location = inputRef.current && inputRef.current.getBoundingClientRect();
    var overlay = React.createElement(React.Fragment, null, React.createElement("div", {
      className: styles$g.overlay,
      onClick: onCloseOverlay
    }), React.createElement("div", {
      className: styles$g.mobileContainer
    }, React.createElement(CSSTransition, {
      timeout: 1000,
      in: true,
      appear: true,
      classNames: {
        appear: styles$g.appear,
        appearActive: styles$g.appearActive
      }
    }, React.createElement("div", __assign({
      className: styles$g.mobile
    }, rest), children, isTimePicker && React.createElement(TimePicker, {
      selectedDay: selectedDay,
      onDayChange: onDayChange,
      defaultTime: defaultTime,
      minDate: minDate
    }), React.createElement("div", {
      className: styles$g.buttonsContainer
    }, React.createElement("button", {
      className: styles$g.cancel,
      onClick: onCloseOverlay
    }, "Cancel"), React.createElement("button", {
      className: styles$g.done,
      onClick: onCloseOverlay,
      "data-testid": "date-done-button"
    }, "Done"))))), location && React.createElement("div", __assign({}, rest, {
      className: styles$g.desktop,
      style: {
        top: location.height + location.top,
        left: location.left
      }
    }), children, isTimePicker && React.createElement(TimePicker, {
      selectedDay: selectedDay,
      onDayChange: onDayChange,
      defaultTime: defaultTime,
      minDate: minDate
    })));
    var parentNode = document.querySelector('body');
    return ReactDOM.createPortal(overlay, parentNode);
  };

  return OverlayComponent;
}(React.Component);

var _extends$p = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$p(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var CalendarIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$p(_ref, ["styles"]);

  return React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "20",
    height: "20",
    viewBox: "0 0 20 20"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M-2-1h24v24H-2z"
  }), React.createElement("g", {
    stroke: "#9F9F9F",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    transform: "translate(1 1)"
  }, React.createElement("path", {
    d: "M13 4V0M5 4V0M0 7h18"
  }), React.createElement("rect", {
    width: "18",
    height: "16",
    y: "2",
    rx: "1"
  }))));
});

var styles$h = {"breakpoint-sm":"(min-width: 768px)","inputContainer":"styles_inputContainer__3fgBN flex","input":"styles_input__-pClH flex items-center","desktopInput":"styles_desktopInput__3w-iN","simpleLabel":"styles_simpleLabel__2KJPs","standardLabel":"styles_standardLabel__3QFO_","label":"styles_label__6UHi7","desktopLabel":"styles_desktopLabel__1U5KH","icon":"styles_icon__1p4Mb","leftIcon":"styles_leftIcon__1pTWQ styles_icon__1p4Mb","rightIcon":"styles_rightIcon__3dLfT styles_icon__1p4Mb","breakpointSm":"(min-width: 768px)"};

var InputComponent = function InputComponent(_a) {
  var _b, _c;

  var _d = _a.styleType,
      styleType = _d === void 0 ? 'responsive' : _d,
      label = _a.label,
      labelHidden = _a.labelHidden,
      iconPlacement = _a.iconPlacement,
      hideIcon = _a.hideIcon,
      innerRef = _a.innerRef,
      setInputRef = _a.setInputRef,
      rest = __rest(_a, ["styleType", "label", "labelHidden", "iconPlacement", "hideIcon", "innerRef", "setInputRef"]);

  var isMobile = !useBreakpoint('sm');
  var addDesktopStyles = styleType === 'standard' || styleType === 'responsive-inverse' && isMobile || styleType === 'responsive' && !isMobile;
  return React.createElement("div", {
    className: styles$h.inputContainer
  }, !labelHidden && React.createElement("label", {
    className: classnames(styles$h.label, (_b = {}, _b[styles$h.desktopLabel] = addDesktopStyles, _b))
  }, label), React.createElement("div", {
    className: classnames(styles$h.input, (_c = {}, _c[styles$h.desktopInput] = addDesktopStyles, _c)),
    ref: setInputRef
  }, iconPlacement === 'left' && !hideIcon && React.createElement(CalendarIcon, {
    className: styles$h.leftIcon
  }), React.createElement("input", __assign({}, rest, {
    ref: innerRef,
    readOnly: true
  }, rest.value ? {
    value: rest.value
  } : {})), iconPlacement === 'right' && !hideIcon && React.createElement(CalendarIcon, {
    className: styles$h.rightIcon
  })));
};

var styles$i = {"container":"styles_container__3S3Tl"};

var setDateToFirstMinute = function setDateToFirstMinute(date) {
  date.setHours(date.getHours() + 1);
  date.setMinutes(0);
  return date.getHours() + ":00";
};

var DatePicker =
/** @class */
function (_super) {
  __extends(DatePicker, _super);

  function DatePicker(props) {
    var _this = _super.call(this, props) || this;

    _this.ref = React.createRef();
    _this.handleCloseOverlay = _this.handleCloseOverlay.bind(_this);
    _this.setInputRef = _this.setInputRef.bind(_this);
    _this.handleDayChange = _this.handleDayChange.bind(_this);
    _this.handleOverflowDate = _this.handleOverflowDate.bind(_this);
    _this.state = {
      inputRef: React.createRef()
    }; // Set initial date as next hour

    var initialHour;

    if (props.selectedDay && props.onDayChange) {
      var newDate = new Date(props.selectedDay);
      initialHour = setDateToFirstMinute(newDate);
      props.onDayChange(newDate);
    } // Must use same instance of overlay component to avoid CSS transitions firing on every render


    _this.OverlayComponent = function (props) {
      return React.createElement(OverlayComponent, __assign({}, props, {
        selectedDay: _this.props.selectedDay || null,
        onCloseOverlay: _this.handleCloseOverlay,
        inputRef: _this.state.inputRef,
        isTimePicker: _this.props.isTimePicker,
        onDayChange: _this.props.onDayChange,
        minDate: _this.props.minDate,
        defaultTime: initialHour
      }));
    };

    _this.InputComponent = React.forwardRef(function (props, ref) {
      return React.createElement(InputComponent, __assign({}, props, {
        innerRef: ref,
        styleType: _this.props.styleType,
        setInputRef: _this.setInputRef,
        label: _this.props.label,
        labelHidden: _this.props.labelHidden,
        iconPlacement: _this.props.iconPlacement,
        hideIcon: _this.props.hideIcon
      }));
    });
    return _this;
  }

  DatePicker.prototype.componentDidUpdate = function (prevProps) {
    var _a = this.props,
        minDate = _a.minDate,
        selectedDay = _a.selectedDay;

    if (minDate && prevProps.minDate !== minDate && selectedDay) {
      if (isBefore(selectedDay, minDate)) {
        this.handleOverflowDate();
      }
    }
  };

  DatePicker.prototype.handleOverflowDate = function () {
    var _a = this.props,
        minDate = _a.minDate,
        onDayChange = _a.onDayChange;

    if (minDate && onDayChange) {
      var parsedMinDate = new Date(minDate);
      setDateToFirstMinute(parsedMinDate);
      onDayChange(parsedMinDate);
    }
  };

  DatePicker.prototype.setInputRef = function (node) {
    this.setState({
      inputRef: {
        current: node
      }
    });
  };

  DatePicker.prototype.handleCloseOverlay = function () {
    this.ref.current && this.ref.current.hideDayPicker();
  };

  DatePicker.prototype.handleDayChange = function (day) {
    var _a = this.props,
        selectedDay = _a.selectedDay,
        onDayChange = _a.onDayChange,
        minDate = _a.minDate;

    if (onDayChange) {
      // Prevent to select a time before specified minDate
      if (minDate && isBefore(day, minDate)) {
        this.handleOverflowDate();
      } else {
        if (selectedDay) {
          // Sync with current hour/minutes
          var hour = selectedDay.getHours();
          var minutes = selectedDay.getMinutes();
          day.setHours(hour);
          day.setMinutes(minutes);
        }

        onDayChange(day);
      }
    }
  };

  DatePicker.prototype.render = function () {
    var _a = this.props,
        selectedDay = _a.selectedDay,
        dayPickerProps = _a.dayPickerProps,
        label = _a.label,
        labelHidden = _a.labelHidden,
        placeholder = _a.placeholder,
        isTimePicker = _a.isTimePicker,
        minDate = _a.minDate,
        className = _a.className,
        props = __rest(_a, ["selectedDay", "dayPickerProps", "label", "labelHidden", "placeholder", "isTimePicker", "minDate", "className"]);

    var dateFormat = isTimePicker ? 'MM/dd/y hh:mm a' : 'MM/dd/y';
    var defaultDayPickerProps = {
      selectedDays: selectedDay,
      showOutsideDays: true,
      navbarElement: Navigation
    };
    return React.createElement("div", {
      className: classnames(styles$i.container, className)
    }, React.createElement(DayPickerInput, __assign({}, props, {
      ref: this.ref,
      value: selectedDay ? format(selectedDay, dateFormat) : undefined,
      onDayChange: this.handleDayChange,
      clickUnselectsDay: false,
      placeholder: placeholder || 'MM/DD/YYYY',
      format: dateFormat,
      formatDate: function formatDate(date, dateFormat) {
        return format(date, dateFormat);
      },
      keepFocus: false,
      dayPickerProps: __assign({}, defaultDayPickerProps, minDate ? {
        disabledDays: {
          before: minDate
        }
      } : {}, dayPickerProps),
      component: this.InputComponent,
      overlayComponent: this.OverlayComponent,
      hideOnDayClick: false
    })));
  };

  DatePicker.defaultProps = {
    iconPlacement: 'right',
    minDate: new Date()
  };
  return DatePicker;
}(React.Component);

var _extends$q = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$q(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ArrowDown = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$q(_ref, ["styles"]);

  return React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M3.115 5.295l-1.41 1.41 6 6 6-6-1.41-1.41-4.59 4.58z"
  }));
});

var styles$j = {"breakpoint-sm":"(min-width: 768px)","container":"styles_container__77PwT relative block","icon":"styles_icon__1yLaI absolute","select":"styles_select__3cL0R styles_select__3cL0R m0","label":"styles_label__2pKwT","mobileSimpleLabel":"styles_mobileSimpleLabel__3_bJS","desktopSimpleLabel":"styles_desktopSimpleLabel__1zqQU","mobileHide":"styles_mobileHide__3-S02","desktopHide":"styles_desktopHide__3uZXD","mobileSimple":"styles_mobileSimple__22jjt","desktopSimple":"styles_desktopSimple__3M-aA","valid":"styles_valid__2siaZ","xsmall":"styles_xsmall__3Bory","small":"styles_small__nRgUo","medium":"styles_medium__CPwkw","large":"styles_large__3B-le","xlarge":"styles_xlarge__eeJlv","breakpointSm":"(min-width: 768px)"};

var Dropdown =
/** @class */
function (_super) {
  __extends(Dropdown, _super);

  function Dropdown() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  Dropdown.prototype.isValid = function () {
    var _this = this;

    return this.props.isRequired && this.props.options.some(function (option) {
      return option.value === _this.props.value;
    });
  };

  Dropdown.prototype.render = function () {
    var _a, _b, _c;

    var _d = this.props,
        _e = _d.icon,
        icon = _e === void 0 ? true : _e,
        _f = _d.fieldSize,
        fieldSize = _f === void 0 ? 'medium' : _f,
        _g = _d.desktopStyle,
        desktopStyle = _g === void 0 ? 'standard' : _g,
        _h = _d.mobileStyle,
        mobileStyle = _h === void 0 ? 'simple' : _h,
        defaultOption = _d.defaultOption,
        isRequired = _d.isRequired,
        label = _d.label,
        className = _d.className,
        options = _d.options,
        rest = __rest(_d, ["icon", "fieldSize", "desktopStyle", "mobileStyle", "defaultOption", "isRequired", "label", "className", "options"]);

    return React.createElement("label", {
      className: className
    }, label && React.createElement("div", {
      className: classnames(styles$j.label, (_a = {}, _a[styles$j.desktopSimpleLabel] = desktopStyle === 'simple', _a[styles$j.mobileSimpleLabel] = mobileStyle === 'simple', _a))
    }, label), React.createElement("div", {
      className: styles$j.container
    }, React.createElement("select", __assign({
      className: classnames(styles$j.select, styles$j[fieldSize], (_b = {}, _b[styles$j.valid] = this.isValid(), _b[styles$j.desktopSimple] = desktopStyle === 'simple', _b[styles$j.mobileSimple] = mobileStyle === 'simple', _b))
    }, rest), defaultOption && React.createElement("option", {
      value: defaultOption.value
    }, defaultOption.label), options.map(function (_a, idx) {
      var label = _a.label,
          rest = __rest(_a, ["label"]);

      return React.createElement("option", __assign({
        key: idx
      }, rest), label);
    })), icon && React.createElement(ArrowDown, {
      className: classnames(styles$j.icon, (_c = {}, _c[styles$j.mobileHide] = mobileStyle === 'simple', _c[styles$j.desktopHide] = desktopStyle === 'simple', _c))
    })));
  };

  return Dropdown;
}(React.Component);

var styles$k = {"wrapper":"styles_wrapper__1NFz6 items-center","disabled":"styles_disabled__3YlCV","input":"styles_input__KSJEj","radioButton":"styles_radioButton__1w0VL","label":"styles_label__1czvY inline-block","xsmall":"styles_xsmall__3XL_w","small":"styles_small__63F3m","medium":"styles_medium__SkWIU","large":"styles_large__2Bgc_","xlarge":"styles_xlarge__1lCzr"};

// All other props are applied to the hidden `input`.

var Radio = function Radio(_a) {
  var _b;

  var _c = _a.height,
      height = _c === void 0 ? 'medium' : _c,
      className = _a.className,
      disabled = _a.disabled,
      children = _a.children,
      labelClassName = _a.labelClassName,
      radioClassName = _a.radioClassName,
      rest = __rest(_a, ["height", "className", "disabled", "children", "labelClassName", "radioClassName"]);

  var classes = classnames(styles$k.wrapper, styles$k[height], className, (_b = {}, _b[styles$k.disabled] = disabled, _b));
  return React.createElement("label", {
    className: classes
  }, React.createElement("input", __assign({
    disabled: disabled,
    type: "radio",
    className: styles$k.input
  }, rest)), React.createElement("span", {
    className: classnames(styles$k.radioButton, radioClassName)
  }), React.createElement("span", {
    className: classnames(styles$k.label, labelClassName)
  }, children));
};

var ErrorBoundary =
/** @class */
function (_super) {
  __extends(ErrorBoundary, _super);

  function ErrorBoundary() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  ErrorBoundary.prototype.componentDidCatch = function (error, errorInfo) {
    console.error(error.name, error.message, error.stack, errorInfo);
    this.props.handleError(error, errorInfo);
  };

  ErrorBoundary.prototype.render = function () {
    return this.props.children;
  };

  return ErrorBoundary;
}(React.Component);

var styles$l = {"icon":"styles_icon__2RUgq"};

var _extends$r = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$r(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var beer = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$r(_ref, ["styles"]);

  return React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M15 6h-1.3c.2-.3.3-.6.3-1 0-1.1-.9-2-2-2-.4 0-.8.1-1.1.3C10.6 1.4 9 0 7 0S3.4 1.4 3.1 3.3C2.8 3.1 2.4 3 2 3 .9 3 0 3.9 0 5c0 .7.4 1.4 1 1.7V15c0 .6.4 1 1 1h10c.6 0 1-.4 1-1v-3h2c.6 0 1-.4 1-1V7c0-.6-.4-1-1-1zm-4 8H3V7h2v2.5c0 .8.7 1.5 1.5 1.5S8 10.3 8 9.5V7h3v7zm3-4h-1V8h1v2z"
  }));
});

var _extends$s = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$s(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var cake = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$s(_ref, ["styles"]);

  return React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M8 6c1.1 0 2-.9 2-2S8 0 8 0 6 2.9 6 4s.9 2 2 2zm7 5h-1V8c0-.6-.4-1-1-1H3c-.6 0-1 .4-1 1v3H1c-.6 0-1 .4-1 1v3c0 .6.4 1 1 1h14c.6 0 1-.4 1-1v-3c0-.6-.4-1-1-1z"
  }));
});

var _extends$t = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$t(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var charters = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$t(_ref, ["styles"]);

  return React.createElement("svg", _extends$t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "15",
    viewBox: "0 0 16 15"
  }, props), React.createElement("path", {
    d: "M16 15h-1a5.534 5.534 0 0 1-3.5-1.229 5.581 5.581 0 0 1-7-.01A5.55 5.55 0 0 1 1 15H0v-2h1a3.525 3.525 0 0 0 2.735-1.277l.764-.91.766.909a3.566 3.566 0 0 0 5.462.01l.773-.943.773.943A3.5 3.5 0 0 0 15 13h1v2zm0-5h-1a5.534 5.534 0 0 1-3.5-1.229 5.581 5.581 0 0 1-7-.01A5.55 5.55 0 0 1 1 10H0V8h1a3.525 3.525 0 0 0 2.735-1.277l.764-.91.766.909a3.566 3.566 0 0 0 5.462.01l.773-.943.773.943A3.5 3.5 0 0 0 15 8h1v2zm0-5h-1a5.534 5.534 0 0 1-3.5-1.229A5.534 5.534 0 0 1 8 5a5.556 5.556 0 0 1-3.5-1.239A5.55 5.55 0 0 1 1 5H0V3h1a3.525 3.525 0 0 0 2.735-1.277L4.5.813l.766.909A3.525 3.525 0 0 0 8 3a3.5 3.5 0 0 0 2.727-1.268L11.5.789l.773.943A3.5 3.5 0 0 0 15 3h1v2z"
  }));
});

var _extends$u = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$u(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var chat = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$u(_ref, ["styles"]);

  return React.createElement("svg", _extends$u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M6.5 0C2.91 0 0 2.462 0 5.5c0 1.075.37 2.074 1 2.922V12l2.699-1.542A7.454 7.454 0 0 0 6.5 11c3.59 0 6.5-2.462 6.5-5.5S10.09 0 6.5 0z"
  }), React.createElement("path", {
    d: "M16 9.5c0-.987-.429-1.897-1.147-2.639C14.124 10.348 10.66 13 6.5 13c-.103 0-.202-.018-.305-.021C7.231 13.617 8.556 14 10 14c.449 0 .886-.04 1.307-.11L15 16v-4h-.012C15.627 11.285 16 10.425 16 9.5z"
  }));
});

var _extends$v = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$v(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var check = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$v(_ref, ["styles"]);

  return React.createElement("svg", _extends$v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z"
  }));
});

var _extends$w = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$w(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var cocktail = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$w(_ref, ["styles"]);

  return React.createElement("svg", _extends$w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M16 5.5C16 3.6 14.4 2 12.5 2c-1.8 0-3.2 1.3-3.4 3H6L5 .8C4.9.3 4.5 0 4 0H1C.4 0 0 .4 0 1s.4 1 1 1h2.2L4 5H1v9c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V8.9c1.7-.2 3-1.6 3-3.4z"
  }));
});

var _extends$x = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$x(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var cycling = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$x(_ref, ["styles"]);

  return React.createElement("svg", _extends$x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M12.809 9.031L10.976.783A1 1 0 0 0 10 0H6v2h3.2l.774 3.484-5.5 3.67A3.451 3.451 0 0 0 3.5 9 3.5 3.5 0 1 0 7 12.5a3.465 3.465 0 0 0-.746-2.134l4.183-2.789.412 1.856a3.5 3.5 0 1 0 1.959-.4l.001-.002zM3.5 14a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm9 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"
  }), React.createElement("path", {
    d: "M4.581 6.674l1.664-1.109-1.413-2.12A1 1 0 0 0 4 3H1v2h2.465l1.116 1.674z"
  }));
});

var _extends$y = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$y(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var diploma = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$y(_ref, ["styles"]);

  return React.createElement("svg", _extends$y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M13 16H1c-.6 0-1-.4-1-1V1c0-.6.4-1 1-1h12v2H2v12h10v-1h2v2c0 .6-.4 1-1 1z"
  }), React.createElement("path", {
    d: "M16 6c0-1.7-1.3-3-3-3s-3 1.3-3 3c0 .9.4 1.7 1 2.2V12l2-2 2 2V8.2c.6-.5 1-1.3 1-2.2zM4 5h4v2H4zm0 4h5v2H4z"
  }));
});

var _extends$z = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$z(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var drink = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$z(_ref, ["styles"]);

  return React.createElement("svg", _extends$z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "16",
    viewBox: "0 0 14 16"
  }, props), React.createElement("path", {
    d: "M13.732.318A1 1 0 0 0 13 0H1a1 1 0 0 0-1 1.071l.934 13.072a2.007 2.007 0 0 0 2 1.857h8.138a2.007 2.007 0 0 0 1.995-1.857L14 1.071a1 1 0 0 0-.268-.753zM11.926 2L11.5 8h-9l-.426-6h9.852z"
  }));
});

var _extends$A = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$A(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var golfClub = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$A(_ref, ["styles"]);

  return React.createElement("svg", _extends$A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "14",
    viewBox: "0 0 12 14"
  }, props), React.createElement("path", {
    d: "M8.038.534L1.372 4.238A2.667 2.667 0 0 0 0 6.569V14h2V9h7.333A2.667 2.667 0 0 0 12 6.333V2.865C12 .832 9.816-.453 8.038.535z"
  }));
});

var _extends$B = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$B(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var golfCourse = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$B(_ref, ["styles"]);

  return React.createElement("svg", _extends$B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "16",
    viewBox: "0 0 14 16"
  }, props), React.createElement("path", {
    d: "M2 14h12v2H2z"
  }), React.createElement("circle", {
    cx: "10.5",
    cy: "10.5",
    r: "2.5"
  }), React.createElement("path", {
    d: "M7 10.5a3.5 3.5 0 0 1 6.367-2l.165-1.156a3 3 0 0 0-3.918-3.27L3.571 6.089 1.958.713a1 1 0 1 0-1.916.574l2.786 9.287A1.988 1.988 0 0 0 4.744 12H7.35A3.464 3.464 0 0 1 7 10.5z"
  }));
});

var _extends$C = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$C(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var jewelry = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$C(_ref, ["styles"]);

  return React.createElement("svg", _extends$C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "15",
    viewBox: "0 0 16 15"
  }, props), React.createElement("path", {
    d: "M2.211.686L2.2.706.167 3.956h3.308zM4.53 3.45L7.429.091H3.231l.007.018L4.53 3.45zM12.762.109l.007-.018H8.572l2.897 3.36L12.762.108zm3.071 3.846L13.8.707l-.012-.02-1.266 3.268zM11 5.172L8 8.924 5 5.172H.086L7.23 13.71l.4.478a.486.486 0 0 0 .37.172.485.485 0 0 0 .368-.172l.4-.479 7.146-8.538H11z"
  }), React.createElement("path", {
    d: "M8 6.96l2.366-3.005L8 1.212 5.634 3.955z"
  }));
});

var _extends$D = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$D(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var heart = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$D(_ref, ["styles"]);

  return React.createElement("svg", _extends$D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "14",
    viewBox: "0 0 16 14"
  }, props), React.createElement("path", {
    d: "M14.682 1.318a4.5 4.5 0 0 0-6.364 0c-.121.121-.214.259-.318.389-.104-.13-.197-.268-.318-.389a4.5 4.5 0 0 0-6.364 6.364L8 14l6.682-6.318a4.5 4.5 0 0 0 0-6.364z"
  }));
});

var _extends$E = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$E(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var location = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$E(_ref, ["styles"]);

  return React.createElement("svg", _extends$E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "16",
    viewBox: "0 0 14 16"
  }, props), React.createElement("path", {
    d: "M7 0C3.1 0 0 3.1 0 7c0 1.9.7 3.7 2.1 5 .1.1 4.1 3.7 4.2 3.8.4.3 1 .3 1.3 0 .1-.1 4.2-3.7 4.2-3.8 1.4-1.3 2.1-3.1 2.1-5 .1-3.9-3-7-6.9-7zm0 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
  }));
});

var _extends$F = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$F(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var mountain = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$F(_ref, ["styles"]);

  return React.createElement("svg", _extends$F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "12",
    viewBox: "0 0 16 12"
  }, props), React.createElement("path", {
    d: "M4.496 3.423L3.5 2 0 7h2zM16 7l-3.5-5-1.01 1.442L13.892 7zm-4.676-2.014L8 0 4.666 5l6.658-.014zM3.995 6.007L0 12h16l-3.999-5.998-8.006.005z"
  }));
});

var _extends$G = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$G(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var star = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$G(_ref, ["styles"]);

  return React.createElement("svg", _extends$G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "18",
    height: "18",
    viewBox: "0 0 18 18"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.65 6.106l5.925.86-4.288 4.18 1.012 5.901L9 14.261l-5.299 2.786 1.012-5.901-4.288-4.18 5.925-.86L9 .737l2.65 5.369z"
  }));
});



var icons = /*#__PURE__*/Object.freeze({
    beer: beer,
    cake: cake,
    charters: charters,
    chat: chat,
    check: check,
    cocktail: cocktail,
    cycling: cycling,
    diploma: diploma,
    drink: drink,
    golfClub: golfClub,
    golfCourse: golfCourse,
    jewelry: jewelry,
    heart: heart,
    location: location,
    mountain: mountain,
    star: star
});

var ExpertProfileHighlightIcon = function ExpertProfileHighlightIcon(_a) {
  var glyph = _a.glyph,
      className = _a.className,
      rest = __rest(_a, ["glyph", "className"]);

  var classes = classnames(styles$l.icon, className);
  var exportName = camelCase(glyph);
  var Icon; // @ts-ignore typescript doesn't recognize the glob import

  Icon = icons[exportName] || check;
  return React.createElement(Icon, __assign({
    className: classes
  }, rest));
};

var styles$m = {"container":"styles_container__2h3lF relative flex align-end","sizing-cover":"styles_sizing-cover__qucOn","image":"styles_image__36sKi absolute top-0 left-0 right-0 bottom-0","sizing-contain":"styles_sizing-contain__sJ-oq","aspect-ratio-wide":"styles_aspect-ratio-wide__2wDrX","aspect-ratio-tall":"styles_aspect-ratio-tall__xGGdu","aspect-ratio-square":"styles_aspect-ratio-square__SJU74","content":"styles_content__2vzgm absolute top-0 left-0 right-0 bottom-0","sizingCover":"styles_sizing-cover__qucOn","sizingContain":"styles_sizing-contain__sJ-oq","aspectRatioWide":"styles_aspect-ratio-wide__2wDrX","aspectRatioTall":"styles_aspect-ratio-tall__xGGdu","aspectRatioSquare":"styles_aspect-ratio-square__SJU74"};

var FixedAspectRatioImage = function FixedAspectRatioImage(_a) {
  var _b = _a.aspectRatio,
      aspectRatio = _b === void 0 ? 'square' : _b,
      _c = _a.sizing,
      sizing = _c === void 0 ? 'contain' : _c,
      className = _a.className,
      contentClassName = _a.contentClassName,
      children = _a.children,
      rest = __rest(_a, ["aspectRatio", "sizing", "className", "contentClassName", "children"]);

  var paddingBottom = typeof aspectRatio === 'number' ? 1 / aspectRatio * 100 + "%" : undefined;
  return React.createElement("div", {
    className: classnames(styles$m.container, styles$m["sizing-" + sizing], typeof aspectRatio === 'string' && styles$m["aspect-ratio-" + aspectRatio]),
    style: {
      paddingBottom: paddingBottom
    }
  }, React.createElement(Image, __assign({
    className: classnames(styles$m.image, className)
  }, rest)), React.createElement("div", {
    className: classnames(styles$m.content, contentClassName)
  }, children));
};

var styles$n = {"wrapper":"styles_wrapper__2FYjs"};

var Header =
/** @class */
function (_super) {
  __extends(Header, _super);

  function Header(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {};
    _this.handleResize = _this.handleResize.bind(_this);
    return _this;
  }

  Header.prototype.handleResize = function (rect) {
    if (rect.bounds) {
      this.setState({
        height: rect.bounds.height
      });
    }
  };

  Header.prototype.render = function () {
    var _a = this.props,
        _b = _a.conceals,
        conceals = _b === void 0 ? true : _b,
        _c = _a.reveals,
        reveals = _c === void 0 ? true : _c,
        _d = _a.zIndex,
        zIndex = _d === void 0 ? 110000 : _d,
        children = _a.children,
        rest = __rest(_a, ["conceals", "reveals", "zIndex", "children"]); // Headroom should manage this for us, but our own height measurement is more reliable


    var height = this.state.height ? this.state.height : undefined;
    var wrapperStyle = {
      position: 'relative',
      minHeight: height,
      maxHeight: height,
      height: height,
      zIndex: zIndex,
      // TODO: This logic is incorrect. The header should support concealing without revealing.
      transform: !reveals ? 'none !important' : undefined
    };
    return React.createElement(Headroom, {
      disable: !conceals,
      wrapperStyle: wrapperStyle,
      className: styles$n.wrapper,
      calcHeightOnResize: false
    }, React.createElement(Measure, {
      bounds: true,
      onResize: this.handleResize
    }, function (_a) {
      var measureRef = _a.measureRef;
      return React.createElement("header", __assign({
        ref: measureRef
      }, rest), children);
    }));
  };

  return Header;
}(React.Component);

function deprecate(Component, message) {
  var logDeprecationWarning = once(function () {
    var displayName = Component.displayName || Component.name;

    if (process.env.NODE_ENV !== 'test') {
      console.warn(message || "The <" + displayName + " /> component is deprecated.");
    }
  });
  return function (props) {
    logDeprecationWarning();
    return React.createElement(Component, __assign({}, props));
  };
}

var styles$o = {"icon":"styles_icon__3am-1"};

var Icon = function Icon(_a) {
  var Glyph = _a.glyph,
      className = _a.className,
      rest = __rest(_a, ["glyph", "className"]);

  return React.createElement(Glyph, __assign({
    className: classnames(styles$o.icon, className)
  }, rest));
};

var index = deprecate(Icon, "The <Icon> component is deprecated. Use the imported SVG as a React component directly in JSX instead.");

var styles$p = {"footer":"styles_footer__2XYQk","fixed":"styles_fixed__dLcbs styles_footer__2XYQk fixed","sticky":"styles_sticky__3PdjY styles_footer__2XYQk absolute"};

var Footer =
/** @class */
function (_super) {
  __extends(Footer, _super);

  function Footer(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {};
    _this.handleResize = _this.handleResize.bind(_this);
    return _this;
  }

  Footer.prototype.handleResize = function (contentRect) {
    if (contentRect.bounds) {
      this.setState({
        height: contentRect.bounds.height
      });
    }
  };

  Footer.prototype.render = function () {
    var _a = this.props,
        className = _a.className,
        children = _a.children,
        position = _a.position,
        rest = __rest(_a, ["className", "children", "position"]);

    return React.createElement("div", {
      style: {
        height: this.state.height
      }
    }, React.createElement(Measure, {
      bounds: true,
      onResize: this.handleResize
    }, function (_a) {
      var measureRef = _a.measureRef;
      return React.createElement("footer", __assign({
        className: classnames(className, styles$p[position]),
        ref: measureRef
      }, rest), children);
    }));
  };

  return Footer;
}(PureComponent);
var FixedFooter = function FixedFooter(props) {
  return React.createElement(Footer, __assign({
    position: "fixed"
  }, props));
};
var StickyFooter = function StickyFooter(props) {
  return React.createElement(Footer, __assign({
    position: "sticky"
  }, props));
};

/**
 * Parse a grid area template into a matrix of strings
 */

function parseTemplate(template) {
  var sourceTemplateRowsMatch = template.match(/\"[^\"]+\"/g);

  if (!sourceTemplateRowsMatch) {
    throw new Error("Can not parse template " + template);
  }

  return sourceTemplateRowsMatch.map(function (row) {
    return row.slice(1, -1);
  }) // Remove the leading and trailing double quotes
  .map(function (row) {
    return row.trim();
  }) // Remove excess leading and trailing whitespace
  .map(function (row) {
    return row.split(/\s+/);
  }); // Tokenize by whitespace
}
/**
 * Trim excess whitespace from a grid area template
 */

function minifyTemplate(template) {
  var parsed = parseTemplate(template);
  return '"' + parsed.map(function (row) {
    return row.join(' ');
  }).join('" "') + '"';
}
/**
 * Calculate the number of columns in a given grid area template
 */

function columnCountFromTemplate(template) {
  var parsed = parseTemplate(template);
  return Math.max.apply(Math, parsed.map(function (row) {
    return row.length;
  }));
}
/**
 * Given a grid areas template, `sourceTemplate`, extrapolate it to fill `targetColumnCount`:
 *
 * For example, the template:
 *
 *     "a a a b c d"
 *     "a a a e f g"
 *
 * Extrapolated to `12` columns:
 *
 *     "a a a a a a b b c c d d"
 *     "a a a a a a e e f f g g"
 *
 * Note: spacing is not preserved.
 */

function extrapolateTemplate(templateString, targetColumnCount) {
  var template = parseTemplate(templateString);
  var scaleFactor = targetColumnCount / template[0].length;

  if (!isInteger(scaleFactor)) {
    throw new Error("Can not evenly extrapolate template " + templateString + " to " + targetColumnCount + " columns.");
  }

  return template.map(function (row) {
    var extrapolatedRow = row.reduce(function (multiplied, areaName) {
      return multiplied.concat(times(scaleFactor, function () {
        return areaName;
      }));
    }, []).join(' ');
    return "\"" + extrapolatedRow + "\"";
  }).join(' ');
}
/**
 * Given a griad areas template, determine if a given area name is present in that template
 */

function doesTemplateIncludeArea(templateString, areaName) {
  return flatten(parseTemplate(templateString)).includes(areaName);
}

/**
 * A mapping of breakpoint names to width values
 *
 * { "xs": "360px", "sm": "768px", "md": "960px", ... }
 */

var BREAKPOINTS = mapKeys(pickBy(cssVars, function (_varValue, varName) {
  return varName.startsWith('breakpoint-');
}), function (_varValue, varName) {
  return varName.slice('breakpoint-'.length).toLowerCase();
});
/**
 * The breakpoint names sorted in ascending order by breakpoint size
 *
 * ["xs", "sm", "md", "lg", ...]
 *
 * Assumes that all breakpoints are defined in the same unit ("px", "rem", etc)
 */

var SORTED_BREAKPOINT_NAMES = Object.keys(BREAKPOINTS).sort(function (a, b) {
  var breakpointA = parseInt(BREAKPOINTS[a], 10);
  var breakpointB = parseInt(BREAKPOINTS[b], 10);
  return breakpointA - breakpointB;
});
/**
 * The breakpoints where the number of columns change:
 *   { "sm": 8, "md": 12 }
 */

var COLUMN_BREAKPOINTS = mapValues(mapKeys(pickBy(cssVars, function (_varValue, varName) {
  return varName.startsWith('columns-');
}), function (_varValue, varName) {
  return varName.slice('columns-'.length).toLowerCase();
}), function (varValue) {
  return parseInt(varValue, 10);
});
/**
 * The COLUMN_BREAKPOINTS, as an array, sorted by number of columns
 *
 * [["sm", 8], ["md", 12]]
 */

var COLUMN_BREAKPOINT_TUPLES = sortBy(toPairs(COLUMN_BREAKPOINTS), function (tuple) {
  return tuple[1];
});
/**
 * Given a breakpoint name (or `null`, representing the "default" breakpoint)
 *   calculate the expected number of columns
 */

function columnCountFromBreakpoint(breakpointName) {
  var columnCount = parseInt(cssVars.defaultColumns, 10);

  if (breakpointName === null) {
    return columnCount;
  }

  for (var _i = 0, SORTED_BREAKPOINT_NAMES_1 = SORTED_BREAKPOINT_NAMES; _i < SORTED_BREAKPOINT_NAMES_1.length; _i++) {
    var otherBreakpointName = SORTED_BREAKPOINT_NAMES_1[_i];

    if (COLUMN_BREAKPOINTS.hasOwnProperty(otherBreakpointName)) {
      columnCount = COLUMN_BREAKPOINTS[otherBreakpointName];
    }

    if (otherBreakpointName == breakpointName) {
      return columnCount;
    }
  }

  return columnCount;
}

var map = {};

var styles$q = {"breakpoint-xs":"360px","breakpoint-sm":"768px","breakpoint-md":"960px","breakpoint-lg":"1200px","breakpoint-xlg":"1440px","breakpoint-xxlg":"1600px","default-columns":"2","columns-sm":"8","columns-md":"12","col-1":"styles_col-1__2BNlB","col-2":"styles_col-2__189Zr","xs-col-1":"styles_xs-col-1__3Aw0a","xs-col-2":"styles_xs-col-2__30GvB","sm-col-1":"styles_sm-col-1__US1rQ","sm-col-2":"styles_sm-col-2__1i0RY","sm-col-3":"styles_sm-col-3__1IHXD","sm-col-4":"styles_sm-col-4__5kwYK","sm-col-5":"styles_sm-col-5__BUzt5","sm-col-6":"styles_sm-col-6__2_cX4","sm-col-7":"styles_sm-col-7__2Zrm3","sm-col-8":"styles_sm-col-8__xGVeN","md-col-1":"styles_md-col-1__2DqmH","md-col-2":"styles_md-col-2__2WNEu","md-col-3":"styles_md-col-3__ZztCl","md-col-4":"styles_md-col-4__zQDOW","md-col-5":"styles_md-col-5__2wHFI","md-col-6":"styles_md-col-6__rOgXY","md-col-7":"styles_md-col-7__fXfRK","md-col-8":"styles_md-col-8__uGVgh","md-col-9":"styles_md-col-9__1JE6h","md-col-10":"styles_md-col-10__3aA_t","md-col-11":"styles_md-col-11__3jkJH","md-col-12":"styles_md-col-12__1RtoU","lg-col-1":"styles_lg-col-1__2xvkV","lg-col-2":"styles_lg-col-2__3iUIl","lg-col-3":"styles_lg-col-3__2EEeN","lg-col-4":"styles_lg-col-4__X-TxL","lg-col-5":"styles_lg-col-5__1gbfo","lg-col-6":"styles_lg-col-6__3m1zS","lg-col-7":"styles_lg-col-7__1Esku","lg-col-8":"styles_lg-col-8__3faSF","lg-col-9":"styles_lg-col-9__z-0rj","lg-col-10":"styles_lg-col-10__IJjQK","lg-col-11":"styles_lg-col-11__s4bar","lg-col-12":"styles_lg-col-12__25NHM","xlg-col-1":"styles_xlg-col-1__3swYJ","xlg-col-2":"styles_xlg-col-2__7j0QE","xlg-col-3":"styles_xlg-col-3__6yHfR","xlg-col-4":"styles_xlg-col-4__2A_O7","xlg-col-5":"styles_xlg-col-5__3ObM7","xlg-col-6":"styles_xlg-col-6__2P0Pb","xlg-col-7":"styles_xlg-col-7__1oeKF","xlg-col-8":"styles_xlg-col-8__2Wwdi","xlg-col-9":"styles_xlg-col-9__3B487","xlg-col-10":"styles_xlg-col-10__1xwME","xlg-col-11":"styles_xlg-col-11__3Pk5B","xlg-col-12":"styles_xlg-col-12__uqjZu","xxlg-col-1":"styles_xxlg-col-1__2rKAo","xxlg-col-2":"styles_xxlg-col-2__3g-HQ","xxlg-col-3":"styles_xxlg-col-3__1VodL","xxlg-col-4":"styles_xxlg-col-4__ZtFFX","xxlg-col-5":"styles_xxlg-col-5__1VGLC","xxlg-col-6":"styles_xxlg-col-6__2Hm7m","xxlg-col-7":"styles_xxlg-col-7__o1pc7","xxlg-col-8":"styles_xxlg-col-8__16FbC","xxlg-col-9":"styles_xxlg-col-9__1B41Y","xxlg-col-10":"styles_xxlg-col-10__3sPN1","xxlg-col-11":"styles_xxlg-col-11__1cfu_","xxlg-col-12":"styles_xxlg-col-12__1bbbb","row-2":"styles_row-2__pY1aL","row-3":"styles_row-3__1r2Fe","row-4":"styles_row-4__7Ytjg","row-5":"styles_row-5__3D11X","row-6":"styles_row-6__3KalP","xs-row-2":"styles_xs-row-2__1KC0n","xs-row-3":"styles_xs-row-3__3vsFf","xs-row-4":"styles_xs-row-4__37SBy","xs-row-5":"styles_xs-row-5__1SrIR","xs-row-6":"styles_xs-row-6__2-M92","sm-row-2":"styles_sm-row-2__3Jrc6","sm-row-3":"styles_sm-row-3__2PUVL","sm-row-4":"styles_sm-row-4__3okvu","sm-row-5":"styles_sm-row-5__-GIhx","sm-row-6":"styles_sm-row-6__2VJtm","md-row-2":"styles_md-row-2__8VvUk","md-row-3":"styles_md-row-3__IO_ss","md-row-4":"styles_md-row-4__4zqbR","md-row-5":"styles_md-row-5__1myKc","md-row-6":"styles_md-row-6__2J6ke","lg-row-2":"styles_lg-row-2__3KLLk","lg-row-3":"styles_lg-row-3__cSV2B","lg-row-4":"styles_lg-row-4__1GN_G","lg-row-5":"styles_lg-row-5__2Zvwg","lg-row-6":"styles_lg-row-6__101_1","xlg-row-2":"styles_xlg-row-2__2pVlH","xlg-row-3":"styles_xlg-row-3__PVMA-","xlg-row-4":"styles_xlg-row-4__346IS","xlg-row-5":"styles_xlg-row-5__3ru5r","xlg-row-6":"styles_xlg-row-6__1D-pJ","xxlg-row-2":"styles_xxlg-row-2__ppRmh","xxlg-row-3":"styles_xxlg-row-3__1y56L","xxlg-row-4":"styles_xxlg-row-4__3MU-o","xxlg-row-5":"styles_xxlg-row-5__3mK_q","xxlg-row-6":"styles_xxlg-row-6__2k-Vu","grid-item-base":"styles_grid-item-base__32oYF","hide":"styles_hide__23mhT","show":"styles_show__ShLdo","xs-hide":"styles_xs-hide__37j4O","xs-show":"styles_xs-show__1rWnY","sm-hide":"styles_sm-hide__jeG1S","sm-show":"styles_sm-show__1o-_v","md-hide":"styles_md-hide__2DhLR","md-show":"styles_md-show__gVteQ","lg-hide":"styles_lg-hide__31eOq","lg-show":"styles_lg-show__1n-oW","xlg-hide":"styles_xlg-hide__1zBca","xlg-show":"styles_xlg-show__YTclr","xxlg-hide":"styles_xxlg-hide__32fq2","xxlg-show":"styles_xxlg-show__19fte","breakpointXs":"360px","breakpointSm":"768px","breakpointMd":"960px","breakpointLg":"1200px","breakpointXlg":"1440px","breakpointXxlg":"1600px","defaultColumns":"2","columnsSm":"8","columnsMd":"12","col1":"styles_col-1__2BNlB","col2":"styles_col-2__189Zr","xsCol1":"styles_xs-col-1__3Aw0a","xsCol2":"styles_xs-col-2__30GvB","smCol1":"styles_sm-col-1__US1rQ","smCol2":"styles_sm-col-2__1i0RY","smCol3":"styles_sm-col-3__1IHXD","smCol4":"styles_sm-col-4__5kwYK","smCol5":"styles_sm-col-5__BUzt5","smCol6":"styles_sm-col-6__2_cX4","smCol7":"styles_sm-col-7__2Zrm3","smCol8":"styles_sm-col-8__xGVeN","mdCol1":"styles_md-col-1__2DqmH","mdCol2":"styles_md-col-2__2WNEu","mdCol3":"styles_md-col-3__ZztCl","mdCol4":"styles_md-col-4__zQDOW","mdCol5":"styles_md-col-5__2wHFI","mdCol6":"styles_md-col-6__rOgXY","mdCol7":"styles_md-col-7__fXfRK","mdCol8":"styles_md-col-8__uGVgh","mdCol9":"styles_md-col-9__1JE6h","mdCol10":"styles_md-col-10__3aA_t","mdCol11":"styles_md-col-11__3jkJH","mdCol12":"styles_md-col-12__1RtoU","lgCol1":"styles_lg-col-1__2xvkV","lgCol2":"styles_lg-col-2__3iUIl","lgCol3":"styles_lg-col-3__2EEeN","lgCol4":"styles_lg-col-4__X-TxL","lgCol5":"styles_lg-col-5__1gbfo","lgCol6":"styles_lg-col-6__3m1zS","lgCol7":"styles_lg-col-7__1Esku","lgCol8":"styles_lg-col-8__3faSF","lgCol9":"styles_lg-col-9__z-0rj","lgCol10":"styles_lg-col-10__IJjQK","lgCol11":"styles_lg-col-11__s4bar","lgCol12":"styles_lg-col-12__25NHM","xlgCol1":"styles_xlg-col-1__3swYJ","xlgCol2":"styles_xlg-col-2__7j0QE","xlgCol3":"styles_xlg-col-3__6yHfR","xlgCol4":"styles_xlg-col-4__2A_O7","xlgCol5":"styles_xlg-col-5__3ObM7","xlgCol6":"styles_xlg-col-6__2P0Pb","xlgCol7":"styles_xlg-col-7__1oeKF","xlgCol8":"styles_xlg-col-8__2Wwdi","xlgCol9":"styles_xlg-col-9__3B487","xlgCol10":"styles_xlg-col-10__1xwME","xlgCol11":"styles_xlg-col-11__3Pk5B","xlgCol12":"styles_xlg-col-12__uqjZu","xxlgCol1":"styles_xxlg-col-1__2rKAo","xxlgCol2":"styles_xxlg-col-2__3g-HQ","xxlgCol3":"styles_xxlg-col-3__1VodL","xxlgCol4":"styles_xxlg-col-4__ZtFFX","xxlgCol5":"styles_xxlg-col-5__1VGLC","xxlgCol6":"styles_xxlg-col-6__2Hm7m","xxlgCol7":"styles_xxlg-col-7__o1pc7","xxlgCol8":"styles_xxlg-col-8__16FbC","xxlgCol9":"styles_xxlg-col-9__1B41Y","xxlgCol10":"styles_xxlg-col-10__3sPN1","xxlgCol11":"styles_xxlg-col-11__1cfu_","xxlgCol12":"styles_xxlg-col-12__1bbbb","row2":"styles_row-2__pY1aL","row3":"styles_row-3__1r2Fe","row4":"styles_row-4__7Ytjg","row5":"styles_row-5__3D11X","row6":"styles_row-6__3KalP","xsRow2":"styles_xs-row-2__1KC0n","xsRow3":"styles_xs-row-3__3vsFf","xsRow4":"styles_xs-row-4__37SBy","xsRow5":"styles_xs-row-5__1SrIR","xsRow6":"styles_xs-row-6__2-M92","smRow2":"styles_sm-row-2__3Jrc6","smRow3":"styles_sm-row-3__2PUVL","smRow4":"styles_sm-row-4__3okvu","smRow5":"styles_sm-row-5__-GIhx","smRow6":"styles_sm-row-6__2VJtm","mdRow2":"styles_md-row-2__8VvUk","mdRow3":"styles_md-row-3__IO_ss","mdRow4":"styles_md-row-4__4zqbR","mdRow5":"styles_md-row-5__1myKc","mdRow6":"styles_md-row-6__2J6ke","lgRow2":"styles_lg-row-2__3KLLk","lgRow3":"styles_lg-row-3__cSV2B","lgRow4":"styles_lg-row-4__1GN_G","lgRow5":"styles_lg-row-5__2Zvwg","lgRow6":"styles_lg-row-6__101_1","xlgRow2":"styles_xlg-row-2__2pVlH","xlgRow3":"styles_xlg-row-3__PVMA-","xlgRow4":"styles_xlg-row-4__346IS","xlgRow5":"styles_xlg-row-5__3ru5r","xlgRow6":"styles_xlg-row-6__1D-pJ","xxlgRow2":"styles_xxlg-row-2__ppRmh","xxlgRow3":"styles_xxlg-row-3__1y56L","xxlgRow4":"styles_xxlg-row-4__3MU-o","xxlgRow5":"styles_xxlg-row-5__3mK_q","xxlgRow6":"styles_xxlg-row-6__2k-Vu","gridItemBase":"styles_grid-item-base__32oYF","xsHide":"styles_xs-hide__37j4O","xsShow":"styles_xs-show__1rWnY","smHide":"styles_sm-hide__jeG1S","smShow":"styles_sm-show__1o-_v","mdHide":"styles_md-hide__2DhLR","mdShow":"styles_md-show__gVteQ","lgHide":"styles_lg-hide__31eOq","lgShow":"styles_lg-show__1n-oW","xlgHide":"styles_xlg-hide__1zBca","xlgShow":"styles_xlg-show__YTclr","xxlgHide":"styles_xxlg-hide__32fq2","xxlgShow":"styles_xxlg-show__19fte"};

var GridItem = function GridItem(_a) {
  var className = _a.className,
      rest = __rest(_a, ["className"]);

  var responsiveProps = ['col', 'xsCol', 'smCol', 'mdCol', 'lgCol', 'xlgCol', 'xxlgCol', 'row', 'xsRow', 'smRow', 'mdRow', 'lgRow', 'xlgRow', 'xxlgRow'];
  var responsiveClassNames = responsiveProps.map(function (propName) {
    var propValue = rest[propName];
    var className = styles$q["" + propName + rest[propName]];

    if (propValue !== undefined && !className) {
      console.warn("<GridItem " + propName + "=\"" + propValue + "\" />: \"" + propValue + "\" is not a valid value for \"" + propName + "\"");
    }

    return className;
  });
  return React.createElement("div", __assign({
    className: classnames(styles$q['grid-item-base'], className, responsiveClassNames)
  }, omit(rest, responsiveProps)));
};

function nextBreakpointWithArea(templates, breakpointName, areaName) {
  var templateBreakpointNames = Object.keys(templates);
  var sortedTemplateBreakpointNames = ['default'].concat(SORTED_BREAKPOINT_NAMES).filter(function (breakpointName) {
    return templateBreakpointNames.includes(breakpointName);
  });
  var largerTemplateBreakpointNames = sortedTemplateBreakpointNames.slice(sortedTemplateBreakpointNames.indexOf(breakpointName || 'default'));
  return largerTemplateBreakpointNames.find(function (templateBreakpointName) {
    return doesTemplateIncludeArea(templates[templateBreakpointName], areaName);
  });
}

function parseTemplatesToComponents(gridTemplates) {
  //  For each grid, we memoize the generated AreaComponentMap so they are re-used across renders.
  //  This has performance benefits, but more importantly it prevents the Grid's children from
  //  losing state across renders (without memoization the AreaComponents would be distinct
  //  constructors with each render)
  var templatesHash = hash(gridTemplates);

  if (map[templatesHash]) {
    return map[templatesHash];
  } // Parse each template into a matrix
  //  { 'default' : [['a', 'b'], ['a, 'b']], 'md' : [['foo', 'bar'], ['foo', 'bar']] }


  var parsedTemplates = mapValues(gridTemplates, parseTemplate); // Flatten the templates since we don't care about rows & columns
  //  { 'default' : ['a', 'b', 'a', 'b'], 'md' : ['foo', 'bar', 'foo', 'bar'] }

  var flattenedTemplates = mapValues(parsedTemplates, flatten); // Find all the unique area name tokens across all the grid templates

  var uniqueAreaNames = uniq(flatten(Object.values(flattenedTemplates))); // Build an object mapping raw area names to React components with the `grid-area` preset

  var componentsMap = uniqueAreaNames.reduce(function (components, areaName) {
    var displayName = upperFirst(camelCase(areaName));

    var Component = function Component(_a) {
      var style = _a.style,
          className = _a.className,
          rest = __rest(_a, ["style", "className"]);

      var responsiveClassNames = []; // If this area name isn't used in a given area template, add classes to hide it until
      //   the device reaches the next breakpoint that has a template with that area name.

      for (var _i = 0, _b = Object.entries(flattenedTemplates); _i < _b.length; _i++) {
        var _c = _b[_i],
            breakpointName = _c[0],
            templateAreas = _c[1];

        if (!templateAreas.includes(areaName)) {
          responsiveClassNames.push(styles$q[breakpointName === 'default' ? 'hide' : breakpointName + "-hide"]);
          var nextBreakpointName = nextBreakpointWithArea(gridTemplates, breakpointName, areaName);

          if (nextBreakpointName) {
            responsiveClassNames.push(styles$q[nextBreakpointName === 'default' ? 'show' : nextBreakpointName + "-show"]);
          }
        }
      }

      return React.createElement(GridItem, __assign({
        style: __assign({
          gridArea: areaName
        }, style),
        className: classnames(className, responsiveClassNames)
      }, rest));
    };

    Component.displayName = "GridItem(" + displayName + ")";
    components[displayName] = Component;
    return components;
  }, {}); // Memoize

  map[templatesHash] = componentsMap;
  return componentsMap;
}

var AREAS_PROP_NAMES = ['areas'].concat(SORTED_BREAKPOINT_NAMES.map(function (breakpointName) {
  return "areas" + upperFirst(breakpointName);
}));
/**
 * Infer missing breakpoint props from a given `ResponsiveGridAreaProps`:
 *   i.e. { "areasXs": "a b c", "areasSm": "a a a b b c", ... }
 *
 * For each breakpoint where the number of columns change, we attempt to extrapolate
 *   the template from the closest smaller breakpoint with a template provided.
 */

function extrapolateProps(providedProps) {
  // Copy the props and re-type with an index signature so we can dynamically access `areasX` later
  var extrapolatedProps = Object.assign({}, providedProps); // COLUMN_BREAKPOINT_TUPLES contains a set of each breakpoint at which the number of columns changes

  COLUMN_BREAKPOINT_TUPLES.forEach(function (_a) {
    var columnBreakpointName = _a[0],
        columnCount = _a[1];
    var areasPropName = "areas" + upperFirst(columnBreakpointName); // If we already have a template for this breakpoint, skip

    if (providedProps.hasOwnProperty(areasPropName)) {
      return;
    } // Otherwise, find the closest smaller template and extrapolate from there


    var smallerPropNames = AREAS_PROP_NAMES.slice(0, AREAS_PROP_NAMES.indexOf("areas" + upperFirst(columnBreakpointName)));
    var sourceTemplate = null;

    for (var _i = 0, _b = smallerPropNames.reverse(); _i < _b.length; _i++) {
      var areasPropName_1 = _b[_i];
      var smallerBreakpointTemplate = providedProps[areasPropName_1];

      if (smallerBreakpointTemplate) {
        sourceTemplate = smallerBreakpointTemplate;
        break;
      }
    } // Extrapolate the template to the next breakpoint where the number of columns change


    if (sourceTemplate) {
      extrapolatedProps[areasPropName] = extrapolateTemplate(sourceTemplate, columnCount);
    }
  });
  return extrapolatedProps;
}

var styles$r = {"breakpoint-xs":"360px","breakpoint-sm":"768px","breakpoint-md":"960px","breakpoint-lg":"1200px","breakpoint-xlg":"1440px","breakpoint-xxlg":"1600px","default-columns":"2","columns-sm":"8","columns-md":"12","wrapper":"styles_wrapper__3iSpY flex flex-auto flex-row justify-center","grid":"styles_grid__257_T mx-auto","responsive-row-gaps":"styles_responsive-row-gaps__1I-_c","breakpointXs":"360px","breakpointSm":"768px","breakpointMd":"960px","breakpointLg":"1200px","breakpointXlg":"1440px","breakpointXxlg":"1600px","defaultColumns":"2","columnsSm":"8","columnsMd":"12","responsiveRowGaps":"styles_responsive-row-gaps__1I-_c"};

var Grid = function Grid(_a) {
  var _b;

  var className = _a.className,
      wrapperClassName = _a.wrapperClassName,
      rowGaps = _a.rowGaps,
      rest = __rest(_a, ["className", "wrapperClassName", "rowGaps"]);

  return React.createElement("div", {
    className: classnames(styles$r.wrapper, wrapperClassName)
  }, React.createElement("div", __assign({
    className: classnames(className, styles$r.grid, (_b = {}, _b[styles$r.responsiveRowGaps] = rowGaps, _b))
  }, rest)));
};

var ResponsiveGrid = function ResponsiveGrid(_a) {
  var style = _a.style,
      className = _a.className,
      children = _a.children,
      rest = __rest(_a, ["style", "className", "children"]);

  var responsiveAreasPropNames = ['areas', 'areasXs', 'areasSm', 'areasMd', 'areasLg', 'areasXlg', 'areasXxlg'];
  var responsiveAreaProps = pick(rest, responsiveAreasPropNames);
  var passthroughProps = omit(rest, responsiveAreasPropNames);
  var extrapolatedAreasProps = extrapolateProps(rest); // Build a map of { breakpoint: template } for all provided area templates

  var allTemplates = {};

  for (var _i = 0, _b = Object.entries(responsiveAreaProps); _i < _b.length; _i++) {
    var _c = _b[_i],
        areaPropName = _c[0],
        areaTemplate = _c[1];

    if (areaTemplate) {
      var breakpointName = areaPropName.slice(5).toLowerCase() || 'default';
      allTemplates[breakpointName] = areaTemplate;
    }
  } // Generate a unique ID for this grid


  var id = hash(allTemplates).slice(0, 8);
  var cssBlocks = [];
  responsiveAreasPropNames.forEach(function (propName) {
    // "areasXs" => "xs"
    var breakpointName = propName.slice(5).toLowerCase() || null; // "areasXs" => "xs"
    // "xs"  => "360px"

    var breakpointSize = cssVars["breakpoint-" + breakpointName] || undefined; // The template value for this breakpoint: "a a b b" "c c c c"

    var templateAreas = extrapolatedAreasProps[propName];

    if (templateAreas) {
      var expectedColumns = columnCountFromBreakpoint(breakpointName);
      var templatedColumns = columnCountFromTemplate(templateAreas);

      if (expectedColumns !== templatedColumns) {
        throw new Error("Expected the \"" + propName + "\" template to have " + expectedColumns + " columns, but found " + templatedColumns + ": " + templateAreas);
      }

      var cssRule = ".grid-" + id + " { grid-template-areas: " + minifyTemplate(templateAreas) + " }";
      var mediaQueryFeatures = [];

      if (breakpointSize) {
        mediaQueryFeatures.push("(min-width: " + breakpointSize + ")");
        cssBlocks.push("@media " + mediaQueryFeatures.join(' and ') + " { " + cssRule + " }");
      } else {
        cssBlocks.push(cssRule);
      }
    }
  });
  return React.createElement(React.Fragment, null, React.createElement("style", {
    type: "text/css",
    dangerouslySetInnerHTML: {
      __html: cssBlocks.join('\n')
    }
  }), React.createElement(Grid, __assign({
    className: classnames(className, "grid-" + id)
  }, passthroughProps), typeof children === 'function' ? children(parseTemplatesToComponents(allTemplates)) : children));
};

var _extends$H = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$H(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var DealIconMark = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$H(_ref, ["styles"]);

  return React.createElement("svg", _extends$H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "22",
    height: "28",
    viewBox: "0 0 22 28"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.743 0a1.28 1.28 0 0 1 1.278 1.283c-.002 2.88-.004 9.77-.002 11.947 0 .456-.158.896-.45 1.247L9.903 27.329a1.537 1.537 0 0 1-1.409.538C3.557 27.143 0 22.12 0 17.593 0 11.88 4.628 7.23 10.317 7.23c1.347 0 2.905.352 4.508 1.018l.943.405V1.269c0-.7.565-1.269 1.263-1.269h2.712zm-.982 20.29c.619-.747 1.628-.743 2.194.044a4.82 4.82 0 0 1 .896 2.557c.127 2.255-1.395 4.336-3.574 4.896a4.745 4.745 0 0 1-3.195-.286c-.914-.424-1.134-1.408-.49-2.185l4.17-5.027zM8.615 21.764c.463-.462 5.366-6.314 5.366-6.314.592-.804.11-1.583-.148-1.796a5.291 5.291 0 0 0-3.258-1.232c-2.978-.064-5.421 2.351-5.421 5.329 0 1.552.672 3.011 1.821 4.019.467.41 1.177.457 1.64-.006z"
  }));
});

var _extends$I = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$I(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var DealWordmark = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$I(_ref, ["styles"]);

  return React.createElement("svg", _extends$I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "75",
    height: "28",
    viewBox: "0 0 75 28"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M29.234 15.258s.72-3.352 4.286-3.352c3.682 0 4.19 3.352 4.19 3.352h-8.476zm13.826 1.409c0 .6-.047 1.177-.1 1.648a1.258 1.258 0 0 1-1.248 1.118H29.136s.861 3.755 5.24 3.755c1.599 0 3.146-.593 4.092-1.239a1.339 1.339 0 0 1 1.764.226l1.3 1.486c.51.583.427 1.478-.183 1.956C39.995 26.68 37.43 28 34.124 28c-5.793 0-10.143-4.569-10.143-10.21 0-5.884 4.244-10.559 9.855-10.559 6.84 0 9.224 5.674 9.224 9.436zM19.743.004a1.28 1.28 0 0 1 1.278 1.283c-.002 2.879-.004 9.769-.002 11.946 0 .457-.158.896-.45 1.247L9.903 27.333a1.537 1.537 0 0 1-1.409.538C3.557 27.147 0 22.124 0 17.597 0 11.883 4.628 7.234 10.317 7.234c1.347 0 2.905.352 4.508 1.017l.943.405V1.273c0-.701.565-1.27 1.263-1.27h2.712zm-.982 20.289c.619-.746 1.628-.742 2.194.044a4.82 4.82 0 0 1 .896 2.557c.127 2.256-1.395 4.337-3.574 4.897a4.745 4.745 0 0 1-3.195-.287c-.914-.424-1.134-1.407-.49-2.185l4.17-5.026zM69.746 1.209c0-.667.54-1.209 1.204-1.209h2.846C74.461 0 75 .542 75 1.21v25.154c0 .663-.535 1.2-1.195 1.2H70.94c-.66 0-1.195-.537-1.195-1.2V1.209zm-8.87 7.393c0-.682.535-1.091 1.214-1.091h2.706c.68 0 1.23.553 1.23 1.235v17.575a1.24 1.24 0 0 1-1.238 1.243H63.74c-.43 0-.828-.232-1.04-.61l-.88-1.559s-2.246 2.49-5.976 2.49c-5.487 0-10.135-4.27-10.423-9.774-.311-5.946 4.416-10.883 10.269-10.883 2.372 0 4.181.758 5.186 1.374zM8.615 21.77c.463-.463 5.366-6.314 5.366-6.314.592-.805.11-1.583-.148-1.797a5.291 5.291 0 0 0-3.258-1.231c-2.978-.064-5.421 2.35-5.421 5.328 0 1.552.672 3.012 1.821 4.02.467.409 1.177.456 1.64-.006zm47.142 1.072c2.904 0 5.267-2.373 5.267-5.29 0-2.918-2.363-5.29-5.267-5.29-2.904 0-5.267 2.372-5.267 5.29 0 2.917 2.363 5.29 5.267 5.29z"
  }));
});

var _extends$J = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$J(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var CuratedIconMark = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$J(_ref, ["styles"]);

  return React.createElement("svg", _extends$J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "28",
    height: "28",
    viewBox: "0 0 28 28"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M-2-2h32v32H-2z"
  }), React.createElement("path", {
    fill: "#000",
    fillRule: "nonzero",
    d: "M2 2v24h24V2H2zM0 0h28v28H0V0z"
  }), React.createElement("path", {
    fill: "#000",
    fillRule: "nonzero",
    d: "M8.5 13.826c0-1.189.291-2.278.873-3.244a6.146 6.146 0 0 1 2.356-2.273 6.679 6.679 0 0 1 3.24-.809c.945 0 1.841.191 2.677.574.824.385 1.54.924 2.144 1.615l.331.38-2.142 1.847-.328-.367a3.642 3.642 0 0 0-1.195-.896 3.504 3.504 0 0 0-1.488-.315c-.995 0-1.806.327-2.456.987-.66.67-.985 1.488-.985 2.501 0 1.014.324 1.83.984 2.488.652.66 1.465.987 2.457.987.538 0 1.041-.109 1.513-.325a3.362 3.362 0 0 0 1.193-.901l.327-.39 2.128 1.849-.316.376a6.19 6.19 0 0 1-2.15 1.644 6.287 6.287 0 0 1-2.695.59 6.7 6.7 0 0 1-2.51-.476 6.353 6.353 0 0 1-2.061-1.31 6.157 6.157 0 0 1-1.386-2.014 6.207 6.207 0 0 1-.511-2.518z"
  })));
});

var _extends$K = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$K(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var CuratedWordmark = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$K(_ref, ["styles"]);

  return React.createElement("svg", _extends$K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "108",
    height: "16",
    viewBox: "0 0 108 16"
  }, props), React.createElement("path", {
    fill: "#222",
    fillRule: "evenodd",
    d: "M0 8.242c0 1.105.207 2.135.626 3.09.421.969.986 1.79 1.698 2.472a7.805 7.805 0 0 0 2.534 1.608 8.27 8.27 0 0 0 3.1.588c1.172 0 2.273-.24 3.31-.725a7.587 7.587 0 0 0 2.638-2.016l-1.83-1.59a5.15 5.15 0 0 1-1.823 1.378 5.439 5.439 0 0 1-2.296.495c-1.497 0-2.752-.506-3.75-1.514-1.004-1.001-1.504-2.26-1.504-3.786 0-1.525.5-2.784 1.504-3.803.998-1.012 2.253-1.515 3.75-1.515.808 0 1.563.158 2.263.481a5.53 5.53 0 0 1 1.813 1.357l1.852-1.597a7.916 7.916 0 0 0-2.639-1.988A7.816 7.816 0 0 0 7.957.474c-1.455 0-2.784.337-4 .997A7.527 7.527 0 0 0 1.07 4.26C.354 5.447 0 6.782 0 8.242zm18.098 1.726c0 .976.157 1.859.472 2.627.315.779.754 1.41 1.309 1.89a5.586 5.586 0 0 0 1.926 1.117 7.32 7.32 0 0 0 2.388.376c.851 0 1.645-.125 2.367-.376a5.619 5.619 0 0 0 1.92-1.116c.554-.48.983-1.112 1.297-1.891.304-.768.461-1.65.461-2.627V.736h-2.702v8.956c0 1.295-.293 2.25-.89 2.881-.597.632-1.416.948-2.453.948-1.058 0-1.894-.316-2.491-.948-.597-.631-.901-1.586-.901-2.881V.736h-2.703v9.232zm16.473 5.77h2.702V10.42h2.66l3.175 5.318h3.067l-3.621-5.981a4.28 4.28 0 0 0 1.977-1.662c.493-.768.743-1.629.743-2.605 0-.886-.218-1.693-.658-2.43a4.728 4.728 0 0 0-1.77-1.714 4.99 4.99 0 0 0-2.44-.61H34.57v15.002zm2.702-7.747V3.186h3.071c.637 0 1.173.22 1.602.664.418.442.63.998.63 1.68 0 .725-.223 1.313-.673 1.765-.45.463-1.048.696-1.77.696h-2.86zm11.36 7.747h2.856l1.33-2.881h6.431l1.309 2.881h2.903L56.058 0l-7.425 15.738zm5.216-5.235l2.188-4.837 2.188 4.837h-4.376zm15.777 5.235h2.703V3.186h4.054V.736H65.565v2.45h4.061v12.552zm10.845-4.783c0 1.546.4 2.734 1.183 3.552.787.822 1.97 1.231 3.532 1.231h4.766v-2.45h-4.63c-.232 0-.46-.03-.683-.105a2.264 2.264 0 0 1-.69-.337 1.567 1.567 0 0 1-.554-.703c-.146-.316-.221-.693-.221-1.123V9.168h6.198v-2.45h-6.198V3.185h6.631V.736h-9.334v10.219zm14.31 4.783h5.383c1.248 0 2.356-.18 3.353-.545.994-.37 1.812-.876 2.47-1.536a6.747 6.747 0 0 0 1.498-2.365c.347-.915.515-1.923.515-3.028s-.179-2.114-.525-3.029a6.942 6.942 0 0 0-1.499-2.386c-.657-.66-1.476-1.187-2.47-1.554-.997-.369-2.116-.56-3.364-.56H94.78v15.003zm2.702-2.45V3.185h2.474c.961 0 1.801.126 2.502.377.7.255 1.244.61 1.655 1.073.408.474.7 1.001.901 1.6.186.6.282 1.281.282 2.028 0 .578-.053 1.116-.168 1.607a4.56 4.56 0 0 1-.575 1.378c-.261.43-.597.79-1.008 1.084-.408.305-.908.534-1.505.703-.608.169-1.302.251-2.067.251h-2.491z"
  }));
});

var _extends$L = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$L(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var CuratedCombomark = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$L(_ref, ["styles"]);

  return React.createElement("svg", _extends$L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "148",
    height: "28",
    viewBox: "0 0 148 28"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    fill: "#222",
    d: "M40 14.242c0 1.105.207 2.135.626 3.09.421.969.986 1.79 1.698 2.472a7.805 7.805 0 0 0 2.534 1.608 8.27 8.27 0 0 0 3.1.588c1.172 0 2.273-.24 3.31-.725a7.587 7.587 0 0 0 2.638-2.016l-1.83-1.59a5.15 5.15 0 0 1-1.823 1.378 5.439 5.439 0 0 1-2.296.495c-1.497 0-2.752-.506-3.75-1.514-1.004-1.001-1.504-2.26-1.504-3.786 0-1.525.5-2.784 1.504-3.803.998-1.012 2.253-1.515 3.75-1.515.808 0 1.563.158 2.263.481a5.53 5.53 0 0 1 1.813 1.357l1.852-1.597a7.916 7.916 0 0 0-2.639-1.988 7.816 7.816 0 0 0-3.289-.703c-1.455 0-2.784.337-4 .997a7.527 7.527 0 0 0-2.888 2.788C40.354 11.447 40 12.782 40 14.242zm18.098 1.726c0 .976.157 1.859.472 2.627.315.779.754 1.41 1.309 1.89a5.586 5.586 0 0 0 1.926 1.117 7.32 7.32 0 0 0 2.388.376c.851 0 1.645-.125 2.367-.376a5.619 5.619 0 0 0 1.92-1.116c.554-.48.983-1.112 1.297-1.891.304-.768.461-1.65.461-2.627V6.736h-2.702v8.956c0 1.295-.293 2.25-.89 2.881-.597.632-1.416.948-2.453.948-1.058 0-1.894-.316-2.491-.948-.597-.631-.901-1.586-.901-2.881V6.736h-2.703v9.232zm16.473 5.77h2.702V16.42h2.66l3.175 5.318h3.067l-3.621-5.981a4.28 4.28 0 0 0 1.977-1.662c.493-.768.743-1.629.743-2.605 0-.886-.218-1.693-.658-2.43a4.728 4.728 0 0 0-1.77-1.714 4.99 4.99 0 0 0-2.44-.61H74.57v15.002zm2.702-7.747V9.186h3.071c.637 0 1.173.22 1.602.664.418.442.63.998.63 1.68 0 .725-.223 1.313-.673 1.765-.45.463-1.048.696-1.77.696h-2.86zm11.36 7.747h2.856l1.33-2.881h6.431l1.309 2.881h2.903L96.058 6l-7.425 15.738zm5.216-5.235l2.188-4.837 2.188 4.837h-4.376zm15.777 5.235h2.703V9.186h4.054v-2.45h-10.818v2.45h4.061v12.552zm10.845-4.783c0 1.546.4 2.734 1.183 3.552.787.822 1.97 1.231 3.532 1.231h4.766v-2.45h-4.63c-.232 0-.46-.03-.683-.105a2.264 2.264 0 0 1-.69-.337 1.567 1.567 0 0 1-.554-.703c-.146-.316-.221-.693-.221-1.123v-1.852h6.198v-2.45h-6.198V9.185h6.631v-2.45h-9.334v10.219zm14.31 4.783h5.383c1.248 0 2.356-.18 3.353-.545.994-.37 1.812-.876 2.47-1.536a6.747 6.747 0 0 0 1.498-2.365c.347-.915.515-1.923.515-3.028s-.179-2.114-.525-3.029a6.942 6.942 0 0 0-1.499-2.386c-.657-.66-1.476-1.187-2.47-1.554-.997-.369-2.116-.56-3.364-.56h-5.362v15.003zm2.702-2.45V9.185h2.474c.961 0 1.801.126 2.502.377.7.255 1.244.61 1.655 1.073.408.474.7 1.001.901 1.6.186.6.282 1.281.282 2.028 0 .578-.053 1.116-.168 1.607a4.56 4.56 0 0 1-.575 1.378c-.261.43-.597.79-1.008 1.084-.408.305-.908.534-1.505.703-.608.169-1.302.251-2.067.251h-2.491z"
  }), React.createElement("path", {
    fill: "#434343",
    fillRule: "nonzero",
    d: "M2 2v24h24V2H2zM0 0h28v28H0V0z"
  })));
});

var styles$s = {"light":"styles_light__1WCxS","dark":"styles_dark__1zpyI","inherit":"styles_inherit__Mh8H2"};

function getMark(mark, brand) {
  if (mark === void 0) {
    mark = 'word';
  }

  if (brand === void 0) {
    brand = 'curated';
  }

  switch (mark) {
    case 'icon':
      return brand === 'deal' ? DealIconMark : CuratedIconMark;

    case 'word':
      return brand === 'deal' ? DealWordmark : CuratedWordmark;

    case 'combo':
      return brand === 'deal' ? DealWordmark : CuratedCombomark;
  }
}

var Logo = function Logo(_a) {
  var _b = _a.color,
      color = _b === void 0 ? 'inherit' : _b,
      mark = _a.mark,
      brand = _a.brand,
      className = _a.className,
      rest = __rest(_a, ["color", "mark", "brand", "className"]);

  var Mark = getMark(mark, brand);
  var classes = classnames(styles$s[color], className);
  return React.createElement(Mark, __assign({
    className: classes
  }, rest));
};

var styles$t = {"modal":"styles_modal__vKCeG","overlay":"styles_overlay__2tQmZ flex justify-center items-center"};

var Modal =
/** @class */
function (_super) {
  __extends(Modal, _super);

  function Modal() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  Modal.prototype.componentWillMount = function () {
    ReactModal.setAppElement('body');
  };

  Modal.prototype.render = function () {
    var _a = this.props,
        children = _a.children,
        isOpen = _a.isOpen,
        className = _a.className,
        disableScrollLock = _a.disableScrollLock,
        props = __rest(_a, ["children", "isOpen", "className", "disableScrollLock"]);

    return React.createElement(ReactModal, __assign({}, props, {
      isOpen: isOpen,
      className: classnames(styles$t.modal, className),
      overlayClassName: styles$t.overlay
    }), React.createElement(ScrollLock, {
      isActive: disableScrollLock ? false : isOpen
    }), children);
  };

  return Modal;
}(React.Component);

var styles$u = {"breakpoint-sm":"(min-width: 768px)","overlay":"styles_overlay__34DOi flex flex-column justify-end","modal":"styles_modal__2giXn","modalContent":"styles_modalContent__2Pz0P flex flex-column","closeContainer":"styles_closeContainer__1pWIL flex justify-center items-center absolute","close":"styles_close__1Bj9x","appear":"styles_appear__2ym7j","appearActive":"styles_appearActive__2-rI3","breakpointSm":"(min-width: 768px)"};

var _extends$M = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$M(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Close = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$M(_ref, ["styles"]);

  return React.createElement("svg", _extends$M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "14",
    viewBox: "0 0 14 14"
  }, props), React.createElement("path", {
    fillRule: "nonzero",
    d: "M.3 13.7c.2.2.4.3.7.3.3 0 .5-.1.7-.3L7 8.4l5.3 5.3c.2.2.5.3.7.3.2 0 .5-.1.7-.3.4-.4.4-1 0-1.4L8.4 7l5.3-5.3c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0L7 5.6 1.7.3C1.3-.1.7-.1.3.3c-.4.4-.4 1 0 1.4L5.6 7 .3 12.3c-.4.4-.4 1 0 1.4z"
  }));
});

var FancyModal =
/** @class */
function (_super) {
  __extends(FancyModal, _super);

  function FancyModal() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  FancyModal.prototype.componentWillMount = function () {
    ReactModal.setAppElement('body');
  };

  FancyModal.prototype.render = function () {
    var _a = this.props,
        children = _a.children,
        disableScrollLock = _a.disableScrollLock,
        isOpen = _a.isOpen,
        onRequestClose = _a.onRequestClose,
        className = _a.className,
        modalContentClassName = _a.modalContentClassName,
        overlayClassName = _a.overlayClassName,
        props = __rest(_a, ["children", "disableScrollLock", "isOpen", "onRequestClose", "className", "modalContentClassName", "overlayClassName"]);

    return React.createElement(ReactModal, __assign({}, props, {
      isOpen: isOpen,
      onRequestClose: onRequestClose,
      className: classnames(styles$u.modal, className),
      overlayClassName: classnames(styles$u.overlay, overlayClassName)
    }), React.createElement(ScrollLock, {
      isActive: disableScrollLock ? false : isOpen
    }, React.createElement(React.Fragment, null, onRequestClose && React.createElement("div", {
      className: styles$u.closeContainer
    }, React.createElement(Close, {
      className: styles$u.close,
      onClick: function onClick(e) {
        return onRequestClose && onRequestClose(e);
      }
    })), React.createElement(CSSTransition, {
      timeout: 1000,
      in: true,
      appear: true,
      classNames: {
        appear: styles$u.appear,
        appearActive: styles$u.appearActive
      }
    }, React.createElement("div", {
      className: classnames(styles$u.modalContent, modalContentClassName)
    }, children)))));
  };

  return FancyModal;
}(React.Component);

var styles$v = {"breakpoint-sm":"(min-width: 768px)","overlay":"styles_overlay__36LsF flex flex-column items-center justify-center","modal":"styles_modal__1rER3","modalContent":"styles_modalContent__1V1MD","closeContainer":"styles_closeContainer__YYCGd flex justify-center items-center absolute","close":"styles_close__2a_dh","small":"styles_small__IuGfC","medium":"styles_medium__1RHmX","large":"styles_large__3NQXu","breakpointSm":"(min-width: 768px)"};

var _extends$N = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$N(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Close$1 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$N(_ref, ["styles"]);

  return React.createElement("svg", _extends$N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "14",
    viewBox: "0 0 14 14"
  }, props), React.createElement("path", {
    fillRule: "nonzero",
    d: "M.3 13.7c.2.2.4.3.7.3.3 0 .5-.1.7-.3L7 8.4l5.3 5.3c.2.2.5.3.7.3.2 0 .5-.1.7-.3.4-.4.4-1 0-1.4L8.4 7l5.3-5.3c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0L7 5.6 1.7.3C1.3-.1.7-.1.3.3c-.4.4-.4 1 0 1.4L5.6 7 .3 12.3c-.4.4-.4 1 0 1.4z"
  }));
});

var SimpleModal =
/** @class */
function (_super) {
  __extends(SimpleModal, _super);

  function SimpleModal() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  SimpleModal.prototype.componentWillMount = function () {
    ReactModal.setAppElement('body');
  };

  SimpleModal.prototype.render = function () {
    var _a = this.props,
        children = _a.children,
        onRequestClose = _a.onRequestClose,
        className = _a.className,
        size = _a.size,
        overlayClassName = _a.overlayClassName,
        props = __rest(_a, ["children", "onRequestClose", "className", "size", "overlayClassName"]);

    return React.createElement(ReactModal, __assign({}, props, {
      onRequestClose: onRequestClose,
      className: classnames(styles$v.modal, className, styles$v[size]),
      overlayClassName: classnames(styles$v.overlay, overlayClassName)
    }), React.createElement(React.Fragment, null, onRequestClose && React.createElement("div", {
      className: styles$v.closeContainer
    }, React.createElement(Close$1, {
      className: styles$v.close,
      onClick: function onClick(e) {
        return onRequestClose && onRequestClose(e);
      }
    })), React.createElement("div", {
      className: styles$v.modalContent
    }, children)));
  };

  SimpleModal.defaultProps = {
    size: 'medium'
  };
  return SimpleModal;
}(React.Component);

var styles$w = {"breakpoint-sm":"(min-width: 768px)","showcase":"styles_showcase__1Uhck","hideInMobile":"styles_hideInMobile__3IpcV","breakpointSm":"(min-width: 768px)"};

var FancyShowcase = function FancyShowcase(_a) {
  var _b;

  var children = _a.children,
      className = _a.className,
      _c = _a.hideInMobile,
      hideInMobile = _c === void 0 ? false : _c;
  return React.createElement("div", {
    className: classnames(styles$w.showcase, className, (_b = {}, _b[styles$w.hideInMobile] = !!hideInMobile, _b))
  }, children);
};

var styles$x = {"breakpoint-sm":"(min-width: 768px)","content":"styles_content__3HSVw","breakpointSm":"(min-width: 768px)"};

var FancyContent = function FancyContent(_a) {
  var children = _a.children,
      className = _a.className;
  return React.createElement("div", {
    className: classnames(styles$x.content, className)
  }, children);
};

var styles$y = {"breakpoint-sm":"(min-width: 768px)","title":"styles_title__3vnhf bold","breakpointSm":"(min-width: 768px)"};

var ModalTitle = function ModalTitle(_a) {
  var children = _a.children,
      className = _a.className;
  return React.createElement("div", {
    className: classnames(styles$y.title, className)
  }, children);
};

var styles$z = {"breakpoint-sm":"(min-width: 768px)","footer":"styles_footer__3bR4e","breakpointSm":"(min-width: 768px)"};

var ModalFooter = function ModalFooter(_a) {
  var children = _a.children,
      className = _a.className;
  return React.createElement("div", {
    className: classnames(styles$z.footer, className)
  }, children);
};

var styles$A = {"breakpoint-sm":"(min-width: 768px)","modalContent":"styles_modalContent__2JBNW","breakpointSm":"(min-width: 768px)"};

var ModalContent = function ModalContent(_a) {
  var children = _a.children,
      className = _a.className;
  return React.createElement("div", {
    className: classnames(styles$A.modalContent, className)
  }, children);
};

var _extends$O = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$O(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Check = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$O(_ref, ["styles"]);

  return React.createElement("svg", _extends$O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm-.56 11.01l4.07-4.07a.999.999 0 1 0-1.414-1.415L6.732 8.89l-.829-.829A1 1 0 0 0 4.49 9.475l1.536 1.535a.999.999 0 0 0 1.414 0z"
  }));
});

var _extends$P = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$P(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var Error$1 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$P(_ref, ["styles"]);

  return React.createElement("svg", _extends$P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm9.315 2.718a.993.993 0 0 0 1.403-1.403L9.41 8.007l1.308-1.309a.993.993 0 0 0-1.403-1.403L8.007 6.603 6.694 5.291a.991.991 0 1 0-1.403 1.403l1.312 1.312-1.312 1.313a.991.991 0 1 0 1.403 1.403L8.007 9.41l1.308 1.308z"
  }));
});

var styles$B = {"box":"styles_box__2eyXR","padding":"styles_padding__54EK1","icon":"styles_icon__21hrL","info":"styles_info__1gOUZ styles_box__2eyXR","error":"styles_error__HAOVj styles_box__2eyXR","success":"styles_success__1Jkoc styles_box__2eyXR","label":"styles_label__2MPQf"};

var Notification = function Notification(_a) {
  var _b = _a.type,
      type = _b === void 0 ? 'info' : _b,
      children = _a.children,
      className = _a.className;
  var classes = classnames(styles$B[type], className);
  var Glyph = Check;

  if (type === 'error') {
    Glyph = Error$1;
  }

  return React.createElement("div", {
    className: classes
  }, React.createElement("div", {
    className: styles$B.padding
  }, React.createElement(Glyph, {
    className: styles$B.icon
  }), React.createElement("span", {
    className: styles$B.label
  }, children)));
};

var PhoneField =
/** @class */
function (_super) {
  __extends(PhoneField, _super);

  function PhoneField(props) {
    var _this = _super.call(this, props) || this;

    if (props.value) {
      throw new Error('Passing a value argument to the MaskedField breaks the phone mask, use `defaultValue` to make this uncontrolled');
    }

    _this.cleanPhoneNumber = _this.cleanPhoneNumber.bind(_this);
    return _this;
  }

  PhoneField.prototype.cleanPhoneNumber = function (phone) {
    // Convert to string
    if (Array.isArray(phone)) {
      phone = phone.join('');
    } // Naive attempt at stripping the country code


    if (phone.length === 12 && phone.startsWith('+1')) {
      phone = phone.slice(2);
    } else if (phone.length === 11 && phone.startsWith('1')) {
      phone = phone.slice(1);
    } // Strip whitespace, parens, pluses, underscores and dashes


    phone = phone.replace(/[\s\(\)\+\-\_]/g, '');
    return phone;
  };

  PhoneField.prototype.render = function () {
    var _a = this.props,
        placeholder = _a.placeholder,
        maskedFieldProps = _a.maskedFieldProps,
        onChange = _a.onChange,
        defaultValue = _a.defaultValue,
        rest = __rest(_a, ["placeholder", "maskedFieldProps", "onChange", "defaultValue"]);

    var cleanedValue = defaultValue && this.cleanPhoneNumber(defaultValue);
    /**
     * This repository is relatively unmaintained, so there are some long standing issues with the
     * phone mask backspace working with guide set to false. This is a work around that will work
     * for as long as we are using a US phone number mask.
     *
     * https://github.com/text-mask/text-mask/issues/483#issuecomment-337259727
     */

    var maskMaker = function maskMaker(mask, nonCharactersList) {
      return function (raw) {
        var length = raw.length;
        var nonDefault = nonCharactersList.some(function (characterIndex) {
          return characterIndex === length;
        });
        return nonDefault ? mask.slice(0, length) : mask;
      };
    };

    var phoneMask = maskMaker(['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/], [4, 9]);
    return React.createElement(MaskedField, __assign({
      type: "tel",
      maskedFieldProps: __assign({
        mask: phoneMask,
        guide: false
      }, maskedFieldProps),
      onChange: onChange,
      placeholder: placeholder,
      defaultValue: cleanedValue
    }, rest));
  };

  PhoneField.defaultProps = {
    autoFocus: false,
    placeholder: 'Enter a phone number'
  };
  return PhoneField;
}(React.Component);

var styles$C = {"handle":"styles_handle__glQ0s block absolute","handle-tap-target":"styles_handle-tap-target__qFLI5 styles_handle__glQ0s block absolute","handle-label":"styles_handle-label__2ikRh absolute","handle-disabled":"styles_handle-disabled__2J2GM","handle-label-disabled":"styles_handle-label-disabled__2TGd_","handleTapTarget":"styles_handle-tap-target__qFLI5 styles_handle__glQ0s block absolute","handleLabel":"styles_handle-label__2ikRh absolute","handleDisabled":"styles_handle-disabled__2J2GM","handleLabelDisabled":"styles_handle-label-disabled__2TGd_"};

var Handle = function Handle(_a) {
  var _b, _c;

  var handle = _a.handle,
      _d = _a.domain,
      min = _d[0],
      max = _d[1],
      label = _a.label,
      format$$1 = _a.format,
      getHandleProps = _a.getHandleProps,
      _e = _a.disabled,
      disabled = _e === void 0 ? false : _e;
  var isInvalidDomain = min === max;
  var value = isInvalidDomain ? min : handle.value;
  return React.createElement(React.Fragment, null, label && React.createElement("span", {
    className: classnames(styles$C['handle-label'], (_b = {}, _b[styles$C['handle-label-disabled']] = disabled, _b)),
    style: {
      left: handle.percent + "%"
    }
  }, format$$1(value)), React.createElement("div", __assign({
    className: styles$C['handle-tap-target'],
    style: {
      left: handle.percent + "%"
    }
  }, getHandleProps(handle.id))), React.createElement("div", {
    className: classnames(styles$C.handle, (_c = {}, _c[styles$C['handle-disabled']] = disabled, _c)),
    role: "slider",
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": value,
    style: {
      left: handle.percent + "%"
    }
  }));
};

var styles$D = {"rail":"styles_rail__1SoxQ absolute","rail-tap-target":"styles_rail-tap-target__-9CVU styles_rail__1SoxQ absolute","rail-disabled":"styles_rail-disabled__1qsD4","railTapTarget":"styles_rail-tap-target__-9CVU styles_rail__1SoxQ absolute","railDisabled":"styles_rail-disabled__1qsD4"};

var Rail$1 = function Rail$$1(_a) {
  var _b;

  var getRailProps = _a.getRailProps,
      _c = _a.disabled,
      disabled = _c === void 0 ? false : _c;
  return React.createElement(React.Fragment, null, React.createElement("div", __assign({
    className: styles$D['rail-tap-target']
  }, getRailProps())), React.createElement("div", {
    className: classnames(styles$D.rail, (_b = {}, _b[styles$D['rail-disabled']] = disabled, _b))
  }));
};

var styles$E = {"track":"styles_track__2xLak absolute","track-disabled":"styles_track-disabled__HegCk","trackDisabled":"styles_track-disabled__HegCk"};

var Track = function Track(_a) {
  var _b;

  var source = _a.source,
      target = _a.target,
      getTrackProps = _a.getTrackProps,
      style = _a.style,
      className = _a.className,
      _c = _a.disabled,
      disabled = _c === void 0 ? false : _c,
      rest = __rest(_a, ["source", "target", "getTrackProps", "style", "className", "disabled"]);

  return React.createElement("div", __assign({
    className: classnames(className, styles$E.track, (_b = {}, _b[styles$E['track-disabled']] = disabled, _b)),
    style: {
      left: source.percent + "%",
      width: target.percent - source.percent + "%"
    }
  }, rest));
};

var styles$F = {"slider":"styles_slider__2EqHe relative","label":"styles_label__2Ej9Q absolute","label-min":"styles_label-min__1KrPY styles_label__2Ej9Q absolute left-0","label-max":"styles_label-max__23Qm9 styles_label__2Ej9Q absolute right-0","label-disabled":"styles_label-disabled__1dr07","container":"styles_container__aFwuG","labelled-values":"styles_labelled-values__Pa8RL","labelled-domain":"styles_labelled-domain__3bK5G","labelMin":"styles_label-min__1KrPY styles_label__2Ej9Q absolute left-0","labelMax":"styles_label-max__23Qm9 styles_label__2Ej9Q absolute right-0","labelDisabled":"styles_label-disabled__1dr07","labelledValues":"styles_labelled-values__Pa8RL","labelledDomain":"styles_labelled-domain__3bK5G"};

//  See: https://sghall.github.io/react-compound-slider/#/component-api/slider

var modes = {
  cross: 1,
  block: 2,
  push: 3
};

var Slider$1 = function Slider$$1(_a) {
  var _b, _c, _d;

  var className = _a.className,
      domain = _a.domain,
      _e = _a.labelDomain,
      labelDomain = _e === void 0 ? false : _e,
      _f = _a.labelValues,
      labelValues = _f === void 0 ? false : _f,
      _g = _a.format,
      format$$1 = _g === void 0 ? function (num) {
    return num.toString();
  } : _g,
      _h = _a.mode,
      mode = _h === void 0 ? 'block' : _h,
      highlight = _a.highlight,
      values = _a.values,
      disabled = _a.disabled,
      rest = __rest(_a, ["className", "domain", "labelDomain", "labelValues", "format", "mode", "highlight", "values", "disabled"]);

  var isMultiSlider = values.length > 1;
  var hasLeftTrack = highlight === 'left' && !isMultiSlider;
  var hasRightTrack = highlight === 'right' && !isMultiSlider;
  var isInvalidDomain = domain[0] === domain[1]; // HACK: A slider can not have a domain [x, x], so we expand it by 1, disable the slider, and "select" the entire expanded domain

  var validatedDomain = isInvalidDomain ? [domain[0], domain[1] + 1] : domain;
  return React.createElement("div", {
    className: classnames(className, styles$F.container, (_b = {}, _b[styles$F['labelled-domain']] = labelDomain, _b[styles$F['labelled-values']] = labelValues, _b))
  }, React.createElement(Slider, __assign({
    mode: modes[mode],
    domain: validatedDomain,
    disabled: disabled || isInvalidDomain,
    values: isInvalidDomain ? validatedDomain : values,
    className: styles$F.slider
  }, rest), labelDomain && React.createElement(React.Fragment, null, React.createElement("span", {
    className: classnames(styles$F['label-min'], (_c = {}, _c[styles$F['label-disabled']] = disabled || isInvalidDomain, _c))
  }, format$$1(domain[0])), React.createElement("span", {
    className: classnames(styles$F['label-max'], (_d = {}, _d[styles$F['label-disabled']] = disabled || isInvalidDomain, _d))
  }, format$$1(domain[1]))), React.createElement(Rail, null, function (_a) {
    var getRailProps = _a.getRailProps;
    return React.createElement(Rail$1, {
      getRailProps: getRailProps
    });
  }), React.createElement(Handles, null, function (_a) {
    var handles = _a.handles,
        getHandleProps = _a.getHandleProps;
    return React.createElement(React.Fragment, null, handles.map(function (handle) {
      return React.createElement(Handle, {
        handle: handle,
        domain: domain,
        label: labelValues,
        format: format$$1,
        getHandleProps: getHandleProps,
        disabled: disabled || isInvalidDomain,
        key: handle.id
      });
    }));
  }), React.createElement(Tracks, {
    left: hasLeftTrack,
    right: hasRightTrack
  }, function (_a) {
    var tracks = _a.tracks,
        getTrackProps = _a.getTrackProps;
    return React.createElement(React.Fragment, null, tracks.map(function (track) {
      return React.createElement(Track, {
        source: track.source,
        target: track.target,
        getTrackProps: getTrackProps,
        disabled: disabled || isInvalidDomain,
        key: track.id
      });
    }));
  })));
};

var styles$G = {"breakpoint-sm":"(min-width: 768px)","input":"styles_input__3YSz0 m0","label":"styles_label__1mHqr","xsmall":"styles_xsmall__JjAFo","icon":"styles_icon__FfS81","small":"styles_small__1kk7G","medium":"styles_medium__2grgO","large":"styles_large__11b9R","xlarge":"styles_xlarge__3Ha1y","breakpointSm":"(min-width: 768px)"};

var TimePicker$1 = function TimePicker(_a) {
  var time = _a.time,
      onTimeChange = _a.onTimeChange,
      label = _a.label,
      _b = _a.fieldSize,
      fieldSize = _b === void 0 ? 'medium' : _b,
      rest = __rest(_a, ["time", "onTimeChange", "label", "fieldSize"]);

  return React.createElement("label", null, label && React.createElement("div", {
    className: styles$G.label
  }, label), React.createElement("input", __assign({
    type: "time",
    value: time,
    className: classnames(styles$G.input, styles$G[fieldSize]),
    onChange: onTimeChange,
    maxLength: 10
  }, rest)));
};

var _extends$Q = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$Q(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var PenIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$Q(_ref, ["styles"]);

  return React.createElement("svg", _extends$Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h16v16H0z"
  }), React.createElement("path", {
    d: "M13 7L9 3M5.5 14.5l-5 1 1-5 10-10 4 4z",
    stroke: "#233034",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
});

var styles$H = {"container":"styles_container__2aoUG relative","icon":"styles_icon__38P03 absolute","input":"styles_input__39wxn"};

var InlineEditor = function InlineEditor(_a) {
  var inputRef = _a.inputRef,
      className = _a.className,
      rest = __rest(_a, ["inputRef", "className"]);

  return React.createElement("div", {
    className: classnames(styles$H.container, className)
  }, React.createElement("input", __assign({
    type: "text",
    ref: inputRef,
    className: styles$H.input
  }, rest)), React.createElement(PenIcon, {
    className: styles$H.icon
  }));
};

var styles$I = {"card":"styles_card__d_9QQ pb2 card"};

var styles$J = {"content":"styles_content__PKYpH p2 flex flex-column justify-end","gradient":"styles_gradient__1sltG"};

var SellableImage = function SellableImage(_a) {
  var contentClassName = _a.contentClassName,
      sizing = _a.sizing,
      children = _a.children,
      rest = __rest(_a, ["contentClassName", "sizing", "children"]); // Add a subtle gradient between the image and the children, if the image takes up the full container


  var showGradient = sizing === 'cover' && Children.toArray(children).filter(Boolean).length > 0;
  return React.createElement(FixedAspectRatioImage, __assign({
    contentClassName: classnames(styles$J.content, contentClassName, showGradient && styles$J.gradient),
    sizing: sizing,
    children: children
  }, rest));
};

var styles$K = {"logo":"styles_logo__h1NyV","placeholder":"styles_placeholder__Kfdja"};

var SellableBrandLogo = function SellableBrandLogo(_a) {
  var src = _a.src;

  if (!src) {
    return React.createElement("div", {
      className: styles$K.placeholder
    });
  }

  return React.createElement(Image, {
    src: src,
    height: 40,
    width: 40,
    imgixParams: {
      fit: 'clip'
    },
    className: styles$K.logo
  });
};

var styles$L = {"breakpoint-md":"(min-width: 960px)","container":"styles_container__1RdR0","title":"styles_title__fl5-v mb1","price":"styles_price__1IWyp","breakpointMd":"(min-width: 960px)"};

var SellableTitleAndPrice = function SellableTitleAndPrice(_a) {
  var title = _a.title,
      price = _a.price;
  return React.createElement("div", {
    className: styles$L.container
  }, React.createElement("div", {
    className: styles$L.title
  }, title), React.createElement("div", {
    className: styles$L.price
  }, price));
};

var styles$M = {"container":"styles_container__Qip_y flex items-center justify-between px2 pt2","attributes":"styles_attributes__2FX1I flex flex-wrap items-center m0 list-reset","attribute":"styles_attribute__3utPs mr2 flex items-center","icon":"styles_icon__2yMAb mr1","from":"styles_from__1_HIQ","price":"styles_price__bPAl1 bold"};

var SellableAttributesAndPrice = function SellableAttributesAndPrice(_a) {
  var _b = _a.attributes,
      attributes = _b === void 0 ? [] : _b,
      price = _a.price,
      priceIsMin = _a.priceIsMin;
  return React.createElement("div", {
    className: styles$M.container
  }, attributes.length > 0 && React.createElement("ul", {
    className: styles$M.attributes
  }, attributes.map(function (_a) {
    var Icon = _a.icon,
        value = _a.value;
    return React.createElement("li", {
      className: styles$M.attribute,
      key: Icon.toString() + value
    }, React.createElement(Icon, {
      className: styles$M.icon
    }), " ", value);
  })), React.createElement("div", {
    className: styles$M.price
  }, priceIsMin && React.createElement("div", {
    className: styles$M.from
  }, "FROM"), price));
};

var SellableCard = function SellableCard(_a) {
  var className = _a.className,
      rest = __rest(_a, ["className"]);

  return React.createElement("div", __assign({
    className: classnames(styles$I.card, className)
  }, rest));
};

var TextArea =
/** @class */
function (_super) {
  __extends(TextArea, _super);

  function TextArea(props) {
    var _this = _super.call(this, props) || this;

    _this.handleChange = _this.handleChange.bind(_this);
    var initialValue = props.value || props.defaultValue;

    if (initialValue !== undefined && props.name) {
      _this.handleValidate(props.name, initialValue);
    }

    return _this;
  }

  TextArea.prototype.componentDidMount = function () {
    this.adjustHeight();
  };

  TextArea.prototype.componentDidUpdate = function () {
    this.adjustHeight();
  };

  TextArea.prototype.componentWillReceiveProps = function (nextProps) {
    var value = nextProps.value,
        name = nextProps.name;

    if (value && name && this.props.value !== value) {
      this.handleValidate(name, value);
    }
  };

  TextArea.prototype.adjustHeight = function () {
    if (this.el && this.props.expand) {
      this.el.style.height = '0';
      this.el.style.height = this.el.scrollHeight + "px";
    }
  };

  TextArea.prototype.handleValidate = function (name, value) {
    var _this = this;

    var validate = this.props.validate;

    if (validate === null) {
      return;
    }

    var rules = uniq(Array.isArray(validate) ? validate : [validate]);
    Checkit.check(name, value, rules).then(function () {
      if (_this.props.onFieldValid) {
        _this.props.onFieldValid(name, value);
      }
    }).catch(function (fieldError) {
      if (fieldError) {
        var errorMessages = [];

        if (fieldError.errors && fieldError.errors.length > 0) {
          errorMessages = fieldError.errors.map(function (err) {
            return err.message;
          });
        }

        if (_this.props.onFieldInvalid) {
          _this.props.onFieldInvalid(name, value, errorMessages);
        }
      } else {
        throw fieldError;
      }
    });
  };

  TextArea.prototype.handleChange = function (event) {
    var value = event.target.value;
    var name = event.target.name;
    this.handleValidate(name, value);

    if (this.props.onChange) {
      this.props.onChange(event);
    }

    this.adjustHeight();
  };

  TextArea.prototype.render = function () {
    var _this = this;

    var props = omit(this.props, 'validate', 'onChange', 'onFieldValid', 'onFieldInvalid', 'inputRef', 'expand');
    var inputRef = this.props.inputRef;
    return React.createElement("textarea", __assign({}, props, {
      onChange: this.handleChange,
      ref: function ref(el) {
        if (el) _this.el = el; // Local ref

        if (inputRef) inputRef(el); // Parent ref
      }
    }), this.props.children);
  };

  TextArea.defaultProps = {
    validate: [],
    expand: false
  };
  return TextArea;
}(React.Component);

var _extends$R = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$R(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ValidIcon$1 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$R(_ref, ["styles"]);

  return React.createElement("svg", _extends$R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.724 5.692L7 11.71a.91.91 0 0 1-1.333 0L3.276 9.197a1.03 1.03 0 0 1 0-1.403.911.911 0 0 1 1.333 0l1.724 1.813 5.057-5.316a.91.91 0 0 1 1.334 0 1.026 1.026 0 0 1 0 1.401"
  }));
});

var _extends$S = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$S(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var InvalidIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$S(_ref, ["styles"]);

  return React.createElement("svg", _extends$S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.308 11.705L8.002 9.401l-2.31 2.308a.99.99 0 1 1-1.401-1.4l2.31-2.31-2.31-2.308a.99.99 0 1 1 1.401-1.4l2.31 2.308 2.306-2.304a.99.99 0 1 1 1.401 1.4L9.404 8l2.305 2.305a.99.99 0 0 1 0 1.4.992.992 0 0 1-1.401 0z"
  }));
});

var styles$N = {"status":"styles_status__15MNR","stateIcon":"styles_stateIcon__TuW0E","validIcon":"styles_validIcon__14Q7T styles_stateIcon__TuW0E","invalidIcon":"styles_invalidIcon__1CMea styles_stateIcon__TuW0E","common":"styles_common__1AYnd","textarea":"styles_textarea__1-rLi styles_common__1AYnd","has-icon":"styles_has-icon__3iZZ_","valid":"styles_valid__3QvfD styles_has-icon__3iZZ_","invalid":"styles_invalid__4ARDB styles_has-icon__3iZZ_","unvalidated":"styles_unvalidated__3TW5i","loading":"styles_loading__1Z5L- styles_has-icon__3iZZ_","container":"styles_container__1_DPR","label":"styles_label__1sQTo bold","error":"styles_error__2L16j my1","errorIcon":"styles_errorIcon__38keg col mr1","hasIcon":"styles_has-icon__3iZZ_"};

var ValidationState;

(function (ValidationState) {
  ValidationState[ValidationState["Valid"] = 0] = "Valid";
  ValidationState[ValidationState["Invalid"] = 1] = "Invalid";
  ValidationState[ValidationState["Unvalidated"] = 2] = "Unvalidated";
})(ValidationState || (ValidationState = {}));

var TextAreaContainer =
/** @class */
function (_super) {
  __extends(TextAreaContainer, _super);

  function TextAreaContainer(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      changed: false,
      errors: [],
      pristine: true,
      validity: ValidationState.Unvalidated
    };
    _this.handleChange = _this.handleChange.bind(_this);
    _this.handleBlur = _this.handleBlur.bind(_this);
    _this.handleValid = _this.handleValid.bind(_this);
    _this.handleInvalid = _this.handleInvalid.bind(_this);
    return _this;
  }

  TextAreaContainer.prototype.handleChange = function (event) {
    this.setState({
      changed: true
    });

    if (this.props.onChange) {
      this.props.onChange(event);
    }
  };

  TextAreaContainer.prototype.handleBlur = function () {
    this.setState(function (prevState) {
      return {
        pristine: !prevState.changed
      };
    });
  };

  TextAreaContainer.prototype.handleValid = function (name, value) {
    this.setState({
      errors: [],
      validity: ValidationState.Valid
    });

    if (this.props.onFieldValid) {
      this.props.onFieldValid(name, value);
    }
  };

  TextAreaContainer.prototype.handleInvalid = function (name, value, errorMessages) {
    this.setState({
      errors: errorMessages,
      validity: ValidationState.Invalid
    });

    if (this.props.onFieldInvalid) {
      this.props.onFieldInvalid(name, value, errorMessages);
    }
  };

  TextAreaContainer.prototype.render = function () {
    var _a;

    var _b = this.props,
        className = _b.className,
        label = _b.label,
        placeholder = _b.placeholder,
        loading = _b.loading;
    var props = omit(this.props, 'ref', 'status');
    var isValid = this.state.validity === ValidationState.Valid;
    var isInvalid = this.state.validity === ValidationState.Invalid;
    var isUnvalidated = this.state.validity === ValidationState.Unvalidated;
    var inputClasses = classnames((_a = {}, _a[styles$N.textarea] = true, _a[styles$N.valid] = isValid, _a[styles$N.invalid] = isInvalid, _a[styles$N.unvalidated] = isUnvalidated, _a[styles$N.loading] = loading, _a));
    var status;

    if (this.props.status) {
      status = this.props.status;
    } else if (isValid) {
      status = React.createElement(ValidIcon$1, {
        className: styles$N.validIcon
      });
    } else if (isInvalid) {
      status = React.createElement(InvalidIcon, {
        className: styles$N.invalidIcon
      });
    }

    return React.createElement("label", {
      className: className
    }, label && React.createElement("div", {
      className: styles$N.label
    }, label), React.createElement("div", {
      className: styles$N.container
    }, React.createElement(TextArea, __assign({}, props, {
      placeholder: placeholder,
      className: inputClasses,
      onFieldValid: this.handleValid,
      onFieldInvalid: this.handleInvalid,
      onChange: this.handleChange,
      onBlur: this.handleBlur
    })), status && React.createElement("div", {
      className: styles$N.status
    }, status)), !this.state.pristine && this.props.errorText && React.createElement("div", {
      className: styles$N.error
    }, this.props.errorText));
  };

  return TextAreaContainer;
}(React.Component);

var styles$O = {"container":"styles_container__2JaZo","suggestionsContainerOpen":"styles_suggestionsContainerOpen__1TSFX","suggestionsList":"styles_suggestionsList__2GyKj m0 list-reset","suggestion":"styles_suggestion__8VidE","suggestionHighlighted":"styles_suggestionHighlighted__1cHeP"};

var Typeahead =
/** @class */
function (_super) {
  __extends(Typeahead, _super);

  function Typeahead() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.renderInputComponent = function (inputProps) {
      return React.createElement(TextField, __assign({
        ref: _this.props.inputRef
      }, _this.props.textFieldProps, {
        // @ts-ignore onChange is typed incorrectly but is compatible
        inputProps: __assign({}, inputProps)
      }));
    };

    _this.renderSuggestion = function (suggestion, data) {
      var rawText = _this.props.getSuggestionValue(suggestion);

      var parts = parse(rawText, match(rawText, data.query));
      return React.createElement("div", null, parts.map(function (part) {
        if (part.highlight) {
          return React.createElement("b", {
            key: part.text
          }, part.text);
        } else {
          return part.text;
        }
      }));
    };

    return _this;
  }

  Typeahead.prototype.render = function () {
    return React.createElement(Autosuggest, __assign({
      focusInputOnSuggestionClick: false,
      renderInputComponent: this.props.renderInputComponent || this.renderInputComponent,
      renderSuggestion: this.props.renderSuggestion || this.renderSuggestion,
      theme: styles$O
    }, this.props));
  };

  return Typeahead;
}(React.Component);

var index$1 = React.forwardRef(function (props, ref) {
  return React.createElement(Typeahead, __assign({
    inputRef: ref
  }, props));
});

var StarRating = function StarRating(_a) {
  var _b = _a.starRatedColor,
      starRatedColor = _b === void 0 ? 'rgb(245, 87, 0)' : _b,
      _c = _a.starSpacing,
      starSpacing = _c === void 0 ? '0px' : _c,
      _d = _a.starDimension,
      starDimension = _d === void 0 ? '16px' : _d,
      _e = _a.numberOfStars,
      numberOfStars = _e === void 0 ? 5 : _e,
      _f = _a.svgIconViewBox,
      svgIconViewBox = _f === void 0 ? '0 0 16 16' : _f,
      _g = _a.svgIconPath,
      svgIconPath = _g === void 0 ? 'M8 .867l2.318 4.696 5.182.753-3.75 3.655.885 5.162L8 12.696l-4.635 2.437.885-5.162L.5 6.316l5.182-.753z' : _g,
      rating = _a.rating,
      rest = __rest(_a, ["starRatedColor", "starSpacing", "starDimension", "numberOfStars", "svgIconViewBox", "svgIconPath", "rating"]);

  return React.createElement(StarRatings, __assign({
    rating: rating,
    starRatedColor: starRatedColor,
    starSpacing: starSpacing,
    starDimension: starDimension,
    numberOfStars: numberOfStars,
    svgIconViewBox: svgIconViewBox,
    svgIconPath: svgIconPath
  }, rest));
};

var styles$P = {"lazy":"styles_lazy__3knV7","loaded":"styles_loaded__2AUy4"};

var ContentfulImage =
/** @class */
function (_super) {
  __extends(ContentfulImage, _super);

  function ContentfulImage() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.state = {
      isVisible: false,
      loaded: false
    };
    return _this;
  } // This is for testing purposes only inside of Storybook


  ContentfulImage.prototype.getRootElement = function () {
    return null;
  };

  ContentfulImage.prototype.render = function () {
    var _a;

    var _this = this;

    var _b = this.props,
        src = _b.src,
        width = _b.width,
        height = _b.height,
        format$$1 = _b.format,
        fit = _b.fit,
        quality = _b.quality,
        _c = _b.lazyLoad,
        lazyLoad = _c === void 0 ? false : _c,
        className = _b.className,
        props = __rest(_b, ["src", "width", "height", "format", "fit", "quality", "lazyLoad", "className"]);

    var formattedSrc = src + "?";

    if (format$$1) {
      if (format$$1 === 'png') {
        // Always request 8bit PNGs, this flag is invalid without a FM parameter even if it is already a PNG.
        // This is likely safe enough considering we only use transparent PNGs for logos and simple icons.
        formattedSrc += '&fm=png&fl=png8';
      } else if (format$$1 === 'jpg') {
        // Progressive JPGs suggest loading the lowest resolution first then loading the balance of the image's
        // quality in progressively higher detail. Network speeds should be fast enough, but this will help quality scores.
        formattedSrc += "&fm=jpg&fl=progressive";
      } else {
        formattedSrc += "&fm=" + format$$1;
      }
    }

    if (quality && format$$1 !== 'png') {
      formattedSrc += "&q=" + quality;
    }

    if (fit) {
      formattedSrc += "&fit=" + fit;
    }

    if (width) {
      formattedSrc += "&w=" + width;
    }

    if (height) {
      formattedSrc += "&h=" + height;
    }

    if (!lazyLoad) {
      return React.createElement("img", __assign({
        src: formattedSrc,
        className: className
      }, props));
    }

    var _d = this.state,
        isVisible = _d.isVisible,
        loaded = _d.loaded;
    return React.createElement(Observer, {
      root: this.getRootElement(),
      onChange: function onChange(entry, unobserve) {
        if (entry.isIntersecting) {
          unobserve();
        }

        _this.setState({
          isVisible: entry.isIntersecting
        });
      }
    }, React.createElement("img", __assign({
      className: classnames(className, styles$P.lazy, (_a = {}, _a[styles$P.loaded] = loaded, _a)),
      onLoad: function onLoad() {
        _this.setState({
          loaded: true
        });
      },
      src: isVisible ? formattedSrc : ''
    }, props)));
  };

  return ContentfulImage;
}(PureComponent);

var index$2 = deprecate(ContentfulImage, 'The <ContentfulImage> component is deprecated. Use the <Image> component with `ContentAsset.imageUrl` instead.');

var LegacyInput =
/** @class */
function (_super) {
  __extends(LegacyInput, _super);

  function LegacyInput(props) {
    var _this = _super.call(this, props) || this;

    _this.handleChange = _this.handleChange.bind(_this);
    var initialValue = props.value || props.defaultValue;

    if (initialValue !== undefined && props.name) {
      _this.handleValidate(props.name, initialValue);
    }

    return _this;
  }

  LegacyInput.prototype.componentWillReceiveProps = function (nextProps) {
    var value = nextProps.value,
        name = nextProps.name,
        showValidation = nextProps.showValidation;
    var validate = this.props.value !== value || this.props.showValidation !== showValidation && showValidation === true;

    if (value !== undefined && name && validate) {
      this.handleValidate(name, value);
    }
  };

  LegacyInput.prototype.handleValidate = function (name, value) {
    var _this = this;

    var validate = this.props.validate;

    if (validate === null) {
      return;
    }

    var rules = uniq(Array.isArray(validate) ? validate : [validate]);
    var errorName = this.props.label ? this.props.label.toLowerCase() : name ? name : '';
    Checkit.check(errorName, value, rules).then(function () {
      if (_this.props.onFieldValid) {
        _this.props.onFieldValid(name, value);
      }
    }).catch(function (fieldError) {
      if (fieldError) {
        var errorMessages = [];

        if (fieldError.errors && fieldError.errors.length > 0) {
          errorMessages = fieldError.errors.map(function (err) {
            return err.message;
          });
        }

        if (_this.props.onFieldInvalid) {
          _this.props.onFieldInvalid(name, value, errorMessages);
        }
      } else {
        throw fieldError;
      }
    });
  };

  LegacyInput.prototype.handleChange = function (event) {
    var value = event.target.value;
    var name = event.target.name;
    this.handleValidate(name, value);

    if (this.props.onChange) {
      this.props.onChange(event);
    }
  };

  LegacyInput.prototype.render = function () {
    var props = omit(this.props, 'validate', 'onChange', 'onFieldValid', 'onFieldInvalid', 'inputRef', 'showValidation');
    var _a = this.props,
        type = _a.type,
        inputRef = _a.inputRef;
    return React.createElement("input", __assign({}, props, {
      type: type,
      onChange: this.handleChange,
      ref: inputRef
    }));
  };

  LegacyInput.defaultProps = {
    validate: []
  };
  return LegacyInput;
}(React.Component);

var LegacyInput$1 = deprecate(LegacyInput, 'The <LegacyInput> component is deprecated. Use the <Input> component with custom validation instead.');

var _extends$T = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$T(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ValidIcon$2 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$T(_ref, ["styles"]);

  return React.createElement("svg", _extends$T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.724 5.692L7 11.71a.91.91 0 0 1-1.333 0L3.276 9.197a1.03 1.03 0 0 1 0-1.403.911.911 0 0 1 1.333 0l1.724 1.813 5.057-5.316a.91.91 0 0 1 1.334 0 1.026 1.026 0 0 1 0 1.401"
  }));
});

var _extends$U = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$U(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var InvalidIcon$1 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$U(_ref, ["styles"]);

  return React.createElement("svg", _extends$U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.308 11.705L8.002 9.401l-2.31 2.308a.99.99 0 1 1-1.401-1.4l2.31-2.31-2.31-2.308a.99.99 0 1 1 1.401-1.4l2.31 2.308 2.306-2.304a.99.99 0 1 1 1.401 1.4L9.404 8l2.305 2.305a.99.99 0 0 1 0 1.4.992.992 0 0 1-1.401 0z"
  }));
});

var _extends$V = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$V(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ErrorIcon$1 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$V(_ref, ["styles"]);

  return React.createElement("svg", _extends$V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    fillRule: "evenodd",
    d: "M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm9.315 2.718a.993.993 0 0 0 1.403-1.403L9.41 8.007l1.308-1.309a.993.993 0 0 0-1.403-1.403L8.007 6.603 6.694 5.291a.991.991 0 1 0-1.403 1.403l1.312 1.312-1.312 1.313a.991.991 0 1 0 1.403 1.403L8.007 9.41l1.308 1.308z"
  }));
});

var styles$Q = {"status":"styles_status__17m9u flex items-center","stateIcon":"styles_stateIcon__QcKuw","validIcon":"styles_validIcon__3ml9K styles_stateIcon__QcKuw","invalidIcon":"styles_invalidIcon__24nZS styles_stateIcon__QcKuw","common":"styles_common__3GFi8","input":"styles_input__4Pgm4 styles_common__3GFi8","has-icon":"styles_has-icon__3afSp","xsmall":"styles_xsmall__1Yhxl","small":"styles_small__jRar4","medium":"styles_medium__16ug-","large":"styles_large__2-428","xlarge":"styles_xlarge__3r_SG","valid":"styles_valid__2mniO styles_has-icon__3afSp","invalid":"styles_invalid__36H43 styles_has-icon__3afSp","unvalidated":"styles_unvalidated__318VO","loading":"styles_loading__1ysFv styles_has-icon__3afSp","container":"styles_container__obwmQ","label":"styles_label__4pCNv bold","error":"styles_error__20Uip my1","errorIcon":"styles_errorIcon__20Kg6 col mr1","hasIcon":"styles_has-icon__3afSp"};

var ValidationState$1;

(function (ValidationState) {
  ValidationState[ValidationState["Valid"] = 0] = "Valid";
  ValidationState[ValidationState["Invalid"] = 1] = "Invalid";
  ValidationState[ValidationState["Unvalidated"] = 2] = "Unvalidated";
})(ValidationState$1 || (ValidationState$1 = {}));

var LegacyTextInput =
/** @class */
function (_super) {
  __extends(LegacyTextInput, _super);

  function LegacyTextInput(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      changed: false,
      errors: [],
      pristine: true,
      validity: ValidationState$1.Unvalidated
    };
    _this.handleChange = _this.handleChange.bind(_this);
    _this.handleBlur = _this.handleBlur.bind(_this);
    _this.handleValid = _this.handleValid.bind(_this);
    _this.handleInvalid = _this.handleInvalid.bind(_this);
    return _this;
  }

  LegacyTextInput.prototype.handleChange = function (event) {
    this.setState({
      changed: true
    });

    if (this.props.onChange) {
      this.props.onChange(event);
    }
  };

  LegacyTextInput.prototype.handleBlur = function (e) {
    this.setState(function (prevState) {
      return {
        pristine: !prevState.changed
      };
    });
    this.props.onBlur && this.props.onBlur(e);
  };

  LegacyTextInput.prototype.handleValid = function (name, value) {
    this.setState({
      errors: [],
      validity: ValidationState$1.Valid
    });

    if (this.props.onFieldValid) {
      this.props.onFieldValid(name, value);
    }
  };

  LegacyTextInput.prototype.handleInvalid = function (name, value, errorMessages) {
    this.setState({
      errors: errorMessages,
      validity: ValidationState$1.Invalid
    });

    if (this.props.onFieldInvalid) {
      this.props.onFieldInvalid(name, value, errorMessages);
    }
  };

  LegacyTextInput.prototype.render = function () {
    var _a;

    var _b = this.props,
        className = _b.className,
        type = _b.type,
        fieldSize = _b.fieldSize,
        label = _b.label,
        placeholder = _b.placeholder,
        showValidation = _b.showValidation;
    var props = omit(this.props, 'ref', 'fieldSize', 'status');
    var isValid = this.state.validity === ValidationState$1.Valid;
    var isInvalid = this.state.validity === ValidationState$1.Invalid;
    var isUnvalidated = this.state.validity === ValidationState$1.Unvalidated;

    if (showValidation === false) {
      isValid = false;
      isInvalid = false;
      isUnvalidated = true;
    }

    var inputClasses = classnames((_a = {}, _a[styles$Q.input] = true, _a[styles$Q.valid] = isValid, _a[styles$Q.invalid] = isInvalid, _a[styles$Q.unvalidated] = isUnvalidated, _a[styles$Q[fieldSize]] = true, _a));
    var status;

    if (this.props.status) {
      status = this.props.status;
    } else if (isValid) {
      status = React.createElement(ValidIcon$2, {
        className: styles$Q.validIcon
      });
    } else if (isInvalid) {
      status = React.createElement(InvalidIcon$1, {
        className: styles$Q.invalidIcon
      });
    }

    return React.createElement("label", {
      className: className
    }, label && React.createElement("div", {
      className: styles$Q.label
    }, label), React.createElement("div", {
      className: styles$Q.container
    }, React.createElement(LegacyInput$1, __assign({}, props, {
      type: type,
      placeholder: placeholder,
      className: inputClasses,
      onFieldValid: this.handleValid,
      onFieldInvalid: this.handleInvalid,
      onChange: this.handleChange,
      onBlur: this.handleBlur
    })), status && React.createElement("div", {
      className: styles$Q.status
    }, status)), (!this.state.pristine && showValidation !== false || showValidation === true) && this.state.errors.map(function (error, i) {
      return React.createElement("div", {
        key: i,
        className: styles$Q.error
      }, React.createElement(ErrorIcon$1, {
        className: styles$Q.errorIcon
      }), React.createElement("span", null, error));
    }));
  };

  LegacyTextInput.defaultProps = {
    fieldSize: 'medium'
  };
  return LegacyTextInput;
}(React.Component);

var MaskedInput =
/** @class */
function (_super) {
  __extends(MaskedInput, _super);

  function MaskedInput(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      value: _this.props.value || _this.props.defaultValue || undefined
    };
    _this.handleChange = _this.handleChange.bind(_this);
    return _this;
  }

  MaskedInput.prototype.shouldComponentUpdate = function (nextProps, nextState) {
    var nextValue = nextProps.value || nextState.value;
    return this.state.value !== nextValue || this.props.showValidation !== nextProps.showValidation;
  };

  MaskedInput.prototype.handleChange = function (e) {
    this.setState({
      value: e.target.value
    });

    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  MaskedInput.prototype.render = function () {
    return React.createElement(Masked, __assign({
      mask: this.props.mask,
      guide: this.props.guide,
      onChange: this.handleChange,
      // current @types/react-text-mask typings are out of date should be fixed by this PR
      //https://github.com/DefinitelyTyped/DefinitelyTyped/pull/25438
      //@ts-ignore
      render: function render(ref, props) {
        return React.createElement(LegacyTextInput, __assign({
          inputRef: ref
        }, props));
      }
    }, this.props));
  };

  return MaskedInput;
}(React.Component);

var numberMask$1 = createNumberMask({
  prefix: '$',
  allowDecimal: true
});

var LegacyCurrencyInput = function LegacyCurrencyInput(_a) {
  var _b = _a.placeholder,
      placeholder = _b === void 0 ? 'Enter Amount' : _b,
      props = __rest(_a, ["placeholder"]);

  return React.createElement(MaskedInput, __assign({
    placeholder: placeholder,
    mask: numberMask$1
  }, props));
};

var numberMask$2 = createNumberMask({
  prefix: '$',
  allowDecimal: true
});

var InlineCurrencyEditor = function InlineCurrencyEditor(_a) {
  var _b = _a.placeholder,
      placeholder = _b === void 0 ? 'Enter Amount' : _b,
      props = __rest(_a, ["placeholder"]);

  return React.createElement(MaskedInput, __assign({
    placeholder: placeholder,
    mask: numberMask$2,
    //@ts-ignore
    render: function render(ref, props) {
      return React.createElement(InlineEditor, __assign({
        inputRef: ref
      }, props));
    }
  }, props));
};

var CustomText =
/** @class */
function (_super) {
  __extends(CustomText, _super);

  function CustomText(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {};
    _this.format = _this.format.bind(_this);
    _this.focus = _this.focus.bind(_this);
    _this.storeInput = _this.storeInput.bind(_this);
    _this.onChange = _this.onChange.bind(_this);
    return _this;
  }

  CustomText.getDerivedStateFromProps = function (props, state) {
    if (state.value && state.value === props.value) {
      return null;
    }

    return {
      value: props.value
    };
  };

  CustomText.prototype.onChange = function (event) {
    var onChange = this.props.onChange;
    var value = this.state.value;
    var newValue = parsePhoneNumberCharacters(event.target.value); // By default, if a value is something like `"(123)"`
    // then Backspace would only erase the rightmost brace
    // becoming something like `"(123"`
    // which would give the same `"123"` value
    // which would then be formatted back to `"(123)"`
    // and so a user wouldn't be able to erase the phone number.
    // Working around this issue with this simple hack.

    if (newValue === value) {
      if (this.format(newValue).indexOf(event.target.value) === 0) {
        // Trim the last digit (or plus sign).
        newValue = newValue.slice(0, -1);
      }
    } // Prevents React from resetting the `<input/>` caret position.
    // https://github.com/reactjs/react-redux/issues/525#issuecomment-254852039
    // https://github.com/facebook/react/issues/955


    this.setState({
      value: newValue
    }, function () {
      return onChange(newValue);
    });
  };

  CustomText.prototype.format = function (value) {
    var _a = this.props,
        country = _a.country,
        metadata = _a.metadata;
    return formatPhoneNumber(value, country, metadata).text;
  };

  CustomText.prototype.focus = function () {
    this.input && this.input.focus();
  };

  CustomText.prototype.storeInput = function (ref) {
    this.input = ref;
  };

  CustomText.prototype.render = function () {
    // Prevents React from resetting the `<input/>` caret position.
    // https://github.com/reactjs/react-redux/issues/525#issuecomment-254852039
    // https://github.com/facebook/react/issues/955
    var passThroughProps = omit(this.props, 'defaultValue', 'onChange', 'country', 'metadata');
    var valueProps = {};

    if (this.state.value) {
      valueProps = {
        value: this.format(this.state.value)
      };
    }

    if (this.props.defaultValue) {
      valueProps = {
        defaultValue: this.props.defaultValue
      };
    }

    return React.createElement(LegacyTextInput, __assign({}, valueProps, {
      inputRef: this.storeInput
    }, passThroughProps, {
      onChange: this.onChange
    }));
  };

  return CustomText;
}(React.Component);

var styles$R = {"flagIcon":"styles_flagIcon__G2xSI","rrui__input-field":"styles_rrui__input-field__3FNBs","rrui__select__button":"styles_rrui__select__button__2F7CL","rrui__select__native-expanded":"styles_rrui__select__native-expanded__3o_IK","rrui__select__options":"styles_rrui__select__options__2Lvre","rrui__select__options--menu":"styles_rrui__select__options--menu__-wFuk","react-phone-number-input__country":"styles_react-phone-number-input__country__PFM2J","react-phone-number-input__country--native-expanded":"styles_react-phone-number-input__country--native-expanded__1Qkib","rrui__input":"styles_rrui__input__25I0x","react-phone-number-input__country-select":"styles_react-phone-number-input__country-select__3P8Cp","react-phone-number-input__country-select-arrow":"styles_react-phone-number-input__country-select-arrow__3KyTt","rruiInputField":"styles_rrui__input-field__3FNBs","rruiSelectButton":"styles_rrui__select__button__2F7CL","rruiSelectNativeExpanded":"styles_rrui__select__native-expanded__3o_IK","rruiSelectOptions":"styles_rrui__select__options__2Lvre","rruiSelectOptionsMenu":"styles_rrui__select__options--menu__-wFuk","reactPhoneNumberInputCountry":"styles_react-phone-number-input__country__PFM2J","reactPhoneNumberInputCountryNativeExpanded":"styles_react-phone-number-input__country--native-expanded__1Qkib","rruiInput":"styles_rrui__input__25I0x","reactPhoneNumberInputCountrySelect":"styles_react-phone-number-input__country-select__3P8Cp","reactPhoneNumberInputCountrySelectArrow":"styles_react-phone-number-input__country-select-arrow__3KyTt"};

var CustomSelect =
/** @class */
function (_super) {
  __extends(CustomSelect, _super);

  function CustomSelect() {
    var _this = _super !== null && _super.apply(this, arguments) || this; // Focuses phone number `<input/>` field
    // on tab out of the country `<select/>`.


    _this.onTabOut = function (event) {
      var focusPhoneInputField = _this.props.focusPhoneInputField;
      event.preventDefault(); // Focus the phone number input upon country selection
      // (do it in a timeout because the `<input/>`
      //  is hidden while selecting a country)

      setTimeout(focusPhoneInputField, 0);
    };

    return _this;
  } // storeSelectRef = (ref) => this.select = ref
  // ref={ this.storeSelectRef }


  CustomSelect.prototype.render = function () {
    var _a = this.props,
        name = _a.name,
        value = _a.value,
        onChange = _a.onChange,
        options = _a.options,
        disabled = _a.disabled,
        tabIndex = _a.tabIndex,
        className = _a.className,
        hidePhoneInputField = _a.hidePhoneInputField,
        ariaLabel = _a.ariaLabel,
        closeAriaLabel = _a.closeAriaLabel,
        saveOnIcons = _a.saveOnIcons,
        nativeExpanded = _a.nativeExpanded,
        maxItems = _a.maxItems,
        inputClassName = _a.inputClassName,
        toggleClassName = _a.toggleClassName;
    return React.createElement(Select, {
      name: name,
      value: value,
      onChange: onChange,
      options: options,
      disabled: disabled,
      tabIndex: tabIndex,
      className: classnames(className, styles$R.flagIcon, {
        'react-phone-number-input__country--native-expanded': nativeExpanded
      }),
      onToggle: hidePhoneInputField,
      onTabOut: this.onTabOut,
      ariaLabel: ariaLabel,
      closeAriaLabel: closeAriaLabel,
      saveOnIcons: saveOnIcons,
      nativeExpanded: nativeExpanded,
      maxItems: maxItems,
      inputClassName: inputClassName,
      toggleClassName: toggleClassName,
      focusUponSelection: false,
      concise: true,
      autocomplete: true,
      autocompleteShowAll: true
    });
  };

  CustomSelect.defaultProps = {
    nativeExpanded: false,
    // If set to `false`, then country flags will be shown
    // for all countries when country `<select/>` is expanded.
    // By default shows flag only for currently selected country.
    // (is `true` by default to save user's traffic
    //  because all flags are about 3 MegaBytes)
    saveOnIcons: true
  };
  return CustomSelect;
}(React.Component);

var version = "1.2.15";
var country_calling_codes = {
	"1": [
		"US",
		"AG",
		"AI",
		"AS",
		"BB",
		"BM",
		"BS",
		"CA",
		"DM",
		"DO",
		"GD",
		"GU",
		"JM",
		"KN",
		"KY",
		"LC",
		"MP",
		"MS",
		"PR",
		"SX",
		"TC",
		"TT",
		"VC",
		"VG",
		"VI"
	],
	"7": [
		"RU",
		"KZ"
	],
	"20": [
		"EG"
	],
	"27": [
		"ZA"
	],
	"30": [
		"GR"
	],
	"31": [
		"NL"
	],
	"32": [
		"BE"
	],
	"33": [
		"FR"
	],
	"34": [
		"ES"
	],
	"36": [
		"HU"
	],
	"39": [
		"IT",
		"VA"
	],
	"40": [
		"RO"
	],
	"41": [
		"CH"
	],
	"43": [
		"AT"
	],
	"44": [
		"GB",
		"GG",
		"IM",
		"JE"
	],
	"45": [
		"DK"
	],
	"46": [
		"SE"
	],
	"47": [
		"NO",
		"SJ"
	],
	"48": [
		"PL"
	],
	"49": [
		"DE"
	],
	"51": [
		"PE"
	],
	"52": [
		"MX"
	],
	"53": [
		"CU"
	],
	"54": [
		"AR"
	],
	"55": [
		"BR"
	],
	"56": [
		"CL"
	],
	"57": [
		"CO"
	],
	"58": [
		"VE"
	],
	"60": [
		"MY"
	],
	"61": [
		"AU",
		"CC",
		"CX"
	],
	"62": [
		"ID"
	],
	"63": [
		"PH"
	],
	"64": [
		"NZ"
	],
	"65": [
		"SG"
	],
	"66": [
		"TH"
	],
	"81": [
		"JP"
	],
	"82": [
		"KR"
	],
	"84": [
		"VN"
	],
	"86": [
		"CN"
	],
	"90": [
		"TR"
	],
	"91": [
		"IN"
	],
	"92": [
		"PK"
	],
	"93": [
		"AF"
	],
	"94": [
		"LK"
	],
	"95": [
		"MM"
	],
	"98": [
		"IR"
	],
	"211": [
		"SS"
	],
	"212": [
		"MA",
		"EH"
	],
	"213": [
		"DZ"
	],
	"216": [
		"TN"
	],
	"218": [
		"LY"
	],
	"220": [
		"GM"
	],
	"221": [
		"SN"
	],
	"222": [
		"MR"
	],
	"223": [
		"ML"
	],
	"224": [
		"GN"
	],
	"225": [
		"CI"
	],
	"226": [
		"BF"
	],
	"227": [
		"NE"
	],
	"228": [
		"TG"
	],
	"229": [
		"BJ"
	],
	"230": [
		"MU"
	],
	"231": [
		"LR"
	],
	"232": [
		"SL"
	],
	"233": [
		"GH"
	],
	"234": [
		"NG"
	],
	"235": [
		"TD"
	],
	"236": [
		"CF"
	],
	"237": [
		"CM"
	],
	"238": [
		"CV"
	],
	"239": [
		"ST"
	],
	"240": [
		"GQ"
	],
	"241": [
		"GA"
	],
	"242": [
		"CG"
	],
	"243": [
		"CD"
	],
	"244": [
		"AO"
	],
	"245": [
		"GW"
	],
	"246": [
		"IO"
	],
	"247": [
		"AC"
	],
	"248": [
		"SC"
	],
	"249": [
		"SD"
	],
	"250": [
		"RW"
	],
	"251": [
		"ET"
	],
	"252": [
		"SO"
	],
	"253": [
		"DJ"
	],
	"254": [
		"KE"
	],
	"255": [
		"TZ"
	],
	"256": [
		"UG"
	],
	"257": [
		"BI"
	],
	"258": [
		"MZ"
	],
	"260": [
		"ZM"
	],
	"261": [
		"MG"
	],
	"262": [
		"RE",
		"YT"
	],
	"263": [
		"ZW"
	],
	"264": [
		"NA"
	],
	"265": [
		"MW"
	],
	"266": [
		"LS"
	],
	"267": [
		"BW"
	],
	"268": [
		"SZ"
	],
	"269": [
		"KM"
	],
	"290": [
		"SH",
		"TA"
	],
	"291": [
		"ER"
	],
	"297": [
		"AW"
	],
	"298": [
		"FO"
	],
	"299": [
		"GL"
	],
	"350": [
		"GI"
	],
	"351": [
		"PT"
	],
	"352": [
		"LU"
	],
	"353": [
		"IE"
	],
	"354": [
		"IS"
	],
	"355": [
		"AL"
	],
	"356": [
		"MT"
	],
	"357": [
		"CY"
	],
	"358": [
		"FI",
		"AX"
	],
	"359": [
		"BG"
	],
	"370": [
		"LT"
	],
	"371": [
		"LV"
	],
	"372": [
		"EE"
	],
	"373": [
		"MD"
	],
	"374": [
		"AM"
	],
	"375": [
		"BY"
	],
	"376": [
		"AD"
	],
	"377": [
		"MC"
	],
	"378": [
		"SM"
	],
	"380": [
		"UA"
	],
	"381": [
		"RS"
	],
	"382": [
		"ME"
	],
	"383": [
		"XK"
	],
	"385": [
		"HR"
	],
	"386": [
		"SI"
	],
	"387": [
		"BA"
	],
	"389": [
		"MK"
	],
	"420": [
		"CZ"
	],
	"421": [
		"SK"
	],
	"423": [
		"LI"
	],
	"500": [
		"FK"
	],
	"501": [
		"BZ"
	],
	"502": [
		"GT"
	],
	"503": [
		"SV"
	],
	"504": [
		"HN"
	],
	"505": [
		"NI"
	],
	"506": [
		"CR"
	],
	"507": [
		"PA"
	],
	"508": [
		"PM"
	],
	"509": [
		"HT"
	],
	"590": [
		"GP",
		"BL",
		"MF"
	],
	"591": [
		"BO"
	],
	"592": [
		"GY"
	],
	"593": [
		"EC"
	],
	"594": [
		"GF"
	],
	"595": [
		"PY"
	],
	"596": [
		"MQ"
	],
	"597": [
		"SR"
	],
	"598": [
		"UY"
	],
	"599": [
		"CW",
		"BQ"
	],
	"670": [
		"TL"
	],
	"672": [
		"NF"
	],
	"673": [
		"BN"
	],
	"674": [
		"NR"
	],
	"675": [
		"PG"
	],
	"676": [
		"TO"
	],
	"677": [
		"SB"
	],
	"678": [
		"VU"
	],
	"679": [
		"FJ"
	],
	"680": [
		"PW"
	],
	"681": [
		"WF"
	],
	"682": [
		"CK"
	],
	"683": [
		"NU"
	],
	"685": [
		"WS"
	],
	"686": [
		"KI"
	],
	"687": [
		"NC"
	],
	"688": [
		"TV"
	],
	"689": [
		"PF"
	],
	"690": [
		"TK"
	],
	"691": [
		"FM"
	],
	"692": [
		"MH"
	],
	"800": [
		"001"
	],
	"808": [
		"001"
	],
	"850": [
		"KP"
	],
	"852": [
		"HK"
	],
	"853": [
		"MO"
	],
	"855": [
		"KH"
	],
	"856": [
		"LA"
	],
	"870": [
		"001"
	],
	"878": [
		"001"
	],
	"880": [
		"BD"
	],
	"881": [
		"001"
	],
	"882": [
		"001"
	],
	"883": [
		"001"
	],
	"886": [
		"TW"
	],
	"888": [
		"001"
	],
	"960": [
		"MV"
	],
	"961": [
		"LB"
	],
	"962": [
		"JO"
	],
	"963": [
		"SY"
	],
	"964": [
		"IQ"
	],
	"965": [
		"KW"
	],
	"966": [
		"SA"
	],
	"967": [
		"YE"
	],
	"968": [
		"OM"
	],
	"970": [
		"PS"
	],
	"971": [
		"AE"
	],
	"972": [
		"IL"
	],
	"973": [
		"BH"
	],
	"974": [
		"QA"
	],
	"975": [
		"BT"
	],
	"976": [
		"MN"
	],
	"977": [
		"NP"
	],
	"979": [
		"001"
	],
	"992": [
		"TJ"
	],
	"993": [
		"TM"
	],
	"994": [
		"AZ"
	],
	"995": [
		"GE"
	],
	"996": [
		"KG"
	],
	"998": [
		"UZ"
	]
};
var countries = {
	AC: [
		"247",
		"00",
		"[46]\\d{4}|[01589]\\d{5}",
		[
			5,
			6
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6[2-467]\\d{3}",
				[
					5
				]
			],
			[
				"4\\d{4}",
				[
					5
				]
			],
			0,
			0,
			0,
			0,
			[
				"[01589]\\d{5}",
				[
					6
				]
			]
		]
	],
	AD: [
		"376",
		"00",
		"[16]\\d{5,8}|[37-9]\\d{5}",
		[
			6,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"[136-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"180",
					"180[02]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"690"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[78]\\d{5}",
				[
					6
				]
			],
			[
				"(?:3\\d|6(?:[0-8]|9(?:0\\d{2})?))\\d{4}",
				[
					6,
					9
				]
			],
			[
				"180[02]\\d{4}",
				[
					8
				]
			],
			[
				"[19]\\d{5}",
				[
					6
				]
			]
		]
	],
	AE: [
		"971",
		"00",
		"[2-79]\\d{7,8}|800\\d{2,9}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"([2-4679])(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-4679][2-8]"
				]
			],
			[
				"(5\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"5"
				]
			],
			[
				"([479]00)(\\d)(\\d{5})",
				"$1 $2 $3",
				[
					"[479]00"
				],
				"$1"
			],
			[
				"([68]00)(\\d{2,9})",
				"$1 $2",
				[
					"[68]00"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"[2-4679][2-8]\\d{6}",
				[
					8
				]
			],
			[
				"5[024-68]\\d{7}",
				[
					9
				]
			],
			[
				"400\\d{6}|800\\d{2,9}"
			],
			[
				"900[02]\\d{5}",
				[
					9
				]
			],
			0,
			0,
			[
				"600[25]\\d{5}",
				[
					9
				]
			],
			0,
			0,
			[
				"700[05]\\d{5}",
				[
					9
				]
			]
		]
	],
	AF: [
		"93",
		"00",
		"[2-7]\\d{8}",
		[
			9
		],
		[
			[
				"([2-7]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-7]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:[25][0-8]|[34][0-4]|6[0-5])[2-9]\\d{6}"
			],
			[
				"7(?:[014-9]\\d|2[89]|3[01])\\d{6}"
			]
		]
	],
	AG: [
		"1",
		"011",
		"[2589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"268",
		[
			[
				"268(?:4(?:6[0-38]|84)|56[0-2])\\d{4}"
			],
			[
				"268(?:464|7(?:1[3-9]|2\\d|3[246]|64|[78][0-689]))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			],
			0,
			0,
			[
				"26840[69]\\d{4}"
			],
			[
				"26848[01]\\d{4}"
			]
		]
	],
	AI: [
		"1",
		"011",
		"[2589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"264",
		[
			[
				"2644(?:6[12]|9[78])\\d{4}"
			],
			[
				"264(?:235|476|5(?:3[6-9]|8[1-4])|7(?:29|72))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	AL: [
		"355",
		"00",
		"[2-57]\\d{7}|6\\d{8}|8\\d{5,7}|9\\d{5}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(4)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4[0-6]"
				]
			],
			[
				"(6\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"6"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2358][2-5]|4[7-9]"
				]
			],
			[
				"(\\d{3})(\\d{3,5})",
				"$1 $2",
				[
					"[235][16-9]|[79]|8[016-9]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:[2358](?:[16-9]\\d[2-9]|[2-5][2-9]\\d)|4(?:[2-57-9][2-9]\\d|6\\d{2}))\\d{4}",
				[
					8
				]
			],
			[
				"6(?:[689][2-9]|7[2-6])\\d{6}",
				[
					9
				]
			],
			[
				"800\\d{4}",
				[
					7
				]
			],
			[
				"900[1-9]\\d{2}",
				[
					6
				]
			],
			[
				"700[2-9]\\d{4}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			[
				"808[1-9]\\d{2}",
				[
					6
				]
			]
		]
	],
	AM: [
		"374",
		"00",
		"[1-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"1|47"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"4[1349]|[5-7]|88|9[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[23]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"8|90"
				],
				"0 $1"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[0-2]\\d|2(?:2[2-46]|3[1-8]|4[2-69]|5[2-7]|6[1-9]|8[1-7])|3[12]2|47\\d)\\d{5}"
			],
			[
				"(?:4[1349]|55|77|88|9[13-9])\\d{6}"
			],
			[
				"800\\d{5}"
			],
			[
				"90[016]\\d{5}"
			],
			0,
			0,
			0,
			0,
			[
				"60(?:2[78]|3[5-9]|4[02-9]|5[0-46-9]|[6-8]\\d|90)\\d{4}"
			],
			[
				"80[1-4]\\d{5}"
			]
		]
	],
	AO: [
		"244",
		"00",
		"[29]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d(?:[26-9]\\d|\\d[26-9])\\d{5}"
			],
			[
				"9[1-49]\\d{7}"
			]
		]
	],
	AR: [
		"54",
		"00",
		"11\\d{8}|[2368]\\d{9}|9\\d{10}",
		[
			10,
			11
		],
		[
			[
				"([68]\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[68]"
				]
			],
			[
				"(9)(11)(\\d{4})(\\d{4})",
				"$2 15-$3-$4",
				[
					"911"
				],
				0,
				0,
				"$1 $2 $3-$4"
			],
			[
				"(9)(\\d{3})(\\d{3})(\\d{4})",
				"$2 15-$3-$4",
				[
					"9(?:2[2-4689]|3[3-8])",
					"9(?:2(?:2[013]|3[067]|49|6[01346]|8|9[147-9])|3(?:36|4[1-358]|5[138]|6|7[069]|8[013578]))",
					"9(?:2(?:2(?:0[013-9]|[13])|3(?:0[013-9]|[67])|49|6(?:[0136]|4[0-59])|8|9(?:[19]|44|7[013-9]|8[14]))|3(?:36|4(?:[12]|3[4-6]|[58]4)|5(?:1|3[0-24-689]|8[46])|6|7[069]|8(?:[01]|34|[578][45])))",
					"9(?:2(?:2(?:0[013-9]|[13])|3(?:0[013-9]|[67])|49|6(?:[0136]|4[0-59])|8|9(?:[19]|44|7[013-9]|8[14]))|3(?:36|4(?:[12]|3(?:4|5[014]|6[1-39])|[58]4)|5(?:1|3[0-24-689]|8[46])|6|7[069]|8(?:[01]|34|[578][45])))"
				],
				0,
				0,
				"$1 $2 $3-$4"
			],
			[
				"(9)(\\d{4})(\\d{2})(\\d{4})",
				"$2 15-$3-$4",
				[
					"9[23]"
				],
				0,
				0,
				"$1 $2 $3-$4"
			],
			[
				"(11)(\\d{4})(\\d{4})",
				"$1 $2-$3",
				[
					"11"
				],
				0,
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2-$3",
				[
					"2(?:2[013]|3[067]|49|6[01346]|8|9[147-9])|3(?:36|4[1-358]|5[138]|6|7[069]|8[013578])",
					"2(?:2(?:0[013-9]|[13])|3(?:0[013-9]|[67])|49|6(?:[0136]|4[0-59])|8|9(?:[19]|44|7[013-9]|8[14]))|3(?:36|4(?:[12]|3[4-6]|[58]4)|5(?:1|3[0-24-689]|8[46])|6|7[069]|8(?:[01]|34|[578][45]))",
					"2(?:2(?:0[013-9]|[13])|3(?:0[013-9]|[67])|49|6(?:[0136]|4[0-59])|8|9(?:[19]|44|7[013-9]|8[14]))|3(?:36|4(?:[12]|3(?:4|5[014]|6[1-39])|[58]4)|5(?:1|3[0-24-689]|8[46])|6|7[069]|8(?:[01]|34|[578][45]))"
				],
				0,
				1
			],
			[
				"(\\d{4})(\\d{2})(\\d{4})",
				"$1 $2-$3",
				[
					"[23]"
				],
				0,
				1
			]
		],
		"0",
		"0$1",
		"0?(?:(11|2(?:2(?:02?|[13]|2[13-79]|4[1-6]|5[2457]|6[124-8]|7[1-4]|8[13-6]|9[1267])|3(?:02?|1[467]|2[03-6]|3[13-8]|[49][2-6]|5[2-8]|[67])|4(?:7[3-578]|9)|6(?:[0136]|2[24-6]|4[6-8]?|5[15-8])|80|9(?:0[1-3]|[19]|2\\d|3[1-6]|4[02568]?|5[2-4]|6[2-46]|72?|8[23]?))|3(?:3(?:2[79]|6|8[2578])|4(?:0[0-24-9]|[12]|3[5-8]?|4[24-7]|5[4-68]?|6[02-9]|7[126]|8[2379]?|9[1-36-8])|5(?:1|2[1245]|3[237]?|4[1-46-9]|6[2-4]|7[1-6]|8[2-5]?)|6[24]|7(?:[069]|1[1568]|2[15]|3[145]|4[13]|5[14-8]|7[2-57]|8[126])|8(?:[01]|2[15-7]|3[2578]?|4[13-6]|5[4-8]?|6[1-357-9]|7[36-8]?|8[5-8]?|9[124])))?15)?",
		"9$1",
		0,
		0,
		[
			[
				"11\\d{8}|(?:2(?:2(?:[013]\\d|2[13-79]|4[1-6]|5[2457]|6[124-8]|7[1-4]|8[13-6]|9[1267])|3(?:[07]\\d|1[467]|2[03-6]|3[13-8]|[49][2-6]|5[2-8]|6[013-9])|4(?:7[3-8]|9\\d)|6(?:[01346]\\d|2[24-6]|5[15-8])|80\\d|9(?:[012789]\\d|3[1-6]|4[02-9]|5[234]|6[2-46]))|3(?:3(?:2[79]|6\\d|8[2578])|4(?:0[0124-9]|[1-357]\\d|4[24-7]|6[02-9]|8[0-79]|9[1236-8])|5(?:[138]\\d|2[1245]|4[1-9]|6[2-4]|7[1-6])|6[24]\\d|7(?:[069]\\d|1[1568]|2[013-9]|3[145]|4[0-35-9]|5[14-8]|7[2-57]|8[0-24-9])|8(?:[01578]\\d|2[15-7]|3[0-24-9]|4[13-6]|6[1-357-9]|9[124]))|670\\d)\\d{6}",
				[
					10
				]
			],
			[
				"675\\d{7}|9(?:11[2-9]\\d{7}|(?:2(?:2[013]|3[067]|49|6[01346]|80|9[147-9])|3(?:36|4[12358]|5[138]|6[24]|7[069]|8[013578]))[2-9]\\d{6}|\\d{4}[2-9]\\d{5})"
			],
			[
				"800\\d{7}",
				[
					10
				]
			],
			[
				"60[04579]\\d{7}",
				[
					10
				]
			],
			0,
			0,
			[
				"810\\d{7}",
				[
					10
				]
			]
		]
	],
	AS: [
		"1",
		"011",
		"[5689]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"684",
		[
			[
				"6846(?:22|33|44|55|77|88|9[19])\\d{4}"
			],
			[
				"684(?:2(?:5[2468]|72)|7(?:3[13]|70))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	AT: [
		"43",
		"00",
		"[1-9]\\d{3,12}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(116\\d{3})",
				"$1",
				[
					"116"
				],
				"$1"
			],
			[
				"(1)(\\d{3,12})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(5\\d)(\\d{3,5})",
				"$1 $2",
				[
					"5[079]"
				]
			],
			[
				"(5\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"5[079]"
				]
			],
			[
				"(5\\d)(\\d{4})(\\d{4,7})",
				"$1 $2 $3",
				[
					"5[079]"
				]
			],
			[
				"(\\d{3})(\\d{3,10})",
				"$1 $2",
				[
					"(?:31|4)6|51|6(?:5[0-3579]|[6-9])|7(?:[28]0|32)|[89]"
				]
			],
			[
				"(\\d{4})(\\d{3,9})",
				"$1 $2",
				[
					"2|3(?:1[1-578]|[3-8])|4[2378]|5[2-6]|6(?:[12]|4[1-9]|5[468])|7(?:[24][1-8]|35|[5-79])"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{3,12}|(?:2(?:1[467]|2[13-8]|5[2357]|6[1-46-8]|7[1-8]|8[124-7]|9[1458])|3(?:1[1-8]|3[23568]|4[5-7]|5[1378]|6[1-38]|8[3-68])|4(?:2[1-8]|35|63|7[1368]|8[2457])|5(?:12|2[1-8]|3[357]|4[147]|5[12578]|6[37])|6(?:13|2[1-47]|4[1-35-8]|5[468]|62)|7(?:2[1-8]|3[25]|4[13478]|5[68]|6[16-8]|7[1-6]|9[45]))\\d{3,10}"
			],
			[
				"6(?:5[0-3579]|6[013-9]|[7-9]\\d)\\d{4,10}",
				[
					7,
					8,
					9,
					10,
					11,
					12,
					13
				]
			],
			[
				"800\\d{6,10}",
				[
					9,
					10,
					11,
					12,
					13
				]
			],
			[
				"9(?:0[01]|3[019])\\d{6,10}",
				[
					9,
					10,
					11,
					12,
					13
				]
			],
			0,
			0,
			[
				"5(?:(?:0[1-9]|17)\\d{2,10}|[79]\\d{3,11})|720\\d{6,10}",
				[
					5,
					6,
					7,
					8,
					9,
					10,
					11,
					12,
					13
				]
			],
			0,
			[
				"780\\d{6,10}",
				[
					9,
					10,
					11,
					12,
					13
				]
			],
			[
				"8(?:10\\d|2(?:[01]\\d|8\\d?))\\d{5,9}",
				[
					8,
					9,
					10,
					11,
					12,
					13
				]
			]
		]
	],
	AU: [
		"61",
		"(?:14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011)|001[14-689]",
		"1\\d{4,9}|[2-578]\\d{8}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"([2378])(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[2378]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"14|[45]"
				],
				"0$1"
			],
			[
				"(16)(\\d{3,4})",
				"$1 $2",
				[
					"16"
				],
				"0$1"
			],
			[
				"(16)(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"16"
				],
				"0$1"
			],
			[
				"(1[389]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[389]0",
					"1(?:[38]0|9)0"
				]
			],
			[
				"(180)(2\\d{3})",
				"$1 $2",
				[
					"180",
					"1802"
				]
			],
			[
				"(19\\d)(\\d{3})",
				"$1 $2",
				[
					"19[13]"
				]
			],
			[
				"(19\\d{2})(\\d{4})",
				"$1 $2",
				[
					"19[679]"
				]
			],
			[
				"(13)(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"13[1-9]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[237]\\d{8}|8(?:51(?:0(?:0[03-9]|[1247]\\d|3[2-9]|5[0-8]|6[1-9]|8[0-6])|1(?:1[69]|[23]\\d|4[0-4]))|[6-8]\\d{4}|9(?:[02-9]\\d{3}|1(?:[0-57-9]\\d{2}|6[0135-9]\\d)))\\d{3}",
				[
					9
				]
			],
			[
				"14(?:5\\d|71)\\d{5}|4(?:[0-3]\\d|4[047-9]|5[0-25-9]|6[6-9]|7[02-9]|8[12457-9]|9[017-9])\\d{6}",
				[
					9
				]
			],
			[
				"180(?:0\\d{3}|2)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"19(?:0[0126]\\d|[679])\\d{5}",
				[
					8,
					10
				]
			],
			[
				"500\\d{6}",
				[
					9
				]
			],
			0,
			0,
			[
				"16\\d{3,7}",
				[
					5,
					6,
					7,
					8,
					9
				]
			],
			[
				"550\\d{6}",
				[
					9
				]
			],
			[
				"13(?:00\\d{3}|45[0-4]|\\d)\\d{3}",
				[
					6,
					8,
					10
				]
			]
		],
		"0011"
	],
	AW: [
		"297",
		"00",
		"[25-9]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"5(?:2\\d|8[1-9])\\d{4}"
			],
			[
				"(?:290|5[69]\\d|6(?:[03]0|22|4[0-2]|[69]\\d)|7(?:[34]\\d|7[07])|9(?:6[45]|9[4-8]))\\d{4}"
			],
			[
				"800\\d{4}"
			],
			[
				"900\\d{4}"
			],
			0,
			0,
			0,
			0,
			[
				"28\\d{5}|501\\d{4}"
			]
		]
	],
	AX: [
		"358",
		"00|99(?:[01469]|5(?:11|3[23]|41|5[59]|77|88|9[09]))",
		"1\\d{5,11}|[35]\\d{5,9}|2\\d{4,9}|4\\d{5,10}|6\\d{7,9}|7\\d{4,9}|8\\d{6,9}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"(?:[1-3]0|[6-8])0"
				]
			],
			[
				"(75\\d{3})",
				"$1",
				[
					"75[12]"
				]
			],
			[
				"(116\\d{3})",
				"$1",
				[
					"116"
				],
				"$1"
			],
			[
				"(\\d{2})(\\d{4,10})",
				"$1 $2",
				[
					"[14]|2[09]|50|7[135]"
				]
			],
			[
				"(\\d)(\\d{4,11})",
				"$1 $2",
				[
					"[25689][1-8]|3"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"18[1-8]\\d{3,9}",
				[
					6,
					7,
					8,
					9,
					10,
					11,
					12
				]
			],
			[
				"4\\d{5,10}|50\\d{4,8}",
				[
					6,
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"800\\d{4,7}",
				[
					7,
					8,
					9,
					10
				]
			],
			[
				"[67]00\\d{5,6}",
				[
					8,
					9
				]
			],
			0,
			0,
			[
				"[13]0\\d{4,8}|2(?:0(?:[016-8]\\d{3,7}|[2-59]\\d{2,7})|9\\d{4,8})|60(?:[12]\\d{5,6}|6\\d{7})|7(?:1\\d{7}|3\\d{8}|5[03-9]\\d{2,7})",
				[
					5,
					6,
					7,
					8,
					9,
					10
				]
			]
		],
		"00"
	],
	AZ: [
		"994",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[28]|2(?:[0-36]|[45]2)|365"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[4-8]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"9"
				],
				"0$1"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[28]\\d{3}|2(?:02|1[24]|2[2-4]|33|[45]2|6[23])\\d{2}|365(?:[0-46-9]\\d|5[0-35-9]))\\d{4}"
			],
			[
				"(?:36554|(?:4[04]|5[015]|60|7[07])\\d{3})\\d{4}"
			],
			[
				"88\\d{7}"
			],
			[
				"900200\\d{3}"
			]
		]
	],
	BA: [
		"387",
		"00",
		"[3-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2-$3",
				[
					"[3-5]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[1-356]|[7-9]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"6[047]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:[05679][2-9]|1[4579]|[23][24-9]|4[2-4689]|8[2457-9])|49[2-579]|5(?:0[2-49]|[13][2-9]|[268][2-4679]|4[4689]|5[2-79]|7[2-69]|9[2-4689]))\\d{5}",
				[
					8
				]
			],
			[
				"6(?:0(?:3\\d|40)|[1-356]\\d|44[0-6]|71[137])\\d{5}"
			],
			[
				"8[08]\\d{6}",
				[
					8
				]
			],
			[
				"9[0246]\\d{6}",
				[
					8
				]
			],
			0,
			0,
			[
				"70(?:3[0146]|[56]0)\\d{4}",
				[
					8
				]
			],
			0,
			0,
			[
				"8[12]\\d{6}",
				[
					8
				]
			]
		]
	],
	BB: [
		"1",
		"011",
		"[2589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"246",
		[
			[
				"246(?:2(?:2[78]|7[0-4])|4(?:1[024-6]|2\\d|3[2-9])|5(?:20|[34]\\d|54|7[1-3])|6(?:2\\d|38)|7(?:37|57)|9(?:1[89]|63))\\d{4}"
			],
			[
				"246(?:2(?:[356]\\d|4[0-57-9]|8[0-79])|45\\d|69[5-7]|8(?:[2-5]\\d|83))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900\\d{7}|246976\\d{4}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			],
			0,
			[
				"246(?:292|367|4(?:1[7-9]|3[01]|44|67)|736)\\d{4}"
			],
			0,
			[
				"24631\\d{5}"
			]
		]
	],
	BD: [
		"880",
		"00",
		"[2-79]\\d{5,9}|1\\d{9}|8[0-7]\\d{4,8}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(2)(\\d{7,8})",
				"$1-$2",
				[
					"2"
				]
			],
			[
				"(\\d{2})(\\d{4,6})",
				"$1-$2",
				[
					"[3-79]1"
				]
			],
			[
				"(\\d{4})(\\d{3,6})",
				"$1-$2",
				[
					"1|3(?:0|[2-58]2)|4(?:0|[25]2|3[23]|[4689][25])|5(?:[02-578]2|6[25])|6(?:[0347-9]2|[26][25])|7[02-9]2|8(?:[023][23]|[4-7]2)|9(?:[02][23]|[458]2|6[01367])"
				]
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1-$2",
				[
					"[3-79][2-9]|8"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[45]\\d{3}|7(?:1[0-267]|2[0-289]|3[0-29]|4[01]|5[1-3]|6[013]|7[0178]|91)|8(?:0[125]|[139][1-6]|2[0157-9]|41|6[1-35]|7[1-5]|8[1-8]|90)|9(?:0[0-2]|1[0-4]|2[568]|3[3-6]|5[5-7]|6[0167]|7[15]|8[0146-9]))\\d{4}|3(?:12?[5-7]\\d{2}|0(?:2(?:[025-79]\\d|[348]\\d{1,2})|3(?:[2-4]\\d|[56]\\d?))|2(?:1\\d{2}|2(?:[12]\\d|[35]\\d{1,2}|4\\d?))|3(?:1\\d{2}|2(?:[2356]\\d|4\\d{1,2}))|4(?:1\\d{2}|2(?:2\\d{1,2}|[47]|5\\d{2}))|5(?:1\\d{2}|29)|[67]1\\d{2}|8(?:1\\d{2}|2(?:2\\d{2}|3|4\\d)))\\d{3}|4(?:0(?:2(?:[09]\\d|7)|33\\d{2})|1\\d{3}|2(?:1\\d{2}|2(?:[25]\\d?|[348]\\d|[67]\\d{1,2}))|3(?:1\\d{2}(?:\\d{2})?|2(?:[045]\\d|[236-9]\\d{1,2})|32\\d{2})|4(?:[18]\\d{2}|2(?:[2-46]\\d{2}|3)|5[25]\\d{2})|5(?:1\\d{2}|2(?:3\\d|5))|6(?:[18]\\d{2}|2(?:3(?:\\d{2})?|[46]\\d{1,2}|5\\d{2}|7\\d)|5(?:3\\d?|4\\d|[57]\\d{1,2}|6\\d{2}|8))|71\\d{2}|8(?:[18]\\d{2}|23\\d{2}|54\\d{2})|9(?:[18]\\d{2}|2[2-5]\\d{2}|53\\d{1,2}))\\d{3}|5(?:02[03489]\\d{2}|1\\d{2}|2(?:1\\d{2}|2(?:2(?:\\d{2})?|[457]\\d{2}))|3(?:1\\d{2}|2(?:[37](?:\\d{2})?|[569]\\d{2}))|4(?:1\\d{2}|2[46]\\d{2})|5(?:1\\d{2}|26\\d{1,2})|6(?:[18]\\d{2}|2|53\\d{2})|7(?:1|24)\\d{2}|8(?:1|26)\\d{2}|91\\d{2})\\d{3}|6(?:0(?:1\\d{2}|2(?:3\\d{2}|4\\d{1,2}))|2(?:2[2-5]\\d{2}|5(?:[3-5]\\d{2}|7)|8\\d{2})|3(?:1|2[3478])\\d{2}|4(?:1|2[34])\\d{2}|5(?:1|2[47])\\d{2}|6(?:[18]\\d{2}|6(?:2(?:2\\d|[34]\\d{2})|5(?:[24]\\d{2}|3\\d|5\\d{1,2})))|72[2-5]\\d{2}|8(?:1\\d{2}|2[2-5]\\d{2})|9(?:1\\d{2}|2[2-6]\\d{2}))\\d{3}|7(?:(?:02|[3-589]1|6[12]|72[24])\\d{2}|21\\d{3}|32)\\d{3}|8(?:(?:4[12]|[5-7]2|1\\d?)|(?:0|3[12]|[5-7]1|217)\\d)\\d{4}|9(?:[35]1|(?:[024]2|81)\\d|(?:1|[24]1)\\d{2})\\d{3}",
				[
					6,
					7,
					8,
					9
				]
			],
			[
				"(?:1[13-9]\\d|(?:3[78]|44)[02-9]|6(?:44|6[02-9]))\\d{7}",
				[
					10
				]
			],
			[
				"80[03]\\d{7}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"96(?:0[469]|1[0-4]|3[389]|6[69]|7[78])\\d{6}",
				[
					10
				]
			]
		]
	],
	BE: [
		"32",
		"00",
		"[1-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4[5-9]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[23]|4[23]|9[2-4]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[156]|7[018]|8(?:0[1-9]|[1-79])"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:80|9)0"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[0-69]|[23][2-8]|4[23]|5\\d|6[013-57-9]|71|8[1-79]|9[2-4])\\d{6}|80[2-8]\\d{5}",
				[
					8
				]
			],
			[
				"4(?:56|6[0135-8]|[79]\\d|8[3-9])\\d{6}",
				[
					9
				]
			],
			[
				"800[1-9]\\d{4}",
				[
					8
				]
			],
			[
				"(?:70(?:2[0-57]|3[0457]|44|69|7[0579])|90(?:0[0-35-8]|1[36]|2[0-3568]|3[0135689]|4[2-68]|5[1-68]|6[0-378]|7[23568]|9[34679]))\\d{4}",
				[
					8
				]
			],
			0,
			0,
			[
				"78[0-7]\\d{5}",
				[
					8
				]
			]
		]
	],
	BF: [
		"226",
		"00",
		"[25-7]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0(?:49|5[23]|6[56]|9[016-9])|4(?:4[569]|5[4-6]|6[56]|7[0179])|5(?:[34]\\d|50|6[5-7]))\\d{4}"
			],
			[
				"(?:5[124-8]|[67]\\d)\\d{6}"
			]
		]
	],
	BG: [
		"359",
		"00",
		"[23567]\\d{5,7}|[489]\\d{6,8}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(2)(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"2"
				]
			],
			[
				"(2)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"43[124-7]|70[1-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3",
				[
					"43[124-7]|70[1-9]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[78]00"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"99[69]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"48|8[7-9]|9[08]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{5,7}|(?:[36]\\d|5[1-9]|8[1-6]|9[1-7])\\d{5,6}|(?:4(?:[124-7]\\d|3[1-6])|7(?:0[1-9]|[1-9]\\d))\\d{4,5}",
				[
					6,
					7,
					8
				]
			],
			[
				"(?:8[7-9]\\d|9(?:8\\d|9[69]))\\d{6}|4(?:3[0789]|8\\d)\\d{5}",
				[
					8,
					9
				]
			],
			[
				"800\\d{5}",
				[
					8
				]
			],
			[
				"90\\d{6}",
				[
					8
				]
			],
			[
				"700\\d{5}",
				[
					8
				]
			]
		]
	],
	BH: [
		"973",
		"00",
		"[136-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:3[1356]|6[0156]|7\\d)\\d|6(?:1[16]\\d|500|6(?:0\\d|3[12]|44|7[7-9])|9[69][69])|7(?:1(?:11|78)|7\\d{2}))\\d{4}"
			],
			[
				"(?:3(?:[1-4679]\\d|5[013-69]|8[0-47-9])\\d|6(?:3(?:00|33|6[16])|6(?:[69]\\d|3[03-9]|7[0-6])))\\d{4}"
			],
			[
				"80\\d{6}"
			],
			[
				"(?:87|9[014578])\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"84\\d{6}"
			]
		]
	],
	BI: [
		"257",
		"00",
		"[2367]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22\\d{6}"
			],
			[
				"(?:29|31|6[189]|7[125-9])\\d{6}"
			]
		]
	],
	BJ: [
		"229",
		"00",
		"[2689]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2689]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:02|1[037]|2[45]|3[68])\\d{5}"
			],
			[
				"(?:6\\d|9[03-9])\\d{6}"
			],
			0,
			0,
			0,
			0,
			[
				"81\\d{6}"
			],
			0,
			[
				"857[58]\\d{4}"
			]
		]
	],
	BL: [
		"590",
		"00",
		"[56]\\d{8}",
		[
			9
		],
		[
			[
				"([56]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"590|69[01]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:2[7-9]|5[12]|87)\\d{4}"
			],
			[
				"69(?:0\\d{2}|1(?:2[29]|3[0-5]))\\d{4}"
			]
		]
	],
	BM: [
		"1",
		"011",
		"[4589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"441",
		[
			[
				"441(?:2(?:02|23|61|[3479]\\d)|[46]\\d{2}|5(?:4\\d|60|89)|824)\\d{4}"
			],
			[
				"441(?:[37]\\d|5[0-39])\\d{5}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	BN: [
		"673",
		"00",
		"[2-578]\\d{6}",
		[
			7
		],
		[
			[
				"([2-578]\\d{2})(\\d{4})",
				"$1 $2",
				[
					"[2-578]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[013-9]\\d|2[0-7])\\d{4}|[3-5]\\d{6}"
			],
			[
				"22[89]\\d{4}|[78]\\d{6}"
			]
		]
	],
	BO: [
		"591",
		"00(1\\d)?",
		"[23467]\\d{7}|8\\d{8}",
		[
			8,
			9
		],
		[
			[
				"([234])(\\d{7})",
				"$1 $2",
				[
					"[2-4]"
				]
			],
			[
				"([67]\\d{7})",
				"$1",
				[
					"[67]"
				]
			],
			[
				"(800)(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"800"
				]
			]
		],
		"0",
		0,
		"0(1\\d)?",
		0,
		0,
		0,
		[
			[
				"(?:2(?:2\\d{2}|5(?:11|[258]\\d|9[67])|6(?:12|2\\d|9[34])|8(?:2[34]|39|62))|3(?:3\\d{2}|4(?:6\\d|8[24])|8(?:25|42|5[257]|86|9[25])|9(?:2\\d|3[234]|4[248]|5[24]|6[2-6]|7\\d))|4(?:4\\d{2}|6(?:11|[24689]\\d|72)))\\d{4}",
				[
					8
				]
			],
			[
				"[67]\\d{7}",
				[
					8
				]
			],
			[
				"80017\\d{4}",
				[
					9
				]
			]
		]
	],
	BQ: [
		"599",
		"00",
		"[347]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[13-7]"
				]
			],
			[
				"(9)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:318[023]|41(?:6[023]|70)|7(?:1[578]|50)\\d)\\d{3}"
			],
			[
				"(?:31(?:8[14-8]|9[14578])|416[145-9]|7(?:0[01]|7[07]|8\\d|9[056])\\d)\\d{3}"
			]
		]
	],
	BR: [
		"55",
		"00(?:1[245]|2[1-35]|31|4[13]|[56]5|99)",
		"[1-46-9]\\d{7,10}|5(?:[0-4]\\d{7,9}|5(?:[2-8]\\d{7}|9\\d{7,8}))",
		[
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"300|4(?:0[02]|37)",
					"300|4(?:0(?:0|20)|370)"
				]
			],
			[
				"([3589]00)(\\d{2,3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3589]00"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2-$3",
				[
					"[1-9][1-9]"
				],
				"($1)"
			],
			[
				"(\\d{2})(\\d{5})(\\d{4})",
				"$1 $2-$3",
				[
					"[1-9][1-9]9"
				],
				"($1)"
			]
		],
		"0",
		0,
		"0(?:(1[245]|2[1-35]|31|4[13]|[56]5|99)(\\d{10,11}))?",
		"$2",
		0,
		0,
		[
			[
				"(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-5]\\d{7}",
				[
					10
				]
			],
			[
				"(?:[189][1-9]|2[12478])(?:7|9\\d)\\d{7}|(?:3[1-578]|[46][1-9]|5[13-5]|7[13-579])(?:[6-9]|9\\d)\\d{7}",
				[
					10,
					11
				]
			],
			[
				"800\\d{6,7}",
				[
					9,
					10
				]
			],
			[
				"(?:300|[59]00\\d?)\\d{6}",
				[
					9,
					10
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"(?:300\\d(?:\\d{2})?|4(?:0(?:0\\d|20)|370))\\d{4}",
				[
					8,
					10
				]
			]
		]
	],
	BS: [
		"1",
		"011?",
		"[2589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"242",
		[
			[
				"242(?:3(?:02|[236][1-9]|4[0-24-9]|5[0-68]|7[347]|8[0-4]|9[2-467])|461|502|6(?:0[1-4]|12|2[013]|[45]0|7[67]|8[78]|9[89])|7(?:02|88))\\d{4}"
			],
			[
				"242(?:3(?:5[79]|7[56]|95)|4(?:[23][1-9]|4[1-35-9]|5[1-8]|6[2-8]|7\\d|81)|5(?:2[45]|3[35]|44|5[1-46-9]|65|77)|6[34]6|7(?:27|38)|8(?:0[1-9]|1[02-9]|2\\d|[89]9))\\d{4}"
			],
			[
				"242300\\d{4}|8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			],
			0,
			[
				"242225[0-46-9]\\d{3}"
			]
		],
		"011"
	],
	BT: [
		"975",
		"00",
		"[1-8]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1|77"
				]
			],
			[
				"([2-8])(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-68]|7[246]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[3-6]|[34][5-7]|5[236]|6[2-46]|7[246]|8[2-4])\\d{5}",
				[
					7
				]
			],
			[
				"(?:1[67]|77)\\d{6}",
				[
					8
				]
			]
		]
	],
	BW: [
		"267",
		"00",
		"[2-79]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-6]"
				]
			],
			[
				"(7\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(90)(\\d{5})",
				"$1 $2",
				[
					"90"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:4[0-48]|6[0-24]|9[0578])|3(?:1[0-35-9]|55|[69]\\d|7[01])|4(?:6[03]|7[1267]|9[0-5])|5(?:3[0389]|4[0489]|7[1-47]|88|9[0-49])|6(?:2[1-35]|5[149]|8[067]))\\d{4}",
				[
					7
				]
			],
			[
				"7(?:[1-6]\\d|7[014-8])\\d{5}",
				[
					8
				]
			],
			0,
			[
				"90\\d{5}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			[
				"79[12][01]\\d{4}",
				[
					8
				]
			]
		]
	],
	BY: [
		"375",
		"810",
		"[1-4]\\d{8}|8(?:0(?:0\\d{3,7}|[13]\\d{7})|(?:10|20\\d)\\d{7})|9\\d{9,10}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"17[0-3589]|2[4-9]|[34]",
					"17(?:[02358]|1[0-2]|9[0189])|2[4-9]|[34]"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"1(?:5[24]|6[235]|7[467])|2(?:1[246]|2[25]|3[26])",
					"1(?:5[24]|6(?:2|3[04-9]|5[0346-9])|7(?:[46]|7[37-9]))|2(?:1[246]|2[25]|3[26])"
				],
				"8 0$1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{3})",
				"$1 $2-$3",
				[
					"1(?:5[169]|6[3-5]|7[179])|2(?:1[35]|2[34]|3[3-5])",
					"1(?:5[169]|6(?:3[1-3]|4|5[125])|7(?:1[3-9]|7[0-24-6]|9[2-7]))|2(?:1[35]|2[34]|3[3-5])"
				],
				"8 0$1"
			],
			[
				"([89]\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8[01]|9"
				],
				"8 $1"
			],
			[
				"(82\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"82"
				],
				"8 $1"
			],
			[
				"(800)(\\d{3})",
				"$1 $2",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(800)(\\d{2})(\\d{2,4})",
				"$1 $2 $3",
				[
					"800"
				],
				"8 $1"
			]
		],
		"8",
		0,
		"8?0?",
		0,
		0,
		0,
		[
			[
				"(?:1(?:5(?:1[1-5]|[24]\\d|6[2-4]|9[1-7])|6(?:[235]\\d|4[1-7])|7\\d{2})|2(?:1(?:[246]\\d|3[0-35-9]|5[1-9])|2(?:[235]\\d|4[0-8])|3(?:[26]\\d|3[02-79]|4[024-7]|5[03-7])))\\d{5}",
				[
					9
				]
			],
			[
				"(?:2(?:5[5679]|9[1-9])|33\\d|44\\d)\\d{6}",
				[
					9
				]
			],
			[
				"8(?:0[13]|20\\d)\\d{7}|800\\d{3,7}"
			],
			[
				"(?:810|902)\\d{7}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"249\\d{6}",
				[
					9
				]
			]
		],
		"8~10"
	],
	BZ: [
		"501",
		"00",
		"[2-8]\\d{6}|0\\d{10}",
		[
			7,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[2-8]"
				]
			],
			[
				"(0)(800)(\\d{4})(\\d{3})",
				"$1-$2-$3-$4",
				[
					"080",
					"0800"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[02]\\d|36)|[3-58][02]\\d|7(?:[02]\\d|32))\\d{4}",
				[
					7
				]
			],
			[
				"6[0-35-7]\\d{5}",
				[
					7
				]
			],
			[
				"0800\\d{7}",
				[
					11
				]
			]
		]
	],
	CA: [
		"1",
		"011",
		"[2-9]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:04|[23]6|[48]9|50)|3(?:06|43|65)|4(?:03|1[68]|3[178]|50)|5(?:06|1[49]|48|79|8[17])|6(?:04|13|39|47)|7(?:0[59]|78|8[02])|8(?:[06]7|19|25|73)|90[25])[2-9]\\d{6}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"(?:5(?:00|2[12]|33|44|66|77|88)|622)[2-9]\\d{6}"
			],
			0,
			0,
			0,
			[
				"600[2-9]\\d{6}"
			]
		]
	],
	CC: [
		"61",
		"(?:14(?:1[14]|34|4[17]|[56]6|7[47]|88))?001[14-689]",
		"[1458]\\d{5,9}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"([2378])(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[2378]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"14|[45]"
				],
				"0$1"
			],
			[
				"(16)(\\d{3,4})",
				"$1 $2",
				[
					"16"
				],
				"0$1"
			],
			[
				"(16)(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"16"
				],
				"0$1"
			],
			[
				"(1[389]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[389]0",
					"1(?:[38]0|9)0"
				]
			],
			[
				"(180)(2\\d{3})",
				"$1 $2",
				[
					"180",
					"1802"
				]
			],
			[
				"(19\\d)(\\d{3})",
				"$1 $2",
				[
					"19[13]"
				]
			],
			[
				"(19\\d{2})(\\d{4})",
				"$1 $2",
				[
					"19[679]"
				]
			],
			[
				"(13)(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"13[1-9]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"8(?:51(?:0(?:02|31|60)|118)|91(?:0(?:1[0-2]|29)|1(?:[28]2|50|79)|2(?:10|64)|3(?:08|22|68)|4[29]8|62\\d|70[23]|959))\\d{3}",
				[
					9
				]
			],
			[
				"14(?:5\\d|71)\\d{5}|4(?:[0-3]\\d|4[047-9]|5[0-25-9]|6[6-9]|7[02-9]|8[12547-9]|9[017-9])\\d{6}",
				[
					9
				]
			],
			[
				"180(?:0\\d{3}|2)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"19(?:0[0126]\\d|[679])\\d{5}",
				[
					8,
					10
				]
			],
			[
				"500\\d{6}",
				[
					9
				]
			],
			0,
			0,
			0,
			[
				"550\\d{6}",
				[
					9
				]
			],
			[
				"13(?:00\\d{2})?\\d{4}",
				[
					6,
					10
				]
			]
		],
		"0011"
	],
	CD: [
		"243",
		"00",
		"[2-6]\\d{6}|[18]\\d{6,8}|9\\d{8}",
		[
			7,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"12"
				]
			],
			[
				"([89]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8[0-2459]|9"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"88"
				]
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[1-6]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:2\\d{7}|\\d{6})|[2-6]\\d{6}"
			],
			[
				"8(?:[0-2459]\\d{2}|8)\\d{5}|9[017-9]\\d{7}"
			]
		]
	],
	CF: [
		"236",
		"00",
		"[278]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[12]\\d{6}"
			],
			[
				"7[0257]\\d{6}"
			],
			0,
			[
				"8776\\d{4}"
			]
		]
	],
	CG: [
		"242",
		"00",
		"[028]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"801"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[02]"
				]
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"800"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"222[1-589]\\d{5}"
			],
			[
				"0[14-6]\\d{7}"
			],
			0,
			[
				"80(?:0\\d{2}|11[0-4])\\d{4}"
			]
		]
	],
	CH: [
		"41",
		"00",
		"[2-9]\\d{8}|860\\d{9}",
		[
			9
		],
		[
			[
				"([2-9]\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-7]|[89]1"
				]
			],
			[
				"([89]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8[047]|90"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"860"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[12467]|3[1-4]|4[134]|5[256]|6[12]|[7-9]1)\\d{7}"
			],
			[
				"7[5-9]\\d{7}"
			],
			[
				"800\\d{6}"
			],
			[
				"90[016]\\d{6}"
			],
			[
				"878\\d{6}"
			],
			0,
			[
				"5[18]\\d{7}"
			],
			[
				"74[0248]\\d{6}"
			],
			0,
			[
				"84[0248]\\d{6}"
			]
		]
	],
	CI: [
		"225",
		"00",
		"[02-8]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:0[023]|1[02357]|[23][045]|4[03-5])|3(?:0[06]|1[069]|[2-4][07]|5[09]|6[08]))\\d{5}"
			],
			[
				"(?:[07][1-9]|[45]\\d|6[014-9]|8[4-9])\\d{6}"
			]
		]
	],
	CK: [
		"682",
		"00",
		"[2-8]\\d{4}",
		[
			5
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[13-7]|4[1-5])\\d{3}"
			],
			[
				"[5-8]\\d{4}"
			]
		]
	],
	CL: [
		"56",
		"(?:0|1(?:1[0-69]|2[0-57]|5[13-58]|69|7[0167]|8[018]))0",
		"1230\\d{7}|[2-46-9]\\d{8,10}|5[1-3578]\\d{7}",
		[
			9,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[23]"
				],
				"($1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[357]|4[1-35]|6[13-57]|8(?:0[1-9]|[1-9])"
				],
				"($1)"
			],
			[
				"(9)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				]
			],
			[
				"(44)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"44"
				]
			],
			[
				"([68]00)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[68]00"
				]
			],
			[
				"(600)(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"600"
				]
			],
			[
				"(1230)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"123",
					"1230"
				]
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"219"
				],
				"($1)"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1962\\d{4}|2\\d{7}|32[0-467]\\d{5})|(?:3[2-5]\\d|[47][1-35]\\d|5[1-3578]\\d|6[13-57]\\d|8(?:0[1-9]|[1-9]\\d)|9[3-9]\\d)\\d{6}",
				[
					9
				]
			],
			[
				"",
				[
					9
				]
			],
			[
				"800\\d{6}|1230\\d{7}",
				[
					9,
					11
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"44\\d{7}",
				[
					9
				]
			],
			[
				"600\\d{7,8}",
				[
					10,
					11
				]
			]
		]
	],
	CM: [
		"237",
		"00",
		"[2368]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"([26])(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[26]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[23]|88"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:22|33|4[23])\\d{6}",
				[
					9
				]
			],
			[
				"6[5-9]\\d{7}",
				[
					9
				]
			],
			[
				"88\\d{6}",
				[
					8
				]
			]
		]
	],
	CN: [
		"86",
		"(?:1(?:[12]\\d{3}|79\\d{2}|9[0-7]\\d{2}))?00",
		"[1-7]\\d{6,11}|8[0-357-9]\\d{6,9}|9\\d{7,10}",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"([48]00)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[48]00"
				]
			],
			[
				"(\\d{2})(\\d{5,6})",
				"$1 $2",
				[
					"(?:10|2\\d)[19]",
					"(?:10|2\\d)(?:10|9[56])",
					"(?:10|2\\d)(?:100|9[56])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"[3-9]",
					"[3-9]\\d\\d[19]",
					"[3-9]\\d\\d(?:10|9[56])"
				],
				"0$1"
			],
			[
				"(21)(\\d{4})(\\d{4,6})",
				"$1 $2 $3",
				[
					"21"
				],
				"0$1",
				1
			],
			[
				"([12]\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"10[1-9]|2[02-9]",
					"10[1-9]|2[02-9]",
					"10(?:[1-79]|8(?:0[1-9]|[1-9]))|2[02-9]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"3(?:1[02-9]|35|49|5|7[02-68]|9[1-68])|4(?:1[02-9]|2[179]|[35][2-9]|6[47-9]|7|8[23])|5(?:3[03-9]|4[36]|5[02-9]|6[1-46]|7[028]|80|9[2-46-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[04-9]|4[3-6]|6[2368])|8(?:1[236-8]|2[5-7]|3|5[1-9]|7[02-9]|8[36-8]|9[1-7])|9(?:0[1-3689]|1[1-79]|[379]|4[13]|5[1-5])"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"3(?:11|7[179])|4(?:[15]1|3[1-35])|5(?:1|2[37]|3[12]|51|7[13-79]|9[15])|7(?:[39]1|5[457]|6[09])|8(?:[57]1|98)"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"807",
					"8078"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:[3-57-9]|66)"
				]
			],
			[
				"(10800)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"108",
					"1080",
					"10800"
				]
			],
			[
				"(\\d{3})(\\d{7,8})",
				"$1 $2",
				[
					"950"
				]
			]
		],
		"0",
		0,
		"(1(?:[12]\\d{3}|79\\d{2}|9[0-7]\\d{2}))|0",
		0,
		0,
		0,
		[
			[
				"21(?:100\\d{2}|95\\d{3,4}|\\d{8,10})|(?:10|2[02-57-9]|3(?:11|7[179])|4(?:[15]1|3[1-35])|5(?:1\\d|2[37]|3[12]|51|7[13-79]|9[15])|7(?:31|5[457]|6[09]|91)|8(?:[57]1|98))(?:100\\d{2}|95\\d{3,4}|\\d{8})|(?:3(?:1[02-9]|35|49|5\\d|7[02-68]|9[1-68])|4(?:1[02-9]|2[179]|3[3-9]|5[2-9]|6[4789]|7\\d|8[23])|5(?:3[03-9]|4[36]|5[02-9]|6[1-46]|7[028]|80|9[2-46-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[17]\\d|2[248]|3[04-9]|4[3-6]|5[0-4689]|6[2368]|9[02-9])|8(?:078|1[236-8]|2[5-7]|3\\d|5[1-9]|7[02-9]|8[3678]|9[1-7])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:100\\d{2}|95\\d{3,4}|\\d{7})"
			],
			[
				"1(?:[38]\\d{3}|4[57]\\d{2}|5[0-35-9]\\d{2}|66\\d{2}|7(?:[0-35-8]\\d{2}|40[0-5])|9[89]\\d{2})\\d{6}",
				[
					11
				]
			],
			[
				"(?:10)?800\\d{7}",
				[
					10,
					12
				]
			],
			[
				"16[08]\\d{5}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"400\\d{7}|950\\d{7,8}|(?:10|2[0-57-9]|3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[4789]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[3678]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))96\\d{3,4}",
				[
					7,
					8,
					9,
					10,
					11
				]
			]
		],
		"00"
	],
	CO: [
		"57",
		"00(?:4(?:[14]4|56)|[579])",
		"(?:[13]\\d{0,3}|[24-8])\\d{7}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"1(?:[2-7]|8[2-9]|9[0-3])|[24-8]",
					"1(?:[2-7]|8[2-9]|9(?:09|[1-3]))|[24-8]"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"3"
				]
			],
			[
				"(1)(\\d{3})(\\d{7})",
				"$1-$2-$3",
				[
					"1(?:80|9[04])",
					"1(?:800|9(?:0[01]|4[78]))"
				],
				"0$1",
				0,
				"$1 $2 $3"
			]
		],
		"0",
		0,
		"0([3579]|4(?:44|56))?",
		0,
		0,
		0,
		[
			[
				"[124-8][2-9]\\d{6}",
				[
					8
				]
			],
			[
				"3(?:0[0-5]|1\\d|2[0-3]|5[01])\\d{7}",
				[
					10
				]
			],
			[
				"1800\\d{7}",
				[
					11
				]
			],
			[
				"19(?:0[01]|4[78])\\d{7}",
				[
					11
				]
			]
		]
	],
	CR: [
		"506",
		"00",
		"[24-9]\\d{7,9}",
		[
			8,
			10
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[24-7]|8[3-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[89]0"
				]
			]
		],
		0,
		0,
		"(19(?:0[012468]|1[09]|20|66|77|99))",
		0,
		0,
		0,
		[
			[
				"2(?:[024-7]\\d{2}|1(?:0[7-9]|[1-9]\\d))\\d{4}",
				[
					8
				]
			],
			[
				"5(?:0[01]|7[0-3])\\d{5}|6(?:[0-4]\\d{3}|500[01])\\d{3}|(?:7[0-3]|8[3-9])\\d{6}",
				[
					8
				]
			],
			[
				"800\\d{7}",
				[
					10
				]
			],
			[
				"90[059]\\d{7}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"210[0-6]\\d{4}|4\\d{7}|5100\\d{4}",
				[
					8
				]
			]
		]
	],
	CU: [
		"53",
		"119",
		"[2-57]\\d{5,7}",
		[
			6,
			7,
			8
		],
		[
			[
				"(\\d)(\\d{6,7})",
				"$1 $2",
				[
					"7"
				]
			],
			[
				"(\\d{2})(\\d{4,6})",
				"$1 $2",
				[
					"[2-4]"
				]
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"2[1-4]\\d{5,6}|3(?:1\\d{6}|[23]\\d{4,6})|4(?:[125]\\d{5,6}|[36]\\d{6}|[78]\\d{4,6})|7\\d{6,7}"
			],
			[
				"5\\d{7}",
				[
					8
				]
			]
		]
	],
	CV: [
		"238",
		"0",
		"[2-59]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2[1-7]|3[0-8]|4[12]|5[1256]|6\\d|7[1-3]|8[1-5])\\d{4}"
			],
			[
				"(?:[34][36]|5[1-389]|9\\d)\\d{5}"
			]
		]
	],
	CW: [
		"599",
		"00",
		"[134679]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[13-7]"
				]
			],
			[
				"(9)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"9(?:[48]\\d{2}|50\\d|7(?:2[0-24]|[34]\\d|6[35-7]|77|8[7-9]))\\d{4}",
				[
					8
				]
			],
			[
				"9(?:5(?:[12467]\\d|3[01])|6(?:[15-9]\\d|3[01]))\\d{4}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"955\\d{5}",
				[
					8
				]
			],
			0,
			[
				"60[0-2]\\d{4}",
				[
					7
				]
			]
		]
	],
	CX: [
		"61",
		"(?:14(?:1[14]|34|4[17]|[56]6|7[47]|88))?001[14-689]",
		"[1458]\\d{5,9}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"([2378])(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[2378]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"14|[45]"
				],
				"0$1"
			],
			[
				"(16)(\\d{3,4})",
				"$1 $2",
				[
					"16"
				],
				"0$1"
			],
			[
				"(16)(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"16"
				],
				"0$1"
			],
			[
				"(1[389]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[389]0",
					"1(?:[38]0|9)0"
				]
			],
			[
				"(180)(2\\d{3})",
				"$1 $2",
				[
					"180",
					"1802"
				]
			],
			[
				"(19\\d)(\\d{3})",
				"$1 $2",
				[
					"19[13]"
				]
			],
			[
				"(19\\d{2})(\\d{4})",
				"$1 $2",
				[
					"19[679]"
				]
			],
			[
				"(13)(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"13[1-9]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"8(?:51(?:0(?:01|30|59)|117)|91(?:00[6-9]|1(?:21|49|78|81)|2(?:09|63)|3(?:12|26|75)|4(?:56|97)|64\\d|7(?:0[01]|1[0-2])|958))\\d{3}",
				[
					9
				]
			],
			[
				"14(?:5\\d|71)\\d{5}|4(?:[0-3]\\d|4[047-9]|5[0-25-9]|6[6-9]|7[02-9]|8[12547-9]|9[017-9])\\d{6}",
				[
					9
				]
			],
			[
				"180(?:0\\d{3}|2)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"19(?:0[0126]\\d|[679])\\d{5}",
				[
					8,
					10
				]
			],
			[
				"500\\d{6}",
				[
					9
				]
			],
			0,
			0,
			0,
			[
				"550\\d{6}",
				[
					9
				]
			],
			[
				"13(?:00\\d{2})?\\d{4}",
				[
					6,
					8,
					10
				]
			]
		],
		"0011"
	],
	CY: [
		"357",
		"00",
		"[257-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[2-6]\\d{6}"
			],
			[
				"9[4-79]\\d{6}"
			],
			[
				"800\\d{5}"
			],
			[
				"90[09]\\d{5}"
			],
			[
				"700\\d{5}"
			],
			0,
			[
				"(?:50|77)\\d{6}"
			],
			0,
			0,
			[
				"80[1-9]\\d{5}"
			]
		]
	],
	CZ: [
		"420",
		"00",
		"[2-8]\\d{8}|9\\d{8,11}",
		[
			9
		],
		[
			[
				"([2-9]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8]|9[015-7]"
				]
			],
			[
				"(96\\d)(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"96"
				]
			],
			[
				"(9\\d)(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9[36]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{8}|(?:3[1257-9]|4[16-9]|5[13-9])\\d{7}"
			],
			[
				"(?:60[1-8]|7(?:0[2-5]|[2379]\\d))\\d{6}"
			],
			[
				"800\\d{6}"
			],
			[
				"9(?:0[05689]|76)\\d{6}"
			],
			[
				"70[01]\\d{6}"
			],
			0,
			[
				"9(?:5\\d|7[234])\\d{6}"
			],
			0,
			[
				"9[17]0\\d{6}"
			],
			[
				"8[134]\\d{7}"
			]
		]
	],
	DE: [
		"49",
		"00",
		"[1-35-9]\\d{3,14}|4(?:[0-8]\\d{3,12}|9(?:[0-37]\\d|4(?:[1-35-8]|4\\d?)|5\\d{1,2}|6[1-8]\\d?)\\d{2,8})",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15
		],
		[
			[
				"(1\\d{2})(\\d{7,8})",
				"$1 $2",
				[
					"1[67]"
				]
			],
			[
				"(15\\d{3})(\\d{6})",
				"$1 $2",
				[
					"15[0568]"
				]
			],
			[
				"(1\\d{3})(\\d{7})",
				"$1 $2",
				[
					"15"
				]
			],
			[
				"(\\d{2})(\\d{3,11})",
				"$1 $2",
				[
					"3[02]|40|[68]9"
				]
			],
			[
				"(\\d{3})(\\d{3,11})",
				"$1 $2",
				[
					"2(?:0[1-389]|1[124]|2[18]|3[14]|[4-9]1)|3(?:[35-9][15]|4[015])|[4-8][1-9]1|9(?:06|[1-9]1)",
					"2(?:0[1-389]|1(?:[14]|2[0-8])|2[18]|3[14]|[4-9]1)|3(?:[35-9][15]|4[015])|[4-8][1-9]1|9(?:06|[1-9]1)"
				]
			],
			[
				"(\\d{4})(\\d{2,11})",
				"$1 $2",
				[
					"[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|[7-9](?:0[1-9]|[1-9])",
					"[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|[46][1246]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|3[1357]|4[13578]|6[1246]|7[1356]|9[1346])|5(?:0[14]|2[1-3589]|3[1357]|[49][1246]|6[1-4]|7[13468]|8[13568])|6(?:0[1356]|2[1-489]|3[124-6]|4[1347]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|3[1357]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|4[1347]|6[0135-9]|7[1467]|8[136])|9(?:0[12479]|2[1358]|3[1357]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|[7-9](?:0[1-9]|[1-9])"
				]
			],
			[
				"(3\\d{4})(\\d{1,10})",
				"$1 $2",
				[
					"3"
				]
			],
			[
				"(800)(\\d{7,12})",
				"$1 $2",
				[
					"800"
				]
			],
			[
				"(\\d{3})(\\d)(\\d{4,10})",
				"$1 $2 $3",
				[
					"1(?:37|80)|900",
					"1(?:37|80)|900[1359]"
				]
			],
			[
				"(1\\d{2})(\\d{5,11})",
				"$1 $2",
				[
					"181"
				]
			],
			[
				"(18\\d{3})(\\d{6})",
				"$1 $2",
				[
					"185",
					"1850",
					"18500"
				]
			],
			[
				"(18\\d{2})(\\d{7})",
				"$1 $2",
				[
					"18[68]"
				]
			],
			[
				"(18\\d)(\\d{8})",
				"$1 $2",
				[
					"18[2-579]"
				]
			],
			[
				"(700)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"700"
				]
			],
			[
				"(138)(\\d{4})",
				"$1 $2",
				[
					"138"
				]
			],
			[
				"(15[013-68])(\\d{2})(\\d{8})",
				"$1 $2 $3",
				[
					"15[013-68]"
				]
			],
			[
				"(15[279]\\d)(\\d{2})(\\d{7})",
				"$1 $2 $3",
				[
					"15[279]"
				]
			],
			[
				"(1[67]\\d)(\\d{2})(\\d{7,8})",
				"$1 $2 $3",
				[
					"1(?:6[023]|7)"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{5,13}|3(?:0\\d{3,13}|2\\d{9}|[3-9]\\d{4,13})|4(?:0\\d{3,12}|[1-8]\\d{4,12}|9(?:[0-37]\\d|4(?:[1-35-8]|4\\d?)|5\\d{1,2}|6[1-8]\\d?)\\d{2,8})|5(?:0[2-8]|[1256]\\d|[38][0-8]|4\\d{0,2}|[79][0-7])\\d{3,11}|6(?:\\d{5,13}|9\\d{3,12})|7(?:0[2-8]|[1-9]\\d)\\d{3,10}|8(?:0[2-9]|[1-8]\\d|9\\d?)\\d{3,10}|9(?:0[6-9]\\d{3,10}|1\\d{4,12}|[2-9]\\d{4,11})",
				[
					5,
					6,
					7,
					8,
					9,
					10,
					11,
					12,
					13,
					14,
					15
				]
			],
			[
				"1(?:5[0-25-9]\\d{8}|6[023]\\d{7,8}|7\\d{8,9})",
				[
					10,
					11
				]
			],
			[
				"800\\d{7,12}",
				[
					10,
					11,
					12,
					13,
					14,
					15
				]
			],
			[
				"137[7-9]\\d{6}|900(?:[135]\\d{6}|9\\d{7})",
				[
					10,
					11
				]
			],
			[
				"700\\d{8}",
				[
					11
				]
			],
			0,
			[
				"18(?:1\\d{5,11}|[2-9]\\d{8})",
				[
					8,
					9,
					10,
					11,
					12,
					13,
					14
				]
			],
			[
				"16(?:4\\d{1,10}|[89]\\d{1,11})",
				[
					4,
					5,
					6,
					7,
					8,
					9,
					10,
					11,
					12,
					13,
					14
				]
			],
			0,
			[
				"1(?:3(?:7[1-6]\\d{6}|8\\d{4})|80\\d{5,11})",
				[
					7,
					8,
					9,
					10,
					11,
					12,
					13,
					14
				]
			]
		]
	],
	DJ: [
		"253",
		"00",
		"[27]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1[2-5]|7[45])\\d{5}"
			],
			[
				"77\\d{6}"
			]
		]
	],
	DK: [
		"45",
		"00",
		"[2-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[2-7]\\d|8[126-9]|9[1-36-9])\\d{6}"
			],
			[
				""
			],
			[
				"80\\d{6}"
			],
			[
				"90\\d{6}"
			]
		]
	],
	DM: [
		"1",
		"011",
		"[57-9]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"767",
		[
			[
				"767(?:2(?:55|66)|4(?:2[01]|4[0-25-9])|50[0-4]|70[1-3])\\d{4}"
			],
			[
				"767(?:2(?:[234689]5|7[5-7])|31[5-7]|61[1-7])\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	DO: [
		"1",
		"011",
		"[589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"8[024]9",
		[
			[
				"8(?:[04]9[2-9]\\d{6}|29(?:2(?:[0-59]\\d|6[04-9]|7[0-27]|8[0237-9])|3(?:[0-35-9]\\d|4[7-9])|[45]\\d{2}|6(?:[0-27-9]\\d|[3-5][1-9]|6[0135-8])|7(?:0[013-9]|[1-37]\\d|4[1-35689]|5[1-4689]|6[1-57-9]|8[1-79]|9[1-8])|8(?:0[146-9]|1[0-48]|[248]\\d|3[1-79]|5[01589]|6[013-68]|7[124-8]|9[0-8])|9(?:[0-24]\\d|3[02-46-9]|5[0-79]|60|7[0169]|8[57-9]|9[02-9]))\\d{4})"
			],
			[
				"8[024]9[2-9]\\d{6}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	DZ: [
		"213",
		"00",
		"(?:[1-4]|[5-9]\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"([1-4]\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[1-4]"
				]
			],
			[
				"([5-8]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-8]"
				]
			],
			[
				"(9\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|2[013-79]|3[0-8]|4[0135689])\\d{6}|9619\\d{5}"
			],
			[
				"(?:5[4-6]|7[7-9])\\d{7}|6(?:[569]\\d|7[0-6])\\d{6}",
				[
					9
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			],
			[
				"80[3-689]1\\d{5}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			[
				"98[23]\\d{6}",
				[
					9
				]
			],
			[
				"80[12]1\\d{5}",
				[
					9
				]
			]
		]
	],
	EC: [
		"593",
		"00",
		"1\\d{9,10}|[2-8]\\d{7}|9\\d{8}",
		[
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2-$3",
				[
					"[247]|[356][2-8]"
				],
				0,
				0,
				"$1-$2-$3"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(1800)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"180",
					"1800"
				],
				"$1"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"[2-7][2-7]\\d{6}",
				[
					8
				]
			],
			[
				"9(?:(?:39|[57][89]|[89]\\d)\\d|6(?:[0-27-9]\\d|30))\\d{5}",
				[
					9
				]
			],
			[
				"1800\\d{6,7}",
				[
					10,
					11
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"[2-7]890\\d{4}",
				[
					8
				]
			]
		]
	],
	EE: [
		"372",
		"00",
		"[3-9]\\d{6,7}|800\\d{6,7}",
		[
			7,
			8,
			10
		],
		[
			[
				"([3-79]\\d{2})(\\d{4})",
				"$1 $2",
				[
					"[369]|4[3-8]|5(?:[0-2]|5[0-478]|6[45])|7[1-9]",
					"[369]|4[3-8]|5(?:[02]|1(?:[0-8]|95)|5[0-478]|6(?:4[0-4]|5[1-589]))|7[1-9]"
				]
			],
			[
				"(70)(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"70"
				]
			],
			[
				"(8000)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"800",
					"8000"
				]
			],
			[
				"([458]\\d{3})(\\d{3,4})",
				"$1 $2",
				[
					"40|5|8(?:00|[1-5])",
					"40|5|8(?:00[1-9]|[1-5])"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[23589]|4[3-8]|6\\d|7[1-9]|88)\\d{5}",
				[
					7
				]
			],
			[
				"(?:5\\d|8[1-5])\\d{6}|5(?:[02]\\d{2}|1(?:[0-8]\\d|95)|5[0-478]\\d|64[0-4]|65[1-589])\\d{3}",
				[
					7,
					8
				]
			],
			[
				"800(?:0\\d{3}|1\\d|[2-9])\\d{3}"
			],
			[
				"(?:40\\d{2}|900)\\d{4}",
				[
					7,
					8
				]
			],
			[
				"70[0-2]\\d{5}",
				[
					8
				]
			]
		]
	],
	EG: [
		"20",
		"00",
		"1\\d{4,9}|[24-6]\\d{8}|3\\d{7}|[89]\\d{8,9}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{7,8})",
				"$1 $2",
				[
					"[23]"
				]
			],
			[
				"(\\d{2})(\\d{6,7})",
				"$1 $2",
				[
					"1(?:3|5[239])|[4-6]|[89][2-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1[0-25]|[89]00"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:3[23]\\d|5(?:[23]|9\\d))|2[2-4]\\d{2}|3\\d{2}|4(?:0[2-5]|[578][23]|64)\\d|5(?:0[2-7]|5\\d|7[23])\\d|6[24-689]3\\d|8(?:2[2-57]|4[26]|6[237]|8[2-4])\\d|9(?:2[27]|3[24]|52|6[2356]|7[2-4])\\d)\\d{5}",
				[
					8,
					9
				]
			],
			[
				"1[0125]\\d{8}",
				[
					10
				]
			],
			[
				"800\\d{7}",
				[
					10
				]
			],
			[
				"900\\d{7}",
				[
					10
				]
			]
		]
	],
	EH: [
		"212",
		"00",
		"[5-9]\\d{8}",
		[
			9
		],
		[
			[
				"([5-7]\\d{2})(\\d{6})",
				"$1-$2",
				[
					"5(?:2[015-7]|3[0-4])|[67]"
				]
			],
			[
				"([58]\\d{3})(\\d{5})",
				"$1-$2",
				[
					"5(?:2[2-489]|3[5-9]|92)|892",
					"5(?:2(?:[2-48]|9[0-7])|3(?:[5-79]|8[0-7])|924)|892"
				]
			],
			[
				"(5\\d{4})(\\d{4})",
				"$1-$2",
				[
					"5(?:29|38)",
					"5(?:29|38)[89]"
				]
			],
			[
				"([5]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5(?:4[067]|5[03])"
				]
			],
			[
				"(8[09])(\\d{7})",
				"$1-$2",
				[
					"8(?:0|9[013-9])"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		"528[89]",
		[
			[
				"528[89]\\d{5}"
			],
			[
				"(?:6(?:[0-79]\\d|8[0-247-9])|7(?:0[067]|6[1267]|7[017]))\\d{6}"
			],
			[
				"80\\d{7}"
			],
			[
				"89\\d{7}"
			],
			0,
			0,
			0,
			0,
			[
				"5924[01]\\d{4}"
			]
		]
	],
	ER: [
		"291",
		"00",
		"[178]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:1[12568]|20|40|55|6[146])\\d{4}|8\\d{6}"
			],
			[
				"17[1-3]\\d{4}|7\\d{6}"
			]
		]
	],
	ES: [
		"34",
		"00",
		"[5-9]\\d{8}",
		[
			9
		],
		[
			[
				"([89]00)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]00"
				]
			],
			[
				"([5-9]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[568]|[79][0-8]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"8(?:[1356]\\d|[28][0-8]|[47][1-9])\\d{6}|9(?:[135]\\d{7}|[28][0-8]\\d{6}|4[1-9]\\d{6}|6(?:[0-8]\\d{6}|9(?:0(?:[0-57-9]\\d{4}|6(?:0[0-8]|1[1-9]|[2-9]\\d)\\d{2})|[1-9]\\d{5}))|7(?:[124-9]\\d{2}|3(?:[0-8]\\d|9[1-9]))\\d{4})"
			],
			[
				"(?:6\\d{6}|7[1-48]\\d{5}|9(?:6906(?:09|10)|7390\\d{2}))\\d{2}"
			],
			[
				"[89]00\\d{6}"
			],
			[
				"80[367]\\d{6}"
			],
			[
				"70\\d{7}"
			],
			0,
			[
				"51\\d{7}"
			],
			0,
			0,
			[
				"90[12]\\d{6}"
			]
		]
	],
	ET: [
		"251",
		"00",
		"[1-59]\\d{8}",
		[
			9
		],
		[
			[
				"([1-59]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-59]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:11(?:1(?:1[124]|2[2-57]|3[1-5]|5[5-8]|8[6-8])|2(?:13|3[6-8]|5[89]|7[05-9]|8[2-6])|3(?:2[01]|3[0-289]|4[1289]|7[1-4]|87)|4(?:1[69]|3[2-49]|4[0-3]|6[5-8])|5(?:1[578]|44|5[0-4])|6(?:18|2[69]|39|4[5-7]|5[1-5]|6[0-59]|8[015-8]))|2(?:2(?:11[1-9]|22[0-7]|33\\d|44[1467]|66[1-68])|5(?:11[124-6]|33[2-8]|44[1467]|55[14]|66[1-3679]|77[124-79]|880))|3(?:3(?:11[0-46-8]|22[0-6]|33[0134689]|44[04]|55[0-6]|66[01467])|4(?:44[0-8]|55[0-69]|66[0-3]|77[1-5]))|4(?:6(?:22[0-24-7]|33[1-5]|44[13-69]|55[14-689]|660|88[1-4])|7(?:11[1-9]|22[1-9]|33[13-7]|44[13-6]|55[1-689]))|5(?:7(?:227|55[05]|(?:66|77)[14-8])|8(?:11[149]|22[013-79]|33[0-68]|44[013-8]|550|66[1-5]|77\\d)))\\d{4}"
			],
			[
				"9\\d{8}"
			]
		]
	],
	FI: [
		"358",
		"00|99(?:[01469]|5(?:11|3[23]|41|5[59]|77|88|9[09]))",
		"1\\d{4,11}|[2-9]\\d{4,10}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"(?:[1-3]0|[6-8])0"
				]
			],
			[
				"(75\\d{3})",
				"$1",
				[
					"75[12]"
				]
			],
			[
				"(116\\d{3})",
				"$1",
				[
					"116"
				],
				"$1"
			],
			[
				"(\\d{2})(\\d{4,10})",
				"$1 $2",
				[
					"[14]|2[09]|50|7[135]"
				]
			],
			[
				"(\\d)(\\d{4,11})",
				"$1 $2",
				[
					"[25689][1-8]|3"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:[3569][1-8]\\d{3,9}|[47]\\d{5,10})|2[1-8]\\d{3,9}|3(?:[1-8]\\d{3,9}|9\\d{4,8})|[5689][1-8]\\d{3,9}"
			],
			[
				"4(?:[0-8]\\d{4,9}|9\\d{3,8})|50\\d{4,8}",
				[
					6,
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"800\\d{4,7}",
				[
					7,
					8,
					9,
					10
				]
			],
			[
				"[67]00\\d{5,6}",
				[
					8,
					9
				]
			],
			0,
			0,
			[
				"[13]0\\d{4,8}|2(?:0(?:[016-8]\\d{3,7}|[2-59]\\d{2,7})|9\\d{4,8})|60(?:[12]\\d{5,6}|6\\d{7})|7(?:1\\d{7}|3\\d{8}|5[03-9]\\d{3,7})",
				[
					5,
					6,
					7,
					8,
					9,
					10
				]
			]
		],
		"00"
	],
	FJ: [
		"679",
		"0(?:0|52)",
		"[2-9]\\d{6}|0\\d{10}",
		[
			7,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-9]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[0-5]\\d|6(?:03|[25-7]\\d)|8[58]\\d)\\d{4}",
				[
					7
				]
			],
			[
				"(?:[279]\\d|45|5[01568]|8[034679])\\d{5}",
				[
					7
				]
			],
			[
				"0800\\d{7}",
				[
					11
				]
			]
		],
		"00"
	],
	FK: [
		"500",
		"00",
		"[2-7]\\d{4}",
		[
			5
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[2-47]\\d{4}"
			],
			[
				"[56]\\d{4}"
			]
		]
	],
	FM: [
		"691",
		"00",
		"[39]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3[2357]0[1-9]\\d{3}|9[2-6]\\d{5}"
			],
			[
				"3[2357]0[1-9]\\d{3}|9[2-7]\\d{5}"
			]
		]
	],
	FO: [
		"298",
		"00",
		"[2-9]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{6})",
				"$1"
			]
		],
		0,
		0,
		"(10(?:01|[12]0|88))",
		0,
		0,
		0,
		[
			[
				"(?:20|[3-4]\\d|8[19])\\d{4}"
			],
			[
				"(?:[27][1-9]|5\\d)\\d{4}"
			],
			[
				"80[257-9]\\d{3}"
			],
			[
				"90(?:[1345][15-7]|2[125-7]|99)\\d{2}"
			],
			0,
			0,
			0,
			0,
			[
				"(?:6[0-36]|88)\\d{4}"
			]
		]
	],
	FR: [
		"33",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"([1-79])(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[1-79]"
				]
			],
			[
				"(8\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0 $1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"[1-5]\\d{8}"
			],
			[
				"(?:6\\d|7[3-9])\\d{7}"
			],
			[
				"80[0-5]\\d{6}"
			],
			[
				"8[129]\\d{7}"
			],
			0,
			0,
			[
				"80[6-9]\\d{6}"
			],
			0,
			[
				"9\\d{8}"
			],
			[
				"884\\d{6}"
			]
		]
	],
	GA: [
		"241",
		"00",
		"0?\\d{7}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-7]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"01\\d{6}",
				[
					8
				]
			],
			[
				"0?[2-7]\\d{6}"
			]
		]
	],
	GB: [
		"44",
		"00",
		"\\d{7,10}",
		[
			7,
			9,
			10
		],
		[
			[
				"(7\\d{3})(\\d{6})",
				"$1 $2",
				[
					"7(?:[1-57-9]|62)",
					"7(?:[1-57-9]|624)"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2|5[56]|7[06]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:[02-9]1|1)|3|9[018]"
				]
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				]
			],
			[
				"(1\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(800)(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				]
			],
			[
				"(845)(46)(4\\d)",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				]
			],
			[
				"(8\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8(?:4[2-5]|7[0-3])"
				]
			],
			[
				"(80\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"80"
				]
			],
			[
				"(800)(\\d{6})",
				"$1 $2",
				[
					"800"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0[01378]|3[0189]|4[017]|8[0-46-9]|9[0-2])\\d{7}|1(?:1(?:3[0-48]|[46][0-4]|5[0-26-9]|[78][0-49])|21[0-7]|31[0-8]|[4-69]1\\d)\\d{6}|1(?:2(?:0[024-9]|2[3-9]|3[3-79]|4[1-689]|[58][02-9]|6[0-47-9]|7[013-9]|9\\d)|3(?:0\\d|[25][02-9]|3[02-579]|[468][0-46-9]|7[1-35-79]|9[2-578])|4(?:0[03-9]|[28][02-57-9]|[37]\\d|4[02-69]|5[0-8]|[69][0-79])|5(?:0[1-35-9]|2[024-9]|3[015689]|4[02-9]|5[03-9]|6\\d|7[0-35-9]|8[0-468]|9[0-57-9])|6(?:0[034689]|2[0-35689]|[38][013-9]|4[1-467]|5[0-69]|6[13-9]|7[0-8]|9[0124578])|7(?:0[0246-9]|2\\d|3[0236-8]|4[03-9]|5[0-46-9]|6[013-9]|7[0-35-9]|8[024-9]|9[02-9])|8(?:0[35-9]|2[1-57-9]|3[02-578]|4[0-578]|5[124-9]|6[2-69]|7\\d|8[02-9]|9[02569])|9(?:0[02-589]|2[02-689]|3[1-57-9]|4[2-9]|5[0-579]|6[2-47-9]|7[0-24578]|8\\d|9[2-57]))\\d{6}|1(?:2(?:0(?:46[1-4]|87[2-9])|545[1-79]|76(?:2\\d|3[1-8]|6[1-6])|9(?:7(?:2[0-4]|3[2-5])|8(?:2[2-8]|7[0-47-9]|8[345])))|3(?:638[2-5]|647[23]|8(?:47[04-9]|64[0157-9]))|4(?:044[1-7]|20(?:2[23]|8\\d)|6(?:0(?:30|5[2-57]|6[1-8]|7[2-8])|140)|8(?:052|87[123]))|5(?:24(?:3[2-79]|6\\d)|276\\d|6(?:26[06-9]|686))|6(?:06(?:4\\d|7[4-79])|295[567]|35[34]\\d|47(?:24|61)|59(?:5[08]|6[67]|74)|955[0-4])|7(?:26(?:6[13-9]|7[0-7])|442\\d|50(?:2[0-3]|[3-68]2|76))|8(?:27[56]\\d|37(?:5[2-5]|8[239])|84(?:3[2-58]))|9(?:0(?:0(?:6[1-8]|85)|52\\d)|3583|4(?:66[1-8]|9(?:2[01]|81))|63(?:23|3[1-4])|9561))\\d{3}|176888[2-46-8]\\d{2}|16977[23]\\d{3}",
				[
					9,
					10
				]
			],
			[
				"7(?:[1-3]\\d{3}|4(?:[0-46-9]\\d{2}|5(?:[0-689]\\d|7[0-57-9]))|5(?:0[0-8]|[13-9]\\d|2[0-35-9])\\d|7(?:0(?:0[01]|[1-9]\\d)|[1-7]\\d{2}|8[02-9]\\d|9[0-689]\\d)|8(?:[014-9]\\d|[23][0-8])\\d|9(?:[024-9]\\d{2}|1(?:[02-9]\\d|1[028])|3[0-689]\\d))\\d{5}",
				[
					10
				]
			],
			[
				"80(?:0(?:1111|\\d{6,7})|8\\d{7})"
			],
			[
				"(?:87[123]|9(?:[01]\\d|8[2349]))\\d{7}",
				[
					10
				]
			],
			[
				"70\\d{8}",
				[
					10
				]
			],
			0,
			[
				"(?:3[0347]|55)\\d{8}",
				[
					10
				]
			],
			[
				"76(?:0[012]|2[356]|4[0134]|5[49]|6[0-369]|77|81|9[39])\\d{6}",
				[
					10
				]
			],
			[
				"56\\d{8}",
				[
					10
				]
			],
			[
				"8(?:4(?:5464\\d|[2-5]\\d{7})|70\\d{7})",
				[
					7,
					10
				]
			]
		],
		0,
		" x"
	],
	GD: [
		"1",
		"011",
		"[4589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"473",
		[
			[
				"473(?:2(?:3[0-2]|69)|3(?:2[89]|86)|4(?:[06]8|3[5-9]|4[0-49]|5[5-79]|68|73|90)|63[68]|7(?:58|84)|800|938)\\d{4}"
			],
			[
				"473(?:4(?:0[2-79]|1[04-9]|2[0-5]|58)|5(?:2[01]|3[3-8])|901)\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	GE: [
		"995",
		"00",
		"[34578]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[348]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5|790"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:[256]\\d|4[124-9]|7[0-4])|4(?:1\\d|2[2-7]|3[1-79]|4[2-8]|7[239]|9[1-7]))\\d{6}"
			],
			[
				"(?:5(?:[14]4|5[0157-9]|68|7[0147-9]|9[1-35-9])|790)\\d{6}"
			],
			[
				"800\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"706\\d{6}"
			]
		]
	],
	GF: [
		"594",
		"00",
		"[56]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"594(?:[023]\\d|1[01]|4[03-9]|5[6-9]|6[0-3]|80|9[014])\\d{4}"
			],
			[
				"694(?:[0-249]\\d|3[0-48])\\d{4}"
			]
		]
	],
	GG: [
		"44",
		"00",
		"[135789]\\d{6,9}",
		[
			7,
			9,
			10
		],
		[
			[
				"(7\\d{3})(\\d{6})",
				"$1 $2",
				[
					"7(?:[1-57-9]|62)",
					"7(?:[1-57-9]|624)"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2|5[56]|7[06]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:[02-9]1|1)|3|9[018]"
				]
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				]
			],
			[
				"(1\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(800)(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				]
			],
			[
				"(845)(46)(4\\d)",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				]
			],
			[
				"(8\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8(?:4[2-5]|7[0-3])"
				]
			],
			[
				"(80\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"80"
				]
			],
			[
				"(800)(\\d{6})",
				"$1 $2",
				[
					"800"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1481[25-9]\\d{5}",
				[
					10
				]
			],
			[
				"7(?:781\\d|839\\d|911[17])\\d{5}",
				[
					10
				]
			],
			[
				"80(?:0(?:1111|\\d{6,7})|8\\d{7})"
			],
			[
				"(?:87[123]|9(?:[01]\\d|8[0-3]))\\d{7}",
				[
					10
				]
			],
			[
				"70\\d{8}",
				[
					10
				]
			],
			0,
			[
				"(?:3[0347]|55)\\d{8}",
				[
					10
				]
			],
			[
				"76(?:0[012]|2[356]|4[0134]|5[49]|6[0-369]|77|81|9[39])\\d{6}",
				[
					10
				]
			],
			[
				"56\\d{8}",
				[
					10
				]
			],
			[
				"8(?:4(?:5464\\d|[2-5]\\d{7})|70\\d{7})",
				[
					7,
					10
				]
			]
		]
	],
	GH: [
		"233",
		"00",
		"[235]\\d{8}|8\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[235]"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"8"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"3(?:0(?:[237]\\d|80)|[167](?:2[0-6]|7\\d|80)|2(?:2[0-5]|7\\d|80)|3(?:2[0-3]|7\\d|80)|4(?:2[013-9]|3[01]|7\\d|80)|5(?:2[0-7]|7\\d|80)|8(?:2[0-2]|7\\d|80)|9(?:[28]0|7\\d))\\d{5}",
				[
					9
				]
			],
			[
				"(?:2[034678]\\d|5(?:[0457]\\d|6[01]))\\d{6}",
				[
					9
				]
			],
			[
				"800\\d{5}",
				[
					8
				]
			]
		]
	],
	GI: [
		"350",
		"00",
		"[256]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"2"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:00\\d{2}|1(?:6[24-7]\\d|90[0-2])|2(?:2[2457]\\d|50[0-2]))\\d{3}"
			],
			[
				"(?:5[46-8]|62)\\d{6}"
			]
		]
	],
	GL: [
		"299",
		"00",
		"[1-689]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:19|3[1-7]|6[14689]|8[14-79]|9\\d)\\d{4}"
			],
			[
				"(?:[25][1-9]|4[2-9])\\d{4}"
			],
			[
				"80\\d{4}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"3[89]\\d{4}"
			]
		]
	],
	GM: [
		"220",
		"00",
		"[2-9]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4(?:[23]\\d{2}|4(?:1[024679]|[6-9]\\d))|5(?:54[0-7]|6(?:[67]\\d)|7(?:1[04]|2[035]|3[58]|48))|8\\d{3})\\d{3}"
			],
			[
				"[23679]\\d{6}"
			]
		]
	],
	GN: [
		"224",
		"00",
		"[367]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"3"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[67]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"30(?:24|3[12]|4[1-35-7]|5[13]|6[189]|[78]1|9[1478])\\d{4}",
				[
					8
				]
			],
			[
				"6[02356]\\d{7}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"722\\d{6}",
				[
					9
				]
			]
		]
	],
	GP: [
		"590",
		"00",
		"[56]\\d{8}",
		[
			9
		],
		[
			[
				"([56]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"590|69[01]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"590(?:0[1-68]|1[0-2]|2[0-68]|3[1289]|4[0-24-9]|5[3-579]|6[0189]|7[08]|8[0-689]|9\\d)\\d{4}"
			],
			[
				"69(?:0\\d{2}|1(?:2[29]|3[0-5]))\\d{4}"
			]
		]
	],
	GQ: [
		"240",
		"00",
		"[23589]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[235]"
				]
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3(?:3(?:3\\d[7-9]|[0-24-9]\\d[46])|5\\d{2}[7-9])\\d{4}"
			],
			[
				"(?:222|55[15])\\d{6}"
			],
			[
				"80\\d[1-9]\\d{5}"
			],
			[
				"90\\d[1-9]\\d{5}"
			]
		]
	],
	GR: [
		"30",
		"00",
		"[26-9]\\d{9}",
		[
			10
		],
		[
			[
				"([27]\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"21|7"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2[2-9]1|[689]"
				]
			],
			[
				"(2\\d{3})(\\d{6})",
				"$1 $2",
				[
					"2[2-9][02-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1\\d{2}|2(?:2[1-46-9]|3[1-8]|4[1-7]|5[1-4]|6[1-8]|7[1-5]|[89][1-9])|3(?:1\\d|2[1-57]|[35][1-3]|4[13]|7[1-7]|8[124-6]|9[1-79])|4(?:1\\d|2[1-8]|3[1-4]|4[13-5]|6[1-578]|9[1-5])|5(?:1\\d|[29][1-4]|3[1-5]|4[124]|5[1-6])|6(?:1\\d|3[1245]|4[1-7]|5[13-9]|[269][1-6]|7[14]|8[1-5])|7(?:1\\d|2[1-5]|3[1-6]|4[1-7]|5[1-57]|6[135]|9[125-7])|8(?:1\\d|2[1-5]|[34][1-4]|9[1-57]))\\d{6}"
			],
			[
				"6(?:8[57-9]|9\\d)\\d{7}"
			],
			[
				"800\\d{7}"
			],
			[
				"90[19]\\d{7}"
			],
			[
				"70\\d{8}"
			],
			0,
			0,
			0,
			0,
			[
				"8(?:0[16]|12|25)\\d{7}"
			]
		]
	],
	GT: [
		"502",
		"00",
		"[2-7]\\d{7}|1[89]\\d{9}",
		[
			8,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[267][2-9]\\d{6}",
				[
					8
				]
			],
			[
				"[345]\\d{7}",
				[
					8
				]
			],
			[
				"18[01]\\d{8}",
				[
					11
				]
			],
			[
				"19\\d{9}",
				[
					11
				]
			]
		]
	],
	GU: [
		"1",
		"011",
		"[5689]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"671",
		[
			[
				"671(?:3(?:00|3[39]|4[349]|55|6[26])|4(?:00|56|7[1-9]|8[0236-9])|5(?:55|6[2-5]|88)|6(?:3[2-578]|4[24-9]|5[34]|78|8[235-9])|7(?:[079]7|2[0167]|3[45]|47|8[7-9])|8(?:[2-57-9]8|6[48])|9(?:2[29]|6[79]|7[1279]|8[7-9]|9[78]))\\d{4}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	GW: [
		"245",
		"00",
		"(?:4(?:0\\d{5}|4\\d{7})|9\\d{8})",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"44|9[5-7]"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"40"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"443\\d{6}",
				[
					9
				]
			],
			[
				"9(?:5\\d|6[569]|77)\\d{6}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"40\\d{5}",
				[
					7
				]
			]
		]
	],
	GY: [
		"592",
		"001",
		"[2-46-9]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:1[6-9]|2[0-35-9]|3[1-4]|5[3-9]|6\\d|7[0-24-79])|3(?:2[25-9]|3\\d)|4(?:4[0-24]|5[56])|77[1-57])\\d{4}"
			],
			[
				"6\\d{6}"
			],
			[
				"(?:289|862)\\d{4}"
			],
			[
				"9008\\d{3}"
			]
		]
	],
	HK: [
		"852",
		"00(?:[126-9]|30|5[09])?",
		"[2-7]\\d{7}|8[0-4]\\d{6,7}|9\\d{4,10}",
		[
			5,
			6,
			7,
			8,
			9,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]|[89](?:0[1-9]|[1-9])"
				]
			],
			[
				"(800)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"800"
				]
			],
			[
				"(900)(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"900"
				]
			],
			[
				"(900)(\\d{2,5})",
				"$1 $2",
				[
					"900"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[13-8]\\d|2[013-9]|9[0-24-9])\\d|3(?:[1569][0-24-9]\\d|4[0-246-9]\\d|7[0-24-69]\\d|8(?:4[04]|9\\d))|58(?:0[1-8]|1[2-9]))\\d{4}",
				[
					8
				]
			],
			[
				"(?:46(?:0[0-6]|4[0-57-9])|5(?:[1-59][0-46-9]\\d|6[0-4689]\\d|7(?:[0-2469]\\d|30))|6(?:0[1-9]\\d|[1459]\\d{2}|2(?:[0-57-9]\\d|6[01])|[368][0-57-9]\\d|7[0-79]\\d)|707[1-5]|8480|9(?:0[1-9]\\d|1[02-9]\\d|2(?:[0-8]\\d|9[03-9])|[358][0-8]\\d|[467]\\d{2}))\\d{4}",
				[
					8
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			],
			[
				"900(?:[0-24-9]\\d{7}|3\\d{1,4})",
				[
					5,
					6,
					7,
					8,
					11
				]
			],
			[
				"8(?:1[0-4679]\\d|2(?:[0-36]\\d|7[0-4])|3(?:[034]\\d|2[09]|70))\\d{4}",
				[
					8
				]
			],
			0,
			[
				"30(?:0[1-9]|[15-7]\\d|2[047]|89)\\d{4}",
				[
					8
				]
			],
			[
				"7(?:1(?:0[0-38]|1[0-3679]|3[013]|69|9[136])|2(?:[02389]\\d|1[18]|7[27-9])|3(?:[0-38]\\d|7[0-369]|9[2357-9])|47\\d|5(?:[178]\\d|5[0-5])|6(?:0[0-7]|2[236-9]|[35]\\d)|7(?:[27]\\d|8[7-9])|8(?:[23689]\\d|7[1-9])|9(?:[025]\\d|6[0-246-8]|7[0-36-9]|8[238]))\\d{4}",
				[
					8
				]
			]
		],
		"00"
	],
	HN: [
		"504",
		"00",
		"[237-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1-$2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2(?:0[019]|1[1-36]|[23]\\d|4[04-6]|5[57]|7[013689]|8[0146-9]|9[012])|4(?:07|2[3-59]|3[13-689]|4[0-68]|5[1-35])|5(?:16|4[03-5]|5\\d|6[4-6]|74)|6(?:[056]\\d|17|3[04]|4[0-378]|[78][0-8]|9[01])|7(?:6[46-9]|7[02-9]|8[034])|8(?:79|8[0-35789]|9[1-57-9]))\\d{4}"
			],
			[
				"[37-9]\\d{7}"
			]
		]
	],
	HR: [
		"385",
		"00",
		"[1-7]\\d{5,8}|[89]\\d{6,8}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(1)(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"([2-5]\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-5]"
				]
			],
			[
				"(9\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				]
			],
			[
				"(6[01])(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"6[01]"
				]
			],
			[
				"([67]\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[67]"
				]
			],
			[
				"(80[01])(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"80[01]"
				]
			],
			[
				"(80[01])(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"80[01]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{7}|(?:2[0-3]|3[1-5]|4[02-47-9]|5[1-3])\\d{6,7}",
				[
					8,
					9
				]
			],
			[
				"9(?:01\\d|[1259]\\d{2}|7(?:[0679]\\d|51)|8\\d{1,2})\\d{5}",
				[
					8,
					9
				]
			],
			[
				"80[01]\\d{4,6}",
				[
					7,
					8,
					9
				]
			],
			[
				"6(?:[01]\\d{0,2}|[459]\\d{2})\\d{4}",
				[
					6,
					7,
					8
				]
			],
			[
				"7[45]\\d{6}",
				[
					8
				]
			],
			0,
			[
				"(?:62\\d?|72)\\d{6}",
				[
					8,
					9
				]
			]
		]
	],
	HT: [
		"509",
		"00",
		"[2-489]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2\\d|5[1-5]|81|9[149])\\d{5}"
			],
			[
				"[34]\\d{7}"
			],
			[
				"8\\d{7}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"(?:9(?:[67][0-4]|8[0-3589]|9\\d))\\d{5}"
			]
		]
	],
	HU: [
		"36",
		"00",
		"[1-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(1)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				]
			]
		],
		"06",
		"($1)",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|2[2-9]|3[2-7]|4[24-9]|5[2-79]|6[23689]|7[2-9]|8[2-57-9]|9[2-69])\\d{6}",
				[
					8
				]
			],
			[
				"(?:[257]0|3[01])\\d{7}",
				[
					9
				]
			],
			[
				"[48]0\\d{6}",
				[
					8
				]
			],
			[
				"9[01]\\d{6}",
				[
					8
				]
			],
			0,
			0,
			[
				"38\\d{7}",
				[
					9
				]
			],
			0,
			[
				"21\\d{7}",
				[
					9
				]
			]
		]
	],
	ID: [
		"62",
		"0(?:0[1789]|10(?:00|1[67]))",
		"(?:[1-79]\\d{6,10}|8\\d{7,11})",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{5,8})",
				"$1 $2",
				[
					"2[124]|[36]1"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5,8})",
				"$1 $2",
				[
					"2[035-9]|[36][02-9]|[4579]"
				],
				"(0$1)"
			],
			[
				"(8\\d{2})(\\d{3,4})(\\d{3})",
				"$1-$2-$3",
				[
					"8[1-35-9]"
				]
			],
			[
				"(8\\d{2})(\\d{4})(\\d{4,5})",
				"$1-$2-$3",
				[
					"8[1-35-9]"
				]
			],
			[
				"(1)(500)(\\d{3})",
				"$1 $2 $3",
				[
					"150",
					"1500"
				],
				"$1"
			],
			[
				"(177)(\\d{6,8})",
				"$1 $2",
				[
					"177"
				]
			],
			[
				"(800)(\\d{5,7})",
				"$1 $2",
				[
					"800"
				]
			],
			[
				"(804)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"804"
				]
			],
			[
				"(80\\d)(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"80[79]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1(?:14\\d{3}|500\\d{3}|\\d{7,8})|2\\d{6,8}|4\\d{7,8})|(?:2(?:[35][1-4]|6[0-8]|7[1-6]|8\\d|9[1-8])|3(?:1|[25][1-8]|3[1-68]|4[1-3]|6[1-3568]|7[0-469]|8\\d)|4(?:0[1-589]|1[01347-9]|2[0-36-8]|3[0-24-68]|43|5[1-378]|6[1-5]|7[134]|8[1245])|5(?:1[1-35-9]|2[25-8]|3[124-9]|4[1-3589]|5[1-46]|6[1-8])|6(?:19?|[25]\\d|3[1-69]|4[1-6])|7(?:02|[125][1-9]|[36]\\d|4[1-8]|7[0-36-9])|9(?:0[12]|1[013-8]|2[0-479]|5[125-8]|6[23679]|7[159]|8[01346]))\\d{5,8}",
				[
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"(?:2(?:1(?:3[145]|4[01]|5[1-469]|60|8[0359])|2(?:88|9[1256])|3[1-4]9|4(?:36|91)|5(?:1[349]|[2-4]9)|6[0-7]9|7(?:[1-36]9|4[39])|8[1-5]9|9[1-48]9)|3(?:19[1-3]|2[12]9|3[13]9|4(?:1[69]|39)|5[14]9|6(?:1[69]|2[89])|709)|4[13]19|5(?:1(?:19|8[39])|4[129]9|6[12]9)|6(?:19[12]|2(?:[23]9|77))|7(?:1[13]9|2[15]9|419|5(?:1[89]|29)|6[15]9|7[178]9))\\d{5,6}|8[1-35-9]\\d{7,10}",
				[
					9,
					10,
					11,
					12
				]
			],
			[
				"177\\d{6,8}|800\\d{5,7}",
				[
					8,
					9,
					10,
					11
				]
			],
			[
				"809\\d{7}",
				[
					10
				]
			],
			0,
			0,
			[
				"1500\\d{3}|8071\\d{6}",
				[
					7,
					10
				]
			],
			0,
			0,
			[
				"804\\d{7}",
				[
					10
				]
			]
		]
	],
	IE: [
		"353",
		"00",
		"[124-9]\\d{6,9}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(1)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"2[24-9]|47|58|6[237-9]|9[35-9]"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"40[24]|50[45]"
				]
			],
			[
				"(48)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"48"
				]
			],
			[
				"(818)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"818"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[24-69]|7[14]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"76|8[35-9]"
				],
				"0$1"
			],
			[
				"(8\\d)(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"8[35-9]5"
				],
				"0$1"
			],
			[
				"(700)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"700"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1(?:5|8[059])",
					"1(?:5|8[059]0)"
				],
				"$1"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{7,8}|2(?:1\\d{6,7}|3\\d{7}|[24-9]\\d{5})|4(?:0[24]\\d{5}|[1-469]\\d{7}|5\\d{6}|7\\d{5}|8[0-46-9]\\d{7})|5(?:0[45]\\d{5}|1\\d{6}|[23679]\\d{7}|8\\d{5})|6(?:1\\d{6}|[237-9]\\d{5}|[4-6]\\d{7})|7[14]\\d{7}|9(?:1\\d{6}|[04]\\d{7}|[35-9]\\d{5})"
			],
			[
				"8(?:22\\d{6}|[35-9]\\d{7})",
				[
					9
				]
			],
			[
				"1800\\d{6}",
				[
					10
				]
			],
			[
				"15(?:1[2-8]|[2-8]0|9[089])\\d{6}",
				[
					10
				]
			],
			[
				"700\\d{6}",
				[
					9
				]
			],
			0,
			[
				"818\\d{6}",
				[
					9
				]
			],
			0,
			[
				"76\\d{7}",
				[
					9
				]
			],
			[
				"18[59]0\\d{6}",
				[
					10
				]
			]
		]
	],
	IL: [
		"972",
		"0(?:0|1[2-9])",
		"1\\d{6,11}|[2-589]\\d{3}(?:\\d{3,6})?|6\\d{3}|7\\d{6,9}",
		[
			4,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"([2-489])(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[2-489]"
				],
				"0$1"
			],
			[
				"([57]\\d)(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[57]"
				],
				"0$1"
			],
			[
				"(153)(\\d{1,2})(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"153"
				]
			],
			[
				"(1)([7-9]\\d{2})(\\d{3})(\\d{3})",
				"$1-$2-$3-$4",
				[
					"1[7-9]"
				]
			],
			[
				"(1255)(\\d{3})",
				"$1-$2",
				[
					"125",
					"1255"
				]
			],
			[
				"(1200)(\\d{3})(\\d{3})",
				"$1-$2-$3",
				[
					"120",
					"1200"
				]
			],
			[
				"(1212)(\\d{2})(\\d{2})",
				"$1-$2-$3",
				[
					"121",
					"1212"
				]
			],
			[
				"(1599)(\\d{6})",
				"$1-$2",
				[
					"159",
					"1599"
				]
			],
			[
				"(151)(\\d{1,2})(\\d{3})(\\d{4})",
				"$1-$2 $3-$4",
				[
					"151"
				]
			],
			[
				"(\\d{4})",
				"*$1",
				[
					"[2-689]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:153\\d{1,2}|[2-489])\\d{7}",
				[
					8,
					11,
					12
				]
			],
			[
				"5(?:[0-489][2-9]\\d|5(?:01|2[2-5]|3[23]|4[45]|5[015689]|6[6-8]|7[0-267]|8[7-9]|9[1-9])|6\\d{2})\\d{5}",
				[
					9
				]
			],
			[
				"1(?:80[019]\\d{3}|255)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"1(?:212|(?:9(?:0[01]|19)|200)\\d{2})\\d{4}",
				[
					8,
					9,
					10
				]
			],
			0,
			0,
			[
				"[2-689]\\d{3}|1599\\d{6}",
				[
					4,
					10
				]
			],
			0,
			[
				"7(?:18\\d|2[23]\\d|3[237]\\d|47\\d|6[58]\\d|7\\d{2}|8(?:2\\d|33|55|77|81)|9[2579]\\d)\\d{5}",
				[
					9
				]
			],
			[
				"1700\\d{6}",
				[
					10
				]
			]
		]
	],
	IM: [
		"44",
		"00",
		"[135789]\\d{6,9}",
		[
			10
		],
		[
			[
				"(7\\d{3})(\\d{6})",
				"$1 $2",
				[
					"7(?:[1-57-9]|62)",
					"7(?:[1-57-9]|624)"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2|5[56]|7[06]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:[02-9]1|1)|3|9[018]"
				]
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				]
			],
			[
				"(1\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(800)(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				]
			],
			[
				"(845)(46)(4\\d)",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				]
			],
			[
				"(8\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8(?:4[2-5]|7[0-3])"
				]
			],
			[
				"(80\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"80"
				]
			],
			[
				"(800)(\\d{6})",
				"$1 $2",
				[
					"800"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1624[5-8]\\d{5}"
			],
			[
				"7(?:4576|[59]24\\d|624[0-4689])\\d{5}"
			],
			[
				"808162\\d{4}"
			],
			[
				"(?:872299|90[0167]624)\\d{4}"
			],
			[
				"70\\d{8}"
			],
			0,
			[
				"3(?:08162\\d|3\\d{5}|4(?:40[49]06|5624\\d)|7(?:0624\\d|2299\\d))\\d{3}|55\\d{8}"
			],
			0,
			[
				"56\\d{8}"
			],
			[
				"8(?:4(?:40[49]06|5624\\d)|70624\\d)\\d{3}"
			]
		]
	],
	IN: [
		"91",
		"00",
		"008\\d{9}|1\\d{7,12}|[2-9]\\d{9,10}",
		[
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d{8})",
				"$1",
				[
					"561",
					"5616",
					"56161"
				],
				"$1"
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"6(?:0[02]|12|2[03689]|3[05-9]|9[019])|7(?:[02-8]|19|9[037-9])|8(?:0[015-9]|[1-9])|9",
					"6(?:0(?:0|26)|127|2(?:0[0-49]|3[589]|[68][0-39]|9[0-46])|3(?:0[0-39]|5[0-46-9]|6[0-2]|7[015-79]|[89][0-79])|9[019])|7(?:[07]|19[0-5]|2(?:[0235-9]|[14][017-9])|3(?:[025-9]|[134][017-9])|4(?:[0-35689]|[47][017-9])|5(?:[02-46-9]|[15][017-9])|6(?:[02-9]|1[0-257-9])|8(?:[0-79]|8[0189])|9(?:[089]|31|7[02-9]))|8(?:0(?:[01589]|6[67]|7[02-9])|1(?:[0-57-9]|6[07-9])|2(?:[014][07-9]|[235-9])|3(?:[03-57-9]|[126][07-9])|[45]|6(?:[02457-9]|[136][07-9])|7(?:[078][07-9]|[1-69])|8(?:[0-25-9]|3[07-9]|4[047-9])|9(?:[02-9]|1[027-9]))|9",
					"6(?:0(?:0|26)|1279|2(?:0[0-49]|3[589]|[68][0-39]|9[0-46])|3(?:0[0-39]|5[0-46-9]|6[0-2]|7[015-79]|[89][0-79])|9[019])|7(?:0|19[0-5]|2(?:[0235-79]|[14][017-9]|8(?:[0-69]|[78][089]))|3(?:[05-8]|1(?:[0189]|7[024-9])|2(?:[0-49][089]|[5-8])|3[017-9]|4(?:[07-9]|11)|9(?:[01689]|[2-5][089]|7[0189]))|4(?:[056]|1(?:[0135-9]|[24][089])|[29](?:[0-7][089]|[89])|3(?:[0-8][089]|9)|[47](?:[089]|11|7[02-8])|8(?:[0-24-7][089]|[389]))|5(?:[0346-9]|[15][017-9]|2(?:[03-9]|[12][089]))|6(?:[0346-9]|1[0-257-9]|2(?:[0-4]|[5-9][089])|5(?:[0-367][089]|[4589]))|7(?:0(?:[02-9]|1[089])|[1-9])|8(?:[0-79]|8(?:0[0189]|11|8[013-9]|9))|9(?:[089]|313|7(?:[02-8]|9[07-9])))|8(?:0(?:[01589]|6[67]|7(?:[02-8]|9[04-9]))|1(?:[02-57-9]|1(?:[0-35-9]|4[0-46-9])|6(?:[089]|7[02-8]))|2(?:[014](?:[089]|7[02-8])|[235-9])|3(?:[03-57-9]|[16](?:[089]|7[02-8])|2(?:[09]|7[02-8]|8[0-689]))|[45]|6(?:[02457-9]|[136](?:[089]|7[02-8]))|7(?:0[07-9]|[1-69]|[78](?:[089]|7[02-8]))|8(?:[0-25-9]|3(?:[089]|7[02-8])|4(?:[0489]|7[02-8]))|9(?:[02-9]|1(?:[0289]|7[02-8])))|9"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"11|2[02]|33|4[04]|79[1-9]|80[2-46]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:2[0-249]|3[0-25]|4[145]|[59][14]|[68][1-9]|7[1257])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|5[12]|[78]1|9[15])|6(?:12|[2-4]1|5[17]|6[13]|7[14]|80)|7(?:12|2[14]|3[134]|4[47]|5[15]|[67]1|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91)"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1(?:[23579]|[468][1-9])|[2-8]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"008"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"140"
				],
				"$1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"160",
					"1600"
				],
				"$1"
			],
			[
				"(\\d{4})(\\d{4,5})",
				"$1 $2",
				[
					"180",
					"1800"
				],
				"$1"
			],
			[
				"(\\d{4})(\\d{2,4})(\\d{4})",
				"$1 $2 $3",
				[
					"180",
					"1800"
				],
				"$1"
			],
			[
				"(\\d{4})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"186",
					"1860"
				],
				"$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"18[06]"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		1,
		0,
		[
			[
				"(?:11|2[02]|33|4[04]|79|80)[2-7]\\d{7}|(?:1(?:2[0-249]|3[0-25]|4[145]|[59][14]|6[014]|7[1257]|8[01346])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|[36][25]|22|4[28]|5[12]|[78]1|9[15])|6(?:12|[2345]1|57|6[13]|7[14]|80)|7(?:12|2[14]|3[134]|4[47]|5[15]|[67]1|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91))[2-7]\\d{6}|(?:(?:1(?:2[35-8]|3[346-9]|4[236-9]|[59][0235-9]|6[235-9]|7[34689]|8[257-9])|2(?:1[134689]|3[24-8]|4[2-8]|5[25689]|6[2-4679]|7[13-79]|8[2-479]|9[235-9])|3(?:01|1[79]|2[1-5]|4[25-8]|5[125689]|6[235-7]|7[157-9]|8[2-46-8])|4(?:1[14578]|2[5689]|3[2-467]|5[4-7]|6[35]|73|8[2689]|9[2389])|5(?:[16][146-9]|2[14-8]|3[1346]|4[14-69]|5[46]|7[2-4]|8[2-8]|9[246])|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|[57][2-689]|6[24-578]|8[1-6])|8(?:1[1357-9]|2[235-8]|3[03-57-9]|4[0-24-9]|5\\d|6[2457-9]|7[1-6]|8[1256]|9[2-4]))\\d|7(?:(?:1[013-9]|2[0235-9]|3[2679]|4[1-35689]|5[2-46-9]|[67][02-9]|9\\d)\\d|8(?:2[0-6]|[013-8]\\d)))[2-7]\\d{5}",
				[
					10
				]
			],
			[
				"(?:6(?:0(?:0[0-35]|26)\\d|1279|2(?:0[0-49]|3[589]|[68][0-49]|9[0-6])\\d|3(?:0[0-39]\\d|5(?:0[0-6]|[1-9]\\d)|6[0-2]\\d|7[015-79]\\d|[89][0-79]\\d)|90[019]\\d)|7(?:0\\d{3}|19[0-5]\\d|2(?:[0235679]\\d{2}|[14][017-9]\\d|8(?:[0-59]\\d|[678][089]))|3(?:[05-8]\\d{2}|1(?:[089]\\d|11|7[024-8])|2(?:[0-49][089]|[5-8]\\d)|3[017-9]\\d|4(?:[07-9]\\d|11)|9(?:[016-9]\\d|[2-5][089]))|4(?:0\\d{2}|1(?:[015-9]\\d|[23][089]|4[089])|2(?:0[089]|[1-7][089]|[89]\\d)|3(?:[0-8][089]|9\\d)|4(?:[089]\\d|11|7[02-8])|[56]\\d[089]|7(?:[089]\\d|11|7[02-8])|8(?:[0-24-7][089]|[389]\\d)|9(?:[0-6][089]|7[089]|[89]\\d))|5(?:[0346-8]\\d{2}|1(?:[07-9]\\d|11)|2(?:[04-9]\\d|[123][089])|5[017-9]\\d|9(?:[0-6][089]|[7-9]\\d))|6(?:0(?:[0-47]\\d|[5689][089])|(?:1[0-257-9]|[6-9]\\d)\\d|2(?:[0-4]\\d|[5-9][089])|3(?:[02-8][089]|[19]\\d)|4\\d[089]|5(?:[0-367][089]|[4589]\\d))|7(?:0(?:0[02-9]|[13-7][089]|[289]\\d)|[1-9]\\d{2})|8(?:[0-79]\\d{2}|8(?:[089]\\d|11|7[02-9]))|9(?:[089]\\d{2}|313|7(?:[02-8]\\d|9[07-9])))|8(?:0(?:[01589]\\d{2}|6[67]\\d|7(?:[02-8]\\d|9[04-9]))|1(?:[02-57-9]\\d{2}|1(?:[0-35-9]\\d|4[0-46-9])|6(?:[089]\\d|7[02-8]))|2(?:0(?:[089]\\d|7[02-8])|[14](?:[089]\\d|7[02-8])|[235-9]\\d{2})|3(?:[0357-9]\\d{2}|1(?:[089]\\d|7[02-8])|2(?:[09]\\d|7[02-8]|8[0-689])|4\\d{2}|6(?:[089]\\d|7[02-8]))|[45]\\d{3}|6(?:[02457-9]\\d{2}|1(?:[089]\\d|7[02-8])|3(?:[089]\\d|7[02-8])|6(?:[08]\\d|7[02-8]|9\\d))|7(?:0[07-9]\\d|[1-69]\\d{2}|[78](?:[089]\\d|7[02-8]))|8(?:[0-25-9]\\d{2}|3(?:[089]\\d|7[02-8])|4(?:[0489]\\d|7[02-8]))|9(?:[02-9]\\d{2}|1(?:[0289]\\d|7[02-8])))|9\\d{4})\\d{5}",
				[
					10
				]
			],
			[
				"00800\\d{7}|1(?:600\\d{6}|80(?:0\\d{4,9}|3\\d{9}))"
			],
			[
				"186[12]\\d{9}",
				[
					13
				]
			],
			0,
			0,
			[
				"140\\d{7}",
				[
					10
				]
			],
			0,
			0,
			[
				"1860\\d{7}",
				[
					11
				]
			]
		]
	],
	IO: [
		"246",
		"00",
		"3\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"37\\d{5}"
			],
			[
				"38\\d{5}"
			]
		]
	],
	IQ: [
		"964",
		"00",
		"[1-7]\\d{7,9}",
		[
			8,
			9,
			10
		],
		[
			[
				"(1)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"([2-6]\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-6]"
				]
			],
			[
				"(7\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{7}|(?:2[13-5]|3[02367]|4[023]|5[03]|6[026])\\d{6,7}",
				[
					8,
					9
				]
			],
			[
				"7[3-9]\\d{8}",
				[
					10
				]
			]
		]
	],
	IR: [
		"98",
		"00",
		"[1-8]\\d{5,9}|9(?:[0-4]\\d{8}|9\\d{8})",
		[
			6,
			7,
			10
		],
		[
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-8]"
				]
			],
			[
				"(\\d{2})(\\d{4,5})",
				"$1 $2",
				[
					"[1-8]"
				]
			],
			[
				"(\\d{4,5})",
				"$1",
				[
					"96"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])(?:\\d{8}|(?:[16]|[289]\\d?)\\d{3}))|94(?:000|11[0-7]|2\\d{2}|30[01]|4(?:11|40))\\d{5}"
			],
			[
				"9(?:0(?:[1-35]\\d{2}|44\\d)|[13]\\d{3}|2[0-2]\\d{2}|9(?:[01]\\d{2}|44\\d|8(?:10|88)|9(?:0[013]|1[134]|21|9[89])))\\d{5}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"993\\d{7}",
				[
					10
				]
			]
		]
	],
	IS: [
		"354",
		"1(?:0(?:01|10|20)|100)|00",
		"[4-9]\\d{6}|38\\d{7}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[4-9]"
				]
			],
			[
				"(3\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4(?:1[0-24-69]|2[0-7]|[37][0-8]|4[0-245]|5[0-68]|6\\d|8[0-36-8])|5(?:05|[156]\\d|2[02578]|3[0-579]|4[03-7]|7[0-2578]|8[0-35-9]|9[013-689])|87[23])\\d{4}",
				[
					7
				]
			],
			[
				"38[589]\\d{6}|(?:6(?:1[1-8]|2[0-6]|3[027-9]|4[014679]|5[0159]|6[0-69]|70|8[06-8]|9\\d)|7(?:5[057]|[6-8]\\d|9[0-3])|8(?:2[0-59]|[3469]\\d|5[1-9]|8[28]))\\d{4}"
			],
			[
				"800\\d{4}",
				[
					7
				]
			],
			[
				"90\\d{5}",
				[
					7
				]
			],
			0,
			0,
			[
				"809\\d{4}",
				[
					7
				]
			],
			0,
			[
				"49\\d{5}",
				[
					7
				]
			]
		],
		"00"
	],
	IT: [
		"39",
		"00",
		"[01589]\\d{5,10}|3(?:[12457-9]\\d{8}|[36]\\d{7,9})",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[26]|55"
				]
			],
			[
				"(0[26])(\\d{4})(\\d{5})",
				"$1 $2 $3",
				[
					"0[26]"
				]
			],
			[
				"(0[26])(\\d{4,6})",
				"$1 $2",
				[
					"0[26]"
				]
			],
			[
				"(0\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][0159]"
				]
			],
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"0[13-57-9][0159]|8(?:03|4[17]|9[245])",
					"0[13-57-9][0159]|8(?:03|4[17]|9(?:2|[45][0-4]))"
				]
			],
			[
				"(0\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][2-46-8]"
				]
			],
			[
				"(0\\d{3})(\\d{2,6})",
				"$1 $2",
				[
					"0[13-57-9][2-46-8]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[13]|8(?:00|4[08]|9[59])",
					"[13]|8(?:00|4[08]|9(?:5[5-9]|9))"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"894",
					"894[5-9]"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"0(?:[26]\\d{4,9}|(?:1(?:[0159]\\d|[27][1-5]|31|4[1-4]|6[1356]|8[2-57])|3(?:[0159]\\d|2[1-4]|3[12]|[48][1-6]|6[2-59]|7[1-7])|4(?:[0159]\\d|[23][1-9]|4[245]|6[1-5]|7[1-4]|81)|5(?:[0159]\\d|2[1-5]|3[2-6]|4[1-79]|6[4-6]|7[1-578]|8[3-8])|7(?:[0159]\\d|2[12]|3[1-7]|4[2346]|6[13569]|7[13-6]|8[1-59])|8(?:[0159]\\d|2[34578]|3[1-356]|[6-8][1-5])|9(?:[0159]\\d|[238][1-5]|4[12]|6[1-8]|7[1-6]))\\d{2,7})"
			],
			[
				"3(?:[12457-9]\\d{8}|6\\d{7,8}|3\\d{7,9})",
				[
					9,
					10,
					11
				]
			],
			[
				"80(?:0\\d{6}|3\\d{3})",
				[
					6,
					9
				]
			],
			[
				"0878\\d{5}|1(?:44|6[346])\\d{6}|89(?:2\\d{3}|4(?:[0-4]\\d{2}|[5-9]\\d{4})|5(?:[0-4]\\d{2}|[5-9]\\d{6})|9\\d{6})",
				[
					6,
					8,
					9,
					10
				]
			],
			[
				"1(?:78\\d|99)\\d{6}",
				[
					9,
					10
				]
			],
			0,
			0,
			0,
			[
				"55\\d{8}",
				[
					10
				]
			],
			[
				"84(?:[08]\\d{6}|[17]\\d{3})",
				[
					6,
					9
				]
			]
		]
	],
	JE: [
		"44",
		"00",
		"[135789]\\d{6,9}",
		[
			10
		],
		[
			[
				"(7\\d{3})(\\d{6})",
				"$1 $2",
				[
					"7(?:[1-57-9]|62)",
					"7(?:[1-57-9]|624)"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2|5[56]|7[06]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:[02-9]1|1)|3|9[018]"
				]
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				]
			],
			[
				"(1\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(800)(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				]
			],
			[
				"(845)(46)(4\\d)",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				]
			],
			[
				"(8\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8(?:4[2-5]|7[0-3])"
				]
			],
			[
				"(80\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"80"
				]
			],
			[
				"(800)(\\d{6})",
				"$1 $2",
				[
					"800"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1534[0-24-8]\\d{5}"
			],
			[
				"7(?:509\\d|7(?:00[378]|97[7-9])|829\\d|937\\d)\\d{5}"
			],
			[
				"80(?:07(?:35|81)|8901)\\d{4}"
			],
			[
				"(?:871206|90(?:066[59]|1810|71(?:07|55)))\\d{4}"
			],
			[
				"701511\\d{4}"
			],
			0,
			[
				"3(?:0(?:07(?:35|81)|8901)|3\\d{4}|4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))\\d{4}|55\\d{8}"
			],
			[
				"76(?:0[012]|2[356]|4[0134]|5[49]|6[0-369]|77|81|9[39])\\d{6}"
			],
			[
				"56\\d{8}"
			],
			[
				"8(?:4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|70002)\\d{4}"
			]
		]
	],
	JM: [
		"1",
		"011",
		"[589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"876",
		[
			[
				"876(?:5(?:0[12]|1[0-468]|2[35]|63)|6(?:0[1-3579]|1[0237-9]|[23]\\d|40|5[06]|6[2-589]|7[05]|8[04]|9[4-9])|7(?:0[2-689]|[1-6]\\d|8[056]|9[45])|9(?:0[1-8]|1[02378]|[2-8]\\d|9[2-468]))\\d{4}"
			],
			[
				"876(?:2[14-9]\\d|[348]\\d{2}|5(?:0[3-9]|[2-57-9]\\d|6[0-24-9])|7(?:0[07]|7\\d|8[1-47-9]|9[0-36-9])|9(?:[01]9|9[0579]))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	JO: [
		"962",
		"00",
		"[235-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2356]|87"
				],
				"(0$1)"
			],
			[
				"(7)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"7[457-9]"
				]
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"70"
				]
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"8[0158]|9"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:6(?:2[0-35-9]|3[0-57-8]|4[24-7]|5[0-24-8]|[6-8][023]|9[0-3])|7(?:0[1-79]|10|2[014-7]|3[0-689]|4[019]|5[0-3578]))|32(?:0[1-69]|1[1-35-7]|2[024-7]|3\\d|4[0-3]|[57][023]|6[03])|53(?:0[0-3]|[13][023]|2[0-59]|49|5[0-35-9]|6[15]|7[45]|8[1-6]|9[0-36-9])|6(?:2[50]0|3(?:00|33)|4(?:0[0125]|1[2-7]|2[0569]|[38][07-9]|4[025689]|6[0-589]|7\\d|9[0-2])|5(?:[01][056]|2[034]|3[0-57-9]|4[17-8]|5[0-69]|6[0-35-9]|7[1-379]|8[0-68]|9[02-39]))|87(?:[02]0|7[08]|90))\\d{4}",
				[
					8
				]
			],
			[
				"7(?:55[0-49]|7[025-9]\\d|8[0-25-9]\\d|9[0-25-9]\\d)\\d{5}",
				[
					9
				]
			],
			[
				"80\\d{6}",
				[
					8
				]
			],
			[
				"900\\d{5}",
				[
					8
				]
			],
			[
				"70\\d{7}",
				[
					9
				]
			],
			0,
			[
				"8(?:10|8\\d)\\d{5}",
				[
					8
				]
			],
			[
				"74(?:66|77)\\d{5}",
				[
					9
				]
			],
			0,
			[
				"85\\d{6}",
				[
					8
				]
			]
		]
	],
	JP: [
		"81",
		"010",
		"[1-9]\\d{8,9}|00(?:[36]\\d{7,14}|7\\d{5,7}|8\\d{7})",
		[
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15,
			16,
			17
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1-$2-$3",
				[
					"(?:12|57|99)0"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"800"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[2579]0|80[1-9]"
				]
			],
			[
				"(\\d{4})(\\d)(\\d{4})",
				"$1-$2-$3",
				[
					"1(?:26|3[79]|4[56]|5[4-68]|6[3-5])|499|5(?:76|97)|746|8(?:3[89]|47|51|63)|9(?:49|80|9[16])",
					"1(?:267|3(?:7[247]|9[278])|4(?:5[67]|66)|5(?:47|58|64|8[67])|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:76|97)9|7468|8(?:3(?:8[78]|96)|477|51[24]|636)|9(?:496|802|9(?:1[23]|69))",
					"1(?:267|3(?:7[247]|9[278])|4(?:5[67]|66)|5(?:47|58|64|8[67])|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:769|979[2-69])|7468|8(?:3(?:8[78]|96[2457-9])|477|51[24]|636[2-57-9])|9(?:496|802|9(?:1[23]|69))"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1-$2-$3",
				[
					"1(?:2[3-6]|3[3-9]|4[2-6]|5[2-8]|[68][2-7]|7[2-689]|9[1-578])|2(?:2[03-689]|3[3-58]|4[0-468]|5[04-8]|6[013-8]|7[06-9]|8[02-57-9]|9[13])|4(?:2[28]|3[689]|6[035-7]|7[05689]|80|9[3-5])|5(?:3[1-36-9]|4[4578]|5[013-8]|6[1-9]|7[2-8]|8[14-7]|9[4-9])|7(?:2[15]|3[5-9]|4[02-9]|6[135-8]|7[0-4689]|9[014-9])|8(?:2[49]|3[3-8]|4[5-8]|5[2-9]|6[35-9]|7[579]|8[03-579]|9[2-8])|9(?:[23]0|4[02-46-9]|5[024-79]|6[4-9]|7[2-47-9]|8[02-7]|9[3-7])",
					"1(?:2[3-6]|3[3-9]|4[2-6]|5(?:[236-8]|[45][2-69])|[68][2-7]|7[2-689]|9[1-578])|2(?:2(?:[04-689]|3[23])|3[3-58]|4[0-468]|5(?:[0468][2-9]|5[78]|7[2-4])|6(?:[0135-8]|4[2-5])|7(?:[0679]|8[2-7])|8(?:[024578]|3[25-9]|9[6-9])|9(?:11|3[2-4]))|4(?:2(?:2[2-9]|8[237-9])|3[689]|6[035-7]|7(?:[059][2-8]|[68])|80|9[3-5])|5(?:3[1-36-9]|4[4578]|5[013-8]|6[1-9]|7[2-8]|8[14-7]|9(?:[4-7]|[89][2-8]))|7(?:2[15]|3[5-9]|4[02-9]|6[135-8]|7[0-4689]|9(?:[017-9]|4[6-8]|5[2-478]|6[2-589]))|8(?:2(?:4[4-8]|9[2-8])|3(?:[3-6][2-9]|7[2-6]|8[2-5])|4[5-8]|5[2-9]|6(?:[37]|5[4-7]|6[2-9]|8[2-8]|9[236-9])|7[579]|8[03-579]|9[2-8])|9(?:[23]0|4[02-46-9]|5[024-79]|6[4-9]|7[2-47-9]|8[02-7]|9(?:3[34]|4[2-69]|[5-7]))",
					"1(?:2[3-6]|3[3-9]|4[2-6]|5(?:[236-8]|[45][2-69])|[68][2-7]|7[2-689]|9[1-578])|2(?:2(?:[04-689]|3[23])|3[3-58]|4[0-468]|5(?:[0468][2-9]|5[78]|7[2-4])|6(?:[0135-8]|4[2-5])|7(?:[0679]|8[2-7])|8(?:[024578]|3[25-9]|9[6-9])|9(?:11|3[2-4]))|4(?:2(?:2[2-9]|8[237-9])|3[689]|6[035-7]|7(?:[059][2-8]|[68])|80|9[3-5])|5(?:3[1-36-9]|4[4578]|5[013-8]|6[1-9]|7[2-8]|8[14-7]|9(?:[4-7]|[89][2-8]))|7(?:2[15]|3[5-9]|4[02-9]|6[135-8]|7[0-4689]|9(?:[017-9]|4[6-8]|5[2-478]|6[2-589]))|8(?:2(?:4[4-8]|9(?:20|[3578]|4[04-9]|6[56]))|3(?:[3-6][2-9]|7(?:[2-5]|6[0-59])|8[2-5])|4[5-8]|5[2-9]|6(?:[37]|5(?:[467]|5[014-9])|6(?:[2-8]|9[02-69])|8[2-8]|9(?:[236-8]|9[23]))|7[579]|8[03-579]|9[2-8])|9(?:[23]0|4[02-46-9]|5[024-79]|6[4-9]|7[2-47-9]|8[02-7]|9(?:3(?:3[02-9]|4[0-24689])|4[2-69]|[5-7]))",
					"1(?:2[3-6]|3[3-9]|4[2-6]|5(?:[236-8]|[45][2-69])|[68][2-7]|7[2-689]|9[1-578])|2(?:2(?:[04-689]|3[23])|3[3-58]|4[0-468]|5(?:[0468][2-9]|5[78]|7[2-4])|6(?:[0135-8]|4[2-5])|7(?:[0679]|8[2-7])|8(?:[024578]|3[25-9]|9[6-9])|9(?:11|3[2-4]))|4(?:2(?:2[2-9]|8[237-9])|3[689]|6[035-7]|7(?:[059][2-8]|[68])|80|9[3-5])|5(?:3[1-36-9]|4[4578]|5[013-8]|6[1-9]|7[2-8]|8[14-7]|9(?:[4-7]|[89][2-8]))|7(?:2[15]|3[5-9]|4[02-9]|6[135-8]|7[0-4689]|9(?:[017-9]|4[6-8]|5[2-478]|6[2-589]))|8(?:2(?:4[4-8]|9(?:20|[3578]|4[04-9]|6(?:5[25]|60)))|3(?:[3-6][2-9]|7(?:[2-5]|6[0-59])|8[2-5])|4[5-8]|5[2-9]|6(?:[37]|5(?:[467]|5[014-9])|6(?:[2-8]|9[02-69])|8[2-8]|9(?:[236-8]|9[23]))|7[579]|8[03-579]|9[2-8])|9(?:[23]0|4[02-46-9]|5[024-79]|6[4-9]|7[2-47-9]|8[02-7]|9(?:3(?:3[02-9]|4[0-24689])|4[2-69]|[5-7]))"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"1|2(?:2[37]|5[5-9]|64|78|8[39]|91)|4(?:2[2689]|64|7[347])|5[2-589]|60|8(?:2[124589]|3[279]|[46-9])|9(?:[235-8]|93)",
					"1|2(?:2[37]|5(?:[57]|[68]0|9[19])|64|78|8[39]|917)|4(?:2(?:20|[68]|9[178])|64|7[347])|5[2-589]|60|8(?:2[124589]|3[279]|[46-9])|9(?:[235-8]|93[34])",
					"1|2(?:2[37]|5(?:[57]|[68]0|9(?:17|99))|64|78|8[39]|917)|4(?:2(?:20|[68]|9[178])|64|7[347])|5[2-589]|60|8(?:2[124589]|3[279]|[46-9])|9(?:[235-8]|93(?:31|4))"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1-$2-$3",
				[
					"2(?:[34]7|[56]9|74|9[14-79])|82|993"
				]
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"3|4(?:2[09]|7[01])|6[1-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[2479][1-9]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:1[235-8]|2[3-6]|3[3-9]|4[2-6]|[58][2-8]|6[2-7]|7[2-9]|9[1-9])|2[2-9]\\d|[36][1-9]\\d|4(?:6[02-8]|[2-578]\\d|9[2-59])|5(?:6[1-9]|7[2-8]|[2-589]\\d)|7(?:3[4-9]|4[02-9]|[25-9]\\d)|8(?:3[2-9]|4[5-9]|5[1-9]|8[03-9]|[2679]\\d)|9(?:[679][1-9]|[2-58]\\d))\\d{6}",
				[
					9
				]
			],
			[
				"[7-9]0[1-9]\\d{7}",
				[
					10
				]
			],
			[
				"120\\d{6}|800\\d{7}|00(?:37\\d{6,13}|66\\d{6,13}|777(?:[01]\\d{2}|5\\d{3}|8\\d{4})|882[1245]\\d{4})"
			],
			[
				"990\\d{6}",
				[
					9
				]
			],
			[
				"60\\d{7}",
				[
					9
				]
			],
			0,
			[
				"570\\d{6}",
				[
					9
				]
			],
			[
				"20\\d{8}",
				[
					10
				]
			],
			[
				"50[1-9]\\d{7}",
				[
					10
				]
			]
		]
	],
	KE: [
		"254",
		"000",
		"20\\d{6,7}|[4-9]\\d{6,9}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5,7})",
				"$1 $2",
				[
					"[24-6]"
				]
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"7"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		],
		"0",
		"0$1",
		"005|0",
		0,
		0,
		0,
		[
			[
				"20\\d{6,7}|4(?:0\\d{6,7}|[136]\\d{7}|[245]\\d{5,7})|5(?:[08]\\d{7}|[1-79]\\d{5,7})|6(?:[01457-9]\\d{5,7}|2\\d{7}|6\\d{6,7})",
				[
					7,
					8,
					9
				]
			],
			[
				"7(?:[0-3679]\\d|4[0-9]|5[0-7]|8[0-25-9])\\d{6}",
				[
					9
				]
			],
			[
				"800[24-8]\\d{5,6}",
				[
					9,
					10
				]
			],
			[
				"900[02-9]\\d{5}",
				[
					9
				]
			]
		]
	],
	KG: [
		"996",
		"00",
		"[235-9]\\d{8,9}",
		[
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[25-79]|31[25]"
				]
			],
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"3(?:1[36]|[2-9])"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d)(\\d{3})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:1(?:[256]\\d|3[1-9]|47)|2(?:22|3[0-479]|6[0-7])|4(?:22|5[6-9]|6\\d)|5(?:22|3[4-7]|59|6\\d)|6(?:22|5[35-7]|6\\d)|7(?:22|3[468]|4[1-9]|59|[67]\\d)|9(?:22|4[1-8]|6\\d))|6(?:09|12|2[2-4])\\d)\\d{5}",
				[
					9
				]
			],
			[
				"(?:2(?:0[0-35]|2\\d)|5[0-24-7]\\d|7(?:[07]\\d|55)|99[69])\\d{6}",
				[
					9
				]
			],
			[
				"800\\d{6,7}"
			]
		]
	],
	KH: [
		"855",
		"00[14-9]",
		"[1-9]\\d{7,9}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1\\d[1-9]|[2-9]"
				],
				"0$1"
			],
			[
				"(1[89]00)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[89]0",
					"1[89]00"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[3-6]|3[2-6]|4[2-4]|[5-7][2-5])(?:[237-9]|4[56]|5\\d|6\\d?)\\d{5}|23(?:4[234]|8\\d{2})\\d{4}",
				[
					8,
					9
				]
			],
			[
				"(?:1(?:[013-79]\\d|[28]\\d{1,2})|2[3-6]48|3(?:[18]\\d{2}|[2-6]48)|4[2-4]48|5[2-5]48|6(?:[016-9]\\d|[2-5]48)|7(?:[07-9]\\d|[16]\\d{2}|[2-5]48)|8(?:[013-79]\\d|8\\d{2})|9(?:6\\d{2}|7\\d{1,2}|[0-589]\\d))\\d{5}",
				[
					8,
					9
				]
			],
			[
				"1800(?:1\\d|2[019])\\d{4}",
				[
					10
				]
			],
			[
				"1900(?:1\\d|2[09])\\d{4}",
				[
					10
				]
			]
		]
	],
	KI: [
		"686",
		"00",
		"[2458]\\d{4}|3\\d{4,7}|[67]\\d{7}",
		[
			5,
			8
		],
		0,
		0,
		0,
		"0",
		0,
		0,
		0,
		[
			[
				"(?:[24]\\d|3[1-9]|50|8[0-5])\\d{3}|(?:65(?:02[12]|12[56]|22[89]|[3-5]00)|7(?:27\\d{2}|3100|5(?:02[12]|12[56]|22[89]|[34](?:00|81)|500)))\\d{3}"
			],
			[
				"(?:6(?:200[01]|30[01]\\d)|7(?:200[01]|3(?:0[0-5]\\d|140)))\\d{3}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"30(?:0[01]\\d{2}|12(?:11|20))\\d{2}",
				[
					8
				]
			]
		]
	],
	KM: [
		"269",
		"00",
		"[3478]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"7[4-7]\\d{5}"
			],
			[
				"[34]\\d{6}"
			],
			0,
			[
				"8\\d{6}"
			]
		]
	],
	KN: [
		"1",
		"011",
		"[589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"869",
		[
			[
				"869(?:2(?:29|36)|302|4(?:6[015-9]|70))\\d{4}"
			],
			[
				"869(?:5(?:5[6-8]|6[5-7])|66\\d|76[02-7])\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	KP: [
		"850",
		"00|99",
		"1\\d{9}|[28]\\d{7}",
		[
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}|85\\d{6}",
				[
					8
				]
			],
			[
				"19[123]\\d{7}",
				[
					10
				]
			]
		]
	],
	KR: [
		"82",
		"00(?:[1259]|3(?:[46]5|91)|7(?:00|27|3|55|6[126]))",
		"00(?:3\\d{8,9}|7\\d{9,11})|[1-7]\\d{4,9}|8\\d{8}",
		[
			5,
			6,
			8,
			9,
			10,
			11,
			12,
			13,
			14
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1-$2",
				[
					"(?:3[1-3]|[46][1-4]|5[1-5])1"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"1(?:5[246-9]|6[046-8]|8[03579])",
					"1(?:5(?:22|44|66|77|88|99)|6(?:[07]0|44|6[16]|88)|8(?:00|33|55|77|99))"
				],
				"$1"
			],
			[
				"(\\d{5})",
				"$1",
				[
					"1[016-9]1",
					"1[016-9]11",
					"1[016-9]114"
				]
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1-$2-$3",
				[
					"2[1-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"60[2-9]|80"
				]
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1-$2-$3",
				[
					"1[0-25-9]|(?:3[1-3]|[46][1-4]|5[1-5])[1-9]"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[57]0"
				]
			]
		],
		"0",
		"0$1",
		"0(8[1-46-8]|85\\d{2})?",
		0,
		0,
		0,
		[
			[
				"2[1-9]\\d{6,7}|(?:3[1-3]|[46][1-4]|5[1-5])(?:1\\d{2,3}|[1-9]\\d{6,7})",
				[
					5,
					6,
					8,
					9,
					10
				]
			],
			[
				"1[0-26-9]\\d{7,8}",
				[
					9,
					10
				]
			],
			[
				"(?:00(?:3(?:08|68\\d)|798\\d{1,3})|80\\d)\\d{6}",
				[
					9,
					11,
					12,
					13,
					14
				]
			],
			[
				"60[2-9]\\d{6}",
				[
					9
				]
			],
			[
				"50\\d{8}",
				[
					10
				]
			],
			0,
			[
				"1(?:5(?:22|44|66|77|88|99)|6(?:00|44|6[16]|70|88)|8(?:00|33|55|77|99))\\d{4}",
				[
					8
				]
			],
			[
				"15\\d{7,8}",
				[
					9,
					10
				]
			],
			[
				"70\\d{8}",
				[
					10
				]
			]
		]
	],
	KW: [
		"965",
		"00",
		"[12569]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{4})(\\d{3,4})",
				"$1 $2",
				[
					"[16]|2(?:[0-35-9]|4[0-35-9])|52[25]|9[0-24-9]"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"244|5(?:[015]|6[56])"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:18\\d|2(?:[23]\\d{2}|4(?:[1-35-9]\\d|44)|5(?:0[034]|[2-46]\\d|5[1-3]|7[1-7])))\\d{4}"
			],
			[
				"(?:5(?:[05]\\d{2}|1[0-7]\\d|2(?:22|5[25])|6[56]\\d)|6(?:0[034679]\\d|222|5[015-9]\\d|6\\d{2}|7(?:0[013-9]|[67]\\d)|9(?:[069]\\d|3[039]))|9(?:0[09]\\d|11[01]|22\\d|4[01479]\\d|55\\d|6[0679]\\d|7(?:02|[1-9]\\d)|8[057-9]\\d|9\\d{2}))\\d{4}",
				[
					8
				]
			]
		]
	],
	KY: [
		"1",
		"011",
		"[3589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"345",
		[
			[
				"345(?:2(?:22|44)|444|6(?:23|38|40)|7(?:4[35-79]|6[6-9]|77)|8(?:00|1[45]|25|[48]8)|9(?:14|4[035-9]))\\d{4}"
			],
			[
				"345(?:32[1-9]|5(?:1[67]|2[5-79]|4[6-9]|50|76)|649|9(?:1[67]|2[2-9]|3[689]))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}|345976\\d{4}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			],
			0,
			0,
			[
				"345849\\d{4}"
			]
		]
	],
	KZ: [
		"7",
		"810",
		"(?:33\\d|7\\d{2}|80[089])\\d{7}",
		[
			10
		],
		[
			[
				"([3489]\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[3489]"
				]
			],
			[
				"(7\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			]
		],
		"8",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"33622\\d{5}|7(?:1(?:0(?:[23]\\d|4[0-3]|59|63)|1(?:[23]\\d|4[0-79]|59)|2(?:[23]\\d|59)|3(?:2\\d|3[0-79]|4[0-35-9]|59)|4(?:[24]\\d|3[013-9]|5[1-9])|5(?:2\\d|3[1-9]|4[0-7]|59)|6(?:[234]\\d|5[19]|61)|72\\d|8(?:[27]\\d|3[1-46-9]|4[0-5]))|2(?:1(?:[23]\\d|4[46-9]|5[3469])|2(?:2\\d|3[0679]|46|5[12679])|3(?:[234]\\d|5[139])|4(?:2\\d|3[1235-9]|59)|5(?:[23]\\d|4[01246-8]|59|61)|6(?:2\\d|3[1-9]|4[0-4]|59)|7(?:[2379]\\d|40|5[279])|8(?:[23]\\d|4[0-3]|59)|9(?:2\\d|3[124578]|59)))\\d{5}"
			],
			[
				"7(?:0[012578]|47|6[02-4]|7[15-8]|85)\\d{7}"
			],
			[
				"800\\d{7}"
			],
			[
				"809\\d{7}"
			],
			[
				"808\\d{7}"
			],
			0,
			0,
			0,
			[
				"751\\d{7}"
			]
		],
		"8~10"
	],
	LA: [
		"856",
		"00",
		"[2-8]\\d{7,9}",
		[
			8,
			9,
			10
		],
		[
			[
				"(20)(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"20"
				]
			],
			[
				"([2-8]\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2[13]|3[14]|[4-8]"
				]
			],
			[
				"(30)(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"30"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[13]|3(?:0\\d|[14])|[5-7][14]|41|8[1468])\\d{6}",
				[
					8,
					9
				]
			],
			[
				"20(?:2[2389]|5[24-689]|7[6-8]|9[1-35-9])\\d{6}",
				[
					10
				]
			]
		]
	],
	LB: [
		"961",
		"00",
		"[13-9]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[13-69]|7(?:[2-57]|62|8[0-7]|9[04-9])|8[02-9]"
				],
				"0$1"
			],
			[
				"([7-9]\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7(?:[01]|6[013-9]|8[89]|9[1-3])|[89][01]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[14-6]\\d{2}|7(?:[2-57]\\d|62|8[0-7]|9[04-9])|8[02-9]\\d|9\\d{2})\\d{4}",
				[
					7
				]
			],
			[
				"(?:3\\d|7(?:[01]\\d|6[013-9]|8[89]|9[1-3])|81\\d)\\d{5}"
			],
			0,
			[
				"9[01]\\d{6}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"80\\d{6}",
				[
					8
				]
			]
		]
	],
	LC: [
		"1",
		"011",
		"[5789]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"758",
		[
			[
				"758(?:4(?:30|5[0-9]|6[2-9]|8[0-2])|57[0-2]|638)\\d{4}"
			],
			[
				"758(?:28[4-7]|384|4(?:6[01]|8[4-9])|5(?:1[89]|20|84)|7(?:1[2-9]|2\\d|3[01]))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	LI: [
		"423",
		"00",
		"6\\d{8}|[23789]\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[237-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[56]"
				]
			],
			[
				"(69)(7\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"697"
				]
			]
		],
		"0",
		0,
		"0|10(?:01|20|66)",
		0,
		0,
		0,
		[
			[
				"(?:2(?:01|1[27]|3\\d|6[02-578]|96)|3(?:7[0135-7]|8[048]|9[0269]))\\d{4}",
				[
					7
				]
			],
			[
				"6(?:5(?:09|1\\d|20)|6(?:0[0-6]|10|2[06-9]|39))\\d{5}|7(?:[37-9]\\d|42|56)\\d{4}"
			],
			[
				"80(?:02[28]|9\\d{2})\\d{2}",
				[
					7
				]
			],
			[
				"90(?:02[258]|1(?:23|3[14])|66[136])\\d{2}",
				[
					7
				]
			],
			0,
			0,
			[
				"870(?:28|87)\\d{2}",
				[
					7
				]
			]
		]
	],
	LK: [
		"94",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[1-689]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:1[2-57]\\d{6}|973\\d{5})|(?:2[13-7]|3[1-8]|4[157]|5[12457]|6[35-7]|[89]1)[2-57]\\d{6}"
			],
			[
				"7[0125-8]\\d{7}"
			]
		]
	],
	LR: [
		"231",
		"00",
		"2\\d{7,8}|[378]\\d{8}|4\\d{6}|5\\d{6,8}",
		[
			7,
			8,
			9
		],
		[
			[
				"(2\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"([4-5])(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[45]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[23578]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d{3}|33333)\\d{4}",
				[
					8,
					9
				]
			],
			[
				"(?:20\\d{2}|330\\d|4[67]|5(?:55)?\\d|77\\d{2}|88\\d{2})\\d{5}",
				[
					7,
					9
				]
			],
			0,
			[
				"332(?:02|[2-5]\\d)\\d{4}",
				[
					9
				]
			]
		]
	],
	LS: [
		"266",
		"00",
		"[2568]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}"
			],
			[
				"[56]\\d{7}"
			],
			[
				"800[256]\\d{4}"
			]
		]
	],
	LT: [
		"370",
		"00",
		"[3-9]\\d{7}",
		[
			8
		],
		[
			[
				"([34]\\d)(\\d{6})",
				"$1 $2",
				[
					"37|4(?:1|5[45]|6[2-4])"
				]
			],
			[
				"([3-6]\\d{2})(\\d{5})",
				"$1 $2",
				[
					"3[148]|4(?:[24]|6[09])|528|6"
				]
			],
			[
				"([7-9]\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"8 $1"
			],
			[
				"(5)(2\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"52[0-79]"
				]
			]
		],
		"8",
		"(8-$1)",
		"[08]",
		0,
		1,
		0,
		[
			[
				"(?:3[1478]|4[124-6]|52)\\d{6}"
			],
			[
				"6\\d{7}"
			],
			[
				"800\\d{5}"
			],
			[
				"9(?:0[0239]|10)\\d{5}"
			],
			[
				"700\\d{5}"
			],
			0,
			[
				"70[67]\\d{5}"
			],
			0,
			0,
			[
				"808\\d{5}"
			]
		]
	],
	LU: [
		"352",
		"00",
		"[24-9]\\d{3,10}|3(?:[0-46-9]\\d{2,9}|5[013-9]\\d{1,8})",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"[2-5]|7[1-9]|[89](?:0[2-9]|[1-9])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[2-5]|7[1-9]|[89](?:0[2-9]|[1-9])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"20"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"20"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4 $5",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,4})",
				"$1 $2 $3 $4",
				[
					"2(?:[12589]|4[12])|[3-5]|7[1-9]|8(?:0[2-9]|[1-9])|9(?:0[2-46-9]|[1-9])"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"70|80[01]|90[015]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		0,
		0,
		"(15(?:0[06]|1[12]|35|4[04]|55|6[26]|77|88|99)\\d)",
		0,
		0,
		0,
		[
			[
				"(?:2[2-9]\\d{2,9}|(?:3(?:[0-46-9]\\d|5[013-9])|[457]\\d{2}|8(?:0[2-9]|[13-9]\\d)|9(?:0[89]|[2-579]\\d))\\d{1,8})"
			],
			[
				"6[25-79][18]\\d{6}",
				[
					9
				]
			],
			[
				"800\\d{5}",
				[
					8
				]
			],
			[
				"90[015]\\d{5}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			[
				"20(?:1\\d{5}|[2-689]\\d{1,7})",
				[
					4,
					5,
					6,
					7,
					8,
					9,
					10
				]
			],
			[
				"801\\d{5}",
				[
					8
				]
			]
		]
	],
	LV: [
		"371",
		"00",
		"[2689]\\d{7}",
		[
			8
		],
		[
			[
				"([2689]\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2689]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6\\d{7}"
			],
			[
				"2\\d{7}"
			],
			[
				"80\\d{6}"
			],
			[
				"90\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"81\\d{6}"
			]
		]
	],
	LY: [
		"218",
		"00",
		"[25679]\\d{8}",
		[
			9
		],
		[
			[
				"([25-79]\\d)(\\d{7})",
				"$1-$2",
				[
					"[25-79]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1345]|5[1347]|6[123479]|71)\\d{7}"
			],
			[
				"9[1-6]\\d{7}"
			]
		]
	],
	MA: [
		"212",
		"00",
		"[5-9]\\d{8}",
		[
			9
		],
		[
			[
				"([5-7]\\d{2})(\\d{6})",
				"$1-$2",
				[
					"5(?:2[015-7]|3[0-4])|[67]"
				]
			],
			[
				"([58]\\d{3})(\\d{5})",
				"$1-$2",
				[
					"5(?:2[2-489]|3[5-9]|92)|892",
					"5(?:2(?:[2-48]|9[0-7])|3(?:[5-79]|8[0-7])|924)|892"
				]
			],
			[
				"(5\\d{4})(\\d{4})",
				"$1-$2",
				[
					"5(?:29|38)",
					"5(?:29|38)[89]"
				]
			],
			[
				"([5]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5(?:4[067]|5[03])"
				]
			],
			[
				"(8[09])(\\d{7})",
				"$1-$2",
				[
					"8(?:0|9[013-9])"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"5(?:2(?:[015-79]\\d|2[02-9]|3[2-57]|4[2-8]|8[235-7])\\d|3(?:[0-48]\\d|[57][2-9]|6[2-8]|9[3-9])\\d|4[067]\\d{2}|5[03]\\d{2})\\d{4}"
			],
			[
				"(?:6(?:[0-79]\\d|8[0-247-9])|7(?:0[067]|6[1267]|7[017]))\\d{6}"
			],
			[
				"80\\d{7}"
			],
			[
				"89\\d{7}"
			],
			0,
			0,
			0,
			0,
			[
				"5924[01]\\d{4}"
			]
		]
	],
	MC: [
		"377",
		"00",
		"[34689]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[39]"
				],
				"$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"4"
				]
			],
			[
				"(6)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"6"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3",
				[
					"8"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"870\\d{5}|9[2-47-9]\\d{6}",
				[
					8
				]
			],
			[
				"3\\d{7}|4(?:4\\d|5[1-9])\\d{5}|6\\d{8}"
			],
			[
				"90\\d{6}",
				[
					8
				]
			]
		]
	],
	MD: [
		"373",
		"00",
		"[235-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"22|3"
				]
			],
			[
				"([25-7]\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"2[13-9]|[5-7]"
				]
			],
			[
				"([89]\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1-9]\\d|3[1-79]\\d|5(?:33|5[257]))\\d{5}"
			],
			[
				"(?:562|6\\d{2}|7(?:[189]\\d|6[07]|7[457-9]))\\d{5}"
			],
			[
				"800\\d{5}"
			],
			[
				"90[056]\\d{5}"
			],
			0,
			0,
			[
				"803\\d{5}"
			],
			0,
			[
				"3[08]\\d{6}"
			],
			[
				"808\\d{5}"
			]
		]
	],
	ME: [
		"382",
		"00",
		"[2-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-57-9]|6[036-9]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:20[2-8]|3(?:[0-2][2-7]|3[24-7])|4(?:0[2-467]|1[2467])|5(?:[01][2467]|2[2-467]))\\d{5}",
				[
					8
				]
			],
			[
				"6(?:00\\d|3[024]\\d|6[0-25]\\d|[7-9]\\d{2})\\d{4}",
				[
					8
				]
			],
			[
				"80(?:[0-2578]|9\\d)\\d{5}"
			],
			[
				"(?:9(?:4[1568]|5[178]))\\d{5}",
				[
					8
				]
			],
			0,
			0,
			[
				"77[1-9]\\d{5}",
				[
					8
				]
			],
			0,
			[
				"78[1-49]\\d{5}",
				[
					8
				]
			]
		]
	],
	MF: [
		"590",
		"00",
		"[56]\\d{8}",
		[
			9
		],
		[
			[
				"([56]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"590|69[01]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:0[079]|13|2[79]|30|43|5[0-268]|7[79]|87)\\d{4}"
			],
			[
				"69(?:0\\d{2}|1(?:2[29]|3[0-5]))\\d{4}"
			]
		]
	],
	MG: [
		"261",
		"00",
		"[23]\\d{8}",
		[
			9
		],
		[
			[
				"([23]\\d)(\\d{2})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[23]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"20(?:2\\d{2}|4[47]\\d|5[3467]\\d|6[279]\\d|7(?:2[29]|[35]\\d)|8[268]\\d|9[245]\\d)\\d{4}"
			],
			[
				"3[2-49]\\d{7}"
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"22\\d{7}"
			]
		]
	],
	MH: [
		"692",
		"011",
		"[2-6]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:247|528|625)\\d{4}"
			],
			[
				"(?:235|329|45[56]|545)\\d{4}"
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"635\\d{4}"
			]
		]
	],
	MK: [
		"389",
		"00",
		"[2-578]\\d{7}",
		[
			8
		],
		[
			[
				"(2)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"([347]\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[347]"
				]
			],
			[
				"([58]\\d{2})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[58]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[23]\\d|5[0-24578]|6[01]|82)|3(?:1[3-68]|[23][2-68]|4[23568])|4(?:[23][2-68]|4[3-68]|5[2568]|6[25-8]|7[24-68]|8[4-68]))\\d{5}"
			],
			[
				"7(?:[0-25-8]\\d{2}|3[2-4]\\d|421|9[23]\\d)\\d{4}"
			],
			[
				"800\\d{5}"
			],
			[
				"5[02-9]\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"8(?:0[1-9]|[1-9]\\d)\\d{5}"
			]
		]
	],
	ML: [
		"223",
		"00",
		"[24-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:0(?:2\\d|7[0-8])|1(?:2[67]|[4-689]\\d))|4(?:0[0-4]|4[1-39])\\d)\\d{4}"
			],
			[
				"(?:2(?:079|17\\d)|50\\d{2}|[679]\\d{3}|8[239]\\d{2})\\d{4}"
			],
			[
				"80\\d{6}"
			]
		]
	],
	MM: [
		"95",
		"00",
		"[178]\\d{5,7}|[24-6]\\d{5,8}|9(?:[279]\\d{0,2}|5|[34]\\d{1,2}|6(?:\\d{1,2})?|8(?:\\d{2})?)\\d{6}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1|2[245]"
				]
			],
			[
				"(2)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"251"
				]
			],
			[
				"(\\d)(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"16|2"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"432|67|81"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[4-8]"
				]
			],
			[
				"(9)(\\d{3})(\\d{4,6})",
				"$1 $2 $3",
				[
					"9(?:2[0-4]|[35-9]|4[137-9])"
				]
			],
			[
				"(9)([34]\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"9(?:3[0-36]|4[0-57-9])"
				]
			],
			[
				"(9)(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"92[56]"
				]
			],
			[
				"(9)(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"93"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:2\\d{1,2}|[35]\\d|4(?:\\d|2[236]|39)|6\\d?|[89][0-6]\\d)\\d{4}|2(?:2(?:000\\d{3}|\\d{4})|3\\d{4}|4(?:0\\d{5}|26\\d{4}|39\\d{4}|\\d{4})|5(?:1\\d{3,6}|[02-9]\\d{3,5})|[6-9]\\d{4})|4(?:2[245-8]|3(?:2(?:02)?|[346]|56?)|[46][2-6]|5[3-5])\\d{4}|5(?:2(?:2(?:\\d{1,2})?|[3-8])|3[2-68]|4(?:21?|[4-8])|5[23]|6[2-4]|7[2-8]|8[24-7]|9[2-7])\\d{4}|6(?:0[23]|1(?:2(?:0|4\\d)?|[356])|2[2-6]|3[24-6]|4(?:2(?:4\\d)?|[3-6])|5[2-4]|6[2-8]|7(?:[2367]|4(?:\\d|39)|5\\d?|8[145]\\d)|8[245]|9(?:20?|4))\\d{4}|7(?:[04][24-8]|1(?:20?|[3-7])|22|3[2-4]|5[2-7])\\d{4}|8(?:1(?:2\\d{1,2}|[3-689]\\d)|2(?:2\\d|3(?:\\d|20)|[4-8]\\d)|3[24]\\d|4[24-7]\\d|5[245]\\d|6[23]\\d)\\d{3}",
				[
					6,
					7,
					8,
					9
				]
			],
			[
				"17[01]\\d{4}|9(?:2(?:[0-4]|5\\d{2}|6[0-5]\\d)|3(?:[0-36]|4[069])\\d|4(?:0[0-4]\\d|[1379]\\d|2\\d{2}|4[0-589]\\d|5\\d{2}|88)|5[0-6]|6(?:1\\d|9\\d{2}|\\d)|7(?:3|5[0-2]|[6-9]\\d)\\d|8(?:\\d|9\\d{2})|9(?:1\\d|[5-7]\\d{2}|[089]))\\d{5}",
				[
					7,
					8,
					9,
					10
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"1333\\d{4}",
				[
					8
				]
			]
		]
	],
	MN: [
		"976",
		"001",
		"[12]\\d{7,9}|[57-9]\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"([12]\\d)(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[12]1"
				]
			],
			[
				"([12]2\\d)(\\d{5,6})",
				"$1 $2",
				[
					"[12]2[1-3]"
				]
			],
			[
				"([12]\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[12](?:27|[3-5])",
					"[12](?:27|[3-5]\\d)2"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[57-9]"
				],
				"$1"
			],
			[
				"([12]\\d{4})(\\d{4,5})",
				"$1 $2",
				[
					"[12](?:27|[3-5])",
					"[12](?:27|[3-5]\\d)[4-9]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"[12](?:1\\d|2(?:[1-3]\\d?|7\\d)|3[2-8]\\d{1,2}|4[2-68]\\d{1,2}|5[1-4689]\\d{1,2})\\d{5}|5[0568]\\d{6}"
			],
			[
				"(?:8(?:[05689]\\d|3[01])|9[013-9]\\d)\\d{5}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"7[05-8]\\d{6}",
				[
					8
				]
			]
		]
	],
	MO: [
		"853",
		"00",
		"[268]\\d{7}",
		[
			8
		],
		[
			[
				"([268]\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[268]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:28[2-57-9]|8(?:11|[2-57-9]\\d))\\d{5}"
			],
			[
				"6(?:[2356]\\d{2}|8(?:[02][5-9]|[1478]\\d|[356][0-4]))\\d{4}"
			]
		]
	],
	MP: [
		"1",
		"011",
		"[5689]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"670",
		[
			[
				"670(?:2(?:3[3-7]|56|8[5-8])|32[1238]|4(?:33|8[348])|5(?:32|55|88)|6(?:64|70|82)|78[3589]|8[3-9]8|989)\\d{4}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	MQ: [
		"596",
		"00",
		"[56]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"596(?:0[0-7]|10|2[7-9]|3[05-9]|4[0-46-8]|[5-7]\\d|8[09]|9[4-8])\\d{4}"
			],
			[
				"69(?:6(?:[0-47-9]\\d|5[0-6]|6[0-4])|727)\\d{4}"
			]
		]
	],
	MR: [
		"222",
		"00",
		"[2-48]\\d{7}",
		[
			8
		],
		[
			[
				"([2-48]\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-48]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"25[08]\\d{5}|35\\d{6}|45[1-7]\\d{5}"
			],
			[
				"[234][0-46-9]\\d{6}"
			],
			[
				"800\\d{5}"
			]
		]
	],
	MS: [
		"1",
		"011",
		"[5689]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"664",
		[
			[
				"664491\\d{4}"
			],
			[
				"66449[2-6]\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	MT: [
		"356",
		"00",
		"[2357-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0(?:[169]\\d|3[1-4])|[1-357]\\d{2})\\d{4}"
			],
			[
				"(?:7(?:210|[79]\\d{2})|9(?:2(?:1[01]|31)|69[67]|8(?:1[1-3]|89|97)|9\\d{2}))\\d{4}"
			],
			[
				"800[3467]\\d{4}"
			],
			[
				"5(?:0(?:0(?:37|43)|6\\d{2}|70\\d|9[0168]\\d)|[12]\\d0[1-5])\\d{3}"
			],
			0,
			0,
			[
				"501\\d{5}"
			],
			[
				"7117\\d{4}"
			],
			[
				"3550\\d{4}"
			]
		]
	],
	MU: [
		"230",
		"0(?:0|[2-7]0|33)",
		"[2-9]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"([2-46-9]\\d{2})(\\d{4})",
				"$1 $2",
				[
					"[2-46-9]"
				]
			],
			[
				"(5\\d{3})(\\d{4})",
				"$1 $2",
				[
					"5"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[03478]\\d|1[0-7]|6[1-69])|4(?:[013568]\\d|2[4-7])|5(?:44\\d|471)|6\\d{2}|8(?:14|3[129]))\\d{4}"
			],
			[
				"5(?:2[589]\\d|4(?:2[1-389]|[489]\\d|7[1-9])|7\\d{2}|8(?:[0-689]\\d|7[15-8])|9[0-8]\\d)\\d{4}",
				[
					8
				]
			],
			[
				"80[012]\\d{4}",
				[
					7
				]
			],
			[
				"30\\d{5}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			[
				"3(?:20|9\\d)\\d{4}",
				[
					7
				]
			]
		],
		"020"
	],
	MV: [
		"960",
		"0(?:0|19)",
		"[346-8]\\d{6,9}|9(?:00\\d{7}|\\d{6})",
		[
			7,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[3467]|9(?:0[1-9]|[1-9])"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]00"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:0[0-3]|3[0-59])|6(?:[57][02468]|6[024568]|8[024689]))\\d{4}",
				[
					7
				]
			],
			[
				"(?:46[46]|7[2-9]\\d|9[14-9]\\d)\\d{4}",
				[
					7
				]
			],
			[
				"800\\d{7}",
				[
					10
				]
			],
			[
				"900\\d{7}",
				[
					10
				]
			],
			0,
			0,
			[
				"4[05]0\\d{4}",
				[
					7
				]
			]
		],
		"00"
	],
	MW: [
		"265",
		"00",
		"(?:1(?:\\d{2})?|[2789]\\d{2}|31\\d)\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(2\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[17-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"31"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[2-9]|21\\d{2})\\d{5}"
			],
			[
				"(?:111|77\\d|88\\d|99\\d)\\d{6}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"31\\d{7}",
				[
					9
				]
			]
		]
	],
	MX: [
		"52",
		"0[09]",
		"[1-9]\\d{9,10}",
		[
			10,
			11
		],
		[
			[
				"([358]\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"33|55|81"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2467]|3[0-2457-9]|5[089]|8[02-9]|9[0-35-9]"
				]
			],
			[
				"(1)([358]\\d)(\\d{4})(\\d{4})",
				"044 $2 $3 $4",
				[
					"1(?:33|55|81)"
				],
				"$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(1)(\\d{3})(\\d{3})(\\d{4})",
				"044 $2 $3 $4",
				[
					"1(?:[2467]|3[0-2457-9]|5[089]|8[2-9]|9[1-35-9])"
				],
				"$1",
				0,
				"$1 $2 $3 $4"
			]
		],
		"01",
		"01 $1",
		"0[12]|04[45](\\d{10})",
		"1$1",
		1,
		0,
		[
			[
				"(?:33|55|81)\\d{8}|(?:2(?:0[01]|2[1-9]|3[1-35-8]|4[13-9]|7[1-689]|8[1-578]|9[467])|3(?:1[1-79]|[2458][1-9]|7[1-8]|9[1-5])|4(?:1[1-57-9]|[24-6][1-9]|[37][1-8]|8[1-35-9]|9[2-689])|5(?:88|9[1-79])|6(?:1[2-68]|[234][1-9]|5[1-3689]|6[12457-9]|7[1-7]|8[67]|9[4-8])|7(?:[13467][1-9]|2[1-9]|5[13-9]|8[1-69]|9[17])|8(?:2[13-689]|3[1-6]|4[124-6]|6[1246-9]|7[1-378]|9[12479])|9(?:1[346-9]|2[1-4]|3[2-46-8]|5[1348]|[69][1-9]|7[12]|8[1-8]))\\d{7}",
				[
					10
				]
			],
			[
				"1(?:(?:33|55|81)\\d{8}|(?:2(?:2[1-9]|3[1-35-8]|4[13-9]|7[1-689]|8[1-578]|9[467])|3(?:1[1-79]|[2458][1-9]|7[1-8]|9[1-5])|4(?:1[1-57-9]|[24-6][1-9]|[37][1-8]|8[1-35-9]|9[2-689])|5(?:88|9[1-79])|6(?:1[2-68]|[2-4][1-9]|5[1-3689]|6[12457-9]|7[1-7]|8[67]|9[4-8])|7(?:[13467][1-9]|2[1-9]|5[13-9]|8[1-69]|9[17])|8(?:2[13-689]|3[1-6]|4[124-6]|6[1246-9]|7[1-378]|9[12479])|9(?:1[346-9]|2[1-4]|3[2-46-8]|5[1348]|[69][1-9]|7[12]|8[1-8]))\\d{7})",
				[
					11
				]
			],
			[
				"8(?:00|88)\\d{7}",
				[
					10
				]
			],
			[
				"900\\d{7}",
				[
					10
				]
			],
			[
				"500\\d{7}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"300\\d{7}",
				[
					10
				]
			]
		]
	],
	MY: [
		"60",
		"00",
		"[13-9]\\d{7,9}",
		[
			8,
			9,
			10
		],
		[
			[
				"([4-79])(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"[4-79]"
				],
				"0$1"
			],
			[
				"(3)(\\d{4})(\\d{4})",
				"$1-$2 $3",
				[
					"3"
				],
				"0$1"
			],
			[
				"([18]\\d)(\\d{3})(\\d{3,4})",
				"$1-$2 $3",
				[
					"1[02-46-9][1-9]|8"
				],
				"0$1"
			],
			[
				"(1)([36-8]00)(\\d{2})(\\d{4})",
				"$1-$2-$3-$4",
				[
					"1[36-8]0",
					"1[36-8]00"
				]
			],
			[
				"(11)(\\d{4})(\\d{4})",
				"$1-$2 $3",
				[
					"11"
				],
				"0$1"
			],
			[
				"(15[49])(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"15[49]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:2(?:0(?:11|2[0-8]|3[0-24-79]|[579]\\d|8[0-57-9])|1(?:0[6-9]|1[03-9]|[467]\\d|8[0-2]|9[13])|2(?:0[1-3]|2[2-4]|4[0-25-7]|6[0-8]|7[02-689]|8\\d|9[5-9])|3(?:0[0-3]|3[0-36]|8[0-35-9]|9[1-3])|6(?:0[0-5]|[139]\\d|8[15-8])|7(?:1\\d|2[0-7]|3[0-3]|42|7[0-356]|8[0-35-8])|8(?:00|4[78]|5[6-9])|93[58])|3(?:0(?:0[0-35-9]|10|5[01]|8[0-6]|9[39])|1(?:0[1-39]|10|2[02-469]|3[245]|4[0-3589]|6[0-35-9]|7[067]|8[0-57]|9[0-278])|2(?:1[3467]|2[45]|4[1-3]|5[0689]|6[04589]|7[0-3579]|8[0-489]|9[0-27])|3(?:1[7-9]|2[1-6]|4[1-689]|5[89]|6[1-3]|7\\d|8[125]|9[236])|6(?:[12]0|3[01]|5[0-256]|6[08]|8[45]))|4(?:0(?:09|[14]\\d|2[0-8]|3[12]|5[01]|65)|1(?:0[0-8]|31|4\\d|6[12])|2(?:17|5[0-35-9]|6[024-6]|7[0189]|8[057-9]|9\\d)|770|8(?:0[0-2]|1[0-3569]|2[01]))|5(?:0(?:2[0-2]|3[0-35-9])|1(?:0[1-3]|1[05]|2[0-6]|31|6[1-7]|9[1258])|270|48[0-5]|5(?:1\\d|2[0-6]|4[0-589]|6[125-9]|9[01])|6(?:1[1-4]|2[0-589]|3\\d|5[0-3])|7(?:21|40)|8(?:7[06-9]|8[025-9]|9[12]))|6(?:0(?:2[0-28]|3[24-9]|4[246-9]|5[57]|6[13-6]|7[358]|8[1247]|9[0-59])|1(?:[04][0-58]|15|2[02-8]|3[015-9]|5[016-8]|7[7-9]|8[14-9]|9[1-356])|2(?:[05]\\d|11|[46][1-3]|7[02-79]|8[015-7])|303|4(?:1[129]|2[01]|3[36]|6[0-2])|690|7(?:3\\d|89))|7(?:118|2(?:0[02]|6[01])|4(?:[59]\\d|8[01])|6(?:1[01]|2\\d|5[0-2]|6[0-69])|7(?:1[0-37-9]|[28]\\d|3[1-5]|7[0-3])|8(?:[04]\\d|3[129]|59|6[1-356]|7[02-79]|8[02-7]|90)|9(?:10|3[1-3]|4[016-9]|[56]\\d|7[12]|8\\d))|8(?:0(?:0[08]|11|2[0-7]|5[12]|6\\d|7[013-69]|8[0-24]|9[0-5])|100|21[0-36]|3(?:08|1\\d|2[0-589]|31)|408|5(?:2[0-35-79]|88)|6(?:0[0-259]|5[5-79]|6[01]|8[46-9]|9[019])|7(?:0[35-8]|2[3-7]|3\\d|4[0149]|5[0-2]|6[016-9]|7[5-9]|8[037]|90)|8(?:00|61|7[0-3]|8\\d|9[0-4])|9(?:1[1-3]|[2-5]\\d|6[1-4689]|8[89]|9[0-689]))|9(?:0(?:1[0-289]|2[15]|5[014-9]|7[04-68]|8[0-256])|1(?:0[0-3568]|3[0-49]|4[015]|7[0-59]|9[15])|2(?:0[0-25-7]|12|2[1-6]|3[1256]|7[14]|8\\d)|3[68]8|41[01]|5(?:14|2[01]|4[03-9]|6[5-79]|7[0-2569])))\\d|4(?:2(?:0[01]|1[07-9]|2[02-46-9]|38|4[0-28]|5[0-589]|6[0-47-9]|8[0-389]|9[0-38])\\d|3(?:0[5-9]|[12][034]|3[0-37]|47|5[168]|6[7-9]|7[0135-8]|8\\d|9[0-27-9])\\d|4(?:[02-9]\\d{2}|1(?:[02-9]\\d|10))|5(?:[07]\\d{2}|1[0-358]\\d|2[1246]\\d|3[06-9]\\d|4[0589]\\d|5[12457-9]\\d|6[0-689]\\d|8(?:[0-57-9]\\d|60)|9(?:[2-57-9]\\d|60))|6(?:0[79]|1[0-689]|2[0135-9]|[3-5]\\d|6[07-9]|7[23]|8[03-9]|99)\\d|7(?:0[0-59]\\d|1[04679]\\d|2(?:[02-9]\\d|10)|[37]\\d{2}|4(?:[0178]\\d|5[0-6])|5(?:[0-57-9]\\d|60)|6[024-689]\\d|8[0-79]\\d|9(?:[0479]\\d|3[01]))|8(?:1[0189]\\d|2[046-9]\\d|3[058]\\d|5[029]\\d|6(?:[0689]\\d|40)|8[14568]\\d|9[0-389]\\d)|9(?:0[1368]\\d|1[13-9]\\d|[27]\\d{2}|3(?:[0-368]\\d|7[0-3])|4[013-79]\\d|5[0-35689]\\d|6[0-46-9]\\d|8[0-24-9]\\d|97\\d))|5(?:2(?:0[13589]\\d|1[0-2]\\d|2[056]\\d|3(?:[067]\\d|2[01])|4[0-69]\\d|5[3-6]\\d|8[0-25-9]\\d|9[0-469]\\d)|3(?:[12][0-38]|3[0-37]|4[89]|5[01346-9]|[67]\\d|8[01589]|95)\\d|4(?:0[1346]\\d|1[02-35-9]\\d|2[1379]\\d|3[0-46-9]\\d|4[013689]\\d|[59]\\d{2}|6(?:[15-9]\\d|20)|7[036-9]\\d|8[015-8]\\d)|5(?:0[167]\\d|17\\d|2[025-9]\\d|3[035-7]\\d|4[0-35-9]\\d|8[13]\\d|9(?:[124-9]\\d|31))|6(?:0[89]\\d|1[02-46-8]\\d|2[0-69]\\d|3(?:[1-367]\\d|40)|4[0-689]\\d|5(?:[0-24-9]\\d|30)|6[1457-9]\\d|7\\d{2}|8[0-57-9]\\d|9[0-389]\\d)|7(?:1(?:[024679]\\d|80)|2[0-35-8]\\d|3[23]\\d|4[34]\\d|5[0-36-9]\\d|6[356-9]\\d|7\\d{2}|8(?:[23]\\d|[01]1)|9(?:[1-689]\\d|70))|8(?:0[145-9]\\d|1[0-2]\\d|2[09]\\d|3(?:[058]\\d|0[017])|4[016-9]\\d|5[02-9]\\d|7(?:10|2\\d)|8[013589]\\d|9[0-57-9]\\d)|9[23]0\\d)|6(?:2(?:2[1246-9]\\d|3[0-5]\\d|4[02]\\d|5[1-3]\\d|[68]\\d{2}|7(?:[04-8]\\d|10)|9[2-5]\\d)|3(?:1(?:[125-9]\\d|3[01])|3[0-7]\\d|5[0-478]\\d|79\\d|8[3-578]\\d|9[018]\\d)|4(?:1[015]|[2-689]\\d|7[1-79])\\d|5(?:09\\d|1(?:10|[2479]\\d)|2[1-579]\\d|3(?:[0-467]\\d|50)|4[0-24-9]\\d|5\\d{2}|60\\d)|6(?:0[13]\\d|1[0-57]\\d|2[03]\\d|3[0-478]\\d|4[0-8]\\d|5[013-689]\\d|6[0-467]\\d|[78]\\d{2}|9(?:[13-9]\\d|20))|7(?:01|2[06-9]|3[367]|5[7-9]|6\\d|7[0156]|8[12]|9[0-47-9])\\d|85[0-26]\\d|9(?:0[167]|10|[57]\\d|6[02-9]|8[013-9])\\d)|7(?:2(?:0[7-9]|1[016-9]|2[0-8]|[39]\\d|4[014]|5[0-79]|6[125-8]|7[1246-9]|8[37-9])\\d|3(?:0[0-5]|[35]\\d|40|5[0-478]|6[0-5]|8[0-26-8])\\d|4(?:0[07-9]|[1-35]\\d|6[7-9]|7[57]|8[1-68])\\d|5(?:0[04-9]|1[0-68]|2[0-378]|3[0-3]|5\\d|6[0-2]|7[018]|8[5-7]|9[05-9])\\d|6(?:5[0-24-9]|6[0-3568]|7[0457-9]|8[02-9]|9\\d)\\d|7(?:0[25-8]\\d|1(?:[01]\\d|20)|2[0-2]\\d|3[489]\\d|[457-9]\\d{2}|6(?:[0-8]\\d|90))|8(?:01[01]|10\\d|[289]\\d{2}|3(?:[028]\\d|10)|50\\d|6[0-357-9]\\d|7(?:[0235-9]\\d|[14]0))|9(?:0(?:[0246-8]\\d|10)|[1-4]\\d{2}|50\\d|6[014589]\\d|[7-9]0\\d))|8(?:2(?:2(?:[013-5]\\d|[28][0-8]|6[016]|7[0-589]|9[0-49])|3(?:[02][0-689]|1[1-59]|[3-7]\\d|8[0-389]|9[13])|4(?:0[1-3]|[1-8]\\d|9[1-359])|5(?:0[1-7]|[17]\\d|20|3[0-26-9]|4[1-9]|5[0-35]|8[0-689]|9[02367])|6(?:[147]\\d|2[0-25-9]|39|5[014-9]|6[1-9]|8[1-8]|9[089])|7(?:0[017-9]|1[1-356]|20|3[01457-9]|4[15]|5[0-25]|6[0-6]|70|8[0-2]|9[01378])|8(?:0[3-57-9]|[15]0|2[124-9]|3[01]|4[02-6]|6[02-578]|7\\d|8[25-8]|9[04-689]))|3(?:220|3(?:0[089]|10|2[0-7]|3[7-9]|4[0-48])|4(?:0[68]|1[0-478]|2[5-79]|3[024-9]|50|6\\d|7[0-57]|8[05]|96)|5(?:4[4578]|55|6[2-9]|70|8[459]|92))|4(?:2(?:[15]\\d|2[0-489]|3[124-9])|3(?:0[1-8]|[1-4]\\d|5[0-3]|6[1-7]|7[56]|8[0-4]|9[1-578])|4(?:05|80)|555|6(?:1[0-3]|20|3[1-4]|4[0-24-7]|5\\d|6[0-2]|71|8[0-47-9]|9[0-689])|7(02|1[79]|21|3[0-2468]|5[0-5]|6[0256]|7[0-8]|8[01589]|9[026-9])|8(?:0[1-9]|1[0-3568]|2[0138]|3[0-4]|4[046-9]|5[06]|6\\d|7[0-579]|80|9[0-8]))|5(?:2(?:0[0-26]|1\\d|2[0-5]|3[18]|4[0-2]|6[3-68]|70|8[025-9]|9[05-9])|3(?:10|2\\d|31|95)|4(?:0[1-7]|[1-4]\\d|5[02-6]|6[0-3]|7[0-245]|8[125]|9[1-6])|5(?:40|55)|6(?:0[1459]|1[0-8]|2[0-36-8]|3[0-35-8]|[45]\\d|6[0-8]|80)|7(?:1[07-9]|20|3[3-9]|4[02-4]|5[05689]|6[015-9]|7\\d|8[045]|9[0-6]))|6(?:2(?:01|3[12]|5[1-7]|60|8[1-6]|9[0-8])|3(?:0[0137]|[134]\\d|2[0-8]|[59][01]|60|[78]1)|4(?:2[2-4]|49|5[17]|6\\d|7[0-46-9]|8[0-689])|5(?:0[6-8]|55|8[0149]|90)|8(?:5\\d|[68]0)|9(?:5[12]|81))|7(?:2(?:0[189]|[12][1-9]|3[0-47]|50)|3(?:[01][1-9]|3\\d|4[0-245]|6[4-9]|7[237-9]|9[12])|4(?:[0-2]\\d|3[167-9]|[45][0-3]|6[0-8]|7[25-9]|8[0-6]|90)|5(?:0[1-9]|[89]\\d)|7(?:1[89]|[27]\\d|3[013-9]|4[0-8]|50|6[01568]|82)|8(?:[03][1-3]|1[0-28]|2[0-38]|48|5[0-4]|6[1-3]|7[124]|8[013-57-9]|9[07]))|8(?:2(?:0[13-7]|[1-9]\\d)|3(?:0[1-47-9]|[128]\\d|3[0-4]|4[1-9]|5[0-35-7]|6[0-46-9]|9[3-7])|4(?:0[1-58]|1[1-5]|[2-57-9]\\d|69)|5(?:1[1-35-9]|2\\d|3[2-8]|52)|6(?:0[2-9]|1[1-8]|[23]\\d|41|5[12367]|6[1-9]|7[14-9]|8[02-8]|9[05])|7(?:0[1-589]|[1-35-8]\\d|4[0-7]|9[0-46-9])|8(?:01|30|5[0-48]|6[13]|7\\d|8[013-689]|9[24-9])|9(?:0[2-4689]|1\\d|2[08]|3[18]|4[03]|59|6[0-36-9]|7[0-35-79]|8[1235]))|9(?:2(?:[057][1-9]|[1-3]\\d|4[0-8]|6[1-5]|8[015-9]|9[04-6])|300|4(?:01|36|6[1-6]|[89][12])|5(?:0[25689]|1\\d|2[0134]|3[1-8]|5[17]|6[0-35-9]|7[0-7]|91)|6(?:1[1-9]|2[23]|3[0-4]|6[03-9]|7[0-7])|7(?:0[1-68]|1[1-8]|2[25-9]|3[056]|4[4-9]|[5-8]\\d|9[0-46-9])|8(?:0[1-7]|[189]\\d|2[1-46]|4[1-689]|5[0-79]|6[1-9]|71)|9(?:0[1-3]|1\\d|2[0-35-7]|3[67]|4[12]|5[0-249]|6[5-9]|75|8[1-6])))|9(?:2(?:0[458]\\d|1[2-57]\\d|2[0-589]\\d|[39]\\d{2}|4(?:[02-9]\\d|10)|5(?:[024-6]\\d|[7-9]0)|6(?:[03-9]\\d|20)|7[0-26-9]\\d|8(?:[0-24-68]\\d|7[01]))|3(?:0[0-2689]\\d|1(?:[0235]\\d|1[0-2]|4[01])|2(?:0[01]|[1-57-9]\\d|6[0-2])|3(?:00|1[0-4]|2\\d)|4[05]\\d|5[0-3568]\\d|6(?:[1-357-9]\\d|60)|[78]0\\d)|4(?:0[24578]\\d|1[02-57-9]\\d|2\\d{2}|3(?:[0278]\\d|1[01]|60)|4[013579]\\d|5[0-8]\\d|6[0678]\\d|7[013-9]\\d|8[01569]\\d)|5(0(?:[589]\\d|7[01])|1\\d{2}|2[0159]\\d|3[14689]\\d|4[0-46-9]\\d|5[0-35-9]\\d|6[0156-8]\\d|7[0-35]\\d|8[0-7]\\d|9[0-3589]\\d)|6(?:0[269]\\d|[12]\\d{2}|3[0-57-9]\\d|44[0-2]|5[01379]\\d|6[02-9]\\d|7[69]\\d|8(?:[0-24-8]\\d|30)|9(?:[0-24-9]\\d|3[01]))|7(?:0[0136-8]|1[02-4]|2[156]|3[258]|[489]\\d|5[0-589]|6[024-9]|7[0-689])\\d|8(?:0(?:0[01]|1\\d)|1[3-9]\\d|[23]\\d{2}|4[0-24-689]\\d|5[0-689]\\d|6[02-9]\\d|7(?:[01]0|[2-4]\\d)|888)|9(?:00\\d|1[2-57-9]\\d|2(?:00|1[01]|[238]\\d)|3(?:[2-467]\\d|50)|4[4-9]\\d|5(?:[0-2457-9]\\d|60)|6[0156]\\d|7[04-9]\\d|8[01]\\d)))\\d{3}",
				[
					8,
					9
				]
			],
			[
				"1(?:0(?:[23568]\\d|4[0-6]|7[016-9]|9[0-8])\\d|1(?:[1-5]\\d{2}|6(?:0[5-9]|[1-9]\\d))\\d|[23679][2-9]\\d{2}|4(?:[235-9]\\d{2}|400)|59\\d{3}|8(?:1[23]\\d|[236]\\d{2}|4(?:[06]\\d|7[0-4])|5[7-9]\\d|7[016-9]\\d|8(?:[01]\\d|[27][0-4])|9[0-8]\\d))\\d{4}",
				[
					9,
					10
				]
			],
			[
				"1[378]00\\d{6}",
				[
					10
				]
			],
			[
				"1600\\d{6}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"154(?:6(?:0\\d|1[0-3])|8(?:[25]1|4[0189]|7[0-4679]))\\d{4}",
				[
					10
				]
			]
		]
	],
	MZ: [
		"258",
		"00",
		"[28]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"([28]\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2|8[2-7]"
				]
			],
			[
				"(80\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"80"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[1346]\\d|5[0-2]|[78][12]|93)\\d{5}",
				[
					8
				]
			],
			[
				"8[2-7]\\d{7}",
				[
					9
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			]
		]
	],
	NA: [
		"264",
		"00",
		"[68]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(8\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8[1-35]"
				]
			],
			[
				"(6\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"6"
				]
			],
			[
				"(88)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"88"
				]
			],
			[
				"(870)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"870"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"6(?:1(?:17|2(?:[0189]\\d|[2-6]|7\\d?)|3(?:[01378]|2\\d)|4(?:[024]|10?|3[15]?)|69|7[014])|2(?:17|5(?:[0-36-8]|4\\d?)|69|70)|3(?:17|2(?:[0237]\\d?|[14-689])|34|6[289]|7[01]|81)|4(?:17|2(?:[012]|7\\d?)|4(?:[06]|1\\d?)|5(?:[01357]|[25]\\d?)|69|7[01])|5(?:17|2(?:[0459]|[23678]\\d?)|69|7[01])|6(?:17|2(?:5|6\\d?)|38|42|69|7[01])|7(?:17|2(?:[569]|[234]\\d?)|3(?:0\\d?|[13])|6[89]|7[01]))\\d{4}"
			],
			[
				"(?:60|8[125])\\d{7}",
				[
					9
				]
			],
			0,
			[
				"8701\\d{5}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			[
				"8(?:3\\d{2}|86)\\d{5}"
			]
		]
	],
	NC: [
		"687",
		"00",
		"[2-57-9]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1.$2.$3",
				[
					"[2-46-9]|5[0-4]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[03-9]|3[0-5]|4[1-7]|88)\\d{4}"
			],
			[
				"(?:5[0-4]|[79]\\d|8[0-79])\\d{4}"
			],
			0,
			[
				"36\\d{4}"
			]
		]
	],
	NE: [
		"227",
		"00",
		"[0289]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"09|[289]"
				]
			],
			[
				"(08)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"08"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0(?:20|3[1-7]|4[134]|5[14]|6[14578]|7[1-578])|1(?:4[145]|5[14]|6[14-68]|7[169]|88))\\d{4}"
			],
			[
				"(?:8[04589]|9\\d)\\d{6}"
			],
			[
				"08\\d{6}"
			],
			[
				"09\\d{6}"
			]
		]
	],
	NF: [
		"672",
		"00",
		"[13]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(\\d)(\\d{5})",
				"$1 $2",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:06|17|28|39)|3[012]\\d)\\d{3}"
			],
			[
				"3[58]\\d{4}"
			]
		]
	],
	NG: [
		"234",
		"009",
		"[1-6]\\d{5,8}|9\\d{5,9}|[78]\\d{5,13}",
		[
			7,
			8,
			10,
			11,
			12,
			13,
			14
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[12]|9(?:0[3-9]|[1-9])"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[3-6]|7(?:0[1-9]|[1-79])|8[2-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"70|8[01]|90[235-9]"
				]
			],
			[
				"([78]00)(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"[78]00"
				]
			],
			[
				"([78]00)(\\d{5})(\\d{5,6})",
				"$1 $2 $3",
				[
					"[78]00"
				]
			],
			[
				"(78)(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"78"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"[12]\\d{6,7}|9(?:0[3-9]|[1-9]\\d)\\d{5}|(?:3\\d|4[023568]|5[02368]|6[02-469]|7[4-69]|8[2-9])\\d{6}|(?:4[47]|5[14579]|6[1578]|7[0-357])\\d{5,6}|(?:78|41)\\d{5}",
				[
					7,
					8
				]
			],
			[
				"(?:1(?:7[34]\\d|8(?:04|[124579]\\d|8[0-3])|95\\d)|287[0-7]|3(?:18[1-8]|88[0-7]|9(?:8[5-9]|6[1-5]))|4(?:28[0-2]|6(?:7[1-9]|8[02-47])|88[0-2])|5(?:2(?:7[7-9]|8\\d)|38[1-79]|48[0-7]|68[4-7])|6(?:2(?:7[7-9]|8\\d)|4(?:3[7-9]|[68][129]|7[04-69]|9[1-8])|58[0-2]|98[7-9])|7(?:38[0-7]|69[1-8]|78[2-4])|8(?:28[3-9]|38[0-2]|4(?:2[12]|3[147-9]|5[346]|7[4-9]|8[014-689]|90)|58[1-8]|78[2-9]|88[5-7])|98[07]\\d)\\d{4}|(?:70(?:[1-689]\\d|7[0-3])|8(?:0(?:1[01]|[2-9]\\d)|1(?:[0-8]\\d|9[01]))|90[235-9]\\d)\\d{6}",
				[
					8,
					10
				]
			],
			[
				"800\\d{7,11}",
				[
					10,
					11,
					12,
					13,
					14
				]
			],
			0,
			0,
			0,
			[
				"700\\d{7,11}",
				[
					10,
					11,
					12,
					13,
					14
				]
			]
		]
	],
	NI: [
		"505",
		"00",
		"[125-8]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}"
			],
			[
				"(?:5(?:5[0-7]|[78]\\d)|6(?:20|3[035]|4[045]|5[05]|77|8[1-9]|9[059])|7[5-8]\\d|8\\d{2})\\d{5}"
			],
			[
				"1800\\d{4}"
			]
		]
	],
	NL: [
		"31",
		"00",
		"1\\d{4,8}|[2-7]\\d{8}|[89]\\d{6,9}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"([1-578]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1[035]|2[0346]|3[03568]|4[0356]|5[0358]|7|8[4578]"
				]
			],
			[
				"([1-5]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[16-8]|2[259]|3[124]|4[17-9]|5[124679]"
				]
			],
			[
				"(6)(\\d{8})",
				"$1 $2",
				[
					"6[0-57-9]"
				]
			],
			[
				"(66)(\\d{7})",
				"$1 $2",
				[
					"66"
				]
			],
			[
				"(14)(\\d{3,4})",
				"$1 $2",
				[
					"14"
				],
				"$1"
			],
			[
				"([89]0\\d)(\\d{4,7})",
				"$1 $2",
				[
					"[89]0"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:[035]\\d|1[13-578]|6[124-8]|7[24]|8[0-467])|2(?:[0346]\\d|2[2-46-9]|5[125]|9[479])|3(?:[03568]\\d|1[3-8]|2[01]|4[1-8])|4(?:[0356]\\d|1[1-368]|7[58]|8[15-8]|9[23579])|5(?:[0358]\\d|[19][1-9]|2[1-57-9]|4[13-8]|6[126]|7[0-3578])|7\\d{2})\\d{6}",
				[
					9
				]
			],
			[
				"6[1-58]\\d{7}",
				[
					9
				]
			],
			[
				"800\\d{4,7}",
				[
					7,
					8,
					9,
					10
				]
			],
			[
				"90[069]\\d{4,7}",
				[
					7,
					8,
					9,
					10
				]
			],
			0,
			0,
			[
				"140(?:1(?:[035]|[16-8]\\d)|2(?:[0346]|[259]\\d)|3(?:[03568]|[124]\\d)|4(?:[0356]|[17-9]\\d)|5(?:[0358]|[124679]\\d)|7\\d|8[458])|8[478]\\d{7}",
				[
					5,
					6,
					9
				]
			],
			[
				"66\\d{7}",
				[
					9
				]
			],
			[
				"(?:6760|85\\d{2})\\d{5}",
				[
					9
				]
			]
		]
	],
	NO: [
		"47",
		"00",
		"0\\d{4}|[2-9]\\d{7}",
		[
			5,
			8
		],
		[
			[
				"([489]\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[489]"
				]
			],
			[
				"([235-7]\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[235-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1-4]|3[1-3578]|5[1-35-7]|6[1-4679]|7[0-8])\\d{6}",
				[
					8
				]
			],
			[
				"(?:4[015-8]|5[89]|9\\d)\\d{6}",
				[
					8
				]
			],
			[
				"80[01]\\d{5}",
				[
					8
				]
			],
			[
				"82[09]\\d{5}",
				[
					8
				]
			],
			[
				"880\\d{5}",
				[
					8
				]
			],
			0,
			[
				"0\\d{4}|81(?:0(?:0[7-9]|1\\d)|5\\d{2})\\d{3}"
			],
			0,
			[
				"85[0-5]\\d{5}",
				[
					8
				]
			],
			[
				"810(?:0[0-6]|[2-8]\\d)\\d{3}",
				[
					8
				]
			]
		]
	],
	NP: [
		"977",
		"00",
		"[1-8]\\d{7}|9(?:[1-69]\\d{6,8}|7[2-6]\\d{5,7}|8\\d{8})",
		[
			8,
			10
		],
		[
			[
				"(1)(\\d{7})",
				"$1-$2",
				[
					"1[2-6]"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1-$2",
				[
					"1[01]|[2-8]|9(?:[1-69]|7[15-9])"
				]
			],
			[
				"(9\\d{2})(\\d{7})",
				"$1-$2",
				[
					"9(?:6[013]|7[245]|8)"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[0-6]\\d|2[13-79][2-6]|3[135-8][2-6]|4[146-9][2-6]|5[135-7][2-6]|6[13-9][2-6]|7[15-9][2-6]|8[1-46-9][2-6]|9[1-79][2-6])\\d{5}",
				[
					8
				]
			],
			[
				"9(?:6[0-3]|7[245]|8[0-24-68])\\d{7}",
				[
					10
				]
			]
		]
	],
	NR: [
		"674",
		"00",
		"[458]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:444|888)\\d{4}"
			],
			[
				"55[4-9]\\d{4}"
			]
		]
	],
	NU: [
		"683",
		"00",
		"[478]\\d{3,7}",
		[
			4,
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"888"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[47]\\d{3}",
				[
					4
				]
			],
			[
				"888[4-9]\\d{3}",
				[
					7
				]
			]
		]
	],
	NZ: [
		"64",
		"0(?:0|161)",
		"6[235-9]\\d{6}|[2-57-9]\\d{7,9}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"240|[346]|7[2-57-9]|9[1-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"21"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,5})",
				"$1 $2 $3",
				[
					"2(?:1[1-9]|[69]|7[0-35-9])|70|86"
				]
			],
			[
				"(2\\d)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[028]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"90"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2(?:10|74)|5|[89]0"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[2-79]|[49][2-9]|6[235-9]|7[2-57-9])\\d{6}|24099\\d{3}",
				[
					8
				]
			],
			[
				"2(?:[028]\\d{7,8}|1\\d{6,8}|[79]\\d{7})"
			],
			[
				"508\\d{6,7}|80\\d{6,8}"
			],
			[
				"90\\d{6,7}",
				[
					8,
					9
				]
			],
			[
				"70\\d{7}",
				[
					9
				]
			],
			0,
			0,
			[
				"[28]6\\d{6,7}",
				[
					8,
					9
				]
			]
		],
		"00"
	],
	OM: [
		"968",
		"00",
		"(?:5|[279]\\d)\\d{6}|800\\d{5,6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(2\\d)(\\d{6})",
				"$1 $2",
				[
					"2"
				]
			],
			[
				"([79]\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[79]"
				]
			],
			[
				"([58]00)(\\d{4,6})",
				"$1 $2",
				[
					"[58]00"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[2-6]\\d{6}",
				[
					8
				]
			],
			[
				"7[19]\\d{6}|9(?:0[1-9]|[1-9]\\d)\\d{5}",
				[
					8
				]
			],
			[
				"8007\\d{4,5}|500\\d{4}"
			],
			[
				"900\\d{5}",
				[
					8
				]
			]
		]
	],
	PA: [
		"507",
		"00",
		"[1-9]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[1-57-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"6"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:0\\d|1[479]|2[37]|3[0137]|4[17]|5[05]|[68][58]|7[0167]|9[39])|2(?:[0235-79]\\d|1[0-7]|4[013-9]|8[026-9])|3(?:[089]\\d|1[014-7]|2[0-35]|33|4[0-579]|55|6[068]|7[06-8])|4(?:00|3[0-579]|4\\d|7[0-57-9])|5(?:[01]\\d|2[0-7]|[56]0|79)|7(?:0[09]|2[0-26-8]|3[03]|4[04]|5[05-9]|6[05]|7[0-24-9]|8[7-9]|90)|8(?:09|2[89]|3\\d|4[0-24-689]|5[014]|8[02])|9(?:0[5-9]|1[0135-8]|2[036-9]|3[35-79]|40|5[0457-9]|6[05-9]|7[04-9]|8[35-8]|9\\d))\\d{4}",
				[
					7
				]
			],
			[
				"(?:1[16]1|21[89]|8(?:1[01]|7[23]))\\d{4}|6(?:[02-9]\\d|1[0-5])\\d{5}"
			],
			[
				"800\\d{4}",
				[
					7
				]
			],
			[
				"(?:8(?:22|55|60|7[78]|86)|9(?:00|81))\\d{4}",
				[
					7
				]
			]
		]
	],
	PE: [
		"51",
		"19(?:1[124]|77|90)00",
		"[14-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(1)(\\d{7})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"([4-8]\\d)(\\d{6})",
				"$1 $2",
				[
					"[4-7]|8[2-4]"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"80"
				]
			],
			[
				"(9\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"$1"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|4[1-4]|5[1-46]|6[1-7]|7[2-46]|8[2-4])\\d{6}",
				[
					8
				]
			],
			[
				"9\\d{8}",
				[
					9
				]
			],
			[
				"800\\d{5}",
				[
					8
				]
			],
			[
				"805\\d{5}",
				[
					8
				]
			],
			[
				"80[24]\\d{5}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			[
				"801\\d{5}",
				[
					8
				]
			]
		],
		0,
		" Anexo "
	],
	PF: [
		"689",
		"00",
		"4\\d{5,7}|8\\d{7}",
		[
			6,
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4[09]|8[79]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"44"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4(?:[09][45689]\\d|4)\\d{4}"
			],
			[
				"8[79]\\d{6}",
				[
					8
				]
			]
		]
	],
	PG: [
		"675",
		"140[1-3]|00",
		"[1-9]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[13-689]|27"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"20|7"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[0-2]\\d|4[257]\\d|5[34]\\d|64[1-9]|77(?:[0-24]\\d|30)|85[02-46-9]|9[78]\\d)\\d{4}",
				[
					7
				]
			],
			[
				"7(?:[0-689]\\d|75)\\d{5}",
				[
					8
				]
			],
			[
				"180\\d{4}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"2(?:0[0-47]|7[568])\\d{4}",
				[
					7
				]
			]
		],
		"00"
	],
	PH: [
		"63",
		"00",
		"1\\d{10,12}|2\\d{5,7}|[3-7]\\d{8}|8\\d{7,9}|9\\d{9}",
		[
			6,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(2)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"(0$1)"
			],
			[
				"(2)(\\d{5})",
				"$1 $2",
				[
					"2"
				],
				"(0$1)"
			],
			[
				"(\\d{4})(\\d{4,6})",
				"$1 $2",
				[
					"3(?:23|39|46)|4(?:2[3-6]|[35]9|4[26]|76)|5(?:22|44)|642|8(?:62|8[245])",
					"3(?:230|397|461)|4(?:2(?:35|[46]4|51)|396|4(?:22|63)|59[347]|76[15])|5(?:221|446)|642[23]|8(?:622|8(?:[24]2|5[13]))"
				],
				"(0$1)"
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"346|4(?:27|9[35])|883",
					"3469|4(?:279|9(?:30|56))|8834"
				],
				"(0$1)"
			],
			[
				"([3-8]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-8]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"81|9"
				],
				"0$1"
			],
			[
				"(1800)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"180",
					"1800"
				]
			],
			[
				"(1800)(\\d{1,2})(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"180",
					"1800"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{5}(?:\\d{2})?|(?:3[2-68]|4[2-9]|5[2-6]|6[2-58]|7[24578]|8[2-8])\\d{7}|88(?:22\\d{6}|42\\d{4})",
				[
					6,
					8,
					9,
					10
				]
			],
			[
				"(?:81[37]|9(?:0[5-9]|1[024-9]|2[0-35-9]|3[02-9]|4[235-9]|5[056]|6[5-7]|7[34-79]|89|9[4-9]))\\d{7}",
				[
					10
				]
			],
			[
				"1800\\d{7,9}",
				[
					11,
					12,
					13
				]
			]
		]
	],
	PK: [
		"92",
		"00",
		"1\\d{8}|[2-8]\\d{5,11}|9(?:[013-9]\\d{4,10}|2\\d(?:111\\d{6}|\\d{3,7}))",
		[
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"([89]00)(\\d{3})(\\d{2})",
				"$1 $2 $3",
				[
					"[89]00"
				],
				"0$1"
			],
			[
				"(1\\d{3})(\\d{5})",
				"$1 $2",
				[
					"1"
				],
				"$1"
			],
			[
				"(\\d{2})(\\d{7,8})",
				"$1 $2",
				[
					"(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]"
				]
			],
			[
				"(\\d{3})(\\d{6,7})",
				"$1 $2",
				[
					"2[349]|45|54|60|72|8[2-5]|9[2-469]",
					"(?:2[349]|45|54|60|72|8[2-5]|9[2-469])\\d[2-9]"
				]
			],
			[
				"(58\\d{3})(\\d{5})",
				"$1 $2",
				[
					"58[126]"
				]
			],
			[
				"(3\\d{2})(\\d{7})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{2})(111)(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)1",
					"(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)11",
					"(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)111"
				]
			],
			[
				"(\\d{3})(111)(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"2[349]|45|54|60|72|8[2-5]|9[2-9]",
					"(?:2[349]|45|54|60|72|8[2-5]|9[2-9])\\d1",
					"(?:2[349]|45|54|60|72|8[2-5]|9[2-9])\\d11",
					"(?:2[349]|45|54|60|72|8[2-5]|9[2-9])\\d111"
				]
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"(?:21|42)[2-9]\\d{7}|(?:2[25]|4[0146-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]\\d{6}|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8]))[2-9]\\d{5,6}|58[126]\\d{7}",
				[
					9,
					10
				]
			],
			[
				"3(?:[014]\\d|2[0-5]|3[0-7]|55|64)\\d{7}",
				[
					10
				]
			],
			[
				"800\\d{5}",
				[
					8
				]
			],
			[
				"900\\d{5}",
				[
					8
				]
			],
			[
				"122\\d{6}",
				[
					9
				]
			],
			0,
			[
				"(?:2(?:[125]|3[2358]|4[2-4]|9[2-8])|4(?:[0-246-9]|5[3479])|5(?:[1-35-7]|4[2-467])|6(?:[1-8]|0[468])|7(?:[14]|2[236])|8(?:[16]|2[2-689]|3[23578]|4[3478]|5[2356])|9(?:1|22|3[27-9]|4[2-6]|6[3569]|9[2-7]))111\\d{6}",
				[
					11,
					12
				]
			]
		]
	],
	PL: [
		"48",
		"00",
		"[1-57-9]\\d{6,8}|6\\d{5,8}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"11[68]|64"
				]
			],
			[
				"(\\d{5})",
				"$1",
				[
					"19"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"64"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"26|39|45|5[0137]|6[0469]|7[02389]|8[08]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[14]|2[0-57-9]|3[2-4]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])(?:\\d{7}|19\\d{3})",
				[
					7,
					9
				]
			],
			[
				"(?:45|5[0137]|6[069]|7[2389]|88)\\d{7}",
				[
					9
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			],
			[
				"70[01346-8]\\d{6}",
				[
					9
				]
			],
			0,
			0,
			[
				"804\\d{6}",
				[
					9
				]
			],
			[
				"64\\d{4,7}"
			],
			[
				"39\\d{7}",
				[
					9
				]
			],
			[
				"801\\d{6}",
				[
					9
				]
			]
		]
	],
	PM: [
		"508",
		"00",
		"[45]\\d{5}",
		[
			6
		],
		[
			[
				"([45]\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[45]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:4[1-3]|50)\\d{4}"
			],
			[
				"(?:4[02-4]|5[05])\\d{4}"
			]
		]
	],
	PR: [
		"1",
		"011",
		"[5789]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"787|939",
		[
			[
				"(?:787|939)[2-9]\\d{6}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	PS: [
		"970",
		"00",
		"1\\d{9}|[24589]\\d{7,8}",
		[
			8,
			9,
			10
		],
		[
			[
				"([2489])(2\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[2489]2"
				]
			],
			[
				"(5[69]\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"5[69]"
				]
			],
			[
				"(1[78]00)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[78]0",
					"1[78]00"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:22[234789]|42[45]|82[01458]|92[369])\\d{5}",
				[
					8
				]
			],
			[
				"5[69]\\d{7}",
				[
					9
				]
			],
			[
				"1800\\d{6}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"1700\\d{6}",
				[
					10
				]
			]
		]
	],
	PT: [
		"351",
		"00",
		"[2-46-9]\\d{8}",
		[
			9
		],
		[
			[
				"(2\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2[12]"
				]
			],
			[
				"([2-46-9]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2[3-9]|[346-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[12]\\d|[35][1-689]|4[1-59]|6[1-35689]|7[1-9]|8[1-69]|9[1256])\\d{6}"
			],
			[
				"9(?:[1236]\\d{2}|480)\\d{5}"
			],
			[
				"80[02]\\d{6}"
			],
			[
				"6(?:0[178]|4[68])\\d{6}|76(?:0[1-57]|1[2-47]|2[237])\\d{5}"
			],
			[
				"884[0-4689]\\d{5}"
			],
			0,
			[
				"7(?:0(?:7\\d|8[17]))\\d{5}"
			],
			0,
			[
				"30\\d{7}"
			],
			[
				"80(?:8\\d|9[1579])\\d{5}"
			]
		]
	],
	PW: [
		"680",
		"01[12]",
		"[2-9]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:55|77)|345|488|5(?:35|44|87)|6(?:22|54|79)|7(?:33|47)|8(?:24|55|76)|900)\\d{4}"
			],
			[
				"(?:6[234689]0|77\\d|88[0-4])\\d{4}"
			]
		]
	],
	PY: [
		"595",
		"00",
		"5[0-5]\\d{4,7}|[2-46-9]\\d{5,8}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"[2-9]0"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"9[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"870",
					"8700"
				]
			],
			[
				"(\\d{3})(\\d{4,5})",
				"$1 $2",
				[
					"[2-8][1-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8][1-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[26]1|3[289]|4[124678]|7[123]|8[1236])\\d{5,7}|(?:2(?:2[4568]|7[15]|9[1-5])|3(?:18|3[167]|4[2357]|51)|4(?:18|2[45]|3[12]|5[13]|64|71|9[1-47])|5(?:[1-4]\\d|5[0234])|6(?:3[1-3]|44|7[1-4678])|7(?:17|4[0-4]|6[1-578]|75|8[0-8])|858)\\d{5,6}",
				[
					7,
					8,
					9
				]
			],
			[
				"9(?:51|6[129]|[78][1-6]|9[1-5])\\d{6}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			[
				"[2-9]0\\d{4,7}"
			],
			0,
			[
				"8700[0-4]\\d{4}",
				[
					9
				]
			]
		]
	],
	QA: [
		"974",
		"00",
		"[2-8]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"([28]\\d{2})(\\d{4})",
				"$1 $2",
				[
					"[28]"
				]
			],
			[
				"([3-7]\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[3-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4[04]\\d{6}",
				[
					8
				]
			],
			[
				"[3567]\\d{7}",
				[
					8
				]
			],
			[
				"800\\d{4}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			[
				"2(?:[12]\\d|61)\\d{4}",
				[
					7
				]
			]
		]
	],
	RE: [
		"262",
		"00",
		"[268]\\d{8}",
		[
			9
		],
		[
			[
				"([268]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[268]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		"262|69|8",
		[
			[
				"262\\d{6}"
			],
			[
				"69(?:2\\d{2}|3(?:0[0-46]|1[013]|2[0-2]|3[0-39]|4\\d|5[05]|6[0-26]|7[0-27]|8[0-38]|9[0-479]))\\d{4}"
			],
			[
				"80\\d{7}"
			],
			[
				"89[1-37-9]\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"8(?:1[019]|2[0156]|84|90)\\d{6}"
			]
		]
	],
	RO: [
		"40",
		"00",
		"[23]\\d{5,8}|[7-9]\\d{8}",
		[
			6,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[23]1"
				]
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"[23]1"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[23][3-7]|[7-9]"
				]
			],
			[
				"(2\\d{2})(\\d{3})",
				"$1 $2",
				[
					"2[3-6]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1(?:\\d{7}|9\\d{3})|[3-6](?:\\d{7}|\\d9\\d{2}))|3(?:1\\d{4}(?:\\d{3})?|[3-6]\\d{7})"
			],
			[
				"7(?:[02-7]\\d{2}|1(?:[01]\\d|20)|8[03-8]\\d|99\\d)\\d{5}",
				[
					9
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			],
			[
				"90[036]\\d{6}",
				[
					9
				]
			],
			0,
			0,
			[
				"37\\d{7}",
				[
					9
				]
			],
			0,
			0,
			[
				"801\\d{6}",
				[
					9
				]
			]
		],
		0,
		" int "
	],
	RS: [
		"381",
		"00",
		"[126-9]\\d{4,11}|3(?:[0-79]\\d{3,10}|8[2-9]\\d{2,9})",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"([23]\\d{2})(\\d{4,9})",
				"$1 $2",
				[
					"(?:2[389]|39)0"
				]
			],
			[
				"([1-3]\\d)(\\d{5,10})",
				"$1 $2",
				[
					"1|2(?:[0-24-7]|[389][1-9])|3(?:[0-8]|9[1-9])"
				]
			],
			[
				"(6\\d)(\\d{6,8})",
				"$1 $2",
				[
					"6"
				]
			],
			[
				"([89]\\d{2})(\\d{3,9})",
				"$1 $2",
				[
					"[89]"
				]
			],
			[
				"(7[26])(\\d{4,9})",
				"$1 $2",
				[
					"7[26]"
				]
			],
			[
				"(7[08]\\d)(\\d{4,9})",
				"$1 $2",
				[
					"7[08]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:[02-9][2-9]|1[1-9])\\d|2(?:[0-24-7][2-9]\\d|[389](?:0[2-9]|[2-9]\\d))|3(?:[0-8][2-9]\\d|9(?:[2-9]\\d|0[2-9])))\\d{3,8}",
				[
					7,
					8,
					9,
					10,
					11,
					12
				]
			],
			[
				"6(?:[0-689]|7\\d)\\d{6,7}",
				[
					8,
					9,
					10
				]
			],
			[
				"800\\d{3,9}"
			],
			[
				"(?:90[0169]|78\\d)\\d{3,7}"
			],
			0,
			0,
			[
				"7[06]\\d{4,10}"
			]
		]
	],
	RU: [
		"7",
		"810",
		"[347-9]\\d{9}",
		[
			10
		],
		[
			[
				"([3489]\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[3489]"
				]
			],
			[
				"(7\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			]
		],
		"8",
		"8 ($1)",
		0,
		0,
		1,
		0,
		[
			[
				"(?:3(?:0[12]|4[1-35-79]|5[1-3]|65|8[1-58]|9[0145])|4(?:01|1[1356]|2[13467]|7[1-5]|8[1-7]|9[1-689])|8(?:1[1-8]|2[01]|3[13-6]|4[0-8]|5[15]|6[1-35-79]|7[1-37-9]))\\d{7}"
			],
			[
				"9\\d{9}"
			],
			[
				"80[04]\\d{7}"
			],
			[
				"80[39]\\d{7}"
			],
			[
				"808\\d{7}"
			]
		],
		"8~10"
	],
	RW: [
		"250",
		"00",
		"[027-9]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(2\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"([7-9]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0$1"
			],
			[
				"(0\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[258]\\d{7}|06\\d{6}"
			],
			[
				"7[238]\\d{7}",
				[
					9
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			],
			[
				"900\\d{6}",
				[
					9
				]
			]
		]
	],
	SA: [
		"966",
		"00",
		"[15]\\d{8}|8\\d{9}|92\\d{7}",
		[
			9,
			10
		],
		[
			[
				"(1\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1[1-467]"
				]
			],
			[
				"(5\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"5"
				]
			],
			[
				"(92\\d{2})(\\d{5})",
				"$1 $2",
				[
					"92"
				],
				"$1"
			],
			[
				"(800)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"800"
				],
				"$1"
			],
			[
				"(811)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"811"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:1\\d|2[24-8]|3[35-8]|4[3-68]|6[2-5]|7[235-7])\\d{6}",
				[
					9
				]
			],
			[
				"(?:5(?:[013-689]\\d|7[0-36-8])|811\\d)\\d{6}"
			],
			[
				"800\\d{7}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"92[05]\\d{6}",
				[
					9
				]
			]
		]
	],
	SB: [
		"677",
		"0[01]",
		"[1-9]\\d{4,6}",
		[
			5,
			7
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[7-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[4-79]|[23]\\d|4[0-2]|5[03]|6[0-37])\\d{3}",
				[
					5
				]
			],
			[
				"48\\d{3}|7(?:30|[46-8]\\d|5[025-9]|9[0-5])\\d{4}|8[4-9]\\d{5}|9(?:1[2-9]|2[013-9]|3[0-2]|[46]\\d|5[0-46-9]|7[0-689]|8[0-79]|9[0-8])\\d{4}"
			],
			[
				"1[38]\\d{3}",
				[
					5
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"5[12]\\d{3}",
				[
					5
				]
			]
		]
	],
	SC: [
		"248",
		"0(?:[02]|10?)",
		"[24689]\\d{5,6}",
		[
			7
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[246]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4[2-46]\\d{5}"
			],
			[
				"2[5-8]\\d{5}"
			],
			[
				"8000\\d{3}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"(?:64\\d|971)\\d{4}"
			]
		],
		"00"
	],
	SD: [
		"249",
		"00",
		"[19]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:5\\d|8[3567])\\d{6}"
			],
			[
				"(?:1[0-2]|9[0-3569])\\d{7}"
			]
		]
	],
	SE: [
		"46",
		"00",
		"[1-35-9]\\d{5,11}|4\\d{6,8}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"([1-469]\\d)(\\d{3})(\\d{2})",
				"$1-$2 $3",
				[
					"[12][136]|3[356]|4[0246]|6[03]|90[1-9]"
				],
				0,
				0,
				"$1 $2 $3"
			],
			[
				"(9[034]\\d)(\\d{4})",
				"$1-$2",
				[
					"9(?:00|39|44)"
				],
				0,
				0,
				"$1 $2"
			],
			[
				"(8)(\\d{2,3})(\\d{2,3})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				0,
				0,
				"$1 $2 $3 $4"
			],
			[
				"([1-69]\\d)(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"1[013689]|2[0136]|3[1356]|4[0246]|54|6[03]|90"
				],
				0,
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[0-5]|4[0-3])"
				],
				0,
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[0-5]|4[0-3])"
				],
				0,
				0,
				"$1 $2 $3"
			],
			[
				"(7\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"7"
				],
				0,
				0,
				"$1 $2 $3 $4"
			],
			[
				"(77)(\\d{2})(\\d{2})",
				"$1-$2$3",
				[
					"77"
				],
				0,
				0,
				"$1 $2 $3"
			],
			[
				"(20)(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"20"
				],
				0,
				0,
				"$1 $2 $3"
			],
			[
				"(9[034]\\d)(\\d{2})(\\d{2})(\\d{3})",
				"$1-$2 $3 $4",
				[
					"9[034]"
				],
				0,
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4 $5",
				[
					"25[245]|67[3-6]"
				],
				0,
				0,
				"$1 $2 $3 $4 $5"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"1(?:0[1-8]\\d{6}|[136]\\d{5,7}|(?:2[0-35]|4[0-4]|5[0-25-9]|7[13-6]|[89]\\d)\\d{5,6})|2(?:[136]\\d{5,7}|(?:2[0-7]|4[0136-8]|5[0138]|7[018]|8[01]|9[0-57])\\d{5,6})|3(?:[356]\\d{5,7}|(?:0[0-4]|1\\d|2[0-25]|4[056]|7[0-2]|8[0-3]|9[023])\\d{5,6})|4(?:[0246]\\d{5,7}|(?:1[013-8]|3[0135]|5[14-79]|7[0-246-9]|8[0156]|9[0-689])\\d{5,6})|5(?:0[0-6]|[15][0-5]|2[0-68]|3[0-4]|4\\d|6[03-5]|7[013]|8[0-79]|9[01])\\d{5,6}|6(?:[03]\\d{5,7}|(?:1[1-3]|2[0-4]|4[02-57]|5[0-37]|6[0-3]|7[0-2]|8[0247]|9[0-356])\\d{5,6})|8\\d{6,8}|9(?:0[1-9]\\d{4,6}|(?:1[0-68]|2\\d|3[02-5]|4[0-3]|5[0-4]|[68][01]|7[0135-8])\\d{5,6})",
				[
					7,
					8,
					9
				]
			],
			[
				"7[02369]\\d{7}",
				[
					9
				]
			],
			[
				"20\\d{4,7}",
				[
					6,
					7,
					8,
					9
				]
			],
			[
				"649\\d{6}|9(?:00|39|44)[1-8]\\d{3,6}",
				[
					7,
					8,
					9,
					10
				]
			],
			[
				"75[1-8]\\d{6}",
				[
					9
				]
			],
			0,
			0,
			[
				"74[02-9]\\d{6}",
				[
					9
				]
			],
			0,
			[
				"77(?:0\\d{3}(?:\\d{3})?|[1-7]\\d{6})",
				[
					6,
					9
				]
			]
		]
	],
	SG: [
		"65",
		"0[0-3]\\d",
		"[36]\\d{7}|[17-9]\\d{7,10}",
		[
			8,
			10,
			11
		],
		[
			[
				"([3689]\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[369]|8[1-9]"
				]
			],
			[
				"(1[89]00)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1[89]0",
					"1[89]00"
				]
			],
			[
				"(7000)(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"700",
					"7000"
				]
			],
			[
				"(800)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"800"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6[1-9]\\d{6}",
				[
					8
				]
			],
			[
				"(?:8[1-8]|9[0-8])\\d{6}",
				[
					8
				]
			],
			[
				"1?800\\d{7}",
				[
					10,
					11
				]
			],
			[
				"1900\\d{7}",
				[
					11
				]
			],
			0,
			0,
			[
				"7000\\d{7}",
				[
					11
				]
			],
			0,
			[
				"3[12]\\d{6}",
				[
					8
				]
			]
		]
	],
	SH: [
		"290",
		"00",
		"[256]\\d{4}|8\\d{3}",
		[
			4,
			5
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[0-57-9]\\d|6[4-9])\\d{2}"
			],
			[
				"[56]\\d{4}",
				[
					5
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"262\\d{2}",
				[
					5
				]
			]
		]
	],
	SI: [
		"386",
		"00",
		"[1-7]\\d{6,7}|[89]\\d{4,7}",
		[
			5,
			6,
			7,
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[12]|[34][24-8]|5[2-8]|7[3-8]"
				],
				"(0$1)"
			],
			[
				"([3-7]\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[37][01]|4[0139]|51|6"
				]
			],
			[
				"([89][09])(\\d{3,6})",
				"$1 $2",
				[
					"[89][09]"
				]
			],
			[
				"([58]\\d{2})(\\d{5})",
				"$1 $2",
				[
					"59|8[1-3]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|[25][2-8]|3[24-8]|4[24-8]|7[3-8])\\d{6}",
				[
					8
				]
			],
			[
				"(?:[37][01]\\d|4[0139]\\d|51\\d|6(?:[48]\\d|5[15-7]|9[69]))\\d{5}",
				[
					8
				]
			],
			[
				"80\\d{4,6}",
				[
					6,
					7,
					8
				]
			],
			[
				"90\\d{4,6}|89[1-3]\\d{2,5}"
			],
			0,
			0,
			0,
			0,
			[
				"(?:59|8[1-3])\\d{6}",
				[
					8
				]
			]
		]
	],
	SJ: [
		"47",
		"00",
		"0\\d{4}|[45789]\\d{7}",
		[
			5,
			8
		],
		[
			[
				"([489]\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[489]"
				]
			],
			[
				"([235-7]\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[235-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"79\\d{6}",
				[
					8
				]
			],
			[
				"(?:4[015-8]|5[89]|9\\d)\\d{6}",
				[
					8
				]
			],
			[
				"80[01]\\d{5}",
				[
					8
				]
			],
			[
				"82[09]\\d{5}",
				[
					8
				]
			],
			[
				"880\\d{5}",
				[
					8
				]
			],
			0,
			[
				"0\\d{4}|81(?:0(?:0[7-9]|1\\d)|5\\d{2})\\d{3}"
			],
			0,
			[
				"85[0-5]\\d{5}",
				[
					8
				]
			],
			[
				"810(?:0[0-6]|[2-8]\\d)\\d{3}",
				[
					8
				]
			]
		]
	],
	SK: [
		"421",
		"00",
		"(?:[2-68]\\d{5,8}|9\\d{6,8})",
		[
			6,
			7,
			9
		],
		[
			[
				"(2)(1[67])(\\d{3,4})",
				"$1 $2 $3",
				[
					"21[67]"
				]
			],
			[
				"([3-5]\\d)(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[3-5]"
				]
			],
			[
				"(2)(\\d{3})(\\d{3})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"2"
				]
			],
			[
				"([3-5]\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"[3-5]"
				]
			],
			[
				"([689]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[689]"
				]
			],
			[
				"(9090)(\\d{3})",
				"$1 $2",
				[
					"909",
					"9090"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1(?:6\\d{3,4}|7\\d{3})|[2-9]\\d{7})|[3-5][1-8](?:1(?:6\\d{2,3}|7\\d{3})|\\d{7})"
			],
			[
				"9(?:0(?:[1-8]\\d|9[1-9])|(?:1[0-24-9]|[45]\\d)\\d)\\d{5}",
				[
					9
				]
			],
			[
				"800\\d{6}",
				[
					9
				]
			],
			[
				"9(?:[78]\\d{7}|00\\d{6})",
				[
					9
				]
			],
			0,
			0,
			[
				"96\\d{7}",
				[
					9
				]
			],
			[
				"9090\\d{3}",
				[
					7
				]
			],
			[
				"6(?:02|5[0-4]|9[0-6])\\d{6}",
				[
					9
				]
			],
			[
				"8[5-9]\\d{7}",
				[
					9
				]
			]
		]
	],
	SL: [
		"232",
		"00",
		"[2-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2"
			]
		],
		"0",
		"(0$1)",
		0,
		0,
		0,
		0,
		[
			[
				"[235]2[2-4][2-9]\\d{4}"
			],
			[
				"(?:2[15]|3[013-5]|4[04]|5[05]|66|7[5-9]|8[08]|99)\\d{6}"
			]
		]
	],
	SM: [
		"378",
		"00",
		"[05-7]\\d{7,9}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-7]"
				]
			],
			[
				"(0549)(\\d{6})",
				"$1 $2",
				[
					"054",
					"0549"
				],
				0,
				0,
				"($1) $2"
			],
			[
				"(\\d{6})",
				"0549 $1",
				[
					"[89]"
				],
				0,
				0,
				"(0549) $1"
			]
		],
		0,
		0,
		"([89]\\d{5})",
		"0549$1",
		0,
		0,
		[
			[
				"0549(?:8[0157-9]|9\\d)\\d{4}",
				[
					10
				]
			],
			[
				"6[16]\\d{6}",
				[
					8
				]
			],
			0,
			[
				"7[178]\\d{6}",
				[
					8
				]
			],
			0,
			0,
			0,
			0,
			[
				"5[158]\\d{6}",
				[
					8
				]
			]
		]
	],
	SN: [
		"221",
		"00",
		"[3789]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[379]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3(?:0(?:1[0-2]|80)|282|3(?:8[1-9]|9[3-9])|611)\\d{5}"
			],
			[
				"7(?:[06-8]\\d|21|90)\\d{6}"
			],
			[
				"800\\d{6}"
			],
			[
				"88[4689]\\d{6}"
			],
			0,
			0,
			0,
			0,
			[
				"39[01]\\d{6}|3392\\d{5}|93330\\d{4}"
			],
			[
				"81[02468]\\d{6}"
			]
		]
	],
	SO: [
		"252",
		"00",
		"[1-9]\\d{5,8}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{6})",
				"$1",
				[
					"[134]"
				]
			],
			[
				"(\\d)(\\d{6})",
				"$1 $2",
				[
					"[13-5]|2[0-79]"
				]
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"24|[67]"
				]
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"8[125]"
				]
			],
			[
				"(\\d{2})(\\d{5,7})",
				"$1 $2",
				[
					"15|28|6[1-35-9]|799|9[2-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"3[59]|4[89]|6[24-6]|79|8[08]|90"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d{1,2}|2[0-79]\\d|3[0-46-8]?\\d|4[0-7]?\\d|59\\d|8[125])\\d{4}",
				[
					6,
					7
				]
			],
			[
				"(?:15\\d|2(?:4\\d|8)|3[59]\\d{2}|4[89]\\d{2}|6[1-9]?\\d{2}|7(?:[1-8]\\d|9\\d{1,2})|8[08]\\d{2}|9(?:0[67]|[2-9])\\d)\\d{5}",
				[
					7,
					8,
					9
				]
			]
		]
	],
	SR: [
		"597",
		"00",
		"[2-8]\\d{5,6}",
		[
			6,
			7
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1-$2",
				[
					"[2-4]|5[2-58]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2-$3",
				[
					"56"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[6-8]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1-3]|3[0-7]|4\\d|5[2-58]|68\\d)\\d{4}"
			],
			[
				"(?:7[124-7]|8[125-9])\\d{5}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"56\\d{4}",
				[
					6
				]
			]
		]
	],
	SS: [
		"211",
		"00",
		"[19]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				0,
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"18\\d{7}"
			],
			[
				"(?:12|9[1257])\\d{7}"
			]
		]
	],
	ST: [
		"239",
		"00",
		"[29]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22\\d{5}"
			],
			[
				"9(?:0(?:0[5-9]|[1-9]\\d)|[89]\\d{2})\\d{3}"
			]
		]
	],
	SV: [
		"503",
		"00",
		"[267]\\d{7}|[89]\\d{6}(?:\\d{4})?",
		[
			7,
			8,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[267]"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[89]"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[1-6]\\d{6}",
				[
					8
				]
			],
			[
				"[67]\\d{7}",
				[
					8
				]
			],
			[
				"800\\d{4}(?:\\d{4})?",
				[
					7,
					11
				]
			],
			[
				"900\\d{4}(?:\\d{4})?",
				[
					7,
					11
				]
			]
		]
	],
	SX: [
		"1",
		"011",
		"[5789]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"721",
		[
			[
				"7215(?:4[2-8]|8[239]|9[056])\\d{4}"
			],
			[
				"7215(?:1[02]|2\\d|5[034679]|8[014-8])\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	SY: [
		"963",
		"00",
		"[1-59]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-5]"
				]
			],
			[
				"(9\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		1,
		0,
		[
			[
				"(?:1(?:1\\d?|4\\d|[2356])|2(?:1\\d?|[235])|3(?:[13]\\d|4)|4[13]|5[1-3])\\d{6}"
			],
			[
				"9(?:22|[3-589]\\d|6[024-9])\\d{6}",
				[
					9
				]
			]
		]
	],
	SZ: [
		"268",
		"00",
		"[0237]\\d{7}|900\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[0237]"
				]
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"900"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[23][2-5]\\d{6}",
				[
					8
				]
			],
			[
				"7[6-9]\\d{6}",
				[
					8
				]
			],
			[
				"0800\\d{4}",
				[
					8
				]
			],
			[
				"900\\d{6}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			[
				"70\\d{6}",
				[
					8
				]
			]
		]
	],
	TA: [
		"290",
		"00",
		"8\\d{3}",
		[
			4
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"8\\d{3}"
			]
		]
	],
	TC: [
		"1",
		"011",
		"[5689]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"649",
		[
			[
				"649(?:712|9(?:4\\d|50))\\d{4}"
			],
			[
				"649(?:2(?:3[129]|4[1-7])|3(?:3[1-389]|4[1-8])|4[34][1-3])\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			],
			0,
			0,
			0,
			[
				"64971[01]\\d{4}"
			]
		]
	],
	TD: [
		"235",
		"00|16",
		"[2679]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22(?:[3789]0|5[0-5]|6[89])\\d{4}"
			],
			[
				"(?:6[023568]\\d|77\\d|9\\d{2})\\d{5}"
			]
		],
		"00"
	],
	TG: [
		"228",
		"00",
		"[279]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[279]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2[2-7]|3[23]|4[45]|55|6[67]|77)\\d{5}"
			],
			[
				"(?:7[09]|9[0-36-9])\\d{6}"
			]
		]
	],
	TH: [
		"66",
		"00[1-9]",
		"1\\d{8,9}|[2-9]\\d{7,8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(2)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"([13-9]\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"14|[3-9]"
				]
			],
			[
				"(1[89]00)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[89]0",
					"1[89]00"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[2-9]|4[2-5]|5[2-6]|7[3-7])\\d{6}",
				[
					8
				]
			],
			[
				"(?:14|6[1-6]|[89]\\d)\\d{7}",
				[
					9
				]
			],
			[
				"1800\\d{6}",
				[
					10
				]
			],
			[
				"1900\\d{6}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"6[08]\\d{7}",
				[
					9
				]
			]
		]
	],
	TJ: [
		"992",
		"810",
		"[3-57-9]\\d{8}",
		[
			9
		],
		[
			[
				"([349]\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[34]7|91[78]"
				]
			],
			[
				"([457-9]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4[148]|[578]|9(?:[0235-9]|1[59])"
				]
			],
			[
				"(331700)(\\d)(\\d{2})",
				"$1 $2 $3",
				[
					"331",
					"3317",
					"33170",
					"331700"
				]
			],
			[
				"(\\d{4})(\\d)(\\d{4})",
				"$1 $2 $3",
				[
					"3[1-5]",
					"3(?:[1245]|3(?:[02-9]|1[0-589]))"
				]
			]
		],
		"8",
		0,
		0,
		0,
		1,
		0,
		[
			[
				"(?:3(?:1[3-5]|2[245]|3[12]|4[24-7]|5[25]|72)|4(?:46|74|87))\\d{6}"
			],
			[
				"(?:41[18]|(?:5[05]|77|88|9[0-35-9])\\d)\\d{6}"
			]
		],
		"8~10"
	],
	TK: [
		"690",
		"00",
		"[2-47]\\d{3,6}",
		[
			4,
			5,
			6,
			7
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[2-4]|[34]\\d)\\d{2,5}"
			],
			[
				"7[2-4]\\d{2,5}"
			]
		]
	],
	TL: [
		"670",
		"00",
		"[2-489]\\d{6}|7\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-489]|70"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"7[3-8]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1-5]|3[1-9]|4[1-4])\\d{5}",
				[
					7
				]
			],
			[
				"7[3-8]\\d{6}",
				[
					8
				]
			],
			[
				"80\\d{5}",
				[
					7
				]
			],
			[
				"90\\d{5}",
				[
					7
				]
			],
			[
				"70\\d{5}",
				[
					7
				]
			]
		]
	],
	TM: [
		"993",
		"810",
		"[1-6]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"12"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"6"
				],
				"8 $1"
			],
			[
				"(\\d{3})(\\d)(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"13|[2-5]"
				]
			]
		],
		"8",
		"(8 $1)",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:2\\d|3[1-9])|2(?:22|4[0-35-8])|3(?:22|4[03-9])|4(?:22|3[128]|4\\d|6[15])|5(?:22|5[7-9]|6[014-689]))\\d{5}"
			],
			[
				"6[1-9]\\d{6}"
			]
		],
		"8~10"
	],
	TN: [
		"216",
		"00",
		"[2-57-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[0-2]\\d{3}|7\\d{4}|81200)\\d{3}"
			],
			[
				"(?:[259]\\d{3}|3(?:001|1(?:[1-35]\\d|40)|240|6[0-4]\\d|91\\d)|4[0-6]\\d{2})\\d{4}"
			],
			[
				"8010\\d{4}"
			],
			[
				"88\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"8[12]10\\d{4}"
			]
		]
	],
	TO: [
		"676",
		"00",
		"[02-8]\\d{4,6}",
		[
			5,
			7
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1-$2",
				[
					"[1-6]|7[0-4]|8[05]"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"7[5-9]|8[46-9]"
				]
			],
			[
				"(\\d{4})(\\d{3})",
				"$1 $2",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[1-8]|4[1-4]|[56]0|7[0149]|8[05])\\d{3}",
				[
					5
				]
			],
			[
				"(?:7[578]|8[46-9])\\d{5}",
				[
					7
				]
			],
			[
				"0800\\d{3}",
				[
					7
				]
			]
		]
	],
	TR: [
		"90",
		"00",
		"[2-589]\\d{9}|444\\d{4}",
		[
			7,
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[23]|4(?:[0-35-9]|4[0-35-9])"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5(?:[02-69]|1[06])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"51|[89]"
				],
				"0$1"
			],
			[
				"(444)(\\d{1})(\\d{3})",
				"$1 $2 $3",
				[
					"444"
				]
			]
		],
		"0",
		0,
		0,
		0,
		1,
		0,
		[
			[
				"(?:2(?:[13][26]|[28][2468]|[45][268]|[67][246])|3(?:[13][28]|[24-6][2468]|[78][02468]|92)|4(?:[16][246]|[23578][2468]|4[26]))\\d{7}",
				[
					10
				]
			],
			[
				"5(?:(?:0[15-7]|1[06]|24|[34]\\d|5[1-59]|9[46])\\d{2}|6161)\\d{5}",
				[
					10
				]
			],
			[
				"800\\d{7}",
				[
					10
				]
			],
			[
				"(?:8[89]8|900)\\d{7}",
				[
					10
				]
			],
			[
				"592(?:21[12]|461)\\d{4}",
				[
					10
				]
			],
			0,
			[
				"444\\d{4}|850\\d{7}"
			],
			[
				"512\\d{7}",
				[
					10
				]
			]
		]
	],
	TT: [
		"1",
		"011",
		"[589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"868",
		[
			[
				"868(?:2(?:01|[23]\\d)|6(?:0[7-9]|1[02-8]|2[1-9]|[3-69]\\d|7[0-79])|82[124])\\d{4}"
			],
			[
				"868(?:2(?:6[6-9]|[789]\\d)|3(?:0[1-9]|1[02-9]|[2-9]\\d)|4[6-9]\\d|6(?:20|78|8\\d)|7(?:0[1-9]|1[02-9]|[2-9]\\d))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	TV: [
		"688",
		"00",
		"[279]\\d{4,6}",
		[
			5,
			6,
			7
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[02-9]\\d{3}",
				[
					5
				]
			],
			[
				"(?:7[01]\\d|90)\\d{4}",
				[
					6,
					7
				]
			]
		]
	],
	TW: [
		"886",
		"0(?:0[25679]|19)",
		"2\\d{6,8}|[3-689]\\d{7,8}|7\\d{7,9}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(20)(\\d)(\\d{4})",
				"$1 $2 $3",
				[
					"202"
				]
			],
			[
				"([258]0)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"20[013-9]|50[0-46-9]|80[0-79]"
				]
			],
			[
				"([2-8])(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"[25][2-8]|[346]|[78][1-9]"
				]
			],
			[
				"(9\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				]
			],
			[
				"(70)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"70"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[235-8]\\d{3}|4\\d{2,3})|3[2-9]\\d{2}|4(?:[239]\\d|[78])\\d{2}|5[2-8]\\d{2}|6[235-79]\\d{2}|7[1-9]\\d{2}|8(?:2(?:3\\d|66)|[7-9]\\d{2}))\\d{4}",
				[
					8,
					9
				]
			],
			[
				"9[0-8]\\d{7}",
				[
					9
				]
			],
			[
				"80[0-79]\\d{6}",
				[
					9
				]
			],
			[
				"20(?:2|[013-9]\\d{2})\\d{4}",
				[
					7,
					9
				]
			],
			[
				"99\\d{7}",
				[
					9
				]
			],
			0,
			[
				"50[0-46-9]\\d{6}",
				[
					9
				]
			],
			0,
			[
				"70\\d{8}",
				[
					10
				]
			]
		],
		0,
		"#"
	],
	TZ: [
		"255",
		"00[056]",
		"\\d{9}",
		[
			7,
			9
		],
		[
			[
				"([24]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[24]"
				]
			],
			[
				"([67]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[67]"
				]
			],
			[
				"([89]\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"2[2-8]\\d{7}"
			],
			[
				"(?:6[2-9]|7[13-9])\\d{7}",
				[
					9
				]
			],
			[
				"80[08]\\d{6}",
				[
					9
				]
			],
			[
				"90\\d{7}",
				[
					9
				]
			],
			0,
			0,
			0,
			0,
			[
				"41\\d{7}",
				[
					9
				]
			],
			[
				"8(?:40|6[01])\\d{6}",
				[
					9
				]
			]
		]
	],
	UA: [
		"380",
		"00",
		"[3-9]\\d{8}",
		[
			9
		],
		[
			[
				"([3-9]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[38]9|4(?:[45][0-5]|87)|5(?:0|[67][37])|6[36-8]|7|9[1-9]",
					"[38]9|4(?:[45][0-5]|87)|5(?:0|6(?:3[14-7]|7)|7[37])|6[36-8]|7|9[1-9]"
				]
			],
			[
				"([3-689]\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:3[1-8]|4[136-8])2|5(?:[12457]2|6[24])|6(?:[12][29]|[49]2|5[24])|8[0-8]|90",
					"3(?:[1-46-8]2[013-9]|52)|4(?:[1378]2|62[013-9])|5(?:[12457]2|6[24])|6(?:[12][29]|[49]2|5[24])|8[0-8]|90"
				]
			],
			[
				"([3-6]\\d{3})(\\d{5})",
				"$1 $2",
				[
					"3(?:[1-46-8]|5[013-9])|4(?:[137][013-9]|[45][6-9]|6|8[4-6])|5(?:[1245][013-9]|3|6[0135689]|7[4-6])|6(?:[12][13-8]|[49][013-9]|5[0135-9])",
					"3(?:[1-46-8](?:[013-9]|22)|5[013-9])|4(?:[137][013-9]|[45][6-9]|6(?:[013-9]|22)|8[4-6])|5(?:[1245][013-9]|3|6(?:[015689]|3[02389])|7[4-6])|6(?:[12][13-8]|[49][013-9]|5[0135-9])"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[1-8]|4[13-8]|5[1-7]|6[12459])\\d{7}"
			],
			[
				"(?:39|50|6[36-8]|7[1-3]|9[1-9])\\d{7}"
			],
			[
				"800\\d{6}"
			],
			[
				"900[2-49]\\d{5}"
			],
			0,
			0,
			0,
			0,
			[
				"89[1-579]\\d{6}"
			]
		],
		"0~0"
	],
	UG: [
		"256",
		"00[057]",
		"\\d{9}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"20[0-8]|4(?:6[45]|[7-9])|[7-9]",
					"20(?:[013-8]|2[5-9])|4(?:6[45]|[7-9])|[7-9]"
				]
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"3|4(?:[1-5]|6[0-36-9])"
				]
			],
			[
				"(2024)(\\d{5})",
				"$1 $2",
				[
					"202",
					"2024"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"20(?:[0147]\\d{3}|2(?:40|[5-9]\\d)\\d|3(?:0[0-4]|[2367]\\d)\\d|5[0-4]\\d{2}|6(?:00[0-2]|30[0-4]|[5-9]\\d{2})|8[0-2]\\d{2})\\d{3}|[34]\\d{8}"
			],
			[
				"7(?:[09][0-7]\\d|[1578]\\d{2}|2(?:[03]\\d|60)|30\\d|4[0-4]\\d)\\d{5}"
			],
			[
				"800[123]\\d{5}"
			],
			[
				"90[123]\\d{6}"
			]
		]
	],
	US: [
		"1",
		"011",
		"[2-9]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		1,
		0,
		[
			[
				"(?:2(?:0[1-35-9]|1[02-9]|2[03-589]|3[149]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-57-9]|1[02-9]|2[0135]|3[0-24679]|4[67]|5[12]|6[014]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[0235]|58|6[39]|7[0589]|8[04])|5(?:0[1-57-9]|1[0235-8]|20|3[0149]|4[01]|5[19]|6[1-47]|7[013-5]|8[056])|6(?:0[1-35-9]|1[024-9]|2[03689]|3[016]|4[16]|5[017]|6[0-279]|78|8[012])|7(?:0[1-46-8]|1[2-9]|2[04-7]|3[1247]|4[037]|5[47]|6[02359]|7[02-59]|8[156])|8(?:0[1-68]|1[02-8]|2[08]|3[0-258]|4[3578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[014678]|4[0179]|5[12469]|7[0-3589]|8[04-69]))[2-9]\\d{6}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			],
			0,
			[
				"710[2-9]\\d{6}"
			]
		]
	],
	UY: [
		"598",
		"0(?:1[3-9]\\d|0)",
		"[2489]\\d{6,7}",
		[
			7,
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[24]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[89]0"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}|4[2-7]\\d{6}",
				[
					8
				]
			],
			[
				"9[1-9]\\d{6}",
				[
					8
				]
			],
			[
				"80[05]\\d{4}",
				[
					7
				]
			],
			[
				"90[0-8]\\d{4}",
				[
					7
				]
			]
		],
		"00",
		" int. "
	],
	UZ: [
		"998",
		"810",
		"[679]\\d{8}",
		[
			9
		],
		[
			[
				"([679]\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[679]"
				]
			]
		],
		"8",
		"8 $1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:6(?:1(?:22|3[124]|4[1-4]|5[123578]|64)|2(?:22|3[0-57-9]|41)|5(?:22|3[3-7]|5[024-8])|6\\d{2}|7(?:[23]\\d|7[69])|9(?:22|4[1-8]|6[135]))|7(?:0(?:5[4-9]|6[0146]|7[12456]|9[135-8])|1[12]\\d|2(?:22|3[1345789]|4[123579]|5[14])|3(?:2\\d|3[1578]|4[1-35-7]|5[1-57]|61)|4(?:2\\d|3[1-579]|7[1-79])|5(?:22|5[1-9]|6[1457])|6(?:22|3[12457]|4[13-8])|9(?:22|5[1-9])))\\d{5}"
			],
			[
				"6(?:1(?:2(?:98|2[01])|35[0-4]|50\\d|61[23]|7(?:[01][017]|4\\d|55|9[5-9]))|2(?:11\\d|2(?:[12]1|9[01379])|5(?:[126]\\d|3[0-4])|7\\d{2})|5(?:19[01]|2(?:27|9[26])|30\\d|59\\d|7\\d{2})|6(?:2(?:1[5-9]|2[0367]|38|41|52|60)|3[79]\\d|4(?:56|83)|7(?:[07]\\d|1[017]|3[07]|4[047]|5[057]|67|8[0178]|9[79])|9[0-3]\\d)|7(?:2(?:24|3[237]|4[5-9]|7[15-8])|5(?:7[12]|8[0589])|7(?:0\\d|[39][07])|9(?:0\\d|7[079]))|9(?:2(?:1[1267]|5\\d|3[01]|7[0-4])|5[67]\\d|6(?:2[0-26]|8\\d)|7\\d{2}))\\d{4}|7(?:0\\d{3}|1(?:13[01]|6(?:0[47]|1[67]|66)|71[3-69]|98\\d)|2(?:2(?:2[79]|95)|3(?:2[5-9]|6[0-6])|57\\d|7(?:0\\d|1[17]|2[27]|3[37]|44|5[057]|66|88))|3(?:2(?:1[0-6]|21|3[469]|7[159])|33\\d|5(?:0[0-4]|5[579]|9\\d)|7(?:[0-3579]\\d|4[0467]|6[67]|8[078])|9[4-6]\\d)|4(?:2(?:29|5[0257]|6[0-7]|7[1-57])|5(?:1[0-4]|8\\d|9[5-9])|7(?:0\\d|1[024589]|2[0127]|3[0137]|[46][07]|5[01]|7[5-9]|9[079])|9(?:7[015-9]|[89]\\d))|5(?:112|2(?:0\\d|2[29]|[49]4)|3[1568]\\d|52[6-9]|7(?:0[01578]|1[017]|[23]7|4[047]|[5-7]\\d|8[78]|9[079]))|6(?:2(?:2[1245]|4[2-4])|39\\d|41[179]|5(?:[349]\\d|5[0-2])|7(?:0[017]|[13]\\d|22|44|55|67|88))|9(?:22[128]|3(?:2[0-4]|7\\d)|57[05629]|7(?:2[05-9]|3[37]|4\\d|60|7[2579]|87|9[07])))\\d{4}|9[0-57-9]\\d{7}"
			]
		],
		"8~10"
	],
	VA: [
		"39",
		"00",
		"(?:0(?:878\\d{5}|6698\\d{5})|[1589]\\d{5,10}|3(?:[12457-9]\\d{8}|[36]\\d{7,9}))",
		[
			6,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[26]|55"
				]
			],
			[
				"(0[26])(\\d{4})(\\d{5})",
				"$1 $2 $3",
				[
					"0[26]"
				]
			],
			[
				"(0[26])(\\d{4,6})",
				"$1 $2",
				[
					"0[26]"
				]
			],
			[
				"(0\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][0159]"
				]
			],
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"0[13-57-9][0159]|8(?:03|4[17]|9[245])",
					"0[13-57-9][0159]|8(?:03|4[17]|9(?:2|[45][0-4]))"
				]
			],
			[
				"(0\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][2-46-8]"
				]
			],
			[
				"(0\\d{3})(\\d{2,6})",
				"$1 $2",
				[
					"0[13-57-9][2-46-8]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[13]|8(?:00|4[08]|9[59])",
					"[13]|8(?:00|4[08]|9(?:5[5-9]|9))"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"894",
					"894[5-9]"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"06698\\d{5}",
				[
					10
				]
			],
			[
				"3(?:[12457-9]\\d{8}|6\\d{7,8}|3\\d{7,9})",
				[
					9,
					10,
					11
				]
			],
			[
				"80(?:0\\d{6}|3\\d{3})",
				[
					6,
					9
				]
			],
			[
				"0878\\d{5}|1(?:44|6[346])\\d{6}|89(?:2\\d{3}|4(?:[0-4]\\d{2}|[5-9]\\d{4})|5(?:[0-4]\\d{2}|[5-9]\\d{6})|9\\d{6})",
				[
					6,
					8,
					9,
					10
				]
			],
			[
				"1(?:78\\d|99)\\d{6}",
				[
					9,
					10
				]
			],
			0,
			0,
			0,
			[
				"55\\d{8}",
				[
					10
				]
			],
			[
				"84(?:[08]\\d{6}|[17]\\d{3})",
				[
					6,
					9
				]
			]
		]
	],
	VC: [
		"1",
		"011",
		"[5789]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"784",
		[
			[
				"784(?:266|3(?:6[6-9]|7\\d|8[0-24-6])|4(?:38|5[0-36-8]|8[0-8])|5(?:55|7[0-2]|93)|638|784)\\d{4}"
			],
			[
				"784(?:4(?:3[0-5]|5[45]|89|9[0-8])|5(?:2[6-9]|3[0-4]))\\d{4}"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	VE: [
		"58",
		"00",
		"[24589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{7})",
				"$1-$2"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:12|3[457-9]|[58][1-9]|[467]\\d|9[1-6])|50[01])\\d{7}"
			],
			[
				"4(?:1[24-8]|2[46])\\d{7}"
			],
			[
				"800\\d{7}"
			],
			[
				"900\\d{7}"
			]
		]
	],
	VG: [
		"1",
		"011",
		"[2589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"284",
		[
			[
				"284(?:(?:229|4(?:22|9[45])|774|8(?:52|6[459]))\\d{4}|496[0-5]\\d{3})"
			],
			[
				"284(?:(?:3(?:0[0-3]|4[0-7]|68|9[34])|4(?:4[0-6]|68|99)|54[0-57])\\d{4}|496[6-9]\\d{3})"
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	VI: [
		"1",
		"011",
		"[3589]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				0,
				0,
				0,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		"340",
		[
			[
				"340(?:2(?:01|2[0678]|44|77)|3(?:32|44)|4(?:22|7[34])|5(?:1[34]|55)|6(?:26|4[23]|77|9[023])|7(?:1[2-57-9]|27|7\\d)|884|998)\\d{4}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	VN: [
		"84",
		"00",
		"1\\d{6,9}|2\\d{9}|6\\d{6,7}|7\\d{6}|8\\d{6,8}|9\\d{8}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"([17]99)(\\d{4})",
				"$1 $2",
				[
					"[17]99"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[48]"
				]
			],
			[
				"(80)(\\d{5})",
				"$1 $2",
				[
					"80"
				]
			],
			[
				"(69\\d)(\\d{4,5})",
				"$1 $2",
				[
					"69"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"2[0-35-79]"
				]
			],
			[
				"([89]\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8(?:8|9[89])|9"
				]
			],
			[
				"(1[2689]\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:[26]|8[68]|99)"
				]
			],
			[
				"(86[689])(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"86[689]"
				]
			],
			[
				"(1[89]00)(\\d{4,6})",
				"$1 $2",
				[
					"1[89]0",
					"1[89]00"
				],
				"$1"
			]
		],
		"0",
		"0$1",
		0,
		0,
		1,
		0,
		[
			[
				"(?:2(?:0[3-9]|1[0-689]|2[0-25-9]|3[2-9]|4[2-8]|5[124-9]|6[0-39]|7[0-7]|8[2-7]|9[0-4679])\\d|866)\\d{6}",
				[
					9,
					10
				]
			],
			[
				"(?:9(?:[0-8]\\d|9[013-9])|1(?:2\\d|6[2-9]|8[68]|99)\\d|8(?:6[689]|8\\d|9[89]))\\d{6}",
				[
					9,
					10
				]
			],
			[
				"1800\\d{4,6}",
				[
					8,
					9,
					10
				]
			],
			[
				"1900\\d{4,6}",
				[
					8,
					9,
					10
				]
			],
			0,
			0,
			[
				"[17]99\\d{4}|69\\d{5,6}|80\\d{5}",
				[
					7,
					8
				]
			],
			0,
			[
				"992\\d{6}",
				[
					9
				]
			]
		]
	],
	VU: [
		"678",
		"00",
		"[2-57-9]\\d{4,6}",
		[
			5,
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[579]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[02-9]\\d|3(?:[5-7]\\d|8[0-8])|48[4-9]|88\\d)\\d{2}",
				[
					5
				]
			],
			[
				"(?:5(?:7[2-5]|[0-689]\\d)|7[013-7]\\d)\\d{4}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			[
				"3[03]\\d{3}|900\\d{4}"
			]
		]
	],
	WF: [
		"681",
		"00",
		"[4-8]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:50|68|72)\\d{4}"
			],
			[
				"(?:50|68|72|8[23])\\d{4}"
			]
		]
	],
	WS: [
		"685",
		"0",
		"[2-578]\\d{4,9}|6[1-9]\\d{3}",
		[
			5,
			6,
			7,
			10
		],
		[
			[
				"(8\\d{2})(\\d{3,7})",
				"$1 $2",
				[
					"8"
				]
			],
			[
				"(7\\d)(\\d{5})",
				"$1 $2",
				[
					"7"
				]
			],
			[
				"(\\d{5})",
				"$1",
				[
					"[2-6]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[2-5]\\d|6[1-9])\\d{3}",
				[
					5
				]
			],
			[
				"(?:7[25-7]|8(?:[3-7]|9\\d{3}))\\d{5}",
				[
					7,
					10
				]
			],
			[
				"800\\d{3}",
				[
					6
				]
			]
		]
	],
	XK: [
		"383",
		"00",
		"[23][89]\\d{6,7}|4[3-79]\\d{6}|[89]00\\d{5}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[23][89]|4[3-79]"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]00"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3"
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[89]0?|3(?:8|90?))\\d{6}"
			],
			[
				"4[3-79]\\d{6}",
				[
					8
				]
			],
			[
				"800\\d{5}",
				[
					8
				]
			],
			[
				"900\\d{5}",
				[
					8
				]
			]
		]
	],
	YE: [
		"967",
		"00",
		"[1-7]\\d{6,8}",
		[
			7,
			8,
			9
		],
		[
			[
				"([1-7])(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-6]|7[24-68]"
				]
			],
			[
				"(7\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7[0137]"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:7\\d|[2-68])|2[2-68]|3[2358]|4[2-58]|5[2-6]|6[3-58]|7[24-68])\\d{5}",
				[
					7,
					8
				]
			],
			[
				"7[0137]\\d{7}",
				[
					9
				]
			]
		]
	],
	YT: [
		"262",
		"00",
		"[268]\\d{8}",
		[
			9
		],
		[
			[
				"([268]\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[268]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		"269|63",
		[
			[
				"269(?:0[67]|5[01]|6\\d|[78]0)\\d{4}"
			],
			[
				"639(?:0[0-79]|1[019]|[267]\\d|3[09]|[45]0|9[04-79])\\d{4}"
			],
			[
				"80\\d{7}"
			]
		]
	],
	ZA: [
		"27",
		"00",
		"[1-79]\\d{8}|8\\d{4,8}",
		[
			5,
			6,
			7,
			8,
			9
		],
		[
			[
				"(860)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"860"
				]
			],
			[
				"(\\d{2})(\\d{3,4})",
				"$1 $2",
				[
					"8[1-4]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"8[1-4]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-79]|8(?:[0-57]|6[1-9])"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[0-8]|2[1-378]|3[1-69]|4\\d|5[1346-8])\\d{7}",
				[
					9
				]
			],
			[
				"(?:6\\d|7[0-46-9])\\d{7}|8(?:[1-4]\\d{1,5}|5\\d{5})\\d{2}"
			],
			[
				"80\\d{7}",
				[
					9
				]
			],
			[
				"86[2-9]\\d{6}|9[0-2]\\d{7}",
				[
					9
				]
			],
			0,
			0,
			[
				"861\\d{6}",
				[
					9
				]
			],
			0,
			[
				"87\\d{7}",
				[
					9
				]
			],
			[
				"860\\d{6}",
				[
					9
				]
			]
		]
	],
	ZM: [
		"260",
		"00",
		"[289]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				0,
				"$1"
			],
			[
				"([1-8])(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-8]"
				],
				"$1"
			],
			[
				"([29]\\d)(\\d{7})",
				"$1 $2",
				[
					"[29]"
				]
			],
			[
				"(800)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"800"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"21[1-8]\\d{6}"
			],
			[
				"9[4-9]\\d{7}"
			],
			[
				"800\\d{6}"
			]
		]
	],
	ZW: [
		"263",
		"00",
		"2(?:[0-57-9]\\d{3,8}|6(?:[14]\\d{7}|\\d{4}))|[13-69]\\d{4,9}|7\\d{8}|8[06]\\d{5,8}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"([49])(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"4|9[2-9]"
				]
			],
			[
				"(7\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(86\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"86[24]"
				]
			],
			[
				"([2356]\\d{2})(\\d{3,5})",
				"$1 $2",
				[
					"2(?:0[45]|2[278]|[49]8|[78])|3(?:[09]8|17|3[78]|7[1569]|8[37])|5[15][78]|6(?:[29]8|37|[68][78]|75)"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2(?:1[39]|2[0157]|31|[56][14]|7[35]|84)|329"
				]
			],
			[
				"([1-356]\\d)(\\d{3,5})",
				"$1 $2",
				[
					"1[3-9]|2[02569]|3[0-69]|5[05689]|6"
				]
			],
			[
				"([235]\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[23]9|54"
				]
			],
			[
				"([25]\\d{3})(\\d{3,5})",
				"$1 $2",
				[
					"(?:25|54)8",
					"258[23]|5483"
				]
			],
			[
				"(8\\d{3})(\\d{6})",
				"$1 $2",
				[
					"86"
				]
			],
			[
				"(80\\d)(\\d{4})",
				"$1 $2",
				[
					"80"
				]
			]
		],
		"0",
		"0$1",
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:0(?:4\\d|5\\d{2})|2[278]\\d|48\\d|7(?:[1-7]\\d|[089]\\d{2})|8(?:[2-57-9]|[146]\\d{2})|98)|3(?:08|17|3[78]|7(?:[19]|[56]\\d)|8[37]|98)|5[15][78]|6(?:28\\d{2}|37|6[78]|75\\d|98|8(?:7\\d|8)))\\d{3}|(?:2(?:1[39]|2[0157]|31|[56][14]|7[35]|84)|329)\\d{7}|(?:1(?:3\\d{2}|[4-8]|9\\d)|2(?:0\\d{2}|12|292|[569]\\d)|3(?:[26]|[013459]\\d)|5(?:0|1[2-4]|26|[37]2|5\\d{2}|[689]\\d)|6(?:[39]|[01246]\\d|[78]\\d{2}))\\d{3}|(?:29\\d|39|54)\\d{6}|(?:(?:25|54)83\\d|2582\\d{2}|65[2-8])\\d{2}|(?:4\\d{6,7}|9[2-9]\\d{4,5})"
			],
			[
				"(?:7(?:1\\d|3[2-9]|7[1-9]|8[2-5])|8644)\\d{6}",
				[
					9,
					10
				]
			],
			[
				"80(?:[01]\\d|20|8[0-8])\\d{3}",
				[
					7
				]
			],
			0,
			0,
			0,
			0,
			0,
			[
				"86(?:1[12]|30|55|77|8[368])\\d{6}",
				[
					10
				]
			]
		]
	],
	"001": [
		"979",
		0,
		"\\d{9}",
		[
			9
		],
		[
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			[
				"\\d{9}"
			]
		]
	]
};
var metadata = {
	version: version,
	country_calling_codes: country_calling_codes,
	countries: countries
};

var LegacyInternationalPhoneInput = function LegacyInternationalPhoneInput(_a) {
  var country = _a.country,
      props = __rest(_a, ["country"]); // This requires a window object to run, we can't test this in jsdom


  if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
    return React.createElement(CircleLoader, null);
  }

  return React.createElement(PhoneInput, __assign({}, props, {
    metadata: metadata,
    country: country || 'US',
    countrySelectComponent: CustomSelect,
    inputComponent: CustomText
  }));
};

var _extends$W = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$W(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}
var ArrowDown$1 = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$W(_ref, ["styles"]);

  return React.createElement("svg", _extends$W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, props), React.createElement("path", {
    d: "M3.115 5.295l-1.41 1.41 6 6 6-6-1.41-1.41-4.59 4.58z"
  }));
});

var styles$S = {"container":"styles_container__3kTSA relative block","icon":"styles_icon__2PKs6 absolute","select":"styles_select__3RiKl styles_select__3RiKl m0","xsmall":"styles_xsmall__w3SqN","small":"styles_small__PauKt","medium":"styles_medium__1zYKw","large":"styles_large__1-BiD","xlarge":"styles_xlarge__3RurA"};

var LegacySelect = function LegacySelect(_a) {
  var _b = _a.icon,
      icon = _b === void 0 ? true : _b,
      _c = _a.fieldSize,
      fieldSize = _c === void 0 ? 'medium' : _c,
      className = _a.className,
      children = _a.children,
      rest = __rest(_a, ["icon", "fieldSize", "className", "children"]);

  return React.createElement("span", {
    className: styles$S.container
  }, React.createElement("select", __assign({
    className: classnames(styles$S.select, styles$S[fieldSize], className)
  }, rest), children), icon && React.createElement(ArrowDown$1, {
    className: styles$S.icon
  }));
};

var styles$T = {"suggestionsContainerOpen":"styles_suggestionsContainerOpen__m-aEk","suggestionsList":"styles_suggestionsList__9R3ou m0 list-reset","suggestion":"styles_suggestion__KMPAA","suggestionHighlighted":"styles_suggestionHighlighted__FtQnX"};

var LegacyTypeahead =
/** @class */
function (_super) {
  __extends(LegacyTypeahead, _super);

  function LegacyTypeahead() {
    var _this = _super !== null && _super.apply(this, arguments) || this; // Render the input


    _this.renderInputComponent = function (inputProps) {
      var ref = inputProps.ref,
          props = __rest(inputProps // @ts-ignore: TextInput onChange is a different type, but compatible
      , ["ref"]); // @ts-ignore: TextInput onChange is a different type, but compatible


      return React.createElement(LegacyTextInput, __assign({
        inputRef: ref
      }, props));
    }; // Render an individual suggestion


    _this.renderSuggestion = function (suggestion, data) {
      var rawText = _this.props.getSuggestionValue(suggestion);

      var parts = parse(rawText, match(rawText, data.query));
      return React.createElement("div", null, parts.map(function (part, i) {
        if (part.highlight) {
          return React.createElement("b", {
            key: i
          }, part.text);
        } else {
          return part.text;
        }
      }));
    };

    return _this;
  }

  LegacyTypeahead.prototype.render = function () {
    var _this = this;

    return React.createElement(Autosuggest, __assign({
      ref: function ref(autosuggest) {
        if (autosuggest) {
          // @ts-ignore: the input exists, but it isn't documented
          _this.input = autosuggest.input;
        }
      },
      renderInputComponent: this.props.renderInputComponent || this.renderInputComponent,
      renderSuggestion: this.props.renderSuggestion || this.renderSuggestion,
      theme: styles$T
    }, this.props));
  };

  return LegacyTypeahead;
}(React.Component);

var isPhone = function isPhone(val) {
  if (cleanPhoneNumber(val).length < 10) {
    throw new Error('Please enter a valid phone number');
  }
};

var cleanPhoneNumber = function cleanPhoneNumber(phone) {
  // Convert to string
  if (Array.isArray(phone)) {
    phone = phone.join('');
  } // Naive attempt at stripping the country code


  if (phone.length === 12 && phone.startsWith('+1')) {
    phone = phone.slice(2);
  } // Strip whitespace, parens, pluses, underscores and dashes


  phone = phone.replace(/[\s\(\)\+\-\_]/g, '');
  return phone;
};

var LegacyPhoneInput = function LegacyPhoneInput(_a) {
  var _b = _a.placeholder,
      placeholder = _b === void 0 ? 'Enter a phone number' : _b,
      validate = _a.validate,
      defaultValue = _a.defaultValue,
      value = _a.value,
      props = __rest(_a, ["placeholder", "validate", "defaultValue", "value"]);

  var validators = [isPhone];
  var valueObject = {};

  if (defaultValue) {
    valueObject = {
      defaultValue: cleanPhoneNumber(defaultValue)
    };
  }

  if (value) {
    valueObject = {
      value: cleanPhoneNumber(value)
    };
  }

  if (validate) {
    validators = validators.concat(Array.isArray(validate) ? validate : [validate]);
  }

  return React.createElement(MaskedInput, __assign({
    mask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    placeholder: placeholder,
    guide: false,
    validate: validators
  }, valueObject, props));
};

var LegacyEmailInput = function LegacyEmailInput(_a) {
  var validate = _a.validate,
      props = __rest(_a, ["validate"]);

  var validators = ['email'];

  if (validate) {
    validators = validators.concat(Array.isArray(validate) ? validate : [validate]);
  }

  return React.createElement(LegacyTextInput, __assign({
    type: "email",
    placeholder: "e.g. jdoe@gmail.com",
    validate: validators
  }, props));
};

var isZip = function isZip(val) {
  if (val.length < 5) {
    throw new Error('Please enter a valid zip code');
  }
};

var LegacyZipInput = function LegacyZipInput(_a) {
  var _b = _a.placeholder,
      placeholder = _b === void 0 ? 'e.g. 55555' : _b,
      validate = _a.validate,
      props = __rest(_a, ["placeholder", "validate"]);

  var validators = [isZip];

  if (validate) {
    validators = validators.concat(Array.isArray(validate) ? validate : [validate]);
  }

  return React.createElement(MaskedInput, __assign({
    mask: [/\d/, /\d/, /\d/, /\d/, /\d/],
    placeholder: placeholder,
    guide: false,
    validate: validators
  }, props));
};

var styles$U = {"switch":"styles_switch__1hCbD relative inline-block","container":"styles_container__EKgI- absolute","activator":"styles_activator__38-Ay","label":"styles_label__2UL6I bold"};

var Switch = function Switch(_a) {
  var id = _a.id,
      className = _a.className,
      label = _a.label,
      props = __rest(_a, ["id", "className", "label"]);

  return React.createElement("div", {
    id: id,
    className: classnames(styles$U.switch, className)
  }, React.createElement("input", __assign({
    id: "toggle-switch-" + id,
    className: classnames(styles$U.container, styles$U.activator),
    type: "checkbox"
  }, omit(props, 'type', 'ref'))), React.createElement("label", {
    htmlFor: "toggle-switch-" + id
  }), label && React.createElement("span", {
    className: styles$U.label
  }, label));
};

var styles$V = {"tooltip":"styles_tooltip__m-tQi","header":"styles_header__2rF9H"};

var TooltipHeader = function TooltipHeader(_a) {
  var header = _a.header,
      className = _a.className;
  return React.createElement("div", {
    className: classnames(styles$V.header, className)
  }, header);
};

var Tooltip = function Tooltip(_a) {
  var target = _a.target,
      children = _a.children,
      className = _a.className,
      rest = __rest(_a, ["target", "children", "className"]);

  return React.createElement(Popover, __assign({}, rest), target, React.createElement("div", {
    className: classnames(styles$V.tooltip, className)
  }, children));
};

export { Avatar, Badge, Box, Button, Card, CategoryIcon, Checkbox, CurrencyField, DatePicker, Dropdown, Radio, ErrorBoundary, ExpertProfileHighlightIcon, FixedAspectRatioImage, BubbleLoader, FullPageLoader, FullBlockLoader, CircleLoader, GlobalLoader, Header, index as Icon, Image, Source$1 as Source, Picture$1 as Picture, Footer, FixedFooter, StickyFooter, ResponsiveGrid as Grid, GridItem, Logo, Modal, FancyModal, SimpleModal, FancyShowcase, FancyContent, ModalTitle, ModalFooter, ModalContent, MaskedField, Notification, PhoneField, Select$1 as Select, Slider$1 as Slider, TimePicker$1 as TimePicker, InlineEditor as InlineTextEditor, SellableCard, SellableImage, SellableBrandLogo, SellableTitleAndPrice, SellableAttributesAndPrice, TextAreaContainer as TextArea, TextField, index$1 as Typeahead, StarRating, index$2 as ContentfulImage, LegacyCurrencyInput, InlineCurrencyEditor as LegacyInlineCurrencyEditor, LegacyInput$1 as LegacyInput, LegacyInternationalPhoneInput, MaskedInput as LegacyMaskedInput, LegacySelect, TextAreaContainer as LegacyTextArea, LegacyTextInput, LegacyTypeahead, LegacyPhoneInput, LegacyEmailInput, LegacyZipInput, Switch, Tooltip, TooltipHeader };
//# sourceMappingURL=index.esm.js.map
